import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { resolvePlace, parseHunterQuery, classifyAndRespondStream, agentStep, searchDebrief, summarizeChat, searchContacts, searchContactsStream, batchSearchChunked, lookalikeStream, companyDiscoveryStream, verifyEmployment, namedContactsBatchStream, linkedinLookup, linkedinResearch, companyResearch, findPerson, findRole, linkdapiBatchEmpVerify, fetchPresets, savePreset, deletePreset } from '../services/hunter';
import {
  summarizeSearchOutcome, summarizeBatchOutcome, summarizeLookalikeOutcome, summarizeCompanyListOutcome, summarizeEnrichOutcome,
  summarizeVerifyOutcome, summarizeResearchOutcome, summarizeCompanyResearch, describeSearchOutcome,
} from '../lib/searchSummary';
import { verifyEmailInChat, verifyEmailsBulk, verifyEmailsStream } from '../services/verifier';
import { enrichCompaniesStream, resolveDomains, resolveWebsites, FIELD_CSV_HEADERS } from '../services/enrichService';
import { fetchProfile, saveProfileToAPI } from '../services/profileService';
import { fetchMemory, sendMemoryEvents, loadMemoryCached, saveMemoryCached, MEMORY_STORAGE_KEY } from '../services/memoryService';
import { summarizeMemory, memoryExcludeKeywords } from '../lib/memory';
import { REVENUE_CHIPS, parseRevenueRange, formatRevenueRange, extractRevenueAnswer } from '../lib/revenueRange';
import { STATE_NAMES, extractStateCode, resolveStateLocal, extractPlaceAnswer, placeClauseOf, placeScopeOf, describePlace, hasPlace } from '../lib/places';
import { findSessionPerson, companyAgrees } from '../lib/sessionPeople';
import { employmentLabel, shouldShowLinkedinUrl, resolveEmployment, currentTitleFor, deliverableLinkedinUrl, personLocationOf } from '../lib/employmentStatus';
import {
  industrySpecificLabel, shouldFlagIndustry, industryTooltip,
} from '../lib/industryClass';
import { useAuth } from '../context/AuthContext';
import { loadSellerProfile, PROFILE_KEY, INDUSTRY_OPTIONS } from './SellerProfilePage';
import { normalizeCompanySize, formatCompanySize } from '../lib/companySizeRange';
import WorkingIndicator from '../components/WorkingIndicator';
import ActivityFeed from '../components/ActivityFeed';
import ElapsedTime from '../components/ElapsedTime';
import ChatBlocks from '../components/chat/ChatBlocks';
import TypingIndicator from '../components/chat/TypingIndicator';
import AgentAvatar from '../components/common/AgentAvatar';
import { renderInline, renderProse } from '../components/chat/inlineMarkdown';
import CopyButton from '../components/common/CopyButton';
import { METER_BY_KEY, estimateLine, actualLine, neverMeteredLine } from '../lib/meters';
import { MAX_VERSIONS, markCtx, forkChat, switchVersion, branchInfoFor, editBlockReason, regenerateSource } from '../lib/chatBranches';
import * as XLSX from 'xlsx';
import ExportPanel from '../components/ExportPanel';
import HubSpotToast from '../components/HubSpotToast';
import OnboardingTour from '../components/onboarding/OnboardingTour';
import { TOUR_STEPS } from '../components/onboarding/tourSteps';
import styles from './VerifierPage.module.css';

// ─── Enrich file helpers ──────────────────────────────────────────────────────

// Auto-detect which column most likely contains company names
function detectEnrichCompanyCol(headers) {
  return headers.find(h => /^(company[\s_-]?name|company|organisation|organization|entity[\s_-]?name|firm)$/i.test(h.trim())) || headers[0] || '';
}

// Extract raw header + data rows from an already-read file buffer/text.
// Used to store original rows for the enriched download.
function extractRawRows(result, fileName) {
  const isXlsx = /\.xlsx?$/i.test(fileName);
  if (isXlsx) {
    const wb    = XLSX.read(result, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const rows  = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    if (!rows || rows.length < 2) return { headers: [], data: [] };
    return { headers: rows[0].map(h => String(h).trim()), data: rows.slice(1) };
  } else {
    const lines = result.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) return { headers: [], data: [] };
    const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g, '').trim());
    const data    = lines.slice(1).map(line => line.split(',').map(v => v.replace(/^"|"$/g, '').trim()));
    return { headers, data };
  }
}

// Detect and parse a named-contacts file (requires firstName + lastName columns).
// Returns an array of { firstName, lastName, companyName, companyDomain } or null
// if the file doesn't look like a named-contacts file.
function parseFileToPersons(result, fileName) {
  const isXlsx = /\.xlsx?$/i.test(fileName);
  let rows = [];

  if (isXlsx) {
    const wb      = XLSX.read(result, { type: 'array' });
    const sheet   = wb.Sheets[wb.SheetNames[0]];
    const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    if (rawRows.length < 2) return null;
    const firstRow = rawRows[0].map(v => String(v || '').trim());
    const hasFirst = firstRow.some(v => /^(first[\s_-]?name|firstname)$/i.test(v));
    const hasLast  = firstRow.some(v => /^(last[\s_-]?name|lastname)$/i.test(v));
    if (!hasFirst || !hasLast) return null;
    rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  } else {
    const lines = result.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) return null;
    const headers  = lines[0].split(',').map(h => h.replace(/^"|"$/g, '').trim());
    const hasFirst = headers.some(h => /^(first[\s_-]?name|firstname)$/i.test(h));
    const hasLast  = headers.some(h => /^(last[\s_-]?name|lastname)$/i.test(h));
    if (!hasFirst || !hasLast) return null;
    rows = lines.slice(1).map(line => {
      const vals = line.split(',').map(v => v.replace(/^"|"$/g, '').trim());
      return Object.fromEntries(headers.map((h, i) => [h, vals[i] || '']));
    });
  }

  if (!rows.length) return null;

  const headers      = Object.keys(rows[0]);
  const firstNameCol = headers.find(h => /^(first[\s_-]?name|firstname)$/i.test(h.trim()));
  const lastNameCol  = headers.find(h => /^(last[\s_-]?name|lastname)$/i.test(h.trim()));
  const companyCol   = headers.find(h => /^(company[\s_-]?name|company|organisation|organization)$/i.test(h.trim()));
  const domainCol    = headers.find(h => /^(domain|website|company[\s_-]?domain|company[\s_-]?url)$/i.test(h.trim()));

  if (!firstNameCol || !lastNameCol) return null;

  return rows
    .map(row => ({
      firstName:     (row[firstNameCol] || '').toString().trim(),
      lastName:      (row[lastNameCol]  || '').toString().trim(),
      companyName:   companyCol  ? (row[companyCol]  || '').toString().trim() : '',
      companyDomain: domainCol   ? (row[domainCol]   || '').toString().trim() : '',
    }))
    .filter(p => p.firstName || p.lastName);
}

// Download the finalized company → website list as a CSV (opens straight in Excel with
// company and resolved URL side by side).
function downloadWebsiteListFile(rows, sourceFileName) {
  const ws = XLSX.utils.aoa_to_sheet([
    ['Company', 'Website', 'Source'],
    ...rows.map(r => [r.company, r.website, r.source]),
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Websites');
  const base = (sourceFileName || 'companies').replace(/\.(csv|txt|xlsx?|xls)$/i, '');
  XLSX.writeFile(wb, `${base}-websites.csv`, { bookType: 'csv' });
}

// Detect and parse an email-list file (a column of email addresses, NOT a named-contacts
// file - those have firstName/lastName and take priority). Returns a deduped array of
// email strings, or null if the file doesn't look like an email list.
function parseFileToEmails(result, fileName) {
  const isXlsx = /\.xlsx?$/i.test(fileName);
  let headers = [], data = [];
  try {
    const extracted = extractRawRows(result, fileName);
    headers = extracted.headers;
    data    = extracted.data;
  } catch { return null; }
  if (!headers.length || !data.length) return null;

  const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  // Prefer an explicitly-named email column; otherwise find the column where most
  // values look like email addresses (headerless single-column lists still work).
  let col = headers.findIndex(h => /^(e-?mail([\s_-]?address)?|email)$/i.test(String(h).trim()));
  if (col === -1) {
    let best = -1, bestRatio = 0;
    for (let c = 0; c < headers.length; c++) {
      const vals = data.map(r => String(r[c] || '').trim()).filter(Boolean);
      if (!vals.length) continue;
      const ratio = vals.filter(v => EMAIL_RE.test(v)).length / vals.length;
      if (ratio > bestRatio) { bestRatio = ratio; best = c; }
    }
    if (bestRatio < 0.6) return null; // no column is mostly emails - not an email list
    col = best;
  }

  const emails = [];
  const seen = new Set();
  // The header row itself may be a data row (headerless list) - include it if it's an email.
  const headerVal = String(headers[col] || '').trim().toLowerCase();
  if (EMAIL_RE.test(headerVal) && !seen.has(headerVal)) { seen.add(headerVal); emails.push(headerVal); }
  for (const row of data) {
    const v = String(row[col] || '').trim().toLowerCase();
    if (EMAIL_RE.test(v) && !seen.has(v)) { seen.add(v); emails.push(v); }
  }
  return emails.length > 0 ? emails : null;
}

// A message that asks RepBuddy to write something ("Draft an intro email to Leo"), which must never
// start a contact search. Mirrors isWritingRequest in backend/services/chatPrompt.js, where the
// routes already keep the search tools and intents away from it; this is the last line, so a
// misroute that got past the server still cannot spend a lead credit on a person lookup.
const WRITING_VERB  = /^\s*(?:(?:please|can you|could you|would you|help me|now)\s+)*(?:draft|write|re-?write|re-?draft|compose|reword|polish|shorten|tighten)\b/i;
const WRITTEN_THING = /\b(?:e-?mails?|intros?|introductions?|messages?|notes?|follow[- ]?ups?|repl(?:y|ies)|subject(?: lines?)?|dms?|inmails?|outreach|openers?|pitch(?:es)?|sequences?|drafts?)\b/i;
function isWritingRequest(message) {
  const m = String(message || '');
  return WRITING_VERB.test(m) && WRITTEN_THING.test(m);
}
const WRITING_NOT_SEARCHED = "That reads as a request to write, so I didn't run a search. Ask again and I'll draft it.";

// ─── A failed step, as the customer sees it ───────────────────────────────────
// A chat step that throws used to print the raw error on the message ("Could not parse Claude
// response", "Stream failed with status 500", even "set ANTHROPIC_API_KEY in backend/.env"),
// under green ticks. Now it is a plain reply, with a chip that sends the request again when
// there is a typed request to resend. The raw error always goes to the console, and is shown
// under the reply only on non-production builds (same env read as EnvBanner).
const APP_ENV         = (process.env.REACT_APP_APP_ENV || 'prod').toLowerCase();
const SHOW_DEBUG      = APP_ENV !== 'prod' && APP_ENV !== 'production';
const FAILED_DEFAULT  = "Something went wrong on my end and I couldn't finish that. Try again, or rephrase it.";
const FAILED_PARSE    = 'I couldn\'t make sense of that request. No credits were used. Try rewording it, e.g. "10 VPs of Sales at fintech companies in Texas".';
function chatFailure(err, { say = FAILED_DEFAULT, retryPrompt = '' } = {}) {
  console.error('[chat failure]', err);
  return {
    status: 'error', done: true, thinking: false,
    isConversational: true, conversationalIntent: 'failure',
    // An error the services already wrote for people (a timeout, an allowance limit) says more
    // than the generic line, so it is shown as is.
    conversationalResponse: err?.userFacing && err.message ? err.message : say,
    conversationalBullets: [], conversationalBlocks: [],
    conversationalSuggestions: retryPrompt ? [{ label: 'Try again', prompt: retryPrompt }] : [],
    debugDetail: err?.message || String(err || ''),
    lines: [], contacts: [], valid: [], risky: [], invalid: [],
  };
}

// Normalise a company name for domain-map key lookup (mirrors backend)
function normEnrichName(name) {
  return (name || '')
    .toLowerCase()
    .replace(/[,.]?\s*(inc\.?|llc\.?|corp\.?|ltd\.?|limited|& co\.?|co\.)?\s*$/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Default fields enriched in the chat flow (no field picker needed)
const DEFAULT_ENRICH_FIELDS = ['employees', 'revenue', 'industry', 'website', 'hqLocation'];

// The enriched-file grid: the user's original columns plus Matched As, the selected fields and
// the status. Returns { headers, rows } for the Export Preview panel, which owns filename/format/
// column choices - the direct .xlsx download this replaced gave the user none of those.
function buildEnrichedExportRows(originalHeaders, originalData, colIndex, selectedFields, results) {
  const resultMap = {};
  (results || []).forEach(r => { resultMap[r.inputName] = r; });
  const headers = [...(originalHeaders || []), 'Matched As', ...(selectedFields || []).map(f => FIELD_CSV_HEADERS[f]), 'Enrich Status'];
  const rows = (originalData || []).map(row => {
    const name   = String(row[colIndex] || '').trim();
    const result = resultMap[name];
    const extra  = result
      ? [result.matchedAs || '', ...(selectedFields || []).map(f => result[f] || ''), result.status]
      : ['', ...(selectedFields || []).map(() => ''), 'Not Found'];
    return [...row.map(v => v ?? ''), ...extra];
  });
  return { headers, rows };
}

const STORAGE_KEY  = 'repbuddy_chats';
const BACKEND_URL  = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// ─── Session context helpers ──────────────────────────────────────────────────

// Build a compact contacts reference from a search result contacts array.
// Keeps only what the model needs to resolve follow-up questions.
function buildContactsRef(contacts, existingRef = []) {
  if (!Array.isArray(contacts) || contacts.length === 0) return existingRef;
  const startIndex = existingRef.length + 1;
  const newEntries = contacts.map((item, i) => ({
    index:   startIndex + i,
    name:    item.contact?.name    || '',
    email:   item.contact?.email   || item.result?.email || '',
    title:   item.contact?.title   || '',
    company: item.contact?.company || '',
    verdict: item.result?.verdict  || 'unknown',
  }));
  return [...existingRef, ...newEntries];
}

const SEEN_REF_MAX = 40;

// People a named-company search saw on its free look and did not pull: name and title, no
// email. Kept on the chat beside contacts_ref so "who is the CTO?" is answered by name a reload
// later. Anyone who is later pulled leaves this list; contacts_ref is the truth. Not the LinkedIn
// roster in linkedinRoster.js, which is a sourcing stage: this is session memory.
function buildSeenRef(people, company, existingRef = [], contactsRef = []) {
  const norm = s => String(s || '').trim().toLowerCase();
  const pulled = new Set(contactsRef.map(c => norm(c?.name)).filter(Boolean));
  const keep = existingRef.filter(r => r?.name && !pulled.has(norm(r.name)));
  const seen = new Set(keep.map(r => `${norm(r.name)}|${norm(r.company)}`));
  for (const p of (Array.isArray(people) ? people : [])) {
    const name = String(p?.name || '').trim();
    if (!name || !company) continue;
    const key = `${norm(name)}|${norm(company)}`;
    if (pulled.has(norm(name)) || seen.has(key)) continue;
    seen.add(key);
    keep.push({ name, title: p.title || '', company, linkedinUrl: p.linkedinUrl || '' });
  }
  return keep.slice(-SEEN_REF_MAX);
}

// Build a compact enrichment reference from enriched company results.
// Enrichment rows use `inputName` (original CSV name) and `matchedAs` (Lead411 match),
// not `company` or `name`. Domain comes from the `website` field.
function buildEnrichmentRef(results) {
  if (!Array.isArray(results) || results.length === 0) return [];
  return results.map((r, i) => ({
    index:     i + 1,
    company:   r.matchedAs || r.inputName || '',
    domain:    r.website   || '',
    status:    r.status    || '',
    employees: r.employees || '',
    revenue:   r.revenue   || '',
    industry:  r.industry  || '',
    location:  r.hqLocation || '',
  }));
}

// What a finished person brief leaves behind for follow-ups: who they are, and their company, so
// "what does their company do?" resolves to it without a guess at the name.
function researchContextFrom(data, slug, url) {
  const snap = data.companySnapshot || null;
  return {
    name:        data.subject  || slug,
    linkedinUrl: url,
    who:         data.who      || '',
    angles:      data.angles   || [],
    painPoints:  data.painPoints || [],
    roleStart:   data.roleStart || '',
    title:       data.freshTitle || '',
    company:            data.freshCompany || snap?.name || data.originalCompany || '',
    companyDomain:      snap?.domain || '',
    companyLinkedinUrl: snap?.linkedinUrl || '',
    companySnapshot:    snap,
  };
}

// Assemble the chatContext payload to send to /api/chat/respond.
function assembleChatContext(chat) {
  if (!chat) return null;
  const allMessages = chat.messages || [];
  // Send last 10 completed turns verbatim; older content is covered by context_summary
  const recentMessages = allMessages
    .filter(m => m.role === 'user' || (m.role === 'assistant' && m.done))
    .slice(-10)
    .map(m => ({
      role:    m.role,
      content: m.role === 'user' ? (m.contentText || m.content || '') : describeAssistantTurn(m),
    }));

  return {
    active_goal:        chat.active_goal        || null,
    active_constraints: chat.active_constraints || null,
    research_context:   chat.research_context   || null,
    context_summary:    chat.context_summary    || null,
    contacts_ref:       chat.contacts_ref       || [],
    seen_ref:           chat.seen_ref           || [],
    enrichment_ref:     chat.enrichment_ref     || [],
    companies_ref:      chat.companies_ref      || [],
    company_research_ref: chat.company_research_ref || [],
    recentMessages,
  };
}

// Build an updated active_goal after a regular single search completes.
function buildUpdatedGoal(existingGoal, criteria, contacts) {
  const valid    = (contacts || []).filter(c => c?.result?.verdict === 'valid').length;
  const allFound = (contacts || []).length;
  const prevFound     = existingGoal?.contactsFound    || 0;
  const prevVerified  = existingGoal?.contactsVerified || 0;

  const parts = [];
  if (criteria.titleKeyword) parts.push(criteria.titleKeyword.split(',')[0].trim());
  if (criteria.companyName || criteria.companyDomain) parts.push(`at ${criteria.companyName || criteria.companyDomain}`);
  if (criteria.stateArray) parts.push(`in ${criteria.stateArray}`);
  const objective = parts.length > 0
    ? `Finding ${parts.join(' ')}`
    : (existingGoal?.objective || 'Prospecting contacts');

  return {
    objective,
    stage:            'searching',
    contactsFound:    prevFound    + allFound,
    contactsVerified: prevVerified + valid,
    lastCriteria: {
      titleKeyword:  criteria.titleKeyword  || '',
      companyName:   criteria.companyName   || '',
      companyDomain: criteria.companyDomain || '',
      stateArray:    criteria.stateArray    || '',
    },
  };
}

// Build an updated active_goal after a batch search completes.
function buildUpdatedGoalForBatch(existingGoal, data, designation) {
  const allContacts = (data.results || []).flatMap(r => r.contacts || []);
  const valid       = allContacts.filter(c => c?.result?.verdict === 'valid').length;
  return {
    objective:        `Batch searching for ${designation || 'contacts'} across ${data.total || 0} companies`,
    stage:            'done',
    contactsFound:    (existingGoal?.contactsFound    || 0) + allContacts.length,
    contactsVerified: (existingGoal?.contactsVerified || 0) + valid,
  };
}

// Build an updated active_goal after enrichment completes.
function buildUpdatedGoalForEnrich(existingGoal, summary) {
  return {
    objective:         `Enriching company data`,
    stage:             'done',
    contactsFound:     existingGoal?.contactsFound    || 0,
    contactsVerified:  existingGoal?.contactsVerified || 0,
    enrichedCompanies: (summary?.enriched || 0) + (summary?.partial || 0),
  };
}

// Extract active search constraints from the criteria that was used in the last search.
// Returns null when nothing meaningful to show; called after every completed search.
function buildConstraintsFromCriteria(criteria) {
  if (!criteria) return null;
  const c = {};
  if (criteria.titleKeyword) {
    c.titles = criteria.titleKeyword.split(',').map(t => t.trim()).filter(Boolean);
  }
  if (criteria.stateArray) {
    c.location = criteria.stateArray.split(',').map(s => s.trim()).filter(Boolean);
  }
  if (criteria.industryOverride) {
    c.industry = criteria.industryOverride;
  }
  if (criteria.companyName) {
    c.company = criteria.companyName;
  } else if (criteria.companyDomain) {
    c.company = criteria.companyDomain;
  }
  if (criteria.employeeRange) {
    c.employeeRange = criteria.employeeRange;
  }
  if (criteria.revenueRange) {
    c.revenueRange = criteria.revenueRange;
  }
  if (criteria.companyStage) {
    c.stage = criteria.companyStage;
  }
  if (criteria.city) {
    c.city = criteria.city;
  }
  return Object.keys(c).length > 0 ? c : null;
}

// "5 startups in saas in Austin": the criteria of a company-list ask, in words, for the echo,
// the running line and the card title.
function describeCompanyCriteria(criteria = {}, count = null) {
  const stage = criteria.stage === 'smb' ? 'small companies'
    : criteria.stage === 'midmarket' ? 'mid-market companies'
    : criteria.stage === 'enterprise' ? 'enterprise companies'
    : criteria.stage ? `${criteria.stage}s`
    : 'companies';
  return [
    count != null ? String(count) : '',
    stage,
    criteria.industry ? `in ${criteria.industry}` : '',
    criteria.location ? `in ${criteria.location}` : '',
    criteria.extra ? `(${criteria.extra})` : '',
  ].filter(Boolean).join(' ');
}

const SUMMARIZE_THRESHOLD = 20; // summarize when chat exceeds this many messages

// Marks the single self-replacing "what is happening right now" status line. Any line
// starting with this is removed before the next one is appended, so the phase ticks in
// place instead of stacking up a wall of history.
const PHASE_PREFIX = '↻ ';

// How long to sit on a chat change before pushing it to the server. See the save effect for
// why this exists at all; the value is short enough that a closed tab loses nothing a user
// would notice, and long enough that a burst of status updates collapses into one write.
const CHAT_SAVE_DEBOUNCE_MS = 1200;

// How many lines of the live activity feed to keep. A window on what just happened, not a
// build log - and it is stored on the message, so an uncapped one would persist every company
// we ever looked at into the user's chat history.
const ACTIVITY_MAX = 6;

// Intents whose reply is prose the user reads, and therefore worth typing into a bubble as
// it streams. Everything else is a routing decision the switch below acts on silently.
// Mirrors CONVERSATIONAL_INTENTS in backend/services/chatPrompt.js.
const CONVERSATIONAL_INTENTS = [
  'general_chat', 'icp_question', 'constraint_remove', 'ambiguous_correction',
  'verify_contacts', 'ambiguous_followup', 'search_clarify', 'memory_update',
];

// The tool-calling loop (/api/chat/agent/step) in place of the classifier switch below.
//
// The model calls a tool (search_contacts, ask_user, remember, ...) and sees what came back
// before it writes, instead of returning one intent and never hearing the outcome. Every tool
// runs through the same flows as the intent it replaced, so the cards, credits and progress the
// user sees are unchanged; what changes is that the take on a result is the model's own next
// turn. Off in production builds until it has been through the live regression set; on in
// local dev; switchable per browser (localStorage repbuddy_agent_loop = '1' or '0') so it can
// be compared against the classifier on the same account. Any step failure falls back to the
// classifier path, which stays intact.
const AGENT_LOOP_ENABLED = (() => {
  try {
    const local = localStorage.getItem('repbuddy_agent_loop');
    if (local === '1') return true;
    if (local === '0') return false;
  } catch {}
  return process.env.REACT_APP_CHAT_AGENT_LOOP === '1';
})();
// Model calls per user message. Mirrors MAX_AGENT_STEPS in backend/services/chatTools.js; the
// server refuses a longer turn, this just stops the loop before it asks.
const AGENT_MAX_STEPS = 4;

// Merge a patch into a chat message.
//
// One rule beyond the spread: a message that has just been marked done drops its activity feed.
// Those rows are a window on work in progress and are never rendered on a finished message, but
// every message is persisted to Supabase - so keeping them would archive a build log of every
// company we ever looked at, on every search, forever. Applied here rather than at each of the
// dozens of done-payloads so it cannot be forgotten at a new one.
function mergeMessage(msg, patch) {
  const next = { ...msg, ...patch };
  if (next.done) {
    delete next.activity;
    // Same rule for the two live flags of a conversational reply. A finished message that
    // still says "streaming" would render a caret forever after a reload.
    delete next.thinking;
    delete next.streaming;
  }
  return next;
}

// A company's fit against the seller's ICP, as the company research judged it.
const FIT_LABELS = { strong: 'Strong fit', possible: 'Possible fit', weak: 'Weak fit', unknown: 'Fit unknown' };
function FitBadge({ fit }) {
  const f = FIT_LABELS[fit] ? fit : 'unknown';
  const tone = f === 'strong' ? styles.fitStrong : f === 'possible' ? styles.fitPossible : f === 'weak' ? styles.fitWeak : '';
  return <span className={`${styles.fitBadge} ${tone}`}>{FIT_LABELS[f]}</span>;
}

// Which of the four agent characters fronts a given assistant message. Derived from fields
// every message already has, so old chats get avatars too and nothing new is persisted.
function agentFor(msg) {
  if (!msg) return 'hunter';
  if (msg.isResearch || msg.isLinkedInPrompt || msg.isCompanyResearch) return 'composer';
  if (msg.conversationalIntent === 'icp_question' || msg.isIcpUpdate) return 'analyst';
  if (Array.isArray(msg.conversationalBlocks) && msg.conversationalBlocks.some(b => b?.type === 'email')) return 'composer';
  if (msg.isEmailVerify || (msg.valid?.length || msg.risky?.length || msg.invalid?.length) && !msg.contacts?.length) return 'verifier';
  return 'hunter';
}

// The model's view of a finished assistant turn. Conversational replies are their text;
// search results used to collapse to "[Found 14 contacts]", which left the model unable to
// answer "which company had the most of those?" about a list it had just produced. With a
// resultSummary on the message it gets the shape of the list, and its own debrief, back.
function describeAssistantTurn(m) {
  let text = m.conversationalResponse
    ? [m.conversationalResponse, ...(m.conversationalBullets || [])].join(' ')
    : (m.lines?.join(' ') || '');
  if (m.resultSummary) {
    text += ' ' + describeSearchOutcome(m.resultSummary, m.resultKind || 'search');
  } else {
    if (m.contacts?.length)      text += ` [Found ${m.contacts.length} contacts]`;
    if (m.enrichResults?.length) text += ` [Enriched ${m.enrichResults.length} companies]`;
    if (m.batchResults)          text += ` [Batch: ${m.batchResults.contactsFound || 0} contacts found]`;
  }
  // What RepBuddy said about it and what it offered, so "yes, the first one" resolves.
  if (m.debrief?.text) text += ` RepBuddy's take: ${m.debrief.text}`;
  const offered = [...(m.debrief?.suggestions || []), ...(m.conversationalSuggestions || [])];
  if (offered.length) text += ` Offered: ${offered.map(s => `"${s.label}" (${s.prompt})`).join('; ')}.`;
  return text;
}

// ─── Batch run merging ────────────────────────────────────────────────────────
// A batch that lost its connection partway delivers what it had; "Finish remaining" runs the
// untouched companies and the two halves are stitched back together here, so the user ends up
// with one result set and one CSV rather than two files to reconcile by hand.
//
// Dedupe is by verified email, matching what exportBatchCSV already does downstream - the resume
// passes the delivered addresses as excludeEmails, so a collision means the server changed its
// mind about someone rather than that we asked twice.
function mergeBatchRuns(prior, next) {
  const seenEmails = new Set();
  const rows = [];
  for (const r of [...(prior?.results || []), ...(next?.results || [])]) {
    const email = r?.result?.email?.toLowerCase?.().trim();
    if (r?.status === 'found' && email) {
      if (seenEmails.has(email)) continue;
      seenEmails.add(email);
    }
    rows.push(r);
  }

  const processedCompanyNames = [
    ...(prior?.processedCompanyNames || []),
    ...(next?.processedCompanyNames || []),
  ];
  const companiesWithResults = new Set(
    rows.filter(r => r.status === 'found').map(r => r.company)
  );

  return {
    ...next,
    // The original file size, not the size of whichever leg ran last.
    total:         prior?.total ?? next?.total ?? 0,
    results:       rows,
    contactsFound: rows.filter(r => r.status === 'found').length,
    notFound:      rows.filter(r => r.status === 'not_found' || r.status === 'error').length,
    companiesFound: companiesWithResults.size,
    found:          companiesWithResults.size,
    processedCompanyNames,
    companiesProcessed: processedCompanyNames.length,
    // Still partial only if the resumed leg itself came up short.
    partial:       !!next?.partial,
    partialReason: next?.partialReason || null,
  };
}

// The "what is happening right now" line for a batch, from one SSE event. Returns null for events
// that say nothing about progress, so the caller can leave the current line alone.
function batchTickerText(event) {
  if (event.type === 'start') {
    return `Searching ${event.total} ${event.total === 1 ? 'company' : 'companies'}…`;
  }
  if (event.type === 'progress') {
    const { current, total, company, phase } = event;
    if (phase === 'unlocking') return `Unlocking contacts at "${company}" (${current}/${total})…`;
    if (phase === 'verifying') return `Verifying emails at "${company}" (${current}/${total})…`;
    if (phase === 'done')      return `Processed "${company}": ${current}/${total} companies done`;
    return `Searching for "${company}" (${current}/${total})…`;
  }
  return null;
}

// The summary shown when a batch finishes - or when it stops early with results in hand.
function batchSummaryLines(data) {
  if (data?.partial) {
    const done = data.companiesProcessed ?? 0;
    const why  =
      data.partialReason === 'timeout' ? 'The connection went quiet' :
      data.partialReason === 'stopped' ? 'You stopped the run'       :
      data.partialReason === 'error'   ? 'The run hit an error'      :
      /* disconnect */                   'The connection dropped';
    return [
      `⚠️ ${why} after ${done} of ${data.total} ${data.total === 1 ? 'company' : 'companies'}.`,
      `${data.contactsFound || 0} contact${(data.contactsFound || 0) === 1 ? '' : 's'} ready to download below — you were only charged for these.`,
      'Use "Finish remaining" to run the companies that were missed.',
    ];
  }
  const companiesLine = `${data.companiesFound} of ${data.total} companies had results`;
  const contactsLine  = (data.contactsFound || 0) > (data.companiesFound || 0)
    ? ` · ${data.contactsFound} total contacts`
    : '';
  return [`✅ Batch complete: ${companiesLine}${contactsLine}. Download your results below.`];
}

// Verified addresses already handed over in this result - what a resume must exclude so the
// second leg does not re-deliver (and re-charge for) the first leg's people.
function deliveredEmailsOf(data) {
  return (data?.results || [])
    .filter(r => r.status === 'found' && r?.result?.email)
    .map(r => r.result.email);
}

// Batch rows carry `contact`, singular. This used to read `r.contacts`, which meant the array was
// empty on every batch ever run - so contacts_ref never populated and a follow-up question after a
// batch had no idea who had just been delivered. buildContactsRef wants { contact, result } pairs.
function contactPairsOf(data) {
  return (data?.results || [])
    .filter(r => r.status === 'found' && r.contact)
    .map(r => ({ contact: r.contact, result: r.result }));
}

// Batch rows for the session-level export: found people only, company backfilled from the
// uploaded list's label when web fallback left contact.company empty (same precedence
// exportBatchCSV uses). Not-found rows carry no person, so they stay out of a contact file.
function exportPairsOfBatch(data) {
  return (data?.results || [])
    .filter(r => r.status === 'found' && r.contact)
    .map(r => ({
      contact: r.contact.company ? r.contact : { ...r.contact, company: r.company || '' },
      result:  r.result || {},
    }));
}

// ─── Search result bridge ─────────────────────────────────────────────────────
// React 18 silently drops setChats() updaters when the component remounts
// (e.g. Supabase background token refresh triggers AuthProvider re-render →
// ProtectedRoute re-evaluates → VerifierPage gets a new instance). The async
// executeSearch closure holds a reference to the OLD instance's setChats, which
// React discards. Fix: store the final result in a module-level object (survives
// remounts) and dispatch a window event so the CURRENT instance can pick it up.
const _pendingSearchResults  = {}; // { [chatId__msgId]: resultPayload }
const _pendingConstraints    = {}; // { [chatId]: active_constraints } — same bridge, avoids stale closure

// The abort handle for each running search, keyed by chat.
//
// Module scope for the same reason the bridges above are: this component is remounted mid-search
// by a background Supabase token refresh, and a controller kept in a ref would be recreated empty
// at exactly the moment Stop needs it - leaving the button doing nothing, silently, which is the
// state it was already in.
//
// Per chat rather than one global, because concurrent searches in different chats are supported
// and stopping one must not touch the other.
const _searchAborts = {}; // { [chatId]: AbortController }

// The agent turn waiting on a tool that paused for the user (the preference card before a
// search, the count card before a look-alike list). When that flow finishes it resumes the loop
// from here with the result. Module scope for the remount reason above, mirrored onto the chat
// object (`agentTurn`) so a reload mid-search can still resume from the persisted copy.
const _agentTurns = {}; // { [chatId]: { query, turn, step, pending: { toolUseId, name } } }

// ─── localStorage helpers (fast cache only) ───────────────────────────────────

// A reply that was mid-stream when the tab closed is persisted with its live flags set (the
// save is debounced, not gated on done). Finish it on load: whatever text had arrived stands
// as the reply, and a reply that never started says so, rather than a caret that blinks
// forever next to a message nobody is writing.
function finalizeLiveFlags(chats) {
  if (!Array.isArray(chats)) return chats;
  let changed = false;
  const out = chats.map(c => {
    if (!Array.isArray(c?.messages)) return c;
    let touched = false;
    const messages = c.messages.map(m => {
      if (m?.role !== 'assistant' || m.done || !(m.thinking || m.streaming)) return m;
      touched = true;
      const hadText = !!(m.conversationalResponse && m.conversationalResponse.trim());
      const next = { ...m, done: true, status: 'done' };
      delete next.thinking;
      delete next.streaming;
      if (!hadText) {
        next.isConversational = true;
        next.conversationalIntent = 'general_chat';
        next.conversationalResponse = 'That reply was cut off by a reload. Ask again and I will pick it up.';
      }
      return next;
    });
    if (!touched) return c;
    changed = true;
    return { ...c, messages };
  });
  return changed ? out : chats;
}

function loadChats() {
  try { return finalizeLiveFlags(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')); }
  catch { return []; }
}

function saveChatsCached(chats) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(chats)); } catch {}
}

// ─── Supabase chat API helpers ────────────────────────────────────────────────

async function getAuthHeaders() {
  try {
    const { supabase } = await import('../lib/supabaseClient');
    if (!supabase) return null;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return null;
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` };
  } catch { return null; }
}

async function loadChatsFromAPI() {
  try {
    const headers = await getAuthHeaders();
    if (!headers) return null;
    const r = await fetch(`${BACKEND_URL}/api/chats`, { headers });
    if (!r.ok) return null;
    const { chats } = await r.json();
    return Array.isArray(chats) ? chats : null;
  } catch { return null; }
}

async function saveChatsToAPI(chats) {
  try {
    const headers = await getAuthHeaders();
    if (!headers) return;
    await fetch(`${BACKEND_URL}/api/chats`, {
      method:  'PUT',
      headers,
      body:    JSON.stringify({ chats }),
    });
  } catch { /* silent - localStorage is the fallback */ }
}

function newChatObj() {
  const now = Date.now();
  return { id: now.toString(), title: 'New search', createdAt: now, updatedAt: now, messages: [] };
}

function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

// Plain-text version of an assistant message for the message-level copy button.
// Only the human-readable card content (conversational answers, research briefs) is
// included - status lines and contact tables have their own export/copy affordances,
// so a message that is only those returns '' and gets no copy button.
function assistantCopyText(msg) {
  const parts = [];
  if (msg.isConversational) {
    if (msg.conversationalResponse) parts.push(msg.conversationalResponse);
    if (msg.conversationalBullets?.length) parts.push(msg.conversationalBullets.map(b => `• ${b}`).join('\n'));
    for (const b of msg.conversationalBlocks || []) {
      if (b?.type === 'list'  && b.items?.length) parts.push(b.items.map(it => `• ${it}`).join('\n'));
      if (b?.type === 'table' && b.rows?.length)  parts.push(b.rows.map(r => (Array.isArray(r) ? r.join('\t') : String(r))).join('\n'));
      if (b?.type === 'email') parts.push([b.subject ? `Subject: ${b.subject}` : '', b.body || ''].filter(Boolean).join('\n\n'));
    }
  }
  if (msg.isResearch && msg.researchData) {
    const r = msg.researchData;
    if (r.subject)            parts.push(r.subject);
    if (r.who)                parts.push(`Who they are:\n${r.who}`);
    if (r.angles?.length)     parts.push(`Outreach angles:\n${r.angles.map(a => `• ${a}`).join('\n')}`);
    if (r.painPoints?.length) parts.push(`Likely priorities / pain points:\n${r.painPoints.map(p => `• ${p}`).join('\n')}`);
    if (r.outreachDraft)      parts.push(r.outreachDraft);
  }
  if (msg.isCompanyResearch && msg.companyResearchData) {
    const d = msg.companyResearchData;
    parts.push(`${d.name}${d.domain ? ` (${d.domain})` : ''}`);
    if (d.whatTheyDo) parts.push(`What they do:\n${d.whatTheyDo}`);
    if (d.fit)        parts.push(`${FIT_LABELS[d.fit] || FIT_LABELS.unknown}${d.fitReason ? `: ${d.fitReason}` : ''}`);
    if (d.signals?.length) parts.push(`Signals:\n${d.signals.map(s => `• ${s}`).join('\n')}`);
  }
  return parts.filter(Boolean).join('\n\n').trim();
}

// ─── File parsing helpers ─────────────────────────────────────────────────────

function cleanDomain(raw) {
  if (!raw) return '';
  const host = String(raw)
    .replace(/\s*\(.*?\)/g, '')
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .split('/')[0]
    .split(':')[0]
    .trim()
    .toLowerCase();
  // Anything that is not a plausible hostname reads as "no website given" so the
  // resolve-domains step looks the real one up. Without this, a shifted column or a stray
  // label ("human capital services", "$5.362 billion") was shipped to Lead411 as
  // company_url (0 rows for every query) and made the roster's website guard reject every
  // LinkedIn page - run #48's Indeed miss.
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(host) ? host : '';
}

// RFC-4180-ish CSV split. The old per-line `split(',')` silently shifted every column left
// on rows with a comma inside a quoted field (revenue "$5,362,000,000", "Austin, TX") - the
// Website column then inherited its neighbour, with the consequences described on
// cleanDomain above. Handles quoted fields containing commas and newlines, and "" escapes;
// rows split on newlines only outside quotes.
function parseCsvText(text) {
  const rows = [];
  let field = '', row = [], inQuotes = false;
  const s = String(text || '');
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (inQuotes) {
      if (ch === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && s[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some(v => v.trim() !== '')) rows.push(row);
      row = [];
    } else {
      field += ch;
    }
  }
  row.push(field);
  if (row.some(v => v.trim() !== '')) rows.push(row);
  return rows.map(r => r.map(v => v.trim()));
}

// US_STATES and extractStateCode live in lib/places.js (shared with the card's location row).

// Returns true if the string looks like a job title rather than a natural-language sentence.
function looksLikeJobTitle(text) {
  if (!text || text.length > 80) return false;
  // Starts with filler verbs / pronouns → not a title
  if (/^(i\s+|please\s+)?(need|want|find|get|fetch|search|looking|give|show|retrieve|pull|provide)/i.test(text.trim())) return false;
  // Contains "each company / the list" → instruction, not a title
  if (/\b(each|every)\s+(compan|the\s+list)/i.test(text)) return false;
  // Contains "contact(s) from / at"
  if (/\bcontacts?\s+(from|at|in|for|of)\b/i.test(text)) return false;
  return true;
}

// Parse "VP of Sales" or "VP of Sales, 3" or "VP of Sales 3" from a chat message.
// Returns { designation, count } - count is null if not found.
// Produces a stable string fingerprint of the ICP filters that affect search results.
// If the hash changes between searches in the same chat, we bypass the cache automatically.
function hashProfile(profile) {
  if (!profile?.icp) return '';
  const icp = profile.icp;
  return JSON.stringify({
    titles:          [...(icp.titles          || [])].sort(),
    industries:      [...(icp.industries      || [])].sort(),
    companySize:     icp.companySize     || '',
    seniorities:     [...(icp.seniorities     || [])].sort(),
    excludeKeywords: [...(icp.excludeKeywords || [])].sort(),
    geo:             icp.geo             || '',
    geoStates:       [...(icp.geoStates       || [])].sort(),
  });
}

export function parseDesignationCount(message) {
  const msg = message.trim();
  // "designation, 3" or "designation / 3"
  const withSep = msg.match(/^(.+?)[,\/]\s*(\d{1,2})\s*$/);
  if (withSep) return { designation: withSep[1].trim(), count: parseInt(withSep[2], 10) };
  // "designation 3" (trailing 1–2 digit number, not a year)
  const trailing = msg.match(/^(.+?)\s+(\d{1,2})\s*$/);
  if (trailing) return { designation: trailing[1].trim(), count: parseInt(trailing[2], 10) };
  // Pure number (answer to "how many?")
  if (/^\d{1,2}$/.test(msg)) return { designation: '', count: parseInt(msg, 10) };
  return { designation: msg, count: null };
}

function autoDetectColumns(headers) {
  const nameCol        = headers.find(h => /entity\s*name|^company\s*name$|^company$|organisation|organization|^name$/i.test(h));
  const domainCol      = headers.find(h => /website|domain|url|web/i.test(h));
  const locationCol    = headers.find(h => /headquarter|^location$|^hq$|^state$|city.*state/i.test(h));
  const designationCol = headers.find(h => /^designation$|^title$|^role$|^position$|^job\s*title$/i.test(h));
  const countCol       = headers.find(h => /^count$|^number$|^qty$|^how\s*many$|^contacts\s*needed$/i.test(h));
  return { nameCol, domainCol, locationCol, designationCol, countCol };
}

function parseFileToCompanies(result, fileName) {
  const isXlsx = /\.xlsx?$/i.test(fileName);
  let rows = [];

  if (isXlsx) {
    const wb    = XLSX.read(result, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];

    // Read as raw 2D array to detect whether row 0 is real headers or actual data.
    // Files without a header row (e.g. "Float, https://www.float.tech/") cause XLSX to
    // treat the first data row as column keys, losing that company and dropping all domains.
    const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    if (rawRows.length) {
      const firstRow = rawRows[0].map(v => String(v || '').trim());
      const looksLikeHeader = firstRow.some(v =>
        /^(company[\s_-]?name|company|organisation|organization|entity[\s_-]?name|firm|name|website|domain|url|web[\s_-]?site|location|hq|state|designation|title|count)$/i.test(v)
      );
      if (looksLikeHeader) {
        rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      } else {
        // No standard headers - synthesize column names from first-row value types
        // and treat ALL rows (including row 0) as data.
        const syntheticHeaders = firstRow.map((v, i) => {
          if (/^https?:\/\/|^www\./i.test(v) || /^[a-z0-9][a-z0-9-]*\.[a-z]{2,}/i.test(v)) return 'Website';
          return i === 0 ? 'Company Name' : `Column${i + 1}`;
        });
        rows = rawRows.map(row =>
          Object.fromEntries(syntheticHeaders.map((h, i) => [h, String(row[i] || '').trim()]))
        );
      }
    }
  } else {
    const table = parseCsvText(result);
    if (table.length) {
      const isHeader = /company|name|entity|organisation|organization|website|url/i.test(table[0].join(','));
      if (isHeader) {
        const headers = table[0];
        rows = table.slice(1).map(vals =>
          Object.fromEntries(headers.map((h, i) => [h, vals[i] || '']))
        );
      } else {
        rows = table.map(([name, domain]) => ({ _name: name || '', _domain: domain || '' }));
      }
    }
  }

  if (!rows.length) return [];

  const headers = Object.keys(rows[0]);
  const { nameCol, domainCol, locationCol, designationCol, countCol } = autoDetectColumns(headers);

  return rows.map(row => ({
    name:        (nameCol        ? row[nameCol]        : row._name  || Object.values(row)[0] || '').toString().trim(),
    domain:      cleanDomain(domainCol      ? row[domainCol]      : row._domain || ''),
    state:       locationCol    ? extractStateCode(row[locationCol])             : '',
    designation: designationCol ? (row[designationCol] || '').toString().trim() : '',
    count:       countCol       ? (parseInt(row[countCol], 10) || 0)             : 0,
  })).filter(c => c.name);
}

// ─── Preference answer parser ─────────────────────────────────────────────────
// Parses a user's natural-language reply to the in-chat diversity question.
// Returns { perCompanyCap, industryDistribution }.
//   perCompanyCap        null = use auto formula  |  0 = no limit  |  N = exactly N
//   industryDistribution 'balanced' | [{industry,count},…]
// Reads a per-company cap out of a typed reply.
//   null = not stated (backend uses its auto formula)  |  0 = no limit  |  N = exactly N
//
// The previous version required a trailing "per company" / "from the same" on every numeric
// pattern, which meant the two shortest ways of answering — "2" and "max 2" — both parsed as
// "not stated" and silently fell back to the auto formula. The card's own hint suggested
// exactly those strings, so the most likely answers were the ones guaranteed not to work, and
// it read as the setting being ignored. It effectively was.
export function parseCapAnswer(text) {
  const lower = (text || '').toLowerCase().trim();

  // Tested first: "no more than 2" must NOT match this. It cannot - this branch requires `no`
  // followed directly by limit/cap/max/restriction, and "more" is none of those.
  if (/\b(?:no|any)\s*(?:limit|cap|max(?:imum)?|restriction)\b|unlimited|doesn'?t\s*matter|don'?t\s*(?:care|mind)|any\s*(?:number|amount)/.test(lower)) {
    return 0;
  }

  const m = lower.match(/max(?:imum)?\s*(?:of\s*)?(\d+)/)                                 // "max 2", "maximum of 3"
         || lower.match(/no\s*more\s*than\s*(\d+)/)                                       // "no more than 2"
         || lower.match(/(?:at\s*most\s*)?(\d+)\s*(?:contacts?\s*)?(?:per|from|each)\b/)   // "2 per company"
         || lower.match(/^(\d+)$/);                                                       // bare "2"
  const n = m ? parseInt(m[1], 10) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parsePreferenceAnswer(text, criteria, profileFilters) {
  const lower      = (text || '').toLowerCase().trim();
  const industries = (profileFilters?.industries || []).map(i => i.toLowerCase().trim());
  const limit      = criteria?.limit || 5;

  const perCompanyCap = parseCapAnswer(text);

  // ── Industry distribution ─────────────────────────────────────────────────
  let industryDistribution = 'balanced';
  if (industries.length > 1) {
    if (/balance[d]?|even(?:ly)?|equal(?:ly)?|split\s*evenly|50.?50/i.test(lower)) {
      industryDistribution = 'balanced';
    } else {
      const distribution = [];
      for (const ind of industries) {
        const esc = ind.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const m =
          lower.match(new RegExp(`(\\d+)\\s+${esc}`, 'i'))   ||
          lower.match(new RegExp(`${esc}\\s+(\\d+)`, 'i'))   ||
          lower.match(new RegExp(`(\\d+)\\s+from\\s+${esc}`, 'i'));
        if (m) distribution.push({ industry: ind, count: parseInt(m[1], 10) });
      }

      if (distribution.length === industries.length) {
        // All industries got explicit counts - use them directly
        industryDistribution = distribution;
      } else if (distribution.length > 0) {
        // Partial - fill missing industries with the remainder split evenly
        const specTotal  = distribution.reduce((s, d) => s + d.count, 0);
        const remainder  = Math.max(0, limit - specTotal);
        const missing    = industries.filter(i => !distribution.find(d => d.industry === i));
        const perMissing = missing.length ? Math.ceil(remainder / missing.length) : 0;
        for (const ind of missing) distribution.push({ industry: ind, count: perMissing });
        industryDistribution = distribution;
      }
      // If no numeric split detected, keep 'balanced'
    }
  }

  return { perCompanyCap, industryDistribution };
}

// Lightweight check: does a query message contain preference override signals?
//
// The `hasCapSignal` guard is load-bearing and deliberately stricter than parseCapAnswer.
// This runs against a whole query, where a bare number is the LIMIT — "find me 2 contacts"
// asks for two contacts, it does not ask for a cap of two per company. parseCapAnswer is
// permissive because it only ever sees a reply to a question that literally asks for a
// per-company number. Do not relax this guard to match it.
function extractInlinePrefs(text, criteria, profileFilters) {
  const lower = (text || '').toLowerCase();
  const hasCapSignal  = /(\d+)\s*per\s*company|max\s*(\d+)\s*(?:per|from)|no\s*(?:limit|cap)/i.test(lower);
  const industries    = (profileFilters?.industries || []).map(i => i.toLowerCase());
  const hasIndSignal  = industries.some(ind =>
    new RegExp(`more\\s+${ind.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}|mostly\\s+${ind.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i').test(lower)
  );
  if (!hasCapSignal && !hasIndSignal) return null;
  return parsePreferenceAnswer(text, criteria, profileFilters);
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function VerifierPage() {
  const { user, signOut }                = useAuth();
  const navigate                         = useNavigate();
  const [chats, setChats]               = useState(() => loadChats());
  // The latest chats for code that runs after an await and needs the current message list
  // without a stale closure (e.g. finding the bubble a flow has been updating "last assistant").
  const chatsRef = useRef(chats);
  useEffect(() => { chatsRef.current = chats; }, [chats]);

  // ── What RepBuddy remembers about this rep ──────────────────────────────
  // Loaded from the last cached copy for the first render, synced from the API on mount.
  // The summary (usual titles, states, cap, avoid list) is what the prompts and the
  // pre-search card read; the ref keeps it reachable after an await without a stale closure.
  const [memory, setMemory] = useState(() => loadMemoryCached());
  const memorySummaryRef = useRef(summarizeMemory(memory));
  useEffect(() => {
    memorySummaryRef.current = summarizeMemory(memory);
    saveMemoryCached(memory);
  }, [memory]);
  // Events are batched for a moment and folded in on the server, which returns the new blob.
  const memoryQueueRef = useRef({ events: [], timer: null });
  function recordMemory(events) {
    const list = (Array.isArray(events) ? events : [events]).filter(Boolean);
    if (list.length === 0) return;
    const q = memoryQueueRef.current;
    q.events.push(...list);
    if (q.timer) return;
    q.timer = setTimeout(async () => {
      const batch = q.events.splice(0);
      q.timer = null;
      const next = await sendMemoryEvents(batch);
      if (next) setMemory(next);
    }, 1500);
  }
  // Gates server saves until the initial /api/chats load finishes, so the empty-on-mount
  // state (localStorage is cleared at sign-out) can't clobber saved history. See below.
  const chatsHydratedRef                = useRef(false);
  // Persist activeChatId so returning from /profile doesn't reset to welcome screen
  const [activeChatId, setActiveChatId] = useState(() => localStorage.getItem('repbuddy_active_chat') || null);
  // Sidebar session management: per-row ⋯ menu ({ id, up } or null - `up` flips the
  // menu above the button when the row sits too close to the bottom of the viewport,
  // since .chatList is a scroll container and would clip a downward menu), inline
  // rename, and title/message search.
  const [chatMenu, setChatMenu]             = useState(null);
  // Two-stage delete: first click on the menu's Delete arms this, second click deletes.
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [renamingChatId, setRenamingChatId] = useState(null);
  const [renameDraft, setRenameDraft]       = useState('');
  const [chatSearch, setChatSearch]         = useState('');
  const chatMenuRef                         = useRef(null);
  const renameCancelledRef                  = useRef(false);
  const [input, setInput]               = useState('');
  // The prompt being edited in place, and its draft text. Separate from `input` so an edit never
  // touches what is sitting in the composer.
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editDraft, setEditDraft]       = useState('');
  const [runningChats, setRunningChats]  = useState({});
  // Helper: true when a specific chat has an active search/batch/enrich running
  const isRunning     = (id) => !!runningChats[id];
  const setRunning    = (id, val) => setRunningChats(prev => {
    if (val) return { ...prev, [id]: true };
    const n = { ...prev }; delete n[id]; return n;
  });
  const [credits, setCredits]           = useState(null);   // real Lead411 quota - null until fetched
  const [liRemaining, setLiRemaining]   = useState(null);   // LinkdAPI verifications left this window
  const [leadCredits, setLeadCredits]   = useState(null);   // user's lead-credit balance; { unlimited:true } for DEV_EMAILS devs
  const [logoOk, setLogoOk]             = useState(true);   // header logo image → lettermark fallback

  // ── Live meter balances for the "before/after" cost lines ───────────────────
  // `leadCredits` caches the whole /api/credits payload: the lead balance plus the three
  // usage meters (email_verifications, company_enrichments, website_lookups). These helpers
  // read a meter's live { remaining, allowance } and patch it after an action WITHOUT
  // dropping the other meters, so successive estimates stay accurate within a session.
  // Devs (unlimited) and un-provisioned schemas report metered:false → no estimate shown.
  const meterBalance = (meterKey) => {
    const lc = leadCredits;
    if (!lc || lc.unlimited || !lc.available) return { metered: false };
    if (meterKey === 'leads') {
      if (typeof lc.remaining !== 'number') return { metered: false };
      return { metered: true, remaining: lc.remaining, allowance: lc.allowance };
    }
    const metric = METER_BY_KEY[meterKey]?.metric;
    const m = metric && lc.meters ? lc.meters[metric] : null;
    if (!m || typeof m.remaining !== 'number') return { metered: false };
    return { metered: true, remaining: m.remaining, allowance: m.allowance };
  };
  // Merge a fresh lead balance in without discarding the cached usage meters.
  const applyLeadBalance = (credits) => {
    if (!credits || typeof credits.remaining !== 'number') return;
    setLeadCredits(prev => ({
      ...(prev || { available: true }),
      available: true, unlimited: false,
      allowance: credits.allowance, used: credits.used, remaining: credits.remaining,
    }));
  };
  // Patch a single usage meter (verify/enrich/website) from an action's `usage` payload.
  const applyMeterUsage = (metric, usage) => {
    if (!metric || !usage || typeof usage.remaining !== 'number') return;
    setLeadCredits(prev => {
      if (!prev) return prev;
      return { ...prev, meters: { ...(prev.meters || {}), [metric]: { allowance: usage.allowance, used: usage.used, remaining: usage.remaining } } };
    });
  };
  const [sidebarOpen, setSidebarOpen]   = useState(false);

  // Below 860px the sidebar is an overlay drawer covering most of the screen
  // (VerifierPage.module.css), so picking a chat has to dismiss it - otherwise the user taps
  // a search and lands on a chat they cannot see, with no obvious way back to it.
  //
  // The width is read at call time rather than tracked in state: it is only ever needed in
  // response to a tap, and a resize listener would re-render the whole page on every frame
  // of a phone rotating. The number matches the breakpoint in the stylesheet - the two have
  // to agree, so it says so.
  const MOBILE_DRAWER_MAX_WIDTH = 860;

  // The composer placeholder is a full sentence written for a 820px composer. On a phone it
  // is the longest text the box ever holds and wraps to three lines, which a fixed-height
  // composer cuts off mid-word - so the narrow screen gets a shorter sentence rather than an
  // ever-taller input eating the chat log.
  //
  // Read at render rather than tracked: this is a hint string, so a stale value between a
  // rotation and the next render costs nothing, where a resize listener on a component this
  // size costs a re-render per frame of the rotation.
  function isNarrowScreen() {
    return typeof window !== 'undefined' && window.innerWidth <= 640;
  }

  function closeSidebarOnMobile() {
    if (typeof window !== 'undefined' && window.innerWidth <= MOBILE_DRAWER_MAX_WIDTH) {
      setSidebarOpen(false);
    }
  }
  const [profileMenuOpen, setProfileMenuOpen] = useState(false); // top-right account dropdown
  const profileMenuRef = useRef(null);

  // ── First-run product walkthrough ──────────────────────────────────────────
  // Opens once per user on their first visit after signup, then stays shut because we
  // record it in localStorage under the user's id. The account menu offers a replay.
  const [tourOpen, setTourOpen] = useState(false);
  const tourKey = user ? `repbuddy_tour_done_${user.id}` : null;

  useEffect(() => {
    if (!tourKey) return;
    try {
      if (!localStorage.getItem(tourKey)) setTourOpen(true);
    } catch { /* storage blocked - skip the tour rather than loop */ }
  }, [tourKey]);

  const closeTour = useCallback(() => {
    setTourOpen(false);
    try { if (tourKey) localStorage.setItem(tourKey, '1'); } catch { /* ignore */ }
  }, [tourKey]);

  const replayTour = useCallback(() => {
    setProfileMenuOpen(false);
    setSidebarOpen(true); // the first anchored steps live in the sidebar
    setTourOpen(true);
  }, []);

  // ── Enrich disambiguation state ───────────────────────────────────────────
  // Set when user triggers enrichment and some companies are ambiguous (Google CSE
  // returned multiple plausible matches). Cleared once all are resolved.
  const [pendingEnrich, setPendingEnrich] = useState(null);
  // { chatId, companiesAll, resolvedDomains, ambiguousItems,
  //   decidedSet, headers, rawData, colIndex, fileName, selectedFields }

  // ── User switch detection - clear localStorage if a different user logs in ──
  useEffect(() => {
    if (!user) return;
    const storedUserId = localStorage.getItem('repbuddy_user_id');
    if (storedUserId && storedUserId !== user.id) {
      // Different user - wipe previous user's data
      localStorage.removeItem('repbuddy_chats');
      localStorage.removeItem('repbuddy_seller_profile');
      localStorage.removeItem('repbuddy_active_chat');
      localStorage.removeItem(MEMORY_STORAGE_KEY);
      // Reset state
      setChats([]);
      setActiveChatId(null);
      setSellerProfile(null);
      setMemory(null);
    }
    // Always store current user id
    localStorage.setItem('repbuddy_user_id', user.id);
  }, [user]);

  // Seller profile - fast init from localStorage, synced from API on mount
  const [sellerProfile, setSellerProfile] = useState(() => loadSellerProfile());

  useEffect(() => {
    // Fetch real Lead411 quota and Li remaining on mount
    import('../lib/supabaseClient').then(({ supabase }) => {
      if (!supabase) return;
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session?.access_token) return;
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        };
        // Lead411 quota
        fetch(`${BACKEND_URL}/api/lead411/quota`, { headers })
          .then(r => r.json())
          .then(d => { if (d.totalQuota != null) setCredits(d.totalQuota); })
          .catch(() => {});
        // Li remaining
        fetch(`${BACKEND_URL}/api/linkdapi/quota`, { headers })
          .then(r => r.json())
          .then(d => { if (d.remaining != null) setLiRemaining(d.remaining); })
          .catch(() => {});
        // User lead credits (trial 30, or { unlimited:true } for DEV_EMAILS developers)
        fetch(`${BACKEND_URL}/api/credits`, { headers })
          .then(r => r.json())
          .then(d => { if (d?.available) setLeadCredits(d); })
          .catch(() => {});
        // HubSpot connection status
        fetch(`${BACKEND_URL}/api/hubspot/status`, { headers })
          .then(r => r.ok ? r.json() : null)
          // Connected means "a push would work", not "a token row exists": the server
          // now also reports whether it can get a usable token and whether this backend
          // has HubSpot credentials at all. Older backends omit both fields; treat absent
          // as fine so the button does not vanish behind a version skew.
          .then(d => { if (d) setHubspotConnected(!!d.connected && d.tokenValid !== false && d.credentialsConfigured !== false); })
          .catch(() => {});
      });
    });

    // Load chats from Supabase on mount - merge so that:
    //   1. All local chats (including any in-progress ones) are preserved as-is.
    //   2. API chats that aren't in local state (older history) are appended.
    // We deliberately keep the LOCAL version when a chat exists in both, because
    // the local version is always more recent (it has any in-progress messages that
    // haven't been persisted to the API yet). Using the API version would silently
    // lose those messages and cause updateLastAssistant to find no target → blank screen.
    // Known trade-off: a rename/pin made on another device won't overwrite a chat this
    // device already has cached (same local-wins rule that applies to messages).
    fetchPresets().then(p => setPresets(p)).catch(() => {});

    loadChatsFromAPI().then(apiChats => {
      // apiChats === null means the load FAILED (network/auth) - we must NOT start
      // saving, or the empty initial state would clobber the server copy. Only once we
      // have a definitive answer from the server (even an empty array) do we merge and
      // then unlock server saves. This prevents losing history on a fresh login where
      // localStorage was cleared at sign-out (chats starts []).
      if (apiChats !== null) {
        if (apiChats.length > 0) {
          setChats(prev => {
            const prevIds = new Set(prev.map(c => c.id));
            // Only add chats from the API that aren't already represented locally
            const apiOnly = finalizeLiveFlags(apiChats.filter(c => !prevIds.has(c.id)));
            if (apiOnly.length === 0) return prev; // nothing new - skip re-render
            return [...prev, ...apiOnly];
          });
        }
        chatsHydratedRef.current = true; // server load done - safe to sync saves now
      }
    });

    // Sync profile from API once on mount.
    // Guard: if localStorage already has ICP titles but the API profile doesn't
    // (e.g. Supabase save failed silently), keep the richer local version so
    // the API sync doesn't silently erase the user's configured titles.
    fetchProfile().then(apiProfile => {
      if (apiProfile) {
        const localProfile = loadSellerProfile();
        const localHasTitles = localProfile?.icp?.titles?.length > 0;
        const apiHasTitles   = apiProfile?.icp?.titles?.length > 0;
        const profileToUse   = (localHasTitles && !apiHasTitles) ? localProfile : apiProfile;
        try { localStorage.setItem(PROFILE_KEY, JSON.stringify(profileToUse)); } catch {}
        setSellerProfile(profileToUse);
      }
    });

    // What RepBuddy remembers, from the server. Null means unavailable: keep the cached copy.
    fetchMemory().then(m => { if (m) setMemory(m); });

    // Refresh from localStorage whenever the window regains focus
    function onFocus() { setSellerProfile(loadSellerProfile()); }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Cancel / stop ref - set to true when user clicks Stop, false at start of each send
  const cancelRef       = useRef(false);
  const profileHashRef  = useRef({});  // { [chatId]: lastProfileHash } - detects profile changes per chat

  // The live "Searching for X (3/20)…" line during a batch, keyed by message id.
  //
  // Deliberately NOT in `chats`. Every write to chats triggers the autosave effect below, which
  // synchronously JSON.stringifies the entire chat list into localStorage and queues a multi-MB
  // PUT. A 50-company batch fires roughly four progress events per company, so routing the ticker
  // through chat state meant ~200 full serialisations of a ~2MB structure on the main thread -
  // the same thread draining the SSE reader. The ticker is transient by nature (mergeMessage
  // already drops `activity` on done for the same reason), so it lives here and chats is written
  // twice per batch instead: once at the start, once at the terminal.
  const [batchTicker, setBatchTicker] = useState({});
  const setTicker = useCallback((msgId, text) => {
    setBatchTicker(prev => (prev[msgId] === text ? prev : { ...prev, [msgId]: text }));
  }, []);
  const clearTicker = useCallback((msgId) => {
    setBatchTicker(prev => {
      if (!(msgId in prev)) return prev;
      const next = { ...prev };
      delete next[msgId];
      return next;
    });
  }, []);

  // ── The pre-search question card ─────────────────────────────────────────────
  // One pending card at a time, holding the criteria until every visible question is answered.
  // null = nothing pending.
  //
  // Deliberately NOT a session-wide latch. The previous design stored the answer in component
  // state that startNewChat and openChat never cleared, so after one reply the questions went
  // silent for the rest of the session - a brand-new chat inherited a cap set an hour earlier
  // in a different one, and the only way to be asked again was a page reload. That is the
  // "sometimes it asks, sometimes it doesn't" everyone hit. Scoping to a single search makes
  // the reset unnecessary rather than easy to forget.
  //
  // Shape: { chatId, msgId, criteria, needsCap, needsIndustry, meta,
  //          answers: { cap, pool, industry } }   // undefined = still unanswered
  const [pendingSearchAsk, setPendingSearchAsk] = useState(null);
  // The look-alike count card ("how many companies like X?"). Deliberately its own state, not
  // folded into pendingSearchAsk, so the look-alike flow shares nothing with the search pref card.
  const [pendingLookalike, setPendingLookalike] = useState(null);
  // The company-list count card ("how many startups in Austin?"), same shape as the look-alike one.
  const [pendingCompanies, setPendingCompanies] = useState(null);

  // Build ICP post-filters from the seller profile (recalculated whenever profile changes).
  // Seniorities are included here for post-filtering; filterLevel (Lead411 request param)
  // is handled separately in the search fallback - single seniority uses filterLevel,
  // multiple seniorities rely entirely on post-filtering.
  const profileFilters = sellerProfile?.icp ? {
    industries:      sellerProfile.icp.industries      || [],
    companySize:     sellerProfile.icp.companySize      || '',
    revenue:         sellerProfile.icp.revenue          || '',
    excludeKeywords: sellerProfile.icp.excludeKeywords  || [],
    seniorities:     sellerProfile.icp.seniorities      || [],
    geoStates:       sellerProfile.icp.geoStates        || [],
    geoLocationType: sellerProfile.icp.geoLocationType  || ['hq'],  // 'hq' | 'office' | 'employee'
  } : null;

  // Attachment state
  const [attachment, setAttachment]   = useState(null);    // { name, companies, hasDesignation, hasCount, hasLocation }
  const [pendingBatch, setPendingBatch] = useState(null);  // { chatId, designation, companies, fileName }
  const [pendingLinkedIn, setPendingLinkedIn] = useState(null); // { chatId, msgId, url, slug }
  const [isDragging, setIsDragging]       = useState(false);
  const [attachmentAction, setAttachmentAction] = useState(null); // 'find_contacts' | null
  const [presets, setPresets]               = useState([]);
  const [showPresetSave, setShowPresetSave] = useState(false);
  const [presetName, setPresetName]         = useState('');
  const [showPresetMenu, setShowPresetMenu] = useState(false);
  // Tracks per-email employment verification results for CSV export.
  // Stored in a ref so Li-button checks don't cause re-renders.
  const empVerifiedRef = useRef(new Map());

  // Tracks per-email email drafts generated via the Draft button or auto-generated at export.
  const draftRef = useRef(new Map());

  // Which export is currently generating drafts - keyed by msg.id + suffix.
  const [exportingKey, setExportingKey] = useState(null);
  // Export preview panel state: null when closed, otherwise the config for what to export.
  const [exportPanel, setExportPanel]   = useState(null);
  // HubSpot integration state.
  const [hubspotConnected,   setHubspotConnected]   = useState(false);
  const [isPushingToHubspot, setIsPushingToHubspot] = useState(false);
  const [hubspotToast,       setHubspotToast]       = useState(null);

  const fileInputRef = useRef(null);
  const bottomRef    = useRef(null);
  const inputRef     = useRef(null);

  const activeChat = chats.find(c => c.id === activeChatId) || null;
  const messages   = activeChat?.messages || [];
  // An edit left open in one chat does not follow the user into another.
  useEffect(() => { setEditingMsgId(null); }, [activeChatId]);

  // All unique chat contacts across the current session, batch results included - batch
  // messages store their people under batchResults rather than contacts (setting contacts
  // on them would render the regular results block), so derive pairs here instead.
  // Recomputes whenever a new result message lands in this chat.
  const sessionChatContacts = useMemo(() => {
    const all = messages
      .filter(m => m.role === 'assistant' && m.done)
      .flatMap(m => [...(m.contacts || []), ...exportPairsOfBatch(m.batchResults)]);
    return dedupeByPerson(all);
  }, [messages]); // dedupeByPerson is a pure function defined in component scope — safe to omit

  // Identity for the top-right profile menu - prefers the registered name, then the seller
  // profile name, then the email local-part. Initials drive the avatar.
  const userMeta    = user?.user_metadata || {};
  const fullName    = (userMeta.full_name || [userMeta.first_name, userMeta.last_name].filter(Boolean).join(' ') || sellerProfile?.name || '').trim();
  const displayName = fullName || (user?.email ? user.email.split('@')[0] : 'Account');
  const initials    = (fullName ? fullName.split(/\s+/).slice(0, 2).map(s => s[0]).join('') : (user?.email?.[0] || '?')).toUpperCase();

  // Save chats to localStorage (instant) + Supabase (debounced, cross-device).
  // The server save waits until chatsHydratedRef is set (after the initial API load),
  // so the empty-on-mount state can't overwrite existing server history before it loads.
  //
  // The network write is debounced because this effect fires on EVERY state change, and a
  // running search changes state on every status update. That was one PUT per update - around
  // thirty per search, each re-uploading the WHOLE chat list (419 KB on a real account) and
  // taking 1.5-2.4 s to land. It is the reason a long search also felt heavy in the browser.
  // The narrator raises the update count several-fold, so this had to become rate-limited
  // rather than merely tolerated.
  //
  // localStorage stays synchronous: it is cheap, and it is the only thing between a closed tab
  // and lost history.
  const latestChatsRef = useRef(chats);
  const savedChatsRef  = useRef(null);
  useEffect(() => {
    latestChatsRef.current = chats;
    saveChatsCached(chats);
    if (!chatsHydratedRef.current) return undefined;
    const t = setTimeout(() => {
      savedChatsRef.current = chats;
      saveChatsToAPI(chats);
    }, CHAT_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [chats]);

  // Every change cancels the pending write, so the last one before an unmount would never be
  // sent - and unmount here is routine, not rare: a Supabase token refresh remounts this page
  // (see the search-result bridge above). Flush whatever the debounce still owed.
  useEffect(() => () => {
    if (chatsHydratedRef.current && latestChatsRef.current !== savedChatsRef.current) {
      saveChatsToAPI(latestChatsRef.current);
    }
  }, []);
  useEffect(() => { localStorage.setItem('repbuddy_active_chat', activeChatId || ''); }, [activeChatId]);

  // Close the profile dropdown on outside-click or Escape.
  useEffect(() => {
    if (!profileMenuOpen) return undefined;
    function onDoc(e) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) setProfileMenuOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setProfileMenuOpen(false); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [profileMenuOpen]);

  // Close the chat-row ⋯ menu on outside-click or Escape. Clicks on any kebab button
  // are ignored here - the button's own onClick handles both toggle directions
  // (mousedown fires before click, so closing here would make the click reopen it).
  useEffect(() => {
    setConfirmDeleteId(null); // disarm the delete confirm whenever the menu opens/closes/moves
    if (!chatMenu) return undefined;
    function onDoc(e) {
      if (e.target.closest?.('[data-kebab]')) return;
      if (chatMenuRef.current && !chatMenuRef.current.contains(e.target)) setChatMenu(null);
    }
    function onKey(e) { if (e.key === 'Escape') setChatMenu(null); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [chatMenu]);
  // Auto-scroll to the newest message, but only when the message COUNT changes
  // (a new message arrived, or a different chat was opened). In-place edits like
  // a tone rewrite or editing a draft keep the count the same and must not yank
  // the view to the bottom.
  const prevMsgCountRef = useRef(0);
  useEffect(() => {
    if (messages.length !== prevMsgCountRef.current) {
      prevMsgCountRef.current = messages.length;
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // ── Search result bridge - survive component remounts ─────────────────────
  // Listens for 'repbuddy:search-complete' events dispatched by executeSearch.
  // Works even when setChats was dropped due to a component remount mid-search.
  useEffect(() => {
    function applyPendingResult(chatId, msgId) {
      const key    = `${chatId}__${msgId}`;
      const result = _pendingSearchResults[key];
      if (!result) return;
      delete _pendingSearchResults[key];

      const pendingConstraints = _pendingConstraints[chatId];
      if (pendingConstraints) delete _pendingConstraints[chatId];

      setChats(prev => prev.map(c => {
        if (c.id !== chatId) return c;
        // Try to find by msgId first; fall back to last assistant message
        const hasMsg = c.messages.some(m => m.id === msgId);
        const msgs   = hasMsg
          ? c.messages.map(m => m.id === msgId ? mergeMessage(m, result) : m)
          : (() => {
              const arr = [...c.messages];
              for (let i = arr.length - 1; i >= 0; i--) {
                if (arr[i].role === 'assistant') { arr[i] = mergeMessage(arr[i], result); break; }
              }
              return arr;
            })();
        return { ...c, messages: msgs, ...(pendingConstraints ? { active_constraints: pendingConstraints } : {}) };
      }));
    }

    function onSearchComplete(e) {
      applyPendingResult(e.detail.chatId, e.detail.msgId);
    }

    window.addEventListener('repbuddy:search-complete', onSearchComplete);

    // Also apply any results that arrived before this instance mounted
    Object.keys(_pendingSearchResults).forEach(key => {
      const [chatId, msgId] = key.split('__');
      applyPendingResult(chatId, msgId);
    });

    return () => window.removeEventListener('repbuddy:search-complete', onSearchComplete);
  }, []); // eslint-disable-line

  // ── Chat management ───────────────────────────────────────────────────────

  function startNewChat() {
    const chat = newChatObj();
    setChats(prev => [chat, ...prev]);
    setActiveChatId(chat.id);
    setInput('');
    setAttachment(null);
    closeSidebarOnMobile();
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function openChat(id) {
    setActiveChatId(id);
    setInput('');
    setAttachment(null);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function deleteChat(e, id) {
    e.stopPropagation();
    setChats(prev => prev.filter(c => c.id !== id));
    if (activeChatId === id) setActiveChatId(null);
  }

  function togglePin(id) {
    setChats(prev => prev.map(c => {
      if (c.id !== id) return c;
      if (c.pinned) { const { pinned, pinnedAt, ...rest } = c; return rest; }
      return { ...c, pinned: true, pinnedAt: Date.now() };
    }));
    setChatMenu(null);
  }

  function beginRename(chat) {
    setRenameDraft(chat.title);
    setRenamingChatId(chat.id);
    setChatMenu(null);
  }

  function commitRename() {
    const title = renameDraft.trim();
    if (title && renamingChatId) {
      // titleEdited blocks the first-message auto-title from clobbering a manual rename.
      // Deliberately not routed through updateChatTitle - that helper is the auto-title
      // path and must never set the flag.
      setChats(prev => prev.map(c => c.id === renamingChatId ? { ...c, title, titleEdited: true } : c));
    }
    setRenamingChatId(null);
  }

  // ── Standalone website lookup (metered: 1 website-lookup credit per resolved row) ──
  // Called when the user attaches a companies file and asks to "find websites".
  // Rows that already carry a URL in the file pass through free; only URL-less rows are
  // sent to the resolver, and only rows it actually resolves are charged. Name collisions
  // come back as "needs confirmation" with the candidate sites listed.
  async function handleWebsiteLookup(chatId, att) {
    const companyNames = att.companies.map(c => c.name).filter(Boolean);
    if (!companyNames.length) {
      addMessageToChat(chatId, {
        role: 'assistant', status: 'error', done: true,
        lines: ['No company names found in the file. Make sure the first column contains company names.'],
      });
      return;
    }

    // Rows that already have a Website/Domain value pass through free (per pricing).
    const fileDomains = {};
    if (att.domainColIndex >= 0 && att.rawData?.length) {
      att.rawData.forEach(row => {
        const companyName = String(row[att.colIndex] || '').trim();
        const rawDomain   = String(row[att.domainColIndex] || '').trim();
        if (companyName && rawDomain) {
          const clean = rawDomain.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0].trim().toLowerCase();
          if (clean && clean.includes('.')) fileDomains[normEnrichName(companyName)] = clean;
        }
      });
    }
    const withUrl    = companyNames.filter(n => fileDomains[normEnrichName(n)]);
    const needLookup = companyNames.filter(n => !fileDomains[normEnrichName(n)]);

    addMessageToChat(chatId, {
      role: 'assistant', status: 'running', done: false,
      lines: [
        `Website lookup: ${companyNames.length} ${companyNames.length === 1 ? 'company' : 'companies'} in your file.`,
        ...(withUrl.length ? [`${withUrl.length} already have a website in the file - those pass through free.`] : []),
        `🔄 Resolving ${needLookup.length} ${needLookup.length === 1 ? 'company' : 'companies'} without a URL…`,
      ],
    });

    if (needLookup.length === 0) {
      updateLastAssistant(chatId, () => ({
        status: 'done', done: true,
        lines: [
          'Every row in your file already carries a website - nothing to resolve, nothing charged.',
          ...withUrl.map(n => `✓ ${n} - ${fileDomains[normEnrichName(n)]}`),
        ],
      }));
      return;
    }

    // ── Before resolving: estimate against the website-lookups meter ──
    // Domain resolution debits website_lookups (1 per URL-less row we actually resolve);
    // rows that already carry a URL are free, so the worst case is every URL-less row.
    const showWebsiteCost = !leadCredits?.unlimited;
    if (showWebsiteCost) {
      const wb = meterBalance('website');
      const upTo = wb.metered ? Math.min(needLookup.length, wb.remaining) : needLookup.length;
      updateLastAssistant(chatId, () => ({ costNote: { kind: 'estimate', text: estimateLine('resolve', { upTo, remaining: wb.remaining, allowance: wb.allowance }) } }));
    }

    try {
      const out = await resolveWebsites(needLookup);

      if (out.blocked) {
        updateLastAssistant(chatId, () => ({
          status: 'done', done: true,
          lines: [(out.reason === 'trial_expired' || out.reason === 'leads_exhausted')
            ? 'Your Demo Week trial has ended. Choose a plan from the Account page to keep resolving websites.'
            : "You've used all your website lookups for this period. Upgrade your plan from the Account page to keep going."],
        }));
        return;
      }

      applyMeterUsage('website_lookups', out.usage); // keep the cached balance current
      const resolved  = (out.resolved || []).filter(r => r.domain);
      const failed    = (out.resolved || []).filter(r => !r.domain);
      // Candidates need a display title for the choice buttons; fall back to the domain.
      const ambiguous = (out.ambiguous || []).map(a => ({
        name: a.name,
        candidates: (a.candidates || []).slice(0, 3).map(c => ({ ...c, title: c.title || c.domain })),
      }));

      // The consolidated base list every path below builds on (usage rides along so the
      // final message can show the meter after confirmations too).
      const websiteBase = {
        fileRows: withUrl.map(n => ({ company: n, website: fileDomains[normEnrichName(n)], source: 'From your file' })),
        autoRows: resolved.map(r => ({ company: r.name, website: r.domain, source: 'Resolved' })),
        failedRows: failed.map(r => ({ company: r.name, website: '', source: 'Not found' })),
        usage: out.usage || null,
        fileName: att.name,
      };

      if (ambiguous.length === 0) {
        // Close the running "Resolving…" message, then post the final list.
        updateLastAssistant(chatId, () => ({
          status: 'done', done: true,
          lines: [`Website lookup complete: ${resolved.length} resolved${withUrl.length ? `, ${withUrl.length} from your file (free)` : ''}${failed.length ? `, ${failed.length} not found (free)` : ''}.`],
        }));
        postWebsiteResults(chatId, websiteBase, [], {}, []);
        return;
      }

      // Name collisions: let the user click the right site per company (same interactive
      // flow as enrichment disambiguation), then finalize into the downloadable list.
      updateLastAssistant(chatId, () => ({
        status: 'done', done: true,
        lines: [
          `Resolved ${resolved.length + withUrl.length} of ${companyNames.length} ${companyNames.length === 1 ? 'company' : 'companies'}${failed.length ? ` (${failed.length} not found, free)` : ''}.`,
          `Found multiple possible websites for ${ambiguous.length} ${ambiguous.length === 1 ? 'company' : 'companies'}: pick the right one${ambiguous.length > 1 ? 's' : ''} below.`,
        ],
        isDisambiguation: true,
        disambiguationItems: ambiguous,
      }));
      setPendingEnrich({ kind: 'website', chatId, ambiguousItems: ambiguous, resolvedDomains: {}, decidedSet: [], websiteBase });
    } catch (err) {
      updateLastAssistant(chatId, () => chatFailure(err, {
        say: "I couldn't look up those websites just now. Try again in a moment.",
      }));
    }
  }

  // Posts the final consolidated company → website list (rendered as a table with a CSV
  // download). Called directly when nothing was ambiguous, or after the user has decided
  // every collision. `chosenDomains`/`decidedSet` cover the ambiguous items.
  function postWebsiteResults(chatId, base, ambiguousItems, chosenDomains, decidedSet) {
    const confirmedRows = (ambiguousItems || []).map(item => {
      const key = normEnrichName(item.name);
      const domain = chosenDomains?.[key];
      return domain
        ? { company: item.name, website: domain, source: 'Confirmed by you' }
        : { company: item.name, website: '', source: 'Skipped' };
    });
    const rows = [...base.fileRows, ...base.autoRows, ...confirmedRows, ...base.failedRows];
    const found = rows.filter(r => r.website).length;

    // Actual website-lookup spend: only rows the resolver actually resolved were charged
    // (from-file and not-found rows are free). Only shown for real metered users - a finite
    // allowance rules out unlimited (dev) accounts, whose meter reports Infinity/null.
    const resolvedCount = base.autoRows.length;
    const costNote = (base.usage && Number.isFinite(base.usage.remaining) && Number.isFinite(base.usage.allowance))
      ? { kind: 'actual', text: actualLine('resolve', { used: resolvedCount, remaining: base.usage.remaining, allowance: base.usage.allowance }) }
      : null;

    addMessageToChat(chatId, {
      role: 'assistant', status: 'done', done: true,
      lines: [
        `Website list ready: ${found} of ${rows.length} ${rows.length === 1 ? 'company has' : 'companies have'} a confirmed website. Download it below to open in Excel.`,
      ],
      ...(costNote ? { costNote } : {}),
      isWebsiteResults: true,
      websiteRows: rows,
      websiteFileName: base.fileName,
      contacts: [], valid: [], risky: [], invalid: [],
    });
  }

  async function handleSavePreset() {
    const name = presetName.trim();
    if (!name || !activeChat?.active_constraints) return;
    try {
      const { preset } = await savePreset(name, activeChat.active_constraints);
      setPresets(prev => [preset, ...prev]);
      setPresetName('');
      setShowPresetSave(false);
    } catch (err) {
      alert(`Could not save preset: ${err.message}`);
    }
  }

  async function handleDeletePreset(id) {
    try {
      await deletePreset(id);
      setPresets(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      alert(`Could not delete preset: ${err.message}`);
    }
  }

  function handleLoadPreset(preset) {
    if (!activeChatId) return;
    setShowPresetMenu(false);
    // Apply the preset criteria as a natural language search so the normal clarification
    // flow fires (leads-per-company question etc.) — build a descriptive query string
    const c = preset.criteria;
    const parts = [];
    if (c.titles?.length)   parts.push(c.titles[0]);
    if (c.location?.length) parts.push(`in ${c.location.join(', ')}`);
    if (c.industry)         parts.push(`in ${c.industry}`);
    if (c.company)          parts.push(`at ${c.company}`);
    const queryText = parts.length ? `Find me ${parts.join(' ')}` : `Find me contacts`;
    setInput(queryText);
    // Clear current constraints so the preset becomes the new baseline
    setChats(prev => prev.map(c2 => c2.id !== activeChatId ? c2 : { ...c2, active_constraints: null }));
  }

  // Remove a single value from active_constraints (visual chip × button).
  // field: 'titles' | 'location' | 'industry' | 'company'
  // value: the specific string to remove (null for scalar fields)
  function removeConstraint(field, value) {
    if (!activeChatId) return;
    setChats(prev => prev.map(c => {
      if (c.id !== activeChatId) return c;
      const cur = c.active_constraints || {};
      let updated;
      if (field === 'titles') {
        updated = { ...cur, titles: (cur.titles || []).filter(t => t !== value) };
      } else if (field === 'location') {
        updated = { ...cur, location: (cur.location || []).filter(s => s !== value) };
      } else {
        updated = { ...cur, [field]: '' };
      }
      return { ...c, active_constraints: updated };
    }));
  }

  // ── Chat-based enrichment handlers ───────────────────────────────────────────

  // Called when user attaches a CSV and types "enrich".
  // Resolves domains via KNOWN_DOMAINS + Google CSE, then either proceeds
  // directly to enrichment or shows a disambiguation UI in the chat.
  async function handleEnrichIntent(chatId, att) {
    const selectedFields = DEFAULT_ENRICH_FIELDS;
    const companyNames   = att.companies.map(c => c.name).filter(Boolean);

    if (!companyNames.length) {
      addMessageToChat(chatId, {
        role: 'assistant', status: 'error', done: true,
        lines: ['No company names found in the file. Make sure the first column contains company names.'],
      });
      return;
    }

    // Extract domains from an existing Website/Domain column in the file (used as hints, not hard truths)
    const fileDomains = {};
    if (att.domainColIndex >= 0 && att.rawData?.length) {
      att.rawData.forEach(row => {
        const companyName = String(row[att.colIndex] || '').trim();
        const rawDomain   = String(row[att.domainColIndex] || '').trim();
        if (companyName && rawDomain) {
          const clean = rawDomain.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0].trim().toLowerCase();
          if (clean) fileDomains[normEnrichName(companyName)] = clean;
        }
      });
    }

    const hasFileDomains  = Object.keys(fileDomains).length > 0;

    addMessageToChat(chatId, {
      role: 'assistant', status: 'running', done: false,
      lines: [
        `Got your file! Enriching ${companyNames.length} ${companyNames.length === 1 ? 'company' : 'companies'}.`,
        ...(companyNames.length >= 10 ? ['This may take a minute or two. Feel free to open a new chat while I work on this in the background.'] : []),
        `🔄 ${hasFileDomains ? 'Querying Custom Search (cross-checking your Website column)…' : 'Querying Custom Search to resolve domains…'}`,
      ],
    });

    // Always run Gemini for all companies - no industry hint so it finds the company
    // as-is, not biased towards the seller's ICP industry (e.g. Healthcare ICP would
    // otherwise resolve "Mercury" → mercuryhealthcare.com instead of mercury.com).
    let resolved = [], ambiguous = [];
    try {
      const result = await resolveDomains(companyNames, '');
      resolved  = result.resolved  || [];
      ambiguous = result.ambiguous || [];
    } catch (err) {
      console.warn('[Enrich] Domain resolution failed, using name-based fallback:', err.message);
      resolved  = companyNames.map(n => ({ name: n, domain: '', source: 'none' }));
      ambiguous = [];
    }

    // Merge file domains + Google results:
    // - If file domain matches Google's top pick → auto-resolve (no interruption)
    // - If file domain differs from Google's pick → show disambiguation, file domain pre-selected
    // - If Google is ambiguous + file has domain → inject file domain as first (pre-selected) candidate
    // - If Google found nothing but file has domain → auto-resolve with file domain
    const resolvedDomains = {};
    const finalAmbiguous  = [];

    for (const company of companyNames) {
      const normKey      = normEnrichName(company);
      const fileDomain   = fileDomains[normKey] || '';
      const gResolved    = resolved.find(r => normEnrichName(r.name) === normKey);
      const gAmbiguous   = ambiguous.find(a => normEnrichName(a.name) === normKey);

      if (!fileDomain) {
        // No file domain - normal Google CSE behavior
        if (gResolved?.domain) resolvedDomains[normKey] = gResolved.domain;
        if (gAmbiguous)        finalAmbiguous.push(gAmbiguous);
      } else if (gResolved?.domain && gResolved.domain === fileDomain) {
        // File and Google agree → auto-resolve silently
        resolvedDomains[normKey] = fileDomain;
      } else if (gResolved?.domain && gResolved.domain !== fileDomain) {
        // File and Google disagree → show both, file domain pre-selected
        finalAmbiguous.push({
          name: company,
          candidates: [
            { domain: fileDomain, score: 10, title: 'From your file', fromFile: true },
            { domain: gResolved.domain, score: gResolved.score || 5, title: gResolved.title || 'Google match' },
          ],
        });
      } else if (gAmbiguous) {
        // Google is ambiguous → inject file domain as first (pre-selected) candidate
        const otherCandidates = gAmbiguous.candidates.filter(c => c.domain !== fileDomain);
        finalAmbiguous.push({
          name: company,
          candidates: [
            { domain: fileDomain, score: 10, title: 'From your file', fromFile: true },
            ...otherCandidates,
          ],
        });
      } else {
        // Google found nothing → trust file domain
        resolvedDomains[normKey] = fileDomain;
      }
    }

    if (finalAmbiguous.length === 0) {
      const autoNote = resolved.filter(r => r.source === 'google').length > 0
        ? 'Domains resolved via Google search. ' : '';
      updateLastAssistant(chatId, m => ({
        lines: [...(m.lines || []), `${autoNote}Enriching ${companyNames.length} ${companyNames.length === 1 ? 'company' : 'companies'}…`],
      }));
      await executeEnrichChatFlow(chatId, companyNames, resolvedDomains, att.rawHeaders, att.rawData, att.colIndex, att.name, selectedFields);
    } else {
      updateLastAssistant(chatId, () => ({
        status: 'done', done: true,
        lines: [
          `Resolved ${companyNames.length - finalAmbiguous.length} of ${companyNames.length} ${companyNames.length === 1 ? 'company' : 'companies'}.`,
          `Found multiple matches for ${finalAmbiguous.length} ${finalAmbiguous.length === 1 ? 'company' : 'companies'}: please confirm the correct one${finalAmbiguous.length > 1 ? 's' : ''}:`,
        ],
        isDisambiguation: true,
        disambiguationItems: finalAmbiguous,
      }));

      setPendingEnrich({
        chatId,
        companiesAll:    companyNames,
        resolvedDomains,
        ambiguousItems:  finalAmbiguous,
        decidedSet:      [],
        headers:         att.rawHeaders  || [],
        rawData:         att.rawData     || [],
        colIndex:        att.colIndex    ?? 0,
        fileName:        att.name,
        selectedFields,
      });
    }
  }

  // Called when the user clicks a disambiguation choice in the chat.
  // Pure state updater - only records the choice and marks the item as decided.
  // The enrichment trigger lives in a useEffect so it always reads committed state,
  // preventing stale-closure bugs where rapid clicks could miscalculate allDecided.
  function handleDisambiguationChoice(companyName, domain) {
    const normKey = normEnrichName(companyName);
    setPendingEnrich(prev => {
      if (!prev) return prev;
      // domain = '' means skip - don't add to resolvedDomains so the company is
      // excluded entirely from enrichment (not just given a name-based fallback).
      const newResolved = { ...prev.resolvedDomains };
      if (domain) newResolved[normKey] = domain;
      const newDecided  = [...new Set([...(prev.decidedSet || []), normKey])];
      return { ...prev, resolvedDomains: newResolved, decidedSet: newDecided };
    });
  }

  // Watches pendingEnrich for the moment every ambiguous item has been decided, then
  // finishes the flow that opened it: kicks off enrichment (default), or finalizes the
  // standalone website-lookup list (kind: 'website'). Runs after React commits state so
  // it always sees the latest decidedSet - no stale-closure risk.
  useEffect(() => { // eslint-disable-line
    if (!pendingEnrich) return;
    const { kind, ambiguousItems, decidedSet, resolvedDomains, websiteBase,
            chatId, companiesAll, headers, rawData, colIndex, fileName, selectedFields,
            batchCompanies, resumeBatch } = pendingEnrich;

    const allDecided = (ambiguousItems || []).every(
      item => (decidedSet || []).includes(normEnrichName(item.name))
    );
    if (!allDecided) return;

    // Stamp final decisions onto the disambiguation message BEFORE clearing pendingEnrich
    // so the UI can still render the "✓ Locked in" / "✓ Skipped" states even after
    // pendingEnrich becomes null (it reads from msg.finalDecidedSet as a fallback).
    setChats(prev => prev.map(c => {
      if (c.id !== chatId) return c;
      const msgs = [...c.messages];
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].isDisambiguation) {
          msgs[i] = { ...msgs[i], finalDecidedSet: decidedSet, finalResolvedDomains: resolvedDomains };
          break;
        }
      }
      return { ...c, messages: msgs };
    }));

    setPendingEnrich(null);

    // ── Batch contact search: fold the confirmed domains in and run it ──
    // A company the user skipped keeps an empty domain rather than being dropped. It still goes
    // to the backend, which reports it as unidentified, so the downloaded CSV has a row for every
    // company the user uploaded.
    if (kind === 'batch') {
      setRunning(chatId, true);
      Promise.resolve(resumeBatch(applyResolvedDomains(batchCompanies, resolvedDomains)))
        .finally(() => {
          setRunning(chatId, false);
          inputRef.current?.focus();
        });
      return;
    }

    // ── Look-alike: the single seed has been disambiguated → open the count card ──
    if (kind === 'lookalike') {
      const dom = resolvedDomains[normEnrichName(pendingEnrich.seedName)] || '';
      if (!dom) {
        // "None of these": don't proceed - the backend's last-resort probe would just re-derive
        // the same rejected guess. Ask for the website instead; the classifier extracts a URL
        // typed alongside the ask ("companies like Otter AI otter.ai").
        addMessageToChat(chatId, {
          role: 'assistant', status: 'done', done: true,
          isConversational: true, conversationalIntent: 'general_chat',
          conversationalResponse: `Got it. Share ${pendingEnrich.seedName}'s website and I'll use that - e.g. "companies like ${pendingEnrich.seedName} (their-site.com)".`,
          conversationalBullets: [], conversationalBlocks: [],
          lines: [],
        });
      } else {
        openLookalikeCountAsk(chatId, pendingEnrich.seedName, dom, pendingEnrich.basis || 'buyer_fit');
      }
      inputRef.current?.focus();
      return;
    }

    // ── Company research: the name has been disambiguated → research the pick ──
    if (kind === 'company_research') {
      const dom = resolvedDomains[normEnrichName(pendingEnrich.seedName)] || '';
      if (!dom) {
        if (readAgentTurn(chatId)?.pending?.name === 'research_company') clearAgentTurn(chatId);
        addMessageToChat(chatId, {
          role: 'assistant', status: 'done', done: true,
          isConversational: true, conversationalIntent: 'general_chat',
          conversationalResponse: `Got it. Share ${pendingEnrich.seedName}'s website and I'll research that one, e.g. "research ${pendingEnrich.seedName} (their-site.com)".`,
          conversationalBullets: [], conversationalBlocks: [],
          lines: [],
        });
        inputRef.current?.focus();
        return;
      }
      const msgId = `${Date.now()}_${Math.random()}`;
      addMessageToChat(chatId, { id: msgId, role: 'assistant', status: 'running', done: false, lines: [], contacts: [] });
      setRunning(chatId, true);
      runCompanyResearch(chatId, msgId, { name: pendingEnrich.seedName, domain: dom, researchedPerson: pendingEnrich.researchedPerson || '' })
        .finally(() => { setRunning(chatId, false); inputRef.current?.focus(); });
      return;
    }

    // ── Standalone website lookup: every collision decided → post the final list ──
    if (kind === 'website') {
      postWebsiteResults(chatId, websiteBase, ambiguousItems, resolvedDomains, decidedSet);
      inputRef.current?.focus();
      return;
    }

    // ── Enrichment: skipped companies (decided but no domain) are dropped entirely ──
    const skippedKeys        = new Set((decidedSet || []).filter(k => !resolvedDomains[k]));
    const companiesForEnrich = companiesAll.filter(name => !skippedKeys.has(normEnrichName(name)));

    setRunning(chatId, true);
    addMessageToChat(chatId, {
      role: 'assistant', status: 'running', done: false,
      lines: [`All companies identified. Enriching ${companiesForEnrich.length} ${companiesForEnrich.length === 1 ? 'company' : 'companies'}…`],
    });
    executeEnrichChatFlow(
      chatId, companiesForEnrich, resolvedDomains,
      headers, rawData, colIndex, fileName, selectedFields
    ).then(() => {
      setRunning(chatId, false);
      inputRef.current?.focus();
    });
  }, [pendingEnrich]); // eslint-disable-line

  // Runs the actual enrichment call and posts results to the chat as a message.
  async function executeEnrichChatFlow(chatId, companyNames, resolvedDomains, rawHeaders, rawData, colIndex, fileName, selectedFields) {
    // ── Before enrichment runs: estimate against the company-enrichments meter ──
    // Enrichment debits company_enrichments (1 per row we return data for; Not Found is
    // free), so the worst case is every company enriched. Skip for unlimited (dev) accounts.
    const showEnrichCost = !leadCredits?.unlimited;
    if (showEnrichCost) {
      const enrichBal = meterBalance('enrich');
      const estUpTo = enrichBal.metered ? Math.min(companyNames.length, enrichBal.remaining) : companyNames.length;
      updateLastAssistant(chatId, () => ({ costNote: { kind: 'estimate', text: estimateLine('enrich', { upTo: estUpTo, remaining: enrichBal.remaining, allowance: enrichBal.allowance }) } }));
    }
    try {
      const { results, summary, usage, blocked } = await enrichCompaniesStream(
        companyNames,
        selectedFields,
        resolvedDomains,
        (event) => {
          if (event.type === 'start') {
            updateLastAssistant(chatId, m => ({
              lines: [...(m.lines || []).filter(l => !l.startsWith('🔄')),
                `🔄 Starting enrichment for ${event.total} ${event.total === 1 ? 'company' : 'companies'}…`],
            }));
          } else if (event.type === 'progress') {
            updateLastAssistant(chatId, m => ({
              lines: [...(m.lines || []).filter(l => !l.startsWith('🔄')),
                `🔄 Enriching companies: ${event.current}/${event.total} done…`],
            }));
          }
        }
      );

      // ── After enrichment: the actual company-enrichment spend + reconciliation ──
      // Charged = rows we returned data for (enriched + partial); Not Found rows are free.
      if (showEnrichCost) {
        applyMeterUsage('company_enrichments', usage);
        const charged = (summary.enriched || 0) + (summary.partial || 0);
        const notFound = summary.notFound || 0;
        const bal = usage && typeof usage.remaining === 'number' ? usage : meterBalance('enrich');
        const note = blocked
          ? `No company enrichments charged - you're out of enrichments. You have 0 of ${usage?.allowance ?? ''} remaining.`
          : actualLine('enrich', {
              used: charged,
              notCharged: notFound,
              notChargedNoun: 'company',
              notChargedReason: 'returned no data',
              remaining: bal.remaining,
              allowance: bal.allowance,
            });
        updateLastAssistant(chatId, () => ({ costNote: { kind: 'actual', text: note } }));
      }

      updateLastAssistant(chatId, () => ({
        status: 'done', done: true,
        lines: [`✅ Enrichment complete: ${summary.enriched} enriched, ${summary.partial} partial, ${summary.notFound} not found. Download your results below.`],
        isEnrichResults:    true,
        enrichResults:      results,
        enrichSummary:      summary,
        enrichFileName:     fileName,
        enrichHeaders:      rawHeaders   || [],
        enrichRawData:      rawData      || [],
        enrichColIndex:     colIndex     ?? 0,
        enrichSelectedFields: selectedFields,
      }));
      // This flow addresses its bubble as "the last assistant message" rather than by id, so
      // resolve the id the same way for the take. The bubble was created long before the
      // network call, so the ref has caught up with it by now.
      const enrichMsgId = (chatsRef.current.find(c => c.id === chatId)?.messages || [])
        .filter(m => m.role === 'assistant').at(-1)?.id;
      runDebrief(chatId, enrichMsgId, summarizeEnrichOutcome({ summary, results, selectedFields, fileName }), 'enrich');

      // Update session context so follow-up questions can reference enriched companies
      if (results?.length > 0) {
        setChats(prev => prev.map(c => {
          if (c.id !== chatId) return c;
          return {
            ...c,
            enrichment_ref: buildEnrichmentRef(results),
            active_goal:    buildUpdatedGoalForEnrich(c.active_goal, summary),
          };
        }));
      }
    } catch (err) {
      updateLastAssistant(chatId, () => chatFailure(err, {
        say: "Enrichment hit a problem and didn't finish. Try attaching the file again in a moment.",
      }));
    }
  }

  function addMessageToChat(chatId, msg) {
    // A user message that did not come through typedUserMessage is a chip or button echoing its
    // label. Resending that label is not the action it stood for, so it is not editable.
    if (msg.role === 'user' && !msg.ctxBefore) msg = { synthetic: true, ...msg };
    // updatedAt drives the sidebar's Today/Yesterday/... grouping. Touched here only -
    // this is the choke-point for new messages; streaming edits to an existing message
    // (updateLastAssistant/updateMessageById) deliberately don't bump it.
    setChats(prev => prev.map(c =>
      c.id === chatId
        ? { ...c, updatedAt: Date.now(), messages: [...c.messages, { id: Date.now() + Math.random(), ...msg }] }
        : c
    ));
  }

  // A prompt the user typed. It carries the chat's context as it stood before this turn ran, so
  // editing the prompt later restores that context instead of keeping what the turn left behind.
  function typedUserMessage(chatId, text, extra = {}) {
    const chat = chatsRef.current.find(c => c.id === chatId);
    return { role: 'user', content: text, contentText: text, ctxBefore: markCtx(chat), ...extra };
  }

  function updateLastAssistant(chatId, updater) {
    setChats(prev => prev.map(c => {
      if (c.id !== chatId) return c;
      const msgs = [...c.messages];
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === 'assistant') {
          msgs[i] = mergeMessage(msgs[i], updater(msgs[i]));
          break;
        }
      }
      return { ...c, messages: msgs };
    }));
  }

  // Update a specific message by ID - prevents concurrent searches from clobbering each other.
  // The "last assistant" approach breaks when a second search starts before the first finishes.
  // NOTE: diagnostic log is outside setChats so it fires even if the component unmounted
  // (React 18 silently discards setChats updaters on unmounted components without calling them).
  function updateMessageById(chatId, msgId, updater) {
    console.log('[updateMessageById] called', { chatId, msgId });
    setChats(prev => {
      const chat = prev.find(c => c.id === chatId);
      const msg  = chat?.messages.find(m => m.id === msgId);
      if (!msg) {
        // Message not found by ID - fall back to updating the last assistant message.
        // This handles cases where the component re-mounted (navigation away and back)
        // which causes React to discard the previous state and run a fresh render.
        // The message with runningMsgId may have been restored from localStorage with
        // a different runtime reference, or the chat/message may genuinely be missing.
        console.warn('[updateMessageById] ❌ message NOT found - falling back to last assistant', { chatId, msgId, chatExists: !!chat });
        return prev.map(c => {
          if (c.id !== chatId) return c;
          const msgs = [...c.messages];
          for (let i = msgs.length - 1; i >= 0; i--) {
            if (msgs[i].role === 'assistant') {
              msgs[i] = mergeMessage(msgs[i], updater(msgs[i]));
              break;
            }
          }
          return { ...c, messages: msgs };
        });
      }
      console.log('[updateMessageById] ✅ message found, updating');
      return prev.map(c => {
        if (c.id !== chatId) return c;
        const msgs = c.messages.map(m =>
          m.id === msgId ? mergeMessage(m, updater(m)) : m
        );
        return { ...c, messages: msgs };
      });
    });
  }

  function updateChatTitle(chatId, title) {
    setChats(prev => prev.map(c => c.id === chatId ? { ...c, title } : c));
  }

  // Set the meter cost note on a message: kind 'estimate' (before an action), 'actual'
  // (after it, with the new balance), or 'free' (the never-metered actions). Merged in as
  // its own field so it survives the streaming updates that replace `lines`.
  function setCostNote(chatId, msgId, kind, text) {
    if (!msgId || !text) return;
    updateMessageById(chatId, msgId, () => ({ costNote: { kind, text } }));
  }

  // Called by ContactRow when the user clicks "Li" and gets a result back.
  // Stores the result keyed by email so exportCSV can include it.
  function handleEmpVerified(email, empResult) {
    if (email) empVerifiedRef.current.set(email.toLowerCase(), empResult);
  }

  // ── Stop / cancel ─────────────────────────────────────────────────────────

  function handleStop() {
    const chatId = activeChatId;

    // Actually stop it. Until this line existed, Stop only hid a running search: the fetch stayed
    // open, the backend kept unlocking contacts, and the credits were still spent - the user just
    // never saw what they paid for. Aborting closes the socket, which is the signal the backend
    // watches to stop the search and skip the debit.
    _searchAborts[chatId]?.abort();
    delete _searchAborts[chatId];

    // A stopped search must not come back later. Results are handed over through a module-level
    // bridge that survives remounts, and anything left in it is applied on the next mount - so
    // without this a search the user stopped could overwrite the 'Stopped.' bubble minutes later.
    for (const key of Object.keys(_pendingSearchResults)) {
      if (key.startsWith(`${chatId}__`)) delete _pendingSearchResults[key];
    }
    delete _pendingConstraints[chatId];
    // A stopped search has no result to hand the model, so the turn waiting on it ends here.
    clearAgentTurn(chatId);

    // Still needed for the stages an abort cannot reach - the parse calls and the batch loops,
    // which check this at their await boundaries.
    cancelRef.current = true;
    setRunning(chatId, false);
    // Mark any still-spinning assistant bubble as stopped
    setChats(prev => prev.map(c => {
      if (c.id !== chatId) return c;
      const msgs = [...c.messages];
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === 'assistant' && !msgs[i].done) {
          msgs[i] = {
            ...msgs[i],
            status: 'done',
            done:   true,
            lines:  [...(msgs[i].lines || []), 'Stopped.'],
          };
          break;
        }
      }
      return { ...c, messages: msgs };
    }));
  }

  // ── Edit a prompt / regenerate a reply / flip between versions ─────────────
  // The versions themselves live in lib/chatBranches.js; these apply them to the page.

  // Put a rewritten chat in place. The ref is set in the same tick because the edit sends straight
  // after, and every read on the send path goes through chatsRef.
  function applyChatRewrite(chatId, next) {
    chatsRef.current = chatsRef.current.map(c => (c.id === chatId ? next : c));
    setChats(prev => prev.map(c => (c.id === chatId ? next : c)));
  }

  // Whatever was waiting on a message that just left the live path. A card still open when its
  // prompt is edited sat below that prompt, so it went with it; a result still in the bridge would
  // otherwise land on whichever assistant message is last. Nothing is running (both callers check),
  // so there is nothing to abort. Cards without a msgId (the batch count prompt, enrich
  // disambiguation) only exist while the chat is waiting on them, so they go too.
  function resetChatInFlight(chatId, removedIds) {
    for (const key of Object.keys(_pendingSearchResults)) {
      if (key.startsWith(`${chatId}__`)) delete _pendingSearchResults[key];
    }
    delete _pendingConstraints[chatId];
    delete _agentTurns[chatId];   // the chat's own copy went with the rewrite
    const stale = p => p?.chatId === chatId && (p.msgId == null || removedIds.has(p.msgId));
    if (stale(pendingSearchAsk)) setPendingSearchAsk(null);
    if (stale(pendingLookalike)) setPendingLookalike(null);
    if (stale(pendingCompanies)) setPendingCompanies(null);
    if (stale(pendingLinkedIn))  setPendingLinkedIn(null);
    if (stale(pendingBatch))     setPendingBatch(null);
    if (stale(pendingEnrich))    setPendingEnrich(null);
  }

  // Resend an edited prompt. The prompt and everything after it are kept as a version; the edited
  // text then goes through handleSend like anything typed, so a search still stops at its card.
  async function handleEditSubmit(chatId, msgId, text) {
    const query = (text || '').trim();
    if (!query || !chatId || isRunning(chatId)) return;
    const chat = chatsRef.current.find(c => c.id === chatId);
    const res  = chat && forkChat(chat, msgId);
    if (!res) return;
    setEditingMsgId(null);
    applyChatRewrite(chatId, res.chat);
    resetChatInFlight(chatId, new Set(res.removedIds));
    await handleSend(query, { fromEdit: true });
  }

  // A new take on a text reply: its prompt, resent as it was, as a new version.
  function handleRegenerate(chatId, msgIndex) {
    const chat   = chatsRef.current.find(c => c.id === chatId);
    const prompt = chat && regenerateSource(chat, msgIndex);
    if (prompt) handleEditSubmit(chatId, prompt.id, prompt.contentText || prompt.content);
  }

  function handleSwitchVersion(chatId, anchorId, k) {
    if (isRunning(chatId)) return;
    const chat = chatsRef.current.find(c => c.id === chatId);
    const res  = chat && switchVersion(chat, anchorId, k);
    if (!res) return;
    setEditingMsgId(null);
    applyChatRewrite(chatId, res.chat);
    resetChatInFlight(chatId, new Set(res.removedIds));
  }

  // ── "Find Contacts" button handler ───────────────────────────────────────
  // Uses ICP titles from seller profile directly - skips Claude parsing and
  // goes straight to the count prompt, exactly like the normal batch flow.

  async function handleFindContactsClick() {
    if (!attachment || attachment.type === 'persons' || attachment.type === 'emails' || isRunning(activeChatId)) return;

    const icpTitles = sellerProfile?.icp?.titles || [];

    if (icpTitles.length === 0) {
      // No ICP profile titles - fall back to letting user type a title
      setAttachmentAction('find_contacts');
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }

    // ICP titles available - build the batch and jump straight to count prompt
    cancelRef.current = false;
    setInput('');
    setAttachmentAction(null);

    const currentAttachment = attachment;
    setAttachment(null);

    let chatId = activeChatId;
    const chatExists = chatId && chats.some(c => c.id === chatId);
    if (!chatExists) {
      const chat = newChatObj();
      setChats(prev => [chat, ...prev]);
      setActiveChatId(chat.id);
      chatId = chat.id;
      await sleep(50);
    }

    setRunning(chatId, true);

    const currentChat = chats.find(c => c.id === chatId);
    if (!currentChat?.messages?.length && !currentChat?.titleEdited) {
      updateChatTitle(chatId, `Batch contacts from ${currentAttachment.name}`);
    }

    const designation        = icpTitles[0];
    const batchTitleKeywords = icpTitles.length > 1 ? icpTitles : [];
    const batchAskTotal      = currentAttachment.companies.length;

    addMessageToChat(chatId, {
      role: 'user', content: 'Find Contacts', contentText: 'Find Contacts',
      attachmentName: currentAttachment.name, attachmentCount: currentAttachment.companies.length,
    });

    const askMsgId     = `${Date.now()}_${Math.random()}`;
    const batchPayload = {
      chatId,
      designation,
      userTypedTitle: false,
      claudeVariations: batchTitleKeywords,
      companies: currentAttachment.companies,
      fileName: currentAttachment.name,
    };

    setPendingBatch(batchPayload);
    askBatchPrefs(chatId, askMsgId, batchPayload, batchAskTotal, (patch) =>
      addMessageToChat(chatId, {
        id: askMsgId, role: 'assistant',
        icpNote: `Using ICP titles: ${icpTitles.slice(0, 3).join(', ')}${icpTitles.length > 3 ? ` +${icpTitles.length - 3} more` : ''}.`,
        ...patch,
      }));

    setRunning(chatId, false);
    inputRef.current?.focus();
  }

  // Put the pref card in front of a batch run.
  //
  // `commit` is injected because the two callers differ in where the card lands: the attachment
  // flow already has a "Got your file!" assistant message to patch, while the Find Contacts button
  // has nothing yet and needs a fresh one. Everything else - the payload, the id, the answers
  // shape - is identical, and keeping it in one place is what stops the two from drifting.
  function askBatchPrefs(chatId, askMsgId, batchPayload, companyCount, commit) {
    setPendingSearchAsk({
      chatId,
      msgId:   askMsgId,
      batch:   batchPayload,
      needsCap:      true,   // for batch this is "how many per company", not a diversity cap
      needsIndustry: false,  // the user supplied the companies; there is nothing to split
      answers: { cap: undefined, pool: undefined, industry: 'balanced' },
    });
    commit({
      id: askMsgId, status: 'done', done: true, isPrefQuestion: true,
      lines: [],
      prefMeta: {
        batch:                true,
        batchCompanyCount:    companyCount,
        batchFileName:        batchPayload.fileName,
        industries:           [],
        showCapQuestion:      true,
        showIndustryQuestion: false,
        shownCount:           getShownEmailsInChat(chatId).length,
        companyLabel:         '',
      },
    });
  }

  // ── Look-alike companies ────────────────────────────────────────────────────
  // "Find me companies like Otter AI." Resolves the seed (disambiguating if needed), asks how many,
  // streams the list from /api/lookalike/stream, then offers Find Contacts / Enrich / Export. This
  // whole flow is self-contained: it never touches the hunter search or its caches.

  // Entry point from the find_similar intent. Resolves the seed to a domain, disambiguating via the
  // existing candidate-picker when the name is common, then opens the count card.
  async function handleLookalikeIntent(chatId, seed, runningMsgId) {
    const basis = 'buyer_fit';
    const seedName = String(seed?.name || '').trim();
    const seedUrl  = String(seed?.url || '').trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '');

    if (!seedName && !seedUrl) {
      updateMessageById(chatId, runningMsgId, () => ({
        status: 'done', done: true, isConversational: true, conversationalIntent: 'general_chat',
        conversationalResponse: 'Which company should I find look-alikes for? Name one (a website helps if the name is common).',
        conversationalBullets: [], conversationalBlocks: [],
        lines: [], contacts: [], valid: [], risky: [], invalid: [],
      }));
      return;
    }

    // A URL was given, so there is nothing to disambiguate.
    if (seedUrl) {
      openLookalikeCountAsk(chatId, seedName || seedUrl, seedUrl, basis, runningMsgId);
      return;
    }

    updateMessageById(chatId, runningMsgId, () => ({ lines: [`Finding the right "${seedName}"…`] }));

    let resolvedList = [], ambiguousList = [];
    try {
      const r = await resolveDomains([seedName], (sellerProfile?.icp?.industries || [])[0] || '');
      resolvedList  = r.resolved  || [];
      ambiguousList = r.ambiguous || [];
    } catch { /* fall through - the backend will re-resolve defensively */ }

    const key = normEnrichName(seedName);
    const amb = ambiguousList.find(a => normEnrichName(a.name) === key);
    if (amb && amb.candidates?.length) {
      // Reuse the disambiguation card + pendingEnrich machinery; the watcher's 'lookalike' branch
      // opens the count card once the user picks.
      updateMessageById(chatId, runningMsgId, () => ({
        status: 'done', done: true,
        isDisambiguation: true, disambiguationItems: [{ name: seedName, candidates: amb.candidates }],
        lines: [], contacts: [],
      }));
      setPendingEnrich({
        kind: 'lookalike', chatId, seedName, basis,
        ambiguousItems: [{ name: seedName, candidates: amb.candidates }],
        decidedSet: [], resolvedDomains: {},
      });
      return;
    }

    const res = resolvedList.find(r => normEnrichName(r.name) === key);
    const resDomain = (res && res.domain) || '';

    // A TLD-probe resolution is a slug guess with a mail server, not an identification - a live
    // run resolved "Otter AI" to otterai.com this way and silently built the list around it. The
    // seed shapes everything downstream, so a probe-sourced domain gets confirmed with one click.
    if (resDomain && res.source === 'tld-probe') {
      const candidates = [{ domain: resDomain, title: `${seedName} - best guess from domain lookup`, snippet: '', confidence: 'low' }];
      updateMessageById(chatId, runningMsgId, () => ({
        status: 'done', done: true,
        isDisambiguation: true, disambiguationItems: [{ name: seedName, candidates }],
        lines: [], contacts: [],
      }));
      setPendingEnrich({
        kind: 'lookalike', chatId, seedName, basis,
        ambiguousItems: [{ name: seedName, candidates }],
        decidedSet: [], resolvedDomains: {},
      });
      return;
    }

    openLookalikeCountAsk(chatId, seedName, resDomain, basis, runningMsgId);
  }

  // ── Company research ("what does Virtus Medium do?", "research Stripe") ─────
  // Entry point from the company_research intent and the research_company tool. The company a
  // just-researched prospect works at is taken as known (its real LinkedIn page came off their
  // profile), so it never goes through a name-to-domain guess. Any other name is resolved the way
  // the look-alike seed is, including the candidate card when the name is ambiguous or the only
  // answer is a TLD-probe guess: a Company Brief about the wrong company is worse than a question.
  async function handleCompanyResearch(chatId, { name, domain }, runningMsgId) {
    const nm  = String(name || '').trim();
    let dom   = cleanDomain(domain);
    let liUrl = '';
    const rc  = chatsRef.current.find(c => c.id === chatId)?.research_context || null;
    const researchedPerson = rc?.name || '';
    if (rc?.company && nm && normEnrichName(rc.company) === normEnrichName(nm)) {
      dom   = dom || rc.companyDomain || '';
      liUrl = rc.companyLinkedinUrl || '';
    }

    if (!dom && !liUrl && nm) {
      updateMessageById(chatId, runningMsgId, () => ({ lines: [`Finding the right "${nm}"…`] }));
      let resolvedList = [], ambiguousList = [];
      try {
        const r = await resolveDomains([nm], (sellerProfile?.icp?.industries || [])[0] || '');
        resolvedList  = r.resolved  || [];
        ambiguousList = r.ambiguous || [];
      } catch { /* the backend resolves again */ }
      const key = normEnrichName(nm);
      const amb = ambiguousList.find(a => normEnrichName(a.name) === key);
      const res = resolvedList.find(x => normEnrichName(x.name) === key);
      const candidates = amb?.candidates?.length ? amb.candidates
        : (res?.domain && res.source === 'tld-probe')
          ? [{ domain: res.domain, title: `${nm} - best guess from domain lookup`, snippet: '', confidence: 'low' }]
          : null;
      if (candidates) {
        updateMessageById(chatId, runningMsgId, () => ({
          status: 'done', done: true,
          isDisambiguation: true, disambiguationItems: [{ name: nm, candidates }],
          lines: [], contacts: [],
        }));
        setPendingEnrich({
          kind: 'company_research', chatId, seedName: nm, researchedPerson,
          ambiguousItems: [{ name: nm, candidates }],
          decidedSet: [], resolvedDomains: {},
        });
        return 'ambiguous';
      }
      dom = res?.domain || '';
    }

    return runCompanyResearch(chatId, runningMsgId, { name: nm, domain: dom, companyLinkedinUrl: liUrl, researchedPerson });
  }

  // Run the research onto `msgId`, then the take: the waiting agent turn's next step when there
  // is one, the separate debrief otherwise. Resolves to 'done' | 'error'.
  async function runCompanyResearch(chatId, msgId, { name, domain, companyLinkedinUrl = '', researchedPerson = '' }) {
    const label = name || domain;
    updateMessageById(chatId, msgId, () => ({ status: 'running', done: false, lines: [`Researching ${label}…`] }));
    try {
      const data = await companyResearch({ company: name, domain, companyLinkedinUrl, sellerProfile });
      const summary = summarizeCompanyResearch(data, { researchedPerson });
      updateMessageById(chatId, msgId, () => ({
        status: 'done', done: true, thinking: false,
        lines: data.notFound ? [`I couldn't find anything reliable on what ${label} does.`] : [],
        isCompanyResearch: !data.notFound,
        companyResearchData: data.notFound ? null : data,
        contacts: [], valid: [], risky: [], invalid: [],
      }));
      // Kept on the chat so "is it a fit?" or "who do they sell to?" is answered from what was
      // found. Newest first, one entry per company.
      const ref = data.notFound
        ? { name: label, domain: data.domain || '', notFound: true }
        : { name: data.name || label, domain: data.domain || '', whatTheyDo: data.whatTheyDo || '', industry: data.industry || '',
            employees: data.employees || '', fit: data.fit || 'unknown', fitReason: data.fitReason || '' };
      setChats(prev => prev.map(c => c.id !== chatId ? c : {
        ...c,
        company_research_ref: [ref, ...(c.company_research_ref || []).filter(x => normEnrichName(x.name) !== normEnrichName(ref.name))].slice(0, 5),
      }));
      if (!resumeAgentTurn(chatId, msgId, 'research_company', summary, 'company')) {
        runDebrief(chatId, msgId, summary, 'company');
      }
      return 'done';
    } catch (err) {
      if (readAgentTurn(chatId)?.pending?.name === 'research_company') clearAgentTurn(chatId);
      updateMessageById(chatId, msgId, () => chatFailure(err, {
        say: `I couldn't finish researching ${label}. Try again in a moment.`,
        retryPrompt: `Research ${label}`,
      }));
      return 'error';
    }
  }

  // ── Person research ─────────────────────────────────────────────────────────
  // One brief from one LinkedIn profile, onto `msgId`: the Outreach Brief card, research_context
  // for follow-ups, then the take (the waiting agent turn's next step when there is one, the
  // separate debrief otherwise). Shared by a pasted URL, the "Research This Person" button and
  // research by name. Resolves to 'researched' | 'failed'.
  async function runPersonResearch(chatId, msgId, { url, slug = '', instruction = '', extra = {} }) {
    const s = slug || (String(url).match(/linkedin\.com\/in\/([^/?#]+)/i) || [])[1] || '';
    const profileUrl = s ? `https://www.linkedin.com/in/${s}/` : url;
    const fail = (line) => {
      if (readAgentTurn(chatId)?.pending?.name === 'research_person') clearAgentTurn(chatId);
      updateMessageById(chatId, msgId, () => ({
        status: 'done', done: true, thinking: false, lines: [line], contacts: [], valid: [], risky: [], invalid: [],
      }));
      return 'failed';
    };
    updateMessageById(chatId, msgId, () => ({ status: 'running', done: false, lines: [`Researching ${profileUrl}…`] }));
    try {
      // The name is the slug on purpose: the backend enriches it from LinkdAPI, and its
      // enrichmentFailed guard only fires when it was given nothing better.
      const data = await linkedinResearch({ profile: { name: s, linkedinUrl: profileUrl }, sellerProfile, instruction });
      if (data.enrichmentFailed) {
        // LinkdAPI returned nothing: a clear retry message instead of a brief built from the bare
        // slug, and no research_context off empty data.
        return fail(data.message || "Couldn't pull LinkedIn data for this profile right now. Try again in a moment.");
      }
      updateMessageById(chatId, msgId, () => ({
        status: 'done', done: true, thinking: false,
        lines: [`Research brief ready for ${data.subject || s}`],
        isResearch: true,
        ...extra,
        researchData: {
          subject:         data.subject         || s,
          who:             data.who             || '',
          angles:          data.angles          || [],
          painPoints:      data.painPoints      || [],
          outreachDraft:   data.outreachDraft   || null,
          companyMismatch: data.companyMismatch || false,
          freshCompany:    data.freshCompany    || '',
          originalCompany: data.originalCompany || '',
          companySnapshot: data.companySnapshot || null,
        },
        contacts: [], valid: [], risky: [], invalid: [],
      }));
      setChats(prev => prev.map(c => c.id !== chatId ? c : {
        ...c,
        research_context: researchContextFrom(data, s, profileUrl),
      }));
      const summary = summarizeResearchOutcome({ researchData: { ...data, subject: data.subject || s } });
      if (!resumeAgentTurn(chatId, msgId, 'research_person', summary, 'research')) {
        runDebrief(chatId, msgId, summary, 'research');
      }
      return 'researched';
    } catch (err) {
      if (readAgentTurn(chatId)?.pending?.name === 'research_person') clearAgentTurn(chatId);
      updateMessageById(chatId, msgId, () => chatFailure(err, {
        say: "I couldn't finish researching that profile. Try again in a moment.",
      }));
      return 'failed';
    }
  }

  // "Research Bruna Souza", "Research the Sales Development Manager at Numeral". Entry point from
  // the person_research intent and the research_person tool.
  //
  // A named person is looked for in this chat first (the researched prospect, people seen at a
  // company, every delivered row), where their LinkedIn URL is already on hand; only a stranger
  // costs a profile lookup, and only with a company, because a name alone matches anyone. A role
  // ("their manager") is a LinkedIn roster lookup at the company: one person is researched at once,
  // several are offered as chips, and everyone found is kept in seen_ref so the chip resolves here
  // for free. Resolves to 'researched' (the take arrives with the brief) | 'ended' (a reply or a
  // failure is already on the message).
  async function handlePersonResearch(chatId, { name, company, role }, msgId) {
    const chat = chatsRef.current.find(c => c.id === chatId) || null;
    const rc   = chat?.research_context || null;
    const nm   = String(name || '').trim();
    const reply = (response, suggestions = []) => {
      if (readAgentTurn(chatId)?.pending?.name === 'research_person') clearAgentTurn(chatId);
      renderConversationalReply(chatId, msgId, { intent: 'general_chat', response, suggestions });
      return 'ended';
    };

    try {
      if (nm) {
        const known = findSessionPerson(chat, nm, company);
        if (known?.linkedinUrl) {
          return (await runPersonResearch(chatId, msgId, { url: known.linkedinUrl })) === 'researched' ? 'researched' : 'ended';
        }
        const co = String(company || known?.company || '').trim();
        if (!co) return reply(`Which company is ${nm} at? Or paste their LinkedIn URL and I'll research them.`);
        updateMessageById(chatId, msgId, () => ({ thinking: false, lines: [`Finding ${nm} at ${co} on LinkedIn…`] }));
        const hit = await findPerson({ name: nm, company: co });
        if (!hit?.found || !hit.url) {
          return reply(`I couldn't find ${nm} at ${co} on LinkedIn. Paste their LinkedIn URL and I'll research them.`);
        }
        return (await runPersonResearch(chatId, msgId, { url: hit.url, slug: hit.slug })) === 'researched' ? 'researched' : 'ended';
      }

      // A role at a company. The researched prospect's own company (and its domain) when none was named.
      const co = String(company || rc?.company || '').trim();
      const titles = String(role || '').trim();
      if (!co) return reply(`Which company should I look for the ${titles.split(',')[0] || 'person'} at?`);
      const domain = (rc?.company && companyAgrees(rc.company, co)) ? (rc.companyDomain || '') : '';
      const firstTitle = titles.split(',')[0].trim();
      updateMessageById(chatId, msgId, () => ({ thinking: false, lines: [`Looking for the ${firstTitle} at ${co} on LinkedIn…`] }));
      const { people = [] } = await findRole({ company: co, domain, titles });
      if (people.length > 0) recordSeenAtCompany(chatId, people, co);

      if (people.length === 0) {
        return reply(
          `No one titled ${titles.split(',').map(t => t.trim()).filter(Boolean).join(' or ')} shows up on LinkedIn at ${co}.`,
          [{ label: `Find the ${firstTitle}`.slice(0, 40), prompt: `Find the ${firstTitle} at ${co}` }],
        );
      }
      if (people.length === 1 && people[0].linkedinUrl) {
        return (await runPersonResearch(chatId, msgId, { url: people[0].linkedinUrl })) === 'researched' ? 'researched' : 'ended';
      }
      const listed = people.map(p => `${p.name}${p.title ? ` (${p.title})` : ''}`).join('; ');
      return reply(
        `${people.length} people at ${co} could be the one: ${listed}. Pick who to research.`,
        people.slice(0, 5).map(p => ({ label: `Research ${p.name}`.slice(0, 40), prompt: `Research ${p.name} at ${co}` })),
      );
    } catch (err) {
      if (readAgentTurn(chatId)?.pending?.name === 'research_person') clearAgentTurn(chatId);
      updateMessageById(chatId, msgId, () => chatFailure(err, {
        say: "I couldn't finish that research. Try again in a moment.",
        retryPrompt: nm ? `Research ${nm}${company ? ` at ${company}` : ''}` : '',
      }));
      return 'ended';
    }
  }

  // Post (or convert a message into) the count card. Capped at 20 - a look-alike list is a
  // prospecting shortlist, and every row buys real classification calls.
  function openLookalikeCountAsk(chatId, seedName, seedDomain, basis, targetMsgId = null) {
    const msgId = targetMsgId || `${Date.now()}_${Math.random()}`;
    setPendingLookalike({ chatId, msgId, seedName, seedDomain, basis, count: 10 });
    const patch = {
      status: 'done', done: true, isLookalikeAsk: true, lines: [],
      lookalikeAsk: { seedName, seedDomain, basis, defaultCount: 10 },
    };
    if (targetMsgId) updateMessageById(chatId, msgId, () => patch);
    else addMessageToChat(chatId, { id: msgId, role: 'assistant', ...patch });
    inputRef.current?.focus();
  }

  // Run the look-alike stream and render the result list. `opts.msgId` reuses an existing running
  // bubble (the typed-number path); otherwise a fresh user echo + assistant bubble are added.
  async function runLookalike(chatId, seedName, seedDomain, basis, count, opts = {}) {
    setPendingLookalike(null);
    let msgId = opts.msgId;
    if (!opts.skipEcho) {
      const echo = `Find ${count} companies like ${seedName}`;
      addMessageToChat(chatId, { role: 'user', content: echo, contentText: echo });
    }
    if (!msgId) {
      msgId = `${Date.now()}_${Math.random()}`;
      addMessageToChat(chatId, {
        id: msgId, role: 'assistant', status: 'running', done: false,
        lines: [`Finding companies like ${seedName}…`], startedAt: Date.now(),
      });
    } else {
      updateMessageById(chatId, msgId, () => ({
        status: 'running', done: false, isLookalikeAsk: false, lookalikeAsk: null,
        lines: [`Finding companies like ${seedName}…`], startedAt: Date.now(),
      }));
    }

    setRunning(chatId, true);
    cancelRef.current = false;
    const ctrl = new AbortController();
    _searchAborts[chatId] = ctrl;
    try {
      const done = await lookalikeStream(
        { seedName, seedDomain, count, basis },
        (event) => {
          if (cancelRef.current) return;
          if (event.type === 'phase' && event.message) {
            updateMessageById(chatId, msgId, () => ({ lines: [event.message] }));
          } else if (event.type === 'progress') {
            updateMessageById(chatId, msgId, () => ({ lines: [`Found ${event.current} of ${event.total}…`] }));
          }
        },
        { signal: ctrl.signal },
      );
      updateMessageById(chatId, msgId, () => ({
        status: 'done', done: true, lines: [],
        isLookalike: true,
        lookalikeSeed:      done.seed || { name: seedName, domain: seedDomain },
        lookalikeResults:   done.results || [],
        lookalikeRequested: done.requested,
        lookalikeDelivered: done.delivered,
        lookalikeShortfall: done.shortfallReason || '',
        lookalikeBasis:     basis,
      }));
      const lookalikeSummary = summarizeLookalikeOutcome({
        seed: done.seed || { name: seedName, domain: seedDomain }, results: done.results || [],
        requested: done.requested, delivered: done.delivered, shortfall: done.shortfallReason || '', basis,
      });
      if (!resumeAgentTurn(chatId, msgId, 'find_similar_companies', lookalikeSummary, 'lookalike')) {
        runDebrief(chatId, msgId, lookalikeSummary, 'lookalike');
      }
    } catch (err) {
      updateMessageById(chatId, msgId, () => (err.cancelled
        ? { status: 'done', done: true, lines: ['Stopped.'] }
        : chatFailure(err, {
          say: `I couldn't finish the list of companies like ${seedName}.`,
          retryPrompt: `Find ${count} companies like ${seedName}`,
        })));
    } finally {
      if (_searchAborts[chatId] === ctrl) delete _searchAborts[chatId];
      setRunning(chatId, false);
      inputRef.current?.focus();
    }
  }

  // "Export List" - opens the same Export Preview panel the contacts export gets (filename,
  // CSV/Excel, column reorder, sortable preview). Client-side, never metered.
  function openLookalikeExport(seedName, rows) {
    const safe = String(seedName || 'lookalikes').replace(/[^a-z0-9]+/gi, '-').toLowerCase().replace(/^-|-$/g, '');
    setExportPanel({
      type: 'custom',
      headers: ['Company', 'Domain', 'Why similar', 'How it differs', 'Industry', 'Confidence'],
      rows: (rows || []).map(r => [r.name || '', r.domain || '', r.whySimilar || '', r.howDiffers || '', r.industry || '', r.confidence || '']),
      canSplit: false,
      defaultFilename: `lookalikes-${safe || 'list'}`,
    });
  }

  // "Find Contacts" - reuse the batch flow. The rows already carry domains, so contact-finding is
  // precise. Builds the batch payload directly rather than round-tripping through attachment state.
  function startLookalikeContacts(chatId, seedName, rows) {
    if (isRunning(chatId)) return;
    const companies = (rows || []).map(r => ({ name: r.name, domain: r.domain })).filter(c => c.name);
    if (companies.length === 0) return;
    const fileLabel = `Look-alikes of ${seedName}`;
    const icpTitles = sellerProfile?.icp?.titles || [];

    if (icpTitles.length === 0) {
      // No ICP titles to search with - hand the list to the attachment bar so the user can pick a
      // title using the existing Find Contacts / Enrich buttons.
      setAttachment({ name: fileLabel, companies });
      setAttachmentAction('find_contacts');
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }

    const askMsgId = `${Date.now()}_${Math.random()}`;
    const batchPayload = {
      chatId, designation: icpTitles[0], userTypedTitle: false,
      claudeVariations: icpTitles.length > 1 ? icpTitles : [],
      companies, fileName: fileLabel,
    };
    addMessageToChat(chatId, { role: 'user', content: 'Find Contacts', contentText: 'Find Contacts' });
    setPendingBatch(batchPayload);
    askBatchPrefs(chatId, askMsgId, batchPayload, companies.length, (patch) =>
      addMessageToChat(chatId, {
        id: askMsgId, role: 'assistant',
        icpNote: `Using ICP titles: ${icpTitles.slice(0, 3).join(', ')}${icpTitles.length > 3 ? ` +${icpTitles.length - 3} more` : ''}.`,
        ...patch,
      }));
    inputRef.current?.focus();
  }

  // ── Companies by category ("find me 5 startups in Austin", "just company names") ──────────
  //
  // The look-alike flow needs a named seed and the contact search needs a job title, so a
  // category ask with neither used to end on a card for a search the user did not want. This is
  // the third flow: a count card, a free streamed list, then the same follow-ons a look-alike
  // list has (pull contacts, find more, export).

  function openCompaniesCountAsk(chatId, criteria, targetMsgId = null) {
    const msgId = targetMsgId || `${Date.now()}_${Math.random()}`;
    const defaultCount = Math.min(Math.max(parseInt(criteria?.count, 10) || 10, 1), 20);
    const clean = {
      industry: String(criteria?.industry || '').trim(),
      location: String(criteria?.location || '').trim(),
      stage:    String(criteria?.stage || '').trim(),
      extra:    String(criteria?.extra || '').trim(),
    };
    setPendingCompanies({ chatId, msgId, criteria: clean, count: defaultCount });
    const patch = {
      status: 'done', done: true, isCompaniesAsk: true, lines: [],
      companiesAsk: { criteria: clean, defaultCount },
    };
    if (targetMsgId) updateMessageById(chatId, msgId, () => patch);
    else addMessageToChat(chatId, { id: msgId, role: 'assistant', ...patch });
    inputRef.current?.focus();
  }

  // Run the company-list stream and render the list. `opts.msgId` reuses an existing bubble (the
  // typed-number path and the card button); otherwise a user echo + assistant bubble are added.
  // `opts.exclude` carries domains already listed, for "Find N more".
  async function runCompanies(chatId, criteria, count, opts = {}) {
    setPendingCompanies(null);
    const label = describeCompanyCriteria(criteria, count);
    let msgId = opts.msgId;
    if (!opts.skipEcho) {
      const echo = `Find ${label}`;
      addMessageToChat(chatId, { role: 'user', content: echo, contentText: echo });
    }
    if (!msgId) {
      msgId = `${Date.now()}_${Math.random()}`;
      addMessageToChat(chatId, {
        id: msgId, role: 'assistant', status: 'running', done: false,
        lines: [`Finding ${label}…`], startedAt: Date.now(),
      });
    } else {
      updateMessageById(chatId, msgId, () => ({
        status: 'running', done: false, isCompaniesAsk: false, companiesAsk: null,
        lines: [`Finding ${label}…`], startedAt: Date.now(),
      }));
    }

    setRunning(chatId, true);
    cancelRef.current = false;
    const ctrl = new AbortController();
    _searchAborts[chatId] = ctrl;
    try {
      const done = await companyDiscoveryStream(
        {
          count,
          industry: criteria.industry || '', location: criteria.location || '',
          stage: criteria.stage || '', extra: criteria.extra || '',
          exclude: opts.exclude || [],
        },
        (event) => {
          if (cancelRef.current) return;
          if (event.type === 'phase' && event.message) {
            updateMessageById(chatId, msgId, () => ({ lines: [event.message] }));
          } else if (event.type === 'progress') {
            updateMessageById(chatId, msgId, () => ({ lines: [`Found ${event.current} of ${event.total}…`] }));
          }
        },
        { signal: ctrl.signal },
      );
      const rows = done.results || [];
      const usedCriteria = { ...criteria, count, ...(done.criteria || {}) };
      const summary = summarizeCompanyListOutcome({
        criteria: usedCriteria, results: rows,
        requested: done.requested, delivered: done.delivered,
        shortfall: done.shortfallReason || '', rejected: done.rejected || {},
      });
      updateMessageById(chatId, msgId, () => ({
        status: 'done', done: true, lines: [],
        isCompanyList: true,
        companyListCriteria:  usedCriteria,
        companyListResults:   rows,
        companyListRequested: done.requested,
        companyListDelivered: done.delivered,
        companyListShortfall: done.shortfallReason || '',
        companyListRejected:  done.rejected || {},
        resultSummary: summary, resultKind: 'companies',
      }));
      // Remembered on the chat so "pull contacts at the first three" resolves to names.
      setChats(prev => prev.map(c => c.id !== chatId ? c : {
        ...c,
        companies_ref: rows.slice(0, 20).map(r => ({
          name: r.name, domain: r.domain, hqCity: r.hqCity, hqState: r.hqState,
          employees: r.employees, foundedYear: r.foundedYear,
        })),
      }));
      if (!resumeAgentTurn(chatId, msgId, 'find_companies', summary, 'companies')) {
        runDebrief(chatId, msgId, summary, 'companies');
      }
    } catch (err) {
      updateMessageById(chatId, msgId, () => (err.cancelled
        ? { status: 'done', done: true, lines: ['Stopped.'] }
        : chatFailure(err, {
          say: "I couldn't finish that company list.",
          retryPrompt: `Find ${label}`,
        })));
    } finally {
      if (_searchAborts[chatId] === ctrl) delete _searchAborts[chatId];
      setRunning(chatId, false);
      inputRef.current?.focus();
    }
  }

  // "Export list" - the same Export Preview panel the contacts export gets. Client-side, never metered.
  function openCompanyListExport(criteria, rows) {
    const safe = describeCompanyCriteria(criteria).replace(/[^a-z0-9]+/gi, '-').toLowerCase().replace(/^-|-$/g, '');
    setExportPanel({
      type: 'custom',
      headers: ['Company', 'Domain', 'HQ city', 'State', 'Employees', 'Founded', 'Why it fits', 'Confidence'],
      rows: (rows || []).map(r => [r.name || '', r.domain || '', r.hqCity || '', r.hqState || '', r.employees || '', r.foundedYear || '', r.why || '', r.confidence || '']),
      canSplit: false,
      defaultFilename: `companies-${safe || 'list'}`,
    });
  }

  // "Pull contacts at these" - the batch flow, which already takes a list of companies with
  // domains and asks the per-company preferences on a card. The single search takes one company;
  // the batch path is the one that takes a list.
  function startCompanyListContacts(chatId, criteria, rows) {
    if (isRunning(chatId)) return;
    const companies = (rows || []).map(r => ({ name: r.name, domain: r.domain })).filter(c => c.name);
    if (companies.length === 0) return;
    const fileLabel = describeCompanyCriteria(criteria).replace(/^\w/, ch => ch.toUpperCase());
    const icpTitles = sellerProfile?.icp?.titles || [];

    if (icpTitles.length === 0) {
      setAttachment({ name: fileLabel, companies });
      setAttachmentAction('find_contacts');
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }

    const askMsgId = `${Date.now()}_${Math.random()}`;
    const batchPayload = {
      chatId, designation: icpTitles[0], userTypedTitle: false,
      claudeVariations: icpTitles.length > 1 ? icpTitles : [],
      companies, fileName: fileLabel,
    };
    addMessageToChat(chatId, { role: 'user', content: 'Pull contacts at these companies', contentText: 'Pull contacts at these companies' });
    setPendingBatch(batchPayload);
    askBatchPrefs(chatId, askMsgId, batchPayload, companies.length, (patch) =>
      addMessageToChat(chatId, {
        id: askMsgId, role: 'assistant',
        icpNote: `Using ICP titles: ${icpTitles.slice(0, 3).join(', ')}${icpTitles.length > 3 ? ` +${icpTitles.length - 3} more` : ''}.`,
        ...patch,
      }));
    inputRef.current?.focus();
  }

  // "Enrich Companies" - reuse the enrich flow directly. The rows carry domains, so no resolve step.
  async function startLookalikeEnrich(chatId, seedName, rows) {
    if (isRunning(chatId)) return;
    const names = (rows || []).map(r => r.name).filter(Boolean);
    if (names.length === 0) return;
    const resolvedDomains = {};
    for (const r of (rows || [])) if (r.name && r.domain) resolvedDomains[normEnrichName(r.name)] = r.domain;
    const fields = ['employees', 'revenue', 'industry', 'website', 'hqLocation', 'foundedYear', 'operatingStatus', 'description', 'specialties', 'linkedinUrl'];
    // Synthesize a one-column "original file" so the shared enriched-file exporter (which builds its
    // rows from originalData, not from results) has a row per company. Without this the download is
    // a header-only file even though the on-screen preview is full.
    const rawHeaders = ['Company'];
    const rawData    = names.map(n => [n]);

    addMessageToChat(chatId, { role: 'user', content: 'Enrich Companies', contentText: 'Enrich Companies' });
    addMessageToChat(chatId, {
      role: 'assistant', status: 'running', done: false,
      lines: [`Enriching ${names.length} ${names.length === 1 ? 'company' : 'companies'}…`],
    });
    setRunning(chatId, true);
    try {
      await executeEnrichChatFlow(chatId, names, resolvedDomains, rawHeaders, rawData, 0, `Look-alikes of ${seedName}`, fields);
    } finally {
      setRunning(chatId, false);
      inputRef.current?.focus();
    }
  }

  // ── File attachment ───────────────────────────────────────────────────────

  function processFile(file) {
    if (!file) return;

    if (/\.numbers$/i.test(file.name)) {
      alert(
        '".numbers" files are not supported.\n\n' +
        'Please export from Numbers:\n' +
        '  File → Export To → Excel (.xlsx)\n\n' +
        'Then attach the .xlsx file instead.'
      );
      return;
    }

    const isXlsx = /\.xlsx?$/i.test(file.name);
    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        // Named-contacts file takes priority (has firstName + lastName columns)
        const persons = parseFileToPersons(evt.target.result, file.name);
        if (persons && persons.length > 0) {
          if (persons.length > 25) {
            alert(`Named contacts batch is capped at 25 people. Only the first 25 will be processed.`);
          }
          setAttachment({ name: file.name, type: 'persons', persons: persons.slice(0, 25) });
          setTimeout(() => inputRef.current?.focus(), 50);
          return;
        }

        // Email-list file (a column of addresses) → the metered verify workflow.
        const emailList = parseFileToEmails(evt.target.result, file.name);
        if (emailList && emailList.length > 0) {
          if (emailList.length > 200) {
            alert('Email verification is capped at 200 addresses per file. Only the first 200 will be checked.');
          }
          setAttachment({ name: file.name, type: 'emails', emails: emailList.slice(0, 200) });
          setTimeout(() => inputRef.current?.focus(), 50);
          return;
        }

        const companies = parseFileToCompanies(evt.target.result, file.name);
        if (companies.length === 0) {
          alert('No companies found in the file. Check the format and try again.');
          return;
        }
        const hasDesignation = companies.some(c => c.designation);
        const hasCount       = companies.some(c => c.count > 0);
        const hasLocation    = companies.some(c => c.state);
        let rawHeaders = [], rawData = [], colIndex = 0, domainColIndex = -1;
        try {
          const extracted = extractRawRows(evt.target.result, file.name);
          rawHeaders = extracted.headers;
          rawData    = extracted.data;
          colIndex   = rawHeaders.indexOf(detectEnrichCompanyCol(rawHeaders));
          if (colIndex === -1) colIndex = 0;
          domainColIndex = rawHeaders.findIndex(h => /^(website|domain|web[\s_-]?site|url|company[\s_-]?url|web)$/i.test(h.trim()));
        } catch { /* non-blocking */ }
        setAttachment({ name: file.name, companies, hasDesignation, hasCount, hasLocation, rawHeaders, rawData, colIndex, domainColIndex });
        setTimeout(() => inputRef.current?.focus(), 50);
      } catch (err) {
        alert('Could not read file: ' + err.message);
      }
    };

    if (isXlsx) reader.readAsArrayBuffer(file);
    else        reader.readAsText(file);
  }

  function handleFileChange(e) {
    processFile(e.target.files?.[0]);
    e.target.value = '';
  }

  // ── Send handler ──────────────────────────────────────────────────────────

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Use typed count if provided, else default to 1
      if (pendingBatch) handleSend(input.trim() || '1');
      else handleSend();
    }
  }

  // ── Repeat-search helpers ──────────────────────────────────────────────────

  // Keywords that signal the user wants a fresh set of contacts.
  const REPEAT_INTENT = /\b(new|more|different|fresh|another|again|additional|other|extra|else)\b/i;

  // Collect the identity of every contact already shown in a chat session.
  //
  // Usually the email, which is what the server's own history keys on. LinkedIn-only rows have no
  // address at all, so they are keyed on the profile URL instead - without that, "find me 5 more"
  // would hand back the same LinkedIn contacts it just showed. The server treats this as an
  // opaque set of strings and matches whichever key a contact carries.
  function getShownEmailsInChat(chatId) {
    const chat = chatsRef.current.find(c => c.id === chatId);
    if (!chat) return [];
    const keys = new Set();
    chat.messages.forEach(msg => {
      (msg.contacts || []).forEach(({ contact }) => {
        if (contact?.email) keys.add(contact.email.toLowerCase().trim());
        else if (contact?.linkedinUrl) keys.add(contact.linkedinUrl.toLowerCase().trim());
      });
      // Batch results live under a different key and were being missed entirely, so asking for a
      // "new pool" in a chat that had already run a batch quietly re-delivered that batch's own
      // people - the one thing new-pool exists to prevent.
      (msg.batchResults?.results || []).forEach(r => {
        if (r?.status !== 'found') return;
        const email = r.result?.email || r.contact?.email;
        if (email) keys.add(email.toLowerCase().trim());
        else if (r.contact?.linkedinUrl) keys.add(r.contact.linkedinUrl.toLowerCase().trim());
      });
    });
    return [...keys];
  }

  // Called when the user clicks "New Pool" or "Not New" on a clarification message.
  async function handleIcpUpdateConfirm(chatId, msgId, proposal) {
    const { field, values, action, proposedValue } = proposal;
    const current = sellerProfile || {};
    const currentIcp = current.icp || {};

    let updatedProfile;
    if (field === 'titles') {
      updatedProfile = { ...current, icp: { ...currentIcp, titles: proposedValue } };
    } else if (field === 'industries') {
      updatedProfile = { ...current, icp: { ...currentIcp, industries: proposedValue } };
    } else if (field === 'companySize') {
      // Normalized again so a card rendered before this fix (raw "51-200") still saves "51,200".
      updatedProfile = { ...current, icp: { ...currentIcp, companySize: normalizeCompanySize(proposedValue) || currentIcp.companySize || '' } };
    } else if (field === 'valueProposition') {
      // The model's field name is valueProposition, but the profile page, the search parser and
      // the debrief all read valueProp. Writing the old key left the edit invisible.
      updatedProfile = { ...current, valueProp: proposedValue };
      delete updatedProfile.valueProposition;
    } else {
      return;
    }

    try {
      localStorage.setItem(PROFILE_KEY, JSON.stringify(updatedProfile));
      setSellerProfile(updatedProfile);
      await saveProfileToAPI(updatedProfile);
      updateMessageById(chatId, msgId, m => ({
        ...m,
        icpUpdateConfirmed: true,
        icpUpdateDismissed: false,
      }));
    } catch {
      updateMessageById(chatId, msgId, m => ({
        ...m,
        icpUpdateError: true,
      }));
    }
  }

  function handleIcpUpdateDismiss(chatId, msgId) {
    updateMessageById(chatId, msgId, m => ({ ...m, icpUpdateDismissed: true }));
  }

  // Human-readable echo of what the user chose, added to the transcript so the answered card
  // reads like a conversation rather than a form that silently vanished.
  // The broad card's place → the criteria fields the search reads. A chosen place is a state
  // filter (with the city when one was picked). Clearing a place the request or an older profile
  // had pre-filled is a deliberate "anywhere", so it goes out as country-wide and the server's
  // own country-wide rules apply. statesSource 'memory' only when every state came from the
  // rep's usual chips, so the take can say "used your usual states" and mean it.
  function placeCriteria(ask, answers) {
    const place  = answers.place || { states: [], city: '' };
    const states = Array.isArray(place.states) ? place.states : [];
    const chosen = hasPlace(place);
    const hadPrefill = hasPlace(ask.prefillPlace);
    const fromUsual  = chosen && !place.city && !hadPrefill && states.every(st => (ask.suggestedStates || []).includes(st));
    return {
      stateArray:     states.join(','),
      city:           place.city || '',
      geographyScope: chosen ? 'state' : (ask.explicitCountry || hadPrefill ? 'country' : ''),
      locationType:   chosen || ask.explicitCountry ? (answers.locationType || 'company') : '',
      statesSource:   fromUsual ? 'memory' : undefined,
    };
  }

  function describeAnswers(ask, answers) {
    const parts = [];
    if (ask.needsCap) {
      parts.push(answers.cap === 0 ? 'No per-company limit'
               : answers.cap ? `Max ${answers.cap} per company` : 'Default per-company limit');
    }
    if (ask.showRevenue && answers.revenue) parts.push(`Companies ${formatRevenueRange(answers.revenue)} in revenue`);
    parts.push(answers.pool === 'new' ? 'New pool'
      : answers.pool === 'both' ? 'New pool, gaps filled from existing'
      : 'Use existing results');
    if (ask.needsIndustry && Array.isArray(answers.industry)) {
      parts.push(answers.industry.map(d => `${d.count} ${d.industry}`).join(', '));
    } else if (ask.needsIndustry) {
      parts.push('Balanced across industries');
    }
    // The website the search was confirmed against, so the transcript records which company.
    const site = answers.company || ask.criteria?.companyDomain;
    if (site) parts.push(`at ${site}`);
    if (ask.needsLocation && answers.locationType) {
      parts.push(answers.locationType === 'person' ? `people located in ${ask.locationLabel}` : `companies based in ${ask.locationLabel}`);
    }
    if (ask.showPlace) {
      const where = describePlace(answers.place);
      if (where || ask.explicitCountry) {
        const label = where || ask.countryLabel;
        parts.push(answers.locationType === 'person' ? `people located in ${label}` : `companies based in ${label}`);
      } else {
        parts.push(`anywhere in ${ask.countryLabel}`);
      }
    }
    return parts.join(' · ');
  }

  // The ONLY place a search starts from the question card - chips and typed replies both land
  // here. That is deliberate: the previous design had a second entry point for the pool
  // question which built its criteria from scratch and quietly omitted the per-company cap, so
  // answering "max 2" and then choosing "New Pool" threw the 2 away. One path, one object,
  // nothing to forget.
  // `targetMsgId` is where the results go. Chip clicks leave it undefined so the card message
  // itself becomes the results - the old flow spawned a second message and left the answered
  // card above it, still showing its buttons. A typed answer already created its own running
  // message, so that one is passed in and the card is retired separately.
  async function submitSearchAsk(ask, answers, targetMsgId = null) {
    setPendingSearchAsk(null);
    const shownEmails = getShownEmailsInChat(ask.chatId);
    const resultMsgId = targetMsgId || ask.msgId;

    // A batch ask carries an uploaded file where a chat ask carries search criteria. The two
    // questions worth asking are the same ones - how many per company, and fresh or cached - so
    // it reuses this card and this submit rather than a second copy that would drift out of step.
    // Batch skips the industry-split question: the user chose the companies, so there is nothing
    // to split across.
    if (ask.batch) {
      setPendingBatch(null);
      const echo = `${answers.cap} per company · ${
        answers.pool === 'new' ? 'new pool' : answers.pool === 'both' ? 'new + existing' : 'use existing'}`;
      if (resultMsgId === ask.msgId) {
        addMessageToChat(ask.chatId, { role: 'user', content: echo, contentText: echo });
      }
      updateMessageById(ask.chatId, ask.msgId, () => ({
        status: 'done', done: true, isPrefQuestion: false, prefMeta: null, lines: [echo],
      }));

      setRunning(ask.chatId, true);
      await handleBatchSend(
        ask.chatId,
        ask.batch.designation,
        { companies: ask.batch.companies, name: ask.batch.fileName },
        answers.cap,
        ask.batch.userTypedTitle,
        ask.batch.claudeVariations,
        // "Both" runs the same new-pool exclusions and adds backfillExisting: the server tops up
        // any company that cannot fill from never-delivered people with cached repeats.
        answers.pool === 'new'  ? { bypassCache: true, newPool: true, excludeEmails: shownEmails }
        : answers.pool === 'both' ? { bypassCache: true, newPool: true, excludeEmails: shownEmails, backfillExisting: true }
        : {},
      );
      setRunning(ask.chatId, false);
      inputRef.current?.focus();
      return;
    }

    const criteria = {
      ...ask.criteria,
      // `newPool` is the part that makes this promise hold. `shownEmails` is only what this chat
      // is holding, so on a fresh chat it is empty and "new pool" quietly meant nothing - the
      // search returned contacts the user had already pulled in an earlier session. The flag
      // tells the server to exclude everything it has ever delivered to this user; the email
      // list stays because it also covers contacts from this session that may not be written
      // yet, and the server unions the two.
      ...(answers.pool === 'new' ? { bypassCache: true, newPool: true, excludeEmails: shownEmails } : {}),
      userPerCompanyCap:        ask.needsCap ? (answers.cap ?? null) : null,
      // The cap came from memory and the rep left it alone: the take can say "used your usual".
      ...(ask.memoryCap != null && answers.cap === ask.memoryCap ? { capSource: 'memory' } : {}),
      userIndustryDistribution: answers.industry ?? 'balanced',
      // The revenue row. '' is Any and must overwrite a range carried forward from the last
      // search: picking Any is the user clearing it. revenueAnswered tells executeSearch that the
      // card decided revenue, so an older profile's saved range cannot quietly put it back.
      ...(ask.showRevenue && answers.revenue !== undefined ? { revenueRange: answers.revenue, revenueAnswered: true } : {}),
      ...(ask.memoryRevenue && answers.revenue === ask.memoryRevenue ? { revenueSource: 'memory' } : {}),
      // A number the user picked is a number we may quote back to them. Touching the control is
      // the same kind of statement as typing "get me 25" - both mean the count is theirs.
      ...(answers.limit ? { limit: answers.limit, limitStated: true } : {}),
      // The company the card confirmed. The domain is what the search runs on; the name is filled
      // from the chosen candidate when the parser left it empty, so the Active filters chip and
      // the transcript name the company that was actually searched.
      ...(ask.needsCompany && answers.company ? {
        companyDomain: answers.company,
        companySource: 'confirmed',
        companyName:   ask.criteria?.companyName
          || (ask.companyCandidates || []).find(c => c.domain === answers.company)?.title
          || answers.company,
      } : {}),
      // Where the place applies, as confirmed on the card.
      ...(ask.needsLocation && answers.locationType ? { locationType: answers.locationType } : {}),
      // The broad card's location row decides the place outright: what it shows is what runs.
      ...(ask.showPlace ? placeCriteria(ask, answers) : {}),
    };

    if (resultMsgId === ask.msgId) {
      // Clicked the chips: echo the choice into the transcript, because nothing else recorded it.
      const echo = describeAnswers(ask, answers);
      addMessageToChat(ask.chatId, { role: 'user', content: echo, contentText: echo });
    } else {
      // Typed the answer: their own words are already in the transcript. Just retire the card
      // so it stops offering buttons for a question that has been answered.
      updateMessageById(ask.chatId, ask.msgId, () => ({
        status: 'done', done: true, isPrefQuestion: false, prefMeta: null,
        lines: [describeAnswers(ask, answers)],
      }));
    }

    updateMessageById(ask.chatId, resultMsgId, () => ({
      status: 'running', done: false, isPrefQuestion: false, prefMeta: null,
      lines: [answers.pool === 'new' ? 'Searching for a fresh pool of contacts…' : 'Searching…'],
    }));

    setRunning(ask.chatId, true);
    await executeSearch(ask.chatId, criteria, resultMsgId);
    setRunning(ask.chatId, false);
    inputRef.current?.focus();
  }

  // Re-run a finished search with a bigger budget.
  //
  // A new message rather than a mutation of the old one: the first result is a real thing the user
  // was shown and may have exported, and quietly swapping it for a longer list would lose whatever
  // they had already done with it. This reads as what it is - a second, deeper search.
  //
  // `newPool` and the exclusion list come along so the deeper run returns people they have not
  // already been given, rather than re-buying the ones on screen.
  async function rerunWithBudget(msg, newLimit) {
    const chatId = activeChatId;
    if (!chatId || !msg.searchCriteria) return;

    const echo = `Pull up to ${newLimit}`;
    addMessageToChat(chatId, { role: 'user', content: echo, contentText: echo });

    const msgId = `${Date.now()}_${Math.random()}`;
    addMessageToChat(chatId, {
      id: msgId, role: 'assistant', status: 'running',
      lines: [`Going deeper — pulling up to ${newLimit} contacts…`],
      contacts: [], done: false,
    });

    const criteria = {
      ...msg.searchCriteria,
      limit:        newLimit,
      limitStated:  true,      // they picked this one, so the copy may say so
      newPool:      true,
      bypassCache:  true,
      excludeEmails: getShownEmailsInChat(chatId),
    };

    setRunning(chatId, true);
    await executeSearch(chatId, criteria, msgId);
    setRunning(chatId, false);
    inputRef.current?.focus();
  }

  // Called when the user clicks "Find Contact Info" or "Research This Person"
  // on a LinkedIn prompt card.
  async function handleLinkedInChoice(action) {
    if (!pendingLinkedIn) return;
    const { chatId, msgId, url, slug, instruction = '' } = pendingLinkedIn;
    // Mark this action done but keep the other button visible
    setPendingLinkedIn(prev => prev ? { ...prev, [`${action}Done`]: true } : null);

    // "Research More" from auto-research follow-up should do standard research, not repeat the instruction
    const isFollowUp = pendingLinkedIn.isLinkedInFollowUp;
    const label = action === 'contact' ? 'Find Contact Info' : (isFollowUp ? 'Research More' : 'Research This Person');
    addMessageToChat(chatId, { role: 'user', content: label, contentText: label });

    const newMsgId = `${Date.now()}_${Math.random()}`;
    addMessageToChat(chatId, {
      id: newMsgId, role: 'assistant', status: 'running',
      lines: [action === 'contact' ? `Looking up contact info for ${url}…` : `Researching ${url}…`],
      contacts: [], done: false,
    });

    const updateMsg = (fn) => setChats(prev => prev.map(c =>
      c.id !== chatId ? c : {
        ...c,
        messages: c.messages.map(m => m.id === newMsgId ? { ...m, ...fn(m) } : m),
      }
    ));

    setRunning(chatId, true);

    if (action === 'contact') {
      try {
        // Check if this contact was already found in the current chat session
        // before spending an API credit on a fresh lookup.
        let data = null;
        const currentChat = chats.find(c => c.id === chatId);
        if (currentChat && slug) {
          const slugLower = slug.toLowerCase();
          for (const msg of (currentChat.messages || [])) {
            for (const { contact, result } of (msg.contacts || [])) {
              const liUrl = (contact?.linkedinUrl || '').toLowerCase();
              if (liUrl && liUrl.includes(slugLower) && result?.email && result?.verdict === 'valid') {
                data = { found: true, contact, result, source: 'session' };
                break;
              }
            }
            if (data) break;
          }
        }
        if (!data) data = await linkedinLookup({ url, slug });
        if (data.found && data.contact && data.result) {
          const verdict  = data.result?.verdict;
          const valid    = verdict === 'valid'   ? [{ contact: data.contact, result: data.result }] : [];
          const risky    = verdict === 'risky'   ? [{ contact: data.contact, result: data.result }] : [];
          const invalid  = (verdict === 'invalid' || verdict === 'unknown') ? [{ contact: data.contact, result: data.result }] : [];
          const summaryLine = data.noEmail
            ? `Found ${data.contact.name}${data.contact.company ? ` at ${data.contact.company}` : ''} — no verified email found`
            : `Found ${data.contact.name}${data.contact.company ? ` at ${data.contact.company}` : ''}: email ${verdict === 'valid' ? 'verified ✓' : verdict === 'risky' ? 'risky ⚠' : 'invalid ✗'}`;
          const sourceNote = data.source === 'cache' ? 'Results loaded from cache: 0 API calls used.' : null;
          const finalPayload = {
            status: 'done', done: true,
            lines: [...(sourceNote ? [sourceNote] : []), summaryLine],
            contacts: [{ contact: data.contact, result: data.result }],
            valid, risky, invalid,
            isPersonSearch: true,
            personSearchName: data.contact.name || '',
          };
          updateMsg(() => finalPayload);
          _pendingSearchResults[`${chatId}__${newMsgId}`] = finalPayload;
          window.dispatchEvent(new CustomEvent('repbuddy:search-complete', { detail: { chatId, msgId: newMsgId } }));
        } else {
          const notFoundPayload = {
            status: 'done', done: true,
            lines: [data.message || 'No contact info found for this profile.'],
            contacts: [], valid: [], risky: [], invalid: [],
          };
          updateMsg(() => notFoundPayload);
          _pendingSearchResults[`${chatId}__${newMsgId}`] = notFoundPayload;
          window.dispatchEvent(new CustomEvent('repbuddy:search-complete', { detail: { chatId, msgId: newMsgId } }));
        }
      } catch (err) {
        updateMsg(() => chatFailure(err, { say: "I couldn't pull contact info for that profile just now. Try again in a moment." }));
      }
    } else {
      // "Research More" follow-up should give standard research, not repeat the original instruction
      await runPersonResearch(chatId, newMsgId, { url, slug, instruction: isFollowUp ? '' : instruction });
    }

    setRunning(chatId, false);
    inputRef.current?.focus();
  }

  // `fromEdit` is an edited prompt being resent (handleEditSubmit): the composer's draft and any
  // file staged in it belong to the next message, not this one, so both are left alone.
  async function handleSend(text, { fromEdit = false } = {}) {
    const query = (text || input).trim();

    // ── If we're waiting for the user to answer "how many per company?" ────────
    if (pendingBatch && !fromEdit) {
      if (isRunning(pendingBatch.chatId)) return;
      const { designation, count: parsedCount } = parseDesignationCount(query || '1');
      // Clamped, finally. The prompt has always advertised "max 10" while sending whatever was
      // typed, so "50" became countPerCompany: 50 - fifty unlocks per company, silently.
      const raw   = parsedCount || (parseInt(query, 10) > 0 ? parseInt(query, 10) : 1);
      const count = Math.min(Math.max(raw, 1), 10);

      setInput('');
      cancelRef.current = false;   // reset stop flag so results always render
      setRunning(pendingBatch.chatId, true);

      const pb = pendingBatch;
      setPendingBatch(null);
      // Typing the answer retires the card - otherwise it sits there offering chips for a
      // question that has already been answered, and clicking one would start a second run.
      if (pendingSearchAsk?.batch) {
        updateMessageById(pendingSearchAsk.chatId, pendingSearchAsk.msgId, () => ({
          status: 'done', done: true, isPrefQuestion: false, prefMeta: null,
          lines: [`${count} per company`],
        }));
        setPendingSearchAsk(null);
      }

      // Show the user's reply in chat
      addMessageToChat(pb.chatId, { role: 'user', content: query || '1', contentText: query || '1', answersCard: true });

      await handleBatchSend(pb.chatId, pb.designation, { companies: pb.companies, name: pb.fileName }, count, pb.userTypedTitle || false, pb.claudeVariations || []);
      setRunning(pb.chatId, false);
      inputRef.current?.focus();
      return;
    }

    if (!query || isRunning(activeChatId)) return;

    cancelRef.current = false;

    // Capture & clear attachment immediately so UI updates
    const currentAttachment = fromEdit ? null : attachment;
    if (!fromEdit) {
      setInput('');
      setAttachment(null);
      setAttachmentAction(null);
    }

    let chatId = activeChatId;
    // Guard: if the stored activeChatId doesn't correspond to any chat in the
    // current state (e.g. stale localStorage ID after a reset), treat it as absent
    // so we create a fresh chat rather than silently writing into a non-existent one.
    // Read through the ref: an edit rewrites the chat and sends in the same click, before any
    // render could refresh the `chats` this closure holds.
    const chatExists = chatId && chatsRef.current.some(c => c.id === chatId);
    if (!chatExists) {
      const chat = newChatObj();
      setChats(prev => [chat, ...prev]);
      setActiveChatId(chat.id);
      chatId = chat.id;
      await sleep(50);
    }

    // chatId is now the definitive chat for this operation - mark it running
    setRunning(chatId, true);

    // Editing the first prompt empties the chat, so it is retitled like a new one (unless renamed).
    const currentChat = chatsRef.current.find(c => c.id === chatId);
    if (!currentChat?.messages?.length && !currentChat?.titleEdited) {
      updateChatTitle(chatId, query.length > 40 ? query.slice(0, 40) + '…' : query);
    }

    // ── Branch: LinkedIn URL detected in message ──────────────────────────────
    const linkedinMatch = query.match(/(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/([a-zA-Z0-9_-]+)\/?/i);
    if (linkedinMatch && !currentAttachment) {
      const slug        = linkedinMatch[1];
      const url         = `https://www.linkedin.com/in/${slug}/`;
      const instruction = query.replace(linkedinMatch[0], '').trim();

      addMessageToChat(chatId, typedUserMessage(chatId, query));

      if (instruction) {
        // URL + instruction → auto-trigger research shaped by the instruction,
        // then show follow-up action chips so the user can also find contact info.
        const msgId = `${Date.now()}_${Math.random()}`;
        addMessageToChat(chatId, {
          id: msgId, role: 'assistant', status: 'running', done: false,
          lines: [`Researching ${url}…`],
          contacts: [],
        });

        setPendingLinkedIn({ chatId, msgId, url, slug, instruction, isLinkedInFollowUp: true });

        await runPersonResearch(chatId, msgId, { url, slug, instruction, extra: { isLinkedInFollowUp: true } });

        setRunning(chatId, false);
        inputRef.current?.focus();
        return;
      }

      // URL only → show two-button prompt
      const msgId = `${Date.now()}_${Math.random()}`;
      addMessageToChat(chatId, {
        id: msgId, role: 'assistant', status: 'done', done: true,
        isLinkedInPrompt: true,
        lines: [`LinkedIn profile detected: ${url}`],
        contacts: [],
      });
      setPendingLinkedIn({ chatId, msgId, url, slug, instruction: '' });
      setRunning(chatId, false);
      inputRef.current?.focus();
      return;
    }

    // ── Branch: enrich / batch (file attached) vs regular chat search ───────────

    if (!currentAttachment && /\benrich\b/i.test(query)) {
      // User said "enrich" but forgot to attach a file
      addMessageToChat(chatId, typedUserMessage(chatId, query));
      addMessageToChat(chatId, {
        role: 'assistant', status: 'error', done: true,
        lines: ['To enrich companies, attach a CSV or Excel file with company names first. 📎'],
      });
      setRunning(chatId, false);
      inputRef.current?.focus();
      return;
    }

    if (currentAttachment) {
      // ── Email-list verification (file of addresses; 1 verification credit each) ──
      if (currentAttachment.type === 'emails') {
        const { emails, name: fileName } = currentAttachment;
        const userText = query || `Verify emails from ${fileName}`;
        addMessageToChat(chatId, { role: 'user', content: userText, contentText: userText, attachmentName: fileName, attachmentCount: emails.length, attachmentType: 'emails' });

        const msgId = `${Date.now()}_${Math.random()}`;
        addMessageToChat(chatId, {
          id: msgId, role: 'assistant', status: 'running',
          lines: [`Verifying ${emails.length} email${emails.length === 1 ? '' : 's'}…`],
          contacts: [], done: false,
        });

        // Before: estimate against the email-verifications meter (1 per address checked).
        const showListVerifyCost = !leadCredits?.unlimited;
        if (showListVerifyCost) {
          const vb = meterBalance('verify');
          const upTo = vb.metered ? Math.min(emails.length, vb.remaining) : emails.length;
          setCostNote(chatId, msgId, 'estimate', estimateLine('verify', { upTo, remaining: vb.remaining, allowance: vb.allowance }));
        }

        try {
          const data = await verifyEmailsStream(emails, (event) => {
            if (event.type === 'progress') {
              updateMessageById(chatId, msgId, () => ({ lines: [`Verifying ${event.current}/${event.total}: ${event.email}…`] }));
            }
          });

          const results = data.results || [];
          const tally = { valid: 0, risky: 0, invalid: 0, skipped: 0 };
          for (const r of results) tally[r.verdict] = (tally[r.verdict] || 0) + 1;
          const icon = (v) => (v === 'valid' ? '✅' : v === 'risky' ? '⚠️' : v === 'invalid' ? '❌' : '⏭');
          const lines = [
            `Email verification complete: ${tally.valid || 0} valid ✓, ${tally.risky || 0} risky ⚠, ${tally.invalid || 0} invalid ✗${tally.skipped ? `, ${tally.skipped} skipped (allowance reached)` : ''} out of ${results.length}.`,
            ...results.map(r => `${icon(r.verdict)} ${r.email}${r.detail ? ` - ${r.detail}` : ''}`),
          ];
          updateMessageById(chatId, msgId, () => ({ status: 'done', done: true, lines, contacts: [], valid: [], risky: [], invalid: [] }));

          // After: only non-skipped addresses were charged; ones skipped once the allowance
          // was reached are called out and not counted.
          applyMeterUsage('email_verifications', data.usage);
          if (showListVerifyCost && data.usage && typeof data.usage.remaining === 'number') {
            const charged = results.length - (tally.skipped || 0);
            setCostNote(chatId, msgId, 'actual', actualLine('verify', {
              used: charged,
              notCharged: tally.skipped || 0,
              notChargedNoun: 'address',
              notChargedReason: 'hit your allowance limit',
              remaining: data.usage.remaining,
              allowance: data.usage.allowance,
            }));
          }
        } catch (err) {
          updateMessageById(chatId, msgId, () => chatFailure(err, {
            say: "Email verification hit a problem and didn't finish. Try attaching the file again in a moment.",
          }));
        }

        setRunning(chatId, false);
        inputRef.current?.focus();
        return;
      }

      // ── Named contacts batch (firstName + lastName file) ──────────────────────
      if (currentAttachment.type === 'persons') {
        const { persons, name: fileName } = currentAttachment;
        const userText = query || `Named contacts lookup from ${fileName}`;
        addMessageToChat(chatId, { role: 'user', content: userText, contentText: userText, attachmentName: fileName, attachmentCount: persons.length, attachmentType: 'persons' });

        const msgId = `${Date.now()}_${Math.random()}`;
        addMessageToChat(chatId, {
          id: msgId, role: 'assistant', status: 'running',
          lines: [`Processing ${persons.length} contact${persons.length === 1 ? '' : 's'}…`],
          contacts: [], done: false,
        });

        const updateMsg = (fn) => setChats(prev => prev.map(c =>
          c.id !== chatId ? c : {
            ...c,
            messages: c.messages.map(m => m.id === msgId ? { ...m, ...fn(m) } : m),
          }
        ));

        try {
          const data = await namedContactsBatchStream(
            { persons },
            (event) => {
              if (event.type === 'progress') {
                updateMsg(() => ({ lines: [`Checking ${event.current}/${event.total}: ${event.name}…`] }));
              }
            }
          );

          const allContacts = data.results.flatMap(r => r.contacts || []);
          const valid   = allContacts.filter(c => c.result?.verdict === 'valid');
          const risky   = allContacts.filter(c => c.result?.verdict === 'risky');
          const invalid = allContacts.filter(c => c.result?.verdict !== 'valid' && c.result?.verdict !== 'risky');
          const notFoundNames = data.results.filter(r => !r.contacts?.length && r.status !== 'skipped').map(r => r.name);

          const lines = [
            `Named contacts batch complete: ${data.found} found, ${data.notFound} not found out of ${data.total}.`,
            ...(allContacts.length > 0 ? [`${valid.length} verified ✓, ${risky.length} risky ⚠, ${invalid.length} invalid ✗`] : []),
            ...(notFoundNames.length > 0 ? [`Not found: ${notFoundNames.join(', ')}`] : []),
          ];

          const finalPayload = {
            status: 'done', done: true, lines,
            contacts: allContacts, valid, risky, invalid,
            isNamedBatch: true,
          };

          // A named-contacts run spends LEAD credits (it delivers verified contacts), so
          // reflect the live balance in the header just like a hunt.
          if (data?.credits && typeof data.credits.remaining === 'number') {
            setLeadCredits({ available: true, unlimited: false, remaining: data.credits.remaining, allowance: data.credits.allowance, used: data.credits.used });
          }

          updateMsg(() => finalPayload);
          _pendingSearchResults[`${chatId}__${msgId}`] = finalPayload;
          window.dispatchEvent(new CustomEvent('repbuddy:search-complete', { detail: { chatId, msgId } }));
          runDebrief(chatId, msgId,
            summarizeBatchOutcome(data, { fileName }), 'batch');

          // Update session context for follow-up questions
          if (allContacts.length > 0) {
            setChats(prev => prev.map(c => {
              if (c.id !== chatId) return c;
              const updatedRef  = buildContactsRef(allContacts, c.contacts_ref || []);
              const updatedGoal = buildUpdatedGoalForBatch(c.active_goal, { results: data.results, total: data.total }, 'named contacts');
              return { ...c, contacts_ref: updatedRef, active_goal: updatedGoal };
            }));
          }
        } catch (err) {
          updateMsg(() => chatFailure(err, {
            say: "The contact lookup for your file hit a problem and didn't finish. Try attaching it again in a moment.",
          }));
        }

        setRunning(chatId, false);
        inputRef.current?.focus();
        return;
      }

      // ── Website lookup - resolve correct sites for a company list (metered) ──
      // "find websites" / "resolve urls" / "lookup websites" on a companies file.
      // 1 website-lookup credit per row we resolve; rows that already carry a URL in
      // the file pass through free (per the pricing model), and failures are free.
      if (/\b(find|resolve|look\s?up|get)\b.*\b(website|websites|domain|domains|url|urls|site|sites)\b/i.test(query)) {
        addMessageToChat(chatId, {
          role:            'user',
          content:         query,
          contentText:     query,
          attachmentName:  currentAttachment.name,
          attachmentCount: currentAttachment.companies.length,
        });
        await handleWebsiteLookup(chatId, currentAttachment);
        setRunning(chatId, false);
        inputRef.current?.focus();
        return;
      }

      // ── NEW: Enrich branch - intercept before batch search logic ─────────────
      if (/\benrich\b/i.test(query)) {
        addMessageToChat(chatId, {
          role:            'user',
          content:         query,
          contentText:     query,
          attachmentName:  currentAttachment.name,
          attachmentCount: currentAttachment.companies.length,
        });
        await handleEnrichIntent(chatId, currentAttachment);
        setRunning(chatId, false);
        inputRef.current?.focus();
        return;
      }

      const { designation: rawDesig, count: parsedCount } = parseDesignationCount(query);
      // Secondary extraction: catch natural-language counts like "Find me 5 contacts"
      // where the number isn't adjacent to a title and parseDesignationCount returns null.
      const extractedCount = parsedCount ?? (() => {
        const m = query.match(/\b([1-9]|10)\b/);
        return m ? parseInt(m[1], 10) : null;
      })();
      const perCompanyFromFile = currentAttachment.hasDesignation && !rawDesig;

      // designation: from chat message, OR flag that each company has its own in the file
      let designation        = rawDesig || (perCompanyFromFile ? '__per_company__' : '');
      let batchTitleKeywords = []; // populated below if Claude returns a comma-joined list
      // true only when user typed an explicit title (e.g. "VP of Sales").
      // Will be set to false below if Claude returns a comma-joined ICP expansion -
      // that means the user wrote a generic query ("Find me 1 contact"), not a real title.
      let userSpecifiedTitle = !!rawDesig;

      // ── Always add user message first ─────────────────────────────────────────
      addMessageToChat(chatId, {
        role:            'user',
        content:         query,
        contentText:     query,
        attachmentName:  currentAttachment.name,
        attachmentCount: currentAttachment.companies.length,
      });

      if (!designation) {
        addMessageToChat(chatId, {
          role: 'assistant', status: 'error', done: true,
          lines: ['Please type a job title (e.g. "VP of Sales") to run the batch search, or type "enrich" to enrich company data.'],
        });
        setRunning(chatId, false);
        inputRef.current?.focus();
        return;
      }

      // ── If designation comes from the file, skip Claude parsing ───────────────
      if (perCompanyFromFile) {
        addMessageToChat(chatId, {
          role: 'assistant', status: 'running', done: false,
          lines: ['Using per-company designations from file…'],
        });
      } else {
        // ── Parse through Claude so natural language works ──────────────────────
        addMessageToChat(chatId, {
          role: 'assistant', status: 'running', done: false,
          lines: ['Parsing your request…'],
        });

        try {
          const criteria = await parseHunterQuery(query, [], sellerProfile, chats.find(c => c.id === chatId)?.active_constraints || null);
          if (cancelRef.current) return;
          if (criteria.titleKeyword) {
            const rawTitle = criteria.titleKeyword.trim();
            // Claude sometimes joins all ICP titles into one comma-separated string.
            // Detect that and split back into individual keywords for per-title retries.
            if (rawTitle.includes(',')) {
              batchTitleKeywords = rawTitle.split(',').map(t => t.trim()).filter(Boolean);
              // Claude returned a comma-joined ICP expansion - user didn't type a specific title.
              // Clear the flag so we don't send titleKeyword to the backend (avoids duplicates).
              userSpecifiedTitle = false;
              // Use first title as the display designation; backend will try all via titleKeywords
              designation = batchTitleKeywords[0];
            } else {
              designation = rawTitle;
            }
            updateLastAssistant(chatId, m => ({
              lines: [...m.lines, `Searching for: "${designation}"${batchTitleKeywords.length > 1 ? ` (+${batchTitleKeywords.length - 1} title variations)` : ''}`],
            }));
          } else {
            updateLastAssistant(chatId, () => ({
              status: 'error', done: true,
              lines: [
                "Couldn't identify a job title from your message.",
                'Type just the role, e.g. "VP of Sales" or "CTO, 2".',
              ],
            }));
            setRunning(chatId, false);
            inputRef.current?.focus();
            return;
          }
        } catch (err) {
          const raw = rawDesig || query;
          if (!looksLikeJobTitle(raw)) {
            console.error('[chat failure]', err);
            updateLastAssistant(chatId, () => ({
              status: 'error', done: true,
              lines: [
                `"${raw.slice(0, 60)}${raw.length > 60 ? '…' : ''}" doesn't look like a job title.`,
                'Type just the role, e.g. "VP of Sales" or "CTO".',
              ],
              debugDetail: err?.message || '',
            }));
            setRunning(chatId, false);
            inputRef.current?.focus();
            return;
          }
          designation = raw;
          updateLastAssistant(chatId, m => ({
            lines: [...m.lines, `Using: "${designation}"`],
          }));
        }
      }

      // ── Count + pool: ask on the same card the chat path uses ────────────────
      // This used to be a free-text question ("how many per company? (max 10)") whose answer was
      // parsed with parseInt and never actually capped - typing 50 sent countPerCompany: 50. It
      // also had no way to offer the fresh-vs-cached choice, because the batch request body had
      // no bypassCache field at all.
      if (!extractedCount && !currentAttachment.hasCount) {
        const batchAskTotal = currentAttachment.companies.length;
        const askMsgId      = `${Date.now()}_${Math.random()}`;
        const batchPayload  = {
          chatId,
          designation,
          userTypedTitle: userSpecifiedTitle,
          claudeVariations: batchTitleKeywords,
          companies: currentAttachment.companies,
          fileName: currentAttachment.name,
        };

        setPendingBatch(batchPayload);
        askBatchPrefs(chatId, askMsgId, batchPayload, batchAskTotal, (patch) => updateLastAssistant(chatId, () => patch));
        setRunning(chatId, false);
        return;
      }

      const effectiveCount   = Math.min(extractedCount || 1, 10);
      const countWasCapped   = extractedCount != null && extractedCount > 10;
      const icpTitlesDirect  = sellerProfile?.icp?.titles || [];
      const titleLabel       = perCompanyFromFile
        ? '(per-company from file)'
        : !userSpecifiedTitle && icpTitlesDirect.length > 0
          ? icpTitlesDirect.join(' · ')
          : `"${designation}"`;
      const perLabel         = effectiveCount > 1 ? ` · ${effectiveCount} per company` : '';
      const capNote          = countWasCapped ? ` (capped at 10)` : '';

      const batchTotal = currentAttachment.companies.length;
      // Stamp an id onto the bubble that already exists, so everything below can address it
      // directly. mergeMessage spreads the patch over the message, so `id` here wins - and from
      // this point the run is immune to another assistant message appearing mid-batch.
      const directMsgId = `${Date.now()}_${Math.random()}`;
      updateLastAssistant(chatId, () => ({
        id: directMsgId,
        status: 'running', done: false,
        startedAt: Date.now(),
        lines: [
          `Got your file! Processing ${batchTotal} ${batchTotal === 1 ? 'company' : 'companies'}: ${titleLabel}${perLabel}${capNote}.`,
          ...(batchTotal >= 10 ? ['This may take a minute or two. Feel free to open a new chat while I work on this in the background.'] : []),
          'Reading your CSV data…',
        ],
      }));

      // Names must become domains before the search runs - see resolveBatchDomains.
      const prepDirect = await resolveBatchDomains(chatId, directMsgId, currentAttachment.companies);
      if (prepDirect.ambiguous.length > 0) {
        askBatchDisambiguation(chatId, directMsgId, prepDirect, (confirmed) => runDirectBatch(confirmed));
        return;
      }
      await runDirectBatch(prepDirect.companies);

      async function runDirectBatch(batchCompanies, prior = null) {
      const ctrl = new AbortController();
      _searchAborts[chatId] = ctrl;
      const publish = (payload) => {
        _pendingSearchResults[`${chatId}__${directMsgId}`] = payload;
        window.dispatchEvent(new CustomEvent('repbuddy:search-complete', { detail: { chatId, msgId: directMsgId } }));
      };
      try {
        const icpTitlesForBatch = sellerProfile?.icp?.titles || [];
        const mergedTitleKeywords = (!perCompanyFromFile && !userSpecifiedTitle && batchTitleKeywords.length > 0)
          ? [...new Set([...icpTitlesForBatch, ...batchTitleKeywords])]
          : icpTitlesForBatch;

        const raw = await batchSearchChunked({
          companies:       batchCompanies,
          titleKeyword:    (!perCompanyFromFile && userSpecifiedTitle) ? designation : '',
          titleKeywords:   mergedTitleKeywords,
          countPerCompany: effectiveCount,
          profileFilters,
          ...(prior ? { excludeEmails: deliveredEmailsOf(prior) } : {}),
        }, (event) => {
          if (cancelRef.current) return;
          const text = batchTickerText(event);
          if (text) setTicker(directMsgId, text);
        }, { signal: ctrl.signal });

        clearTicker(directMsgId);
        const data = prior ? mergeBatchRuns(prior, raw) : raw;

        if (cancelRef.current && (data.results || []).length === 0) {
          publish({ status: 'done', done: true, lines: ['Stopped.'] });
          return;
        }

        publish({
          status: 'done', done: true,
          lines: batchSummaryLines(data),
          batchResults: data,
          ...(data.partial ? {
            batchResume: {
              companies:       batchCompanies,
              titleKeyword:    (!perCompanyFromFile && userSpecifiedTitle) ? designation : '',
              titleKeywords:   mergedTitleKeywords,
              countPerCompany: effectiveCount,
              cacheOpts:       {},
            },
          } : {}),
        });
        runDebrief(chatId, directMsgId,
          summarizeBatchOutcome(data, { titles: mergedTitleKeywords, countPerCompany: effectiveCount, fileName: currentAttachment?.name }), 'batch');

        // Reflect the debited lead-credit balance (metered users only; devs return null).
        if (data?.credits && typeof data.credits.remaining === 'number') {
          setLeadCredits({ available: true, unlimited: false, remaining: data.credits.remaining, allowance: data.credits.allowance, used: data.credits.used });
        }

        // Same session-context update the pref-card path does - this branch never had it, so a
        // follow-up question after a direct batch had nothing to reference.
        const batchContacts = contactPairsOf(data);
        if (batchContacts.length > 0) {
          setChats(prev => prev.map(c => {
            if (c.id !== chatId) return c;
            return {
              ...c,
              contacts_ref: buildContactsRef(batchContacts, c.contacts_ref || []),
              active_goal:  buildUpdatedGoalForBatch(c.active_goal, data, designation),
            };
          }));
        }
      } catch (err) {
        clearTicker(directMsgId);
        publish(chatFailure(err, { say: "The batch hit a problem and didn't finish. Try attaching the file again in a moment." }));
      } finally {
        if (_searchAborts[chatId] === ctrl) delete _searchAborts[chatId];
      }
      } // end runDirectBatch
    } else {
      await handleRegularSend(chatId, query, { fromEdit });
    }

    setRunning(chatId, false);
    inputRef.current?.focus();
  }

  // ── Company names → domains, before a batch contact search ──────────────────
  //
  // Lead411's people search has no employer field: a domain and a name both go into the same
  // free-text parameter. A domain resolves a company; a bare name is a free-text match that
  // returned Clay Utley of BQE Software for "Clay" and a chiropractor called Basis Holistics for
  // "Basis", while Hightouch, Statsig and Glean returned nothing at all. So a name-only file has
  // to be resolved here, before the search, exactly as the enrichment flow already does.
  //
  // Companies that resolve to nothing keep an empty domain and are still sent: the backend
  // reports them as unidentified, which keeps every uploaded row present in the downloaded CSV
  // for the user to reconcile against their own list.
  function applyResolvedDomains(companies, byKey) {
    return companies.map(c => (
      c.domain ? c : { ...c, domain: byKey[normEnrichName(c.name)] || '' }
    ));
  }

  async function resolveBatchDomains(chatId, msgId, companies) {
    const unresolvedNames = companies.filter(c => !c.domain).map(c => c.name);
    if (unresolvedNames.length === 0) return { companies, ambiguous: [] };

    setTicker(msgId, `Identifying ${unresolvedNames.length} ${unresolvedNames.length === 1 ? 'company' : 'companies'}…`);

    let resolved = [], ambiguous = [];
    try {
      // No industry hint, for the reason the enrichment flow gives: a Healthcare ICP would
      // otherwise resolve "Mercury" to mercuryhealthcare.com instead of mercury.com.
      const result = await resolveDomains(unresolvedNames, '');
      resolved  = result.resolved  || [];
      ambiguous = result.ambiguous || [];
    } catch (err) {
      // Resolution being down is not a reason to lose the user's file. Every company goes on
      // with an empty domain and the backend explains itself per row.
      console.warn('[Batch] Domain resolution failed:', err.message);
      return { companies, ambiguous: [] };
    }

    const byKey = {};
    for (const r of resolved) if (r.domain) byKey[normEnrichName(r.name)] = r.domain;
    return { companies: applyResolvedDomains(companies, byKey), ambiguous };
  }

  // Posts the disambiguation card and parks the run. `resume` is called by the pendingEnrich
  // effect once every collision has been decided - see the `kind === 'batch'` branch there.
  function askBatchDisambiguation(chatId, msgId, { companies, ambiguous }, resume) {
    clearTicker(msgId);
    updateMessageById(chatId, msgId, () => ({
      status: 'done', done: true,
      lines: [
        `Identified ${companies.length - ambiguous.length} of ${companies.length} ${companies.length === 1 ? 'company' : 'companies'}.`,
        `Found multiple matches for ${ambiguous.length} ${ambiguous.length === 1 ? 'company' : 'companies'}: please confirm the correct one${ambiguous.length > 1 ? 's' : ''}:`,
      ],
      isDisambiguation: true,
      disambiguationItems: ambiguous,
    }));

    setPendingEnrich({
      kind:            'batch',
      chatId,
      ambiguousItems:  ambiguous,
      resolvedDomains: {},
      decidedSet:      [],
      batchCompanies:  companies,
      resumeBatch:     resume,
    });
    setRunning(chatId, false);
  }

  // ── Batch search - called only from the pendingBatch (count-reply) flow ─────
  // The caller is responsible for adding the user message before calling this.

  async function handleBatchSend(chatId, designation, att, countPerCompany = 1, userTypedTitle = false, claudeVariations = [], cacheOpts = {}) {
    const perLabel   = countPerCompany > 1 ? ` · ${countPerCompany} per company` : '';
    const icpTitles  = sellerProfile?.icp?.titles || [];
    const titleWord  = designation === '__per_company__'
      ? '(from file)'
      : !userTypedTitle && icpTitles.length > 0
        ? icpTitles.join(' · ')
        : `"${designation}"`;

    const batchTotal = att.companies.length;
    // A stable id for this batch's bubble.
    //
    // Every update used to address "the last assistant message in the chat", which is only correct
    // for as long as nothing else appends one. Send a second message mid-batch, or let a LinkedIn
    // prompt render, and every remaining tick - plus the results themselves - landed on the wrong
    // bubble while the batch's own stayed spinning forever. It is also the key the remount bridge
    // addresses, so it has to exist before the message does.
    const batchMsgId = `${Date.now()}_${Math.random()}`;
    addMessageToChat(chatId, {
      id: batchMsgId,
      role: 'assistant', status: 'running', done: false,
      startedAt: Date.now(),
      lines: [
        `Got your file! Processing ${batchTotal} ${batchTotal === 1 ? 'company' : 'companies'}: ${titleWord}${perLabel}.`,
        ...(batchTotal >= 10 ? ['This may take a minute or two. Feel free to open a new chat while I work on this in the background.'] : []),
        'Reading your CSV data…',
      ],
    });

    // Names must become domains before the search runs - see resolveBatchDomains. When more than
    // one domain is plausible the run parks here and the disambiguation card resumes it.
    const prep = await resolveBatchDomains(chatId, batchMsgId, att.companies);
    if (prep.ambiguous.length > 0) {
      askBatchDisambiguation(chatId, batchMsgId, prep, (confirmed) => runBatch(confirmed));
      return;
    }
    await runBatch(prep.companies);

    // `prior` carries a previous partial run when this is a "Finish remaining" resume.
    async function runBatch(batchCompanies, prior = null) {
      // Registered before the request so Stop has something to abort from the first moment, and
      // torn down in the finally so a later Stop cannot abort a batch that already finished.
      // Until now the batch registered nothing here at all, so Stop set a flag that hid the
      // output while the server ran the whole file to completion and charged for it.
      const ctrl = new AbortController();
      _searchAborts[chatId] = ctrl;

      // Results go through the module-level bridge rather than setChats directly: a Supabase
      // token refresh remounts this component mid-batch and React 18 silently discards the old
      // instance's setChats updaters - which is how a finished batch could leave its bubble
      // spinning with the results gone. Same mechanism the search path uses.
      const publish = (payload) => {
        _pendingSearchResults[`${chatId}__${batchMsgId}`] = payload;
        window.dispatchEvent(new CustomEvent('repbuddy:search-complete', { detail: { chatId, msgId: batchMsgId } }));
      };

      try {
        // Merge ICP titles + Claude's expanded variations (ICP first, then extras).
        // Only applies when user didn't type a specific title.
        const mergedTitleKeywords = (!userTypedTitle && claudeVariations.length > 0)
          ? [...new Set([...icpTitles, ...claudeVariations])]
          : icpTitles;

        const raw = await batchSearchChunked({
          companies:    batchCompanies,
          titleKeyword:    (userTypedTitle && designation !== '__per_company__') ? designation : '',
          titleKeywords:   mergedTitleKeywords,
          countPerCompany,
          profileFilters,
          ...cacheOpts,   // bypassCache + excludeEmails when the user chose a new pool
          // A resume must not re-deliver what the first leg already handed over.
          ...(prior ? { excludeEmails: [...(cacheOpts.excludeEmails || []), ...deliveredEmailsOf(prior)] } : {}),
        }, (event) => {
          if (cancelRef.current) return;
          const text = batchTickerText(event);
          if (text) setTicker(batchMsgId, text);
        }, { signal: ctrl.signal });

        clearTicker(batchMsgId);
        const data = prior ? mergeBatchRuns(prior, raw) : raw;

        // Stop with nothing to show is the only case that still reports as stopped. If companies
        // came back before the user hit Stop, those contacts were delivered and charged for -
        // throwing them away would be the one outcome nobody wants.
        if (cancelRef.current && (data.results || []).length === 0) {
          publish({ status: 'done', done: true, lines: ['Stopped.'] });
          return;
        }

        publish({
          status: 'done', done: true,
          lines: batchSummaryLines(data),
          batchResults: data,
          // Only carried on a partial - it is what "Finish remaining" needs to run the tail, and
          // there is no reason to persist a copy of the company list on every completed batch.
          ...(data.partial ? {
            batchResume: {
              companies:     batchCompanies,
              titleKeyword:  (userTypedTitle && designation !== '__per_company__') ? designation : '',
              titleKeywords: mergedTitleKeywords,
              countPerCompany,
              cacheOpts,
            },
          } : {}),
        });
        runDebrief(chatId, batchMsgId,
          summarizeBatchOutcome(data, { titles: mergedTitleKeywords, countPerCompany, fileName: att?.name }), 'batch');

        // Reflect the debited lead-credit balance (metered users only; devs return null).
        if (data?.credits && typeof data.credits.remaining === 'number') {
          setLeadCredits({ available: true, unlimited: false, remaining: data.credits.remaining, allowance: data.credits.allowance, used: data.credits.used });
        }

        // Update session context so follow-up questions can reference these contacts.
        const batchContacts = contactPairsOf(data);
        if (batchContacts.length > 0) {
          setChats(prev => prev.map(c => {
            if (c.id !== chatId) return c;
            const updatedRef  = buildContactsRef(batchContacts, c.contacts_ref || []);
            const updatedGoal = buildUpdatedGoalForBatch(c.active_goal, data, designation);
            return { ...c, contacts_ref: updatedRef, active_goal: updatedGoal };
          }));
        }
      } catch (err) {
        clearTicker(batchMsgId);
        publish(chatFailure(err, { say: "The batch hit a problem and didn't finish. Try attaching the file again in a moment." }));
      } finally {
        if (_searchAborts[chatId] === ctrl) delete _searchAborts[chatId];
      }
    } // end runBatch
  }

  // ── "Finish remaining" on a partial batch ───────────────────────────────────
  // Runs only the companies the dropped run never reached, into the SAME message, and merges the
  // two halves - so the user ends up with one result set and one CSV instead of two files to
  // reconcile by hand. The already-delivered addresses go out as excludeEmails, so the second leg
  // cannot re-deliver (or re-charge for) anyone from the first.
  async function handleFinishRemaining(chatId, msg) {
    const meta  = msg?.batchResume;
    const prior = msg?.batchResults;
    if (!meta || !prior || isRunning(chatId)) return;

    const done = new Set((prior.processedCompanyNames || []).map(n => (n || '').trim().toLowerCase()));
    const remaining = (meta.companies || []).filter((c, i) => {
      const label = (c.name || c.domain || `Company ${i + 1}`).trim().toLowerCase();
      return !done.has(label);
    });
    if (remaining.length === 0) return;

    const msgId = msg.id;
    const ctrl  = new AbortController();
    _searchAborts[chatId] = ctrl;
    const publish = (payload) => {
      _pendingSearchResults[`${chatId}__${msgId}`] = payload;
      window.dispatchEvent(new CustomEvent('repbuddy:search-complete', { detail: { chatId, msgId } }));
    };

    cancelRef.current = false;
    setRunning(chatId, true);
    updateMessageById(chatId, msgId, () => ({ status: 'running', done: false, startedAt: Date.now() }));

    try {
      const raw = await batchSearchChunked({
        companies:       remaining,
        titleKeyword:    meta.titleKeyword  || '',
        titleKeywords:   meta.titleKeywords || [],
        countPerCompany: meta.countPerCompany || 1,
        profileFilters,
        ...(meta.cacheOpts || {}),
        excludeEmails: [
          ...((meta.cacheOpts || {}).excludeEmails || []),
          ...deliveredEmailsOf(prior),
        ],
      }, (event) => {
        if (cancelRef.current) return;
        const text = batchTickerText(event);
        if (text) setTicker(msgId, text);
      }, { signal: ctrl.signal });

      clearTicker(msgId);
      const data = mergeBatchRuns(prior, raw);
      publish({
        status: 'done', done: true,
        lines: batchSummaryLines(data),
        batchResults: data,
        ...(data.partial ? { batchResume: meta } : {}),
      });
      runDebrief(chatId, msgId,
        summarizeBatchOutcome(data, { titles: meta?.titleKeywords, countPerCompany: meta?.countPerCompany }), 'batch');
      if (data?.credits && typeof data.credits.remaining === 'number') {
        setLeadCredits({ available: true, unlimited: false, remaining: data.credits.remaining, allowance: data.credits.allowance, used: data.credits.used });
      }
    } catch (err) {
      clearTicker(msgId);
      // The first leg's contacts survive a failed resume. Replacing a working download with an
      // error message would lose results the user already paid for.
      console.error('[chat failure]', err);
      publish({
        status: 'done', done: true,
        lines: [...batchSummaryLines(prior), "Couldn't finish the rest just now. What's above is saved; try Finish remaining again in a moment."],
        debugDetail: err?.message || '',
        batchResults: prior,
        batchResume:  meta,
      });
    } finally {
      if (_searchAborts[chatId] === ctrl) delete _searchAborts[chatId];
      setRunning(chatId, false);
    }
  }

  // ── In-chat email verification (metered: 1 email-verification credit each) ──
  // Single address → /api/verify (+ZeroBounce signal). 2-20 inline addresses → /api/verify/bulk.
  // The most addresses /api/verify/emails/stream will take in one request (its own zod cap).
  const VERIFY_STREAM_MAX = 200;
  // Above this the inline bulk route refuses (`Max 20 emails per request`), so anything larger
  // goes to the streaming route instead.
  const VERIFY_BULK_MAX = 20;

  async function handleEmailVerifyChat(chatId, msgId, emails, { confirmed = false } = {}) {
    const verdictIcon = (v) => (v === 'valid' ? '✅' : v === 'risky' ? '⚠️' : v === 'invalid' ? '❌' : v === 'skipped' ? '⏭️' : '❔');
    const verdictText = (v) =>
      v === 'valid'   ? 'Valid - the mailbox exists and accepts mail.'
    : v === 'risky'   ? 'Risky - the domain accepts mail but we couldn\'t confirm this exact mailbox (catch-all or greylisting). Use with caution.'
    : v === 'invalid' ? 'Invalid - this mailbox does not appear to exist.'
      // 'skipped' is what the streaming route returns for rows it could not afford to check.
      // Without this case it fell to the "Unknown" text below, which reads as a verification
      // result for an address nothing was ever spent on.
    : v === 'skipped' ? 'Not checked - your verification allowance ran out before reaching it.'
    :                   'Unknown - the mail server didn\'t give a definitive answer.';

    // "We checked fewer than you gave us" has two causes and both used to be silent: the
    // streaming route marks the remainder `skipped`, and the bulk route just returns a shorter
    // list (server.js: emails.slice(0, evPf.remaining)). Counting covers both.
    const coverageLines = (results, asked) => {
      const checked = results.filter(r => r.verdict !== 'skipped').length;
      if (checked >= asked) return [];
      return [`Verified ${checked} of ${asked}. ${asked - checked} not checked - your verification allowance ran out.`];
    };
    // NOTE: develop's `usageLine` helper is deliberately gone. The balance it printed as a
    // status line is now carried by the cost note (finishVerifyCost below), which states the
    // actual charge AND the new balance in one place. Keeping both printed it twice.
    const blockedLines = (reason) => (reason === 'trial_expired' || reason === 'leads_exhausted')
      ? ['Your Demo Week trial has ended. Choose a plan from the Account page to keep verifying emails.']
      : ["You've used all your email verifications for this period. Upgrade your plan from the Account page to keep verifying."];

    // List verification debits email_verifications (1 per address checked). Declared here but
    // not fired yet: the two guards below can refuse the run outright, and an estimate posted
    // for a search that never happens is exactly the kind of phantom cost this feature exists
    // to remove. `finishVerifyCost` reports the actual charge + new balance after each route.
    const showVerifyCost = !leadCredits?.unlimited;
    const finishVerifyCost = (usage, checked) => {
      applyMeterUsage('email_verifications', usage);
      if (showVerifyCost && usage && typeof usage.remaining === 'number') {
        setCostNote(chatId, msgId, 'actual', actualLine('verify', { used: checked, remaining: usage.remaining, allowance: usage.allowance }));
      }
    };

    // ── Too many to check in one go ───────────────────────────────────────────
    // A refusal, not a fall-through. The caller has already committed to answering here.
    if (emails.length > VERIFY_STREAM_MAX) {
      updateMessageById(chatId, msgId, () => ({
        status: 'done', done: true,
        lines: [
          `That's ${emails.length} addresses - I can verify up to ${VERIFY_STREAM_MAX} at a time.`,
          `Split the list, or upload it as a CSV and I'll work through it.`,
        ],
        contacts: [], valid: [], risky: [], invalid: [],
      }));
      return;
    }

    // ── Confirm before spending ───────────────────────────────────────────────
    // Two triggers, because either can cost more than the user expects: a large paste, or any
    // paste bigger than the remaining allowance (20 addresses against 5 credits never crosses
    // the count threshold but still returns a partial answer).
    //
    // leadCredits holds the whole /api/credits payload, meters included. It is fetched on mount
    // so it can be a little stale - fine for a warning, and the server's own pre-flight remains
    // the authority on what actually gets spent.
    const evRemaining = leadCredits?.unlimited
      ? null
      : (leadCredits?.meters?.email_verifications?.remaining ?? null);
    const overAllowance = evRemaining != null && emails.length > evRemaining;

    if (!confirmed && (emails.length > VERIFY_BULK_MAX || overAllowance)) {
      updateMessageById(chatId, msgId, () => ({
        status: 'done', done: true,
        lines: [
          `Verify ${emails.length} addresses? That uses 1 email-verification credit each.`,
          ...(overAllowance
            ? [`You have ${evRemaining} remaining, so ${evRemaining} would be checked and ${emails.length - evRemaining} left unchecked.`]
            : evRemaining != null ? [`You have ${evRemaining} remaining.`] : []),
        ],
        isVerifyConfirm: true,
        verifyEmails: emails,
        contacts: [], valid: [], risky: [], invalid: [],
      }));
      return;
    }

    // Past both guards, so this run is really going to spend. Post the worst-case estimate
    // against the email-verifications meter (1 per address checked) before it starts.
    if (showVerifyCost) {
      const vb = meterBalance('verify');
      const upTo = vb.metered ? Math.min(emails.length, vb.remaining) : emails.length;
      setCostNote(chatId, msgId, 'estimate', estimateLine('verify', { upTo, remaining: vb.remaining, allowance: vb.allowance }));
    }

    try {
      if (emails.length === 1) {
        const email = emails[0];
        updateMessageById(chatId, msgId, m => ({ ...m, lines: [`Verifying ${email}…`] }));
        const out = await verifyEmailInChat(email);

        if (out.blocked) {
          updateMessageById(chatId, msgId, () => ({ status: 'done', done: true, lines: blockedLines(out.reason), contacts: [], valid: [], risky: [], invalid: [] }));
          return;
        }
        if (out.error) {
          updateMessageById(chatId, msgId, () => ({ status: 'error', done: true, lines: [out.error], contacts: [], valid: [], risky: [], invalid: [] }));
          return;
        }

        const stepSummary = (out.steps || [])
          .filter(s => s.label && s.status)
          .map(s => `${s.status === 'pass' ? '✓' : s.status === 'fail' ? '✗' : '·'} ${s.label}${s.detail ? ` - ${s.detail}` : ''}`);
        updateMessageById(chatId, msgId, () => ({
          status: 'done', done: true,
          lines: [
            `${verdictIcon(out.verdict)} ${out.email} - ${verdictText(out.verdict)}`,
            ...(out.score != null ? [`Confidence score: ${out.score}/100.`] : []),
            ...stepSummary,
          ],
          contacts: [], valid: [], risky: [], invalid: [],
        }));
        finishVerifyCost(out.usage, 1);
        return;
      }

      // ── 21-200: the streaming route ─────────────────────────────────────────
      // The same endpoint the CSV upload uses. It meters per address, marks anything it cannot
      // afford as `skipped` rather than dropping it, and reports progress - which matters, since
      // 200 SMTP handshakes is not a request anyone should watch in silence.
      if (emails.length > VERIFY_BULK_MAX) {
        updateMessageById(chatId, msgId, m => ({ ...m, lines: [`Verifying ${emails.length} email addresses…`] }));

        const done = await verifyEmailsStream(emails, (event) => {
          if (event?.type === 'progress') {
            updateMessageById(chatId, msgId, m => ({
              ...m, lines: [`Verifying ${event.current} of ${event.total}…`],
            }));
          } else if (event?.type === 'blocked') {
            updateMessageById(chatId, msgId, () => ({
              status: 'done', done: true, lines: blockedLines(event.reason),
              contacts: [], valid: [], risky: [], invalid: [],
            }));
          }
        });

        const streamed = done?.results || [];
        if (streamed.length === 0) return;   // a `blocked` event already wrote the message

        // Skipped rows are counted in the coverage line, not listed. Twenty lines of "not
        // checked" would bury the handful that were.
        const checkedRows = streamed.filter(r => r.verdict !== 'skipped');
        const sTally = { valid: 0, risky: 0, invalid: 0 };
        for (const r of checkedRows) sTally[r.verdict] = (sTally[r.verdict] || 0) + 1;
        updateMessageById(chatId, msgId, () => ({
          status: 'done', done: true,
          lines: [
            `Checked ${checkedRows.length} email${checkedRows.length === 1 ? '' : 's'}: ${sTally.valid || 0} valid ✓, ${sTally.risky || 0} risky ⚠, ${sTally.invalid || 0} invalid ✗.`,
            ...checkedRows.map(r => `${verdictIcon(r.verdict)} ${r.email}${r.detail ? ` - ${r.detail}` : ''}`),
            ...coverageLines(streamed, emails.length),
          ],
          contacts: [], valid: [], risky: [], invalid: [],
        }));
        // Only the rows actually checked were charged; `skipped` rows cost nothing. This is the
        // 21-200 route develop added, so it had no cost note of its own - without this the
        // largest, most expensive verification in the product was the one that reported nothing.
        finishVerifyCost(done.usage, checkedRows.length);
        if (checkedRows.length >= 2) {
          runDebrief(chatId, msgId, summarizeVerifyOutcome({ rows: checkedRows, tally: sTally, requested: emails.length }), 'verify');
        }
        return;
      }

      // 2-20 inline addresses
      updateMessageById(chatId, msgId, m => ({ ...m, lines: [`Verifying ${emails.length} email addresses…`] }));
      const out = await verifyEmailsBulk(emails);

      if (out.blocked) {
        updateMessageById(chatId, msgId, () => ({ status: 'done', done: true, lines: blockedLines(out.reason), contacts: [], valid: [], risky: [], invalid: [] }));
        return;
      }
      if (out.error) {
        updateMessageById(chatId, msgId, () => ({ status: 'error', done: true, lines: [out.error], contacts: [], valid: [], risky: [], invalid: [] }));
        return;
      }

      const results = out.results || [];
      const tally = { valid: 0, risky: 0, invalid: 0 };
      for (const r of results) tally[r.verdict] = (tally[r.verdict] || 0) + 1;
      updateMessageById(chatId, msgId, () => ({
        status: 'done', done: true,
        lines: [
          `Checked ${results.length} email${results.length === 1 ? '' : 's'}: ${tally.valid || 0} valid ✓, ${tally.risky || 0} risky ⚠, ${tally.invalid || 0} invalid ✗.`,
          ...results.map(r => `${verdictIcon(r.verdict)} ${r.email}${r.detail ? ` - ${r.detail}` : r.smtpNote ? ` - ${r.smtpNote}` : ''}`),
          ...coverageLines(results, emails.length),
        ],
        contacts: [], valid: [], risky: [], invalid: [],
      }));
      finishVerifyCost(out.usage, results.length);
      if (results.length >= 2) {
        runDebrief(chatId, msgId, summarizeVerifyOutcome({ rows: results, tally, requested: emails.length }), 'verify');
      }
    } catch (err) {
      updateMessageById(chatId, msgId, () => chatFailure(err, {
        say: "Email verification hit a problem and didn't finish. Try again in a moment.",
      }));
    }
  }

  // ── Regular single-query search ───────────────────────────────────────────

  // What the parser reads for follow-ups like "10 more like those": the same structured account
  // of each search that the chat model gets. Read through the ref so a call made after an await
  // (a resumed agent turn) sees the chat as it is now, not as it was when the closure formed.
  function buildParserHistory(chatId) {
    return (chatsRef.current.find(c => c.id === chatId)?.messages || [])
      .filter(m => m.role === 'user' || (m.role === 'assistant' && m.done))
      .slice(-8)
      .map(m => ({
        role:    m.role,
        content: m.role === 'user' ? (m.contentText || m.content) : describeAssistantTurn(m),
      }));
  }

  async function handleRegularSend(chatId, query, { fromEdit = false } = {}) {
    const history = buildParserHistory(chatId);

    // Pre-generate a stable ID so executeSearch always updates THIS message,
    // even if the user fires a second search before the first finishes.
    const runningMsgId = `${Date.now()}_${Math.random()}`;
    // The prompt's id too, so a card interceptor below can mark it as that card's answer.
    const userMsgId = `${Date.now()}_${Math.random()}_u`;
    // Not updateMessageById: its miss falls back to the last assistant message.
    const markCardAnswer = () => setChats(prev => prev.map(c => c.id !== chatId ? c : {
      ...c, messages: c.messages.map(m => (m.id === userMsgId ? { ...m, answersCard: true } : m)),
    }));

    addMessageToChat(chatId, typedUserMessage(chatId, query, { id: userMsgId }));
    addMessageToChat(chatId, {
      id: runningMsgId,
      role: 'assistant', status: 'running',
      // No status line yet. `thinking` renders the typing indicator until the model has
      // either started a reply (streaming) or decided this is a search (status lines).
      lines: [], thinking: true,
      contacts: [], done: false,
      // The clock's origin. It lives on the message rather than in component state so it keeps
      // counting through a remount, a tab switch or a reload - see ElapsedTime.
      startedAt: Date.now(),
    });

    try {
      await sleep(300);

      // ── Answer to the pre-search card - must run BEFORE intent classification.
      // classifyAndRespond would read "max 2 per company, new pool" as general_chat and reply
      // conversationally, so the typed answer is intercepted here.
      //
      // Chips and typing converge on submitSearchAsk, so the two can never diverge - which is
      // the point, because the typed path is where the old parser silently dropped "2".
      //
      // None of the three card interceptors applies to an edited prompt: any open card sat below
      // the prompt being edited and went away with it (resetChatInFlight), but this closure still
      // holds the state from the click.
      if (!fromEdit && pendingSearchAsk?.chatId === chatId) {
        const ask   = pendingSearchAsk;
        const prefs = parsePreferenceAnswer(query, ask.criteria, profileFilters);
        const lower = query.toLowerCase();
        // "both" is checked first: "both new and existing" matches the other two patterns too.
        // Batch-only, matching the chip - a chat ask typing "both" falls through to the others.
        const pool  = ask.batch && /\bboth\b/.test(lower)                                      ? 'both'
                    : /\bnew\s*pool\b|\bfresh\b|\bdifferent\b|\bnew\s*contacts?\b/.test(lower) ? 'new'
                    : /\bexisting\b|\bsame\b|\bcache\b|\bkeep\b|\bcurrent\b/.test(lower)       ? 'existing'
                    : undefined;

        // Only a message that reads as an answer is one. This used to swallow whatever came next:
        // a click on "Draft intro to Numeral founder" while a card was open was parsed as
        // preferences (no cap named, so the card's own defaults stood), the card was retired as
        // answered, and the search the user had walked away from ran anyway, twice over. The
        // look-alike count card already has this rule; a card the user changed their mind about
        // is abandoned, and their message is handled as the fresh request it is.
        const wordCount    = query.trim().split(/\s+/).length;
        const goAhead      = /^(go ahead|just run it|run it|yes|yeah|yep|sure|do it|proceed|as is|ok|okay)\b/.test(lower);
        // Revenue in a typed reply ("max 2, under 10 million"); only the money clause is read.
        const typedRevenue = ask.showRevenue ? extractRevenueAnswer(query) : undefined;
        // A place named in a typed reply. States are read locally ("in Texas", "TX and CA"); a
        // city ("Austin, Texas", "in Round Rock") goes through the same lookup the location box
        // uses, so typing it in the chat and typing it in the box land the same answer. The lookup
        // is skipped when the reply already reads as a cap or pool answer, or as "go ahead".
        //
        // The lookup runs whenever the clause is more than a state name: "Austin, Texas" names a
        // state too, and reading it locally kept the state and lost the city.
        let typedPlace = ask.showPlace ? extractPlaceAnswer(query) : undefined;
        if (ask.showPlace && prefs.perCompanyCap == null && pool === undefined && !goAhead) {
          const clause = placeClauseOf(query);
          const justStates = !clause || !!resolveStateLocal(clause) || /^[A-Za-z]{2}(\s*(?:,|and)\s*[A-Za-z]{2})*$/.test(clause);
          if (clause && !justStates && !/\d/.test(clause)) {
            const r = await resolvePlace(clause);
            if (r?.kind === 'city' && r.city) typedPlace = { states: r.states || [], city: r.city };
            else if (r?.kind === 'state' && r.states?.[0] && !typedPlace) typedPlace = { states: r.states };
          }
        }
        const namedAnswer  = prefs.perCompanyCap != null || pool !== undefined || Array.isArray(prefs.industryDistribution) ||
                             typedRevenue !== undefined || typedPlace !== undefined ||
                             /\bbalanced?\b|\bevenly\b|\bsplit\b/.test(lower);
        if (!namedAnswer && !goAhead && wordCount > 6) {
          setPendingSearchAsk(null);
          updateMessageById(chatId, ask.msgId, () => ({
            status: 'done', done: true, isPrefQuestion: false, prefMeta: null, lines: [],
          }));
          // Falls through: the message is classified below like any other.
        } else {
        markCardAnswer();

        // Typed values merge OVER whatever was already clicked, so a half-answered card can be
        // finished either way round. The fields a typed answer cannot express (the budget, the
        // company, the location) carry over from the clicks; rebuilding without them is how a
        // budget the user had already raised was silently dropped by their next sentence.
        const answers = {
          cap:      ask.needsCap ? (prefs.perCompanyCap ?? ask.answers.cap) : null,
          pool:     pool ?? ask.answers.pool,
          industry: ask.needsIndustry ? (prefs.industryDistribution ?? ask.answers.industry) : 'balanced',
          ...(ask.answers.limit        ? { limit: ask.answers.limit }               : {}),
          ...(ask.answers.company      ? { company: ask.answers.company }           : {}),
          ...(ask.answers.locationType ? { locationType: ask.answers.locationType } : {}),
          ...(ask.showRevenue ? { revenue: typedRevenue ?? ask.answers.revenue } : {}),
          ...(ask.showPlace ? {
            place: typedPlace ? { states: typedPlace.states || [], city: typedPlace.city || '' } : ask.answers.place,
            // "people located in" / "companies based in" in the reply decides what the place means;
            // otherwise the card's pre-selected answer stands.
            ...(typedPlace || placeScopeOf(query)
              ? { locationType: placeScopeOf(query) || ask.answers.locationType || ask.locationDefault }
              : {}),
          } : {}),
        };

        // `pool` is not in this list: it is auto-picked when the card is built, so it is never
        // undefined and can never be the thing holding a search up. The chip still overrides it.
        const missing = [];
        if (ask.needsCap && answers.cap === undefined)      missing.push('a per-company limit');
        if (ask.needsIndustry && answers.industry === undefined) missing.push('an industry split');

        if (missing.length > 0) {
          // Re-render the card rather than guessing. Blocking means blocking.
          setPendingSearchAsk({ ...ask, answers });
          updateMessageById(chatId, runningMsgId, () => ({
            status: 'done', done: true,
            lines: [`Still need ${missing.join(' and ')} — pick below or type it.`],
          }));
          return;
        }

        await submitSearchAsk(ask, answers, runningMsgId);
        return;
        }
      }

      // ── Answer to the look-alike count card - typed number path. Same idea as the search-ask
      // interceptor above: a typed "25" must not be classified as general_chat. Only a count-shaped
      // reply is captured; a longer message abandons the card and is handled as a fresh request, so
      // the user is never trapped by a card they changed their mind about.
      if (!fromEdit && pendingLookalike?.chatId === chatId) {
        const numMatch  = query.match(/\d{1,4}/);
        const shortReply = query.trim().split(/\s+/).length <= 3;
        if (numMatch || shortReply) {
          markCardAnswer();
          const n = numMatch ? Math.min(Math.max(parseInt(numMatch[0], 10), 1), 20) : pendingLookalike.count;
          const { seedName, seedDomain, basis, msgId: askMsgId } = pendingLookalike;
          if (askMsgId) updateMessageById(chatId, askMsgId, () => ({ isLookalikeAsk: false, lookalikeAsk: null, status: 'done', done: true, lines: [] }));
          await runLookalike(chatId, seedName, seedDomain, basis, n, { msgId: runningMsgId, skipEcho: true });
          return;
        }
        // A longer message: abandon the card and let this run be handled normally below.
        updateMessageById(chatId, pendingLookalike.msgId, () => ({ isLookalikeAsk: false, lookalikeAsk: null, status: 'done', done: true, lines: [] }));
        setPendingLookalike(null);
      }

      // ── Answer to the company-list count card - typed number path. Same rules as above.
      if (!fromEdit && pendingCompanies?.chatId === chatId) {
        const numMatch  = query.match(/\d{1,4}/);
        const shortReply = query.trim().split(/\s+/).length <= 3;
        if (numMatch || shortReply) {
          markCardAnswer();
          const n = numMatch ? Math.min(Math.max(parseInt(numMatch[0], 10), 1), 20) : pendingCompanies.count;
          const { criteria, msgId: askMsgId } = pendingCompanies;
          if (askMsgId) updateMessageById(chatId, askMsgId, () => ({ isCompaniesAsk: false, companiesAsk: null, status: 'done', done: true, lines: [] }));
          await runCompanies(chatId, criteria, n, { msgId: runningMsgId, skipEcho: true });
          return;
        }
        updateMessageById(chatId, pendingCompanies.msgId, () => ({ isCompaniesAsk: false, companiesAsk: null, status: 'done', done: true, lines: [] }));
        setPendingCompanies(null);
      }

      // ── Email verification - "is x@y.com a valid email?" ──────────────────────
      // Detected locally, BEFORE the general-chat AI: the AI cannot verify emails and
      // its fallback answer used to recommend competitor tools for a feature we sell.
      // /api/verify is the metered verifier (1 email-verification credit per address).
      const emailsInQuery = query.match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g) || [];
      const wantsVerify   = /\b(verify|valid|check|deliverab\w*|bounce\w*|legit\w*|real|active|exists?|work(s|ing)?)\b/i.test(query);
      const bareEmailOnly = emailsInQuery.length === 1 && query.trim() === emailsInQuery[0];
      // Once this is an email question, it is answered HERE - by the verifier or by an explicit
      // refusal - and never by falling through to the model.
      //
      // The size test used to be part of the condition (`<= 20`), which meant a 25-address paste
      // failed it and continued on to classifyAndRespond. The model then wrote a confident list
      // of valid and invalid addresses, nothing was verified, and no credits moved - so the only
      // signal that anything was wrong was the balance not changing. Size now decides HOW the
      // question is answered, never WHETHER.
      if (emailsInQuery.length >= 1 && (wantsVerify || bareEmailOnly)) {
        await handleEmailVerifyChat(chatId, runningMsgId, emailsInQuery);
        return;
      }

      // ── Answer to a clarifying question the model asked before a search ─────────
      // The question lives on the persisted message, not in React state, so a reload with a
      // question still open does not strand it. Three kinds of reply:
      //   a fresh request        -> retire the question, classify normally below
      //   "go ahead" / "run it"  -> run the original query as written
      //   anything else          -> it is the answer; parse original + answer as one search
      //
      // Read through the ref, so a question that went away with an edited prompt is not found.
      // An edited answer is still an answer: the question right above it was marked answered the
      // first time, so for an edit that one is taken as open again.
      const liveMsgs = chatsRef.current.find(c => c.id === chatId)?.messages || [];
      const promptAt = liveMsgs.findIndex(m => m.id === userMsgId);
      const above    = promptAt > 0 ? liveMsgs[promptAt - 1] : null;
      const openAsk  = fromEdit
        ? (above?.role === 'assistant' && above.isSearchAsk && above.searchAsk ? above : null)
        : liveMsgs.slice().reverse().find(m => m.role === 'assistant' && m.isSearchAsk && m.searchAsk && !m.searchAsk.answered);
      let clarifiedQuery = null;
      if (openAsk) {
        updateMessageById(chatId, openAsk.id, m => ({ searchAsk: { ...m.searchAsk, answered: true } }));
        // In the agent loop the answer goes to the model, which sees its own question in the
        // recent conversation and writes the merged search itself. The splice below is the
        // classifier's stand-in for that.
        if (!AGENT_LOOP_ENABLED) {
          const lower = query.trim().toLowerCase();
          const words = lower.split(/\s+/).length;
          const isFresh = /^(find|get|search|show|pull|look up|give me)\b/.test(lower) || words > 12;
          const isGoAhead = /^(go ahead|just run it|run it|yes|yeah|yep|sure|do it|proceed|as is)\b/.test(lower);
          if (isGoAhead) clarifiedQuery = openAsk.searchAsk.originalQuery;
          else if (!isFresh) clarifiedQuery = `${openAsk.searchAsk.originalQuery}. Clarification: ${query}`;
        }
      }

      // ── The tool-calling loop ──────────────────────────────────────────────────
      // Model steps on the server, tools in this page. Returns 'fallback' only when the step
      // route is unreachable or failed before anything was shown, in which case the classifier
      // below takes the message exactly as it always has.
      let classified;
      if (!clarifiedQuery && AGENT_LOOP_ENABLED) {
        clearAgentTurn(chatId);   // a new message abandons any tool still waiting on a card
        const outcome = await runAgentTurn(chatId, query, { msgId: runningMsgId, into: 'reply' }, [], 0);
        if (outcome !== 'fallback') return;
        updateMessageById(chatId, runningMsgId, () => ({
          thinking: true, streaming: false, isConversational: false, conversationalIntent: undefined, conversationalResponse: undefined,
        }));
      }

      // ── Intent classification: detect general chat / ICP questions / ICP updates ──
      // Streamed. The model's reply types into the bubble as it is written; the intent arrives
      // first so a search can start its status lines without waiting for prose it will never show.
      if (clarifiedQuery) {
        classified = { intent: 'search' };
      } else {
        // The chat as it is now (an edit may have just rewritten it), minus this prompt, which
        // goes as the query - the same history the `chats` closure used to give before an edit.
        const liveChat = chatsRef.current.find(c => c.id === chatId);
        const ctxChat  = liveChat && { ...liveChat, messages: (liveChat.messages || []).filter(m => m.id !== userMsgId) };
        const chatCtxPayload = { ...assembleChatContext(ctxChat), memory: memorySummaryRef.current };
        const streamCtrl = new AbortController();
        _searchAborts[chatId] = streamCtrl;   // so Stop can end a reply mid-sentence
        // Deltas are batched: a setChats per token would re-render this page dozens of
        // times a second. ~40ms is below what reads as lag and above what reads as thrash.
        let pendingText = '';
        let flushTimer = null;
        const flush = () => {
          flushTimer = null;
          if (!pendingText) return;
          const chunk = pendingText;
          pendingText = '';
          updateMessageById(chatId, runningMsgId, m => ({ conversationalResponse: (m.conversationalResponse || '') + chunk }));
        };
        try {
          classified = await classifyAndRespondStream(query, sellerProfile, chatCtxPayload, (event) => {
            if (event.type === 'intent') {
              if (CONVERSATIONAL_INTENTS.includes(event.intent)) {
                updateMessageById(chatId, runningMsgId, () => ({
                  thinking: false, streaming: true, isConversational: true,
                  conversationalIntent: event.intent, conversationalResponse: '', lines: [],
                }));
              }
              // A search-bound intent keeps the typing indicator; the parse step below replaces it.
            } else if (event.type === 'delta') {
              pendingText += event.text;
              if (!flushTimer) flushTimer = setTimeout(flush, 40);
            }
          }, { signal: streamCtrl.signal });
        } catch (err) {
          clearTimeout(flushTimer);
          // handleStop already wrote 'Stopped.'; anything else is a real failure.
          if (err.cancelled || streamCtrl.signal.aborted) return;
          updateMessageById(chatId, runningMsgId, () => ({
            status: 'done', done: true, isConversational: true, conversationalIntent: 'general_chat',
            conversationalResponse: "I lost the thread on that one. Ask again and I'll pick it up.",
            conversationalBullets: [], conversationalBlocks: [], lines: [],
          }));
          return;
        } finally {
          if (_searchAborts[chatId] === streamCtrl) delete _searchAborts[chatId];
        }
        clearTimeout(flushTimer);
        flush();
        // The intent event may have opened a bubble that the final classification does not
        // confirm (a clarify with nothing to ask is coerced to a search, for instance). Reset
        // the live flags so a search does not run behind a half-typed reply.
        if (!CONVERSATIONAL_INTENTS.includes(classified.intent)) {
          updateMessageById(chatId, runningMsgId, () => ({
            streaming: false, isConversational: false, conversationalIntent: undefined, conversationalResponse: undefined,
          }));
        }
      }

      // ── The model asked before spending ────────────────────────────────────────
      // Its own question in its own words, with chips for the likely answers. The reply is
      // intercepted at the top of the next turn and folded into the search.
      // ── "Remember that..." / "don't show me X again" ───────────────────────
      // Applied at once (the server folds it in and returns the new memory), confirmed in the
      // model's own words, and editable later under Account, Memory.
      if (classified.intent === 'memory_update') {
        applyMemoryUpdate(classified);
        updateMessageById(chatId, runningMsgId, () => ({
          status: 'done', done: true, isConversational: true, conversationalIntent: 'memory_update',
          conversationalResponse: classified.response || 'Noted. You can change this under Account, Memory.',
          conversationalBullets: [], conversationalBlocks: [], lines: [],
          contacts: [], valid: [], risky: [], invalid: [],
        }));
        return;
      }

      if (classified.intent === 'search_clarify') {
        renderSearchAsk(chatId, runningMsgId, {
          question: classified.response, chips: classified.chips, reason: classified.reason, originalQuery: query,
        });
        return;
      }

      // ── New intents: constraint removal, session reset, corrections, verification, ambiguity ──

      if (classified.intent === 'constraint_remove') {
        const field = classified.field;
        if (field) applyClearFilter(chatId, field);
        updateMessageById(chatId, runningMsgId, () => ({
          status: 'done', done: true,
          isConversational: true, conversationalIntent: 'general_chat',
          conversationalResponse: classified.response || `Removed the ${field || ''} filter. Your next search won't include it.`,
          conversationalBullets: [],
          conversationalBlocks: [],
          lines: [], contacts: [], valid: [], risky: [], invalid: [],
        }));
        return;
      }

      if (classified.intent === 'new_search_reset') {
        applyStartFresh(chatId);
        // Fall through to parse + search — treat the message as a fresh search
        classified.intent = 'search';
      }

      if (classified.intent === 'ambiguous_correction') {
        updateMessageById(chatId, runningMsgId, () => ({
          status: 'done', done: true,
          isConversational: true, conversationalIntent: 'general_chat',
          conversationalResponse: classified.response || 'Which part would you like to change — the title, location, industry, or company?',
          conversationalBullets: [],
          conversationalBlocks: [],
          lines: [], contacts: [], valid: [], risky: [], invalid: [],
        }));
        return;
      }

      if (classified.intent === 'verify_contacts') {
        updateMessageById(chatId, runningMsgId, () => ({
          status: 'done', done: true,
          isConversational: true, conversationalIntent: 'general_chat',
          conversationalResponse: classified.response || 'Email verdicts (verified / risky / invalid) are already shown on each contact. For employment verification — checking if someone is still at the same company — use the Li button on any contact row.',
          conversationalBullets: [],
          conversationalBlocks: [],
          lines: [], contacts: [], valid: [], risky: [], invalid: [],
        }));
        return;
      }

      if (classified.intent === 'ambiguous_followup') {
        updateMessageById(chatId, runningMsgId, () => ({
          status: 'done', done: true,
          isConversational: true, conversationalIntent: 'general_chat',
          conversationalResponse: classified.response || 'Could you clarify? Did you mean to search for that as a job title, or change the industry filter?',
          conversationalBullets: classified.bullets || [],
          lines: [], contacts: [], valid: [], risky: [], invalid: [],
        }));
        return;
      }

      if (classified.intent === 'icp_update') {
        renderIcpProposal(chatId, runningMsgId, classified);
        return;
      }

      if (classified.intent === 'find_companies') {
        openCompaniesCountAsk(chatId, {
          count: classified.count, industry: classified.industry || '', location: classified.location || '',
          stage: classified.stage || '', extra: classified.extra || '',
        }, runningMsgId);
        return;
      }

      if (classified.intent === 'company_research') {
        await handleCompanyResearch(chatId, classified.company || {}, runningMsgId);
        return;
      }

      if (classified.intent === 'person_research') {
        await handlePersonResearch(chatId, classified.person || {}, runningMsgId);
        return;
      }

      if (classified.intent === 'find_similar') {
        // Misfire guard: a look-alike ask needs a comparison phrase AND a seed. Without both, treat
        // it as a normal search - this is what stops a misclassified "SDRs at Salesforce" from
        // hijacking the search flow. Mirrors the backend looksLikeLookalike guard.
        const hasCompare = /\b(like|similar to|look[\s-]?alikes?|competitors?\s+(of|to)|alternatives?\s+to|in the same space as|comparable to)\b/i.test(query);
        const seedName   = String(classified.seed?.name || '').trim();
        const seedUrl    = String(classified.seed?.url || '').trim();
        if (hasCompare && (seedName || seedUrl)) {
          await handleLookalikeIntent(chatId, classified.seed || {}, runningMsgId);
          return;
        }
        classified.intent = 'search';   // fall through to the normal parse + search path
      }

      if (classified.intent !== 'search') {
        renderConversationalReply(chatId, runningMsgId, classified);
        return;
      }

      // What the parser reads. In order: the answer to a clarifying question folded into the
      // original ask; the request the classifier rewrote when the message did not state its
      // own criteria ("yes please" to an offer, "10 more"); or the message as typed.
      if (isWritingRequest(query)) {
        renderConversationalReply(chatId, runningMsgId, { intent: 'general_chat', response: WRITING_NOT_SEARCHED });
        return;
      }
      await startSearchFromQuery(chatId, runningMsgId, { parseInput: clarifiedQuery || classified.query || query, query, history });
    } catch (err) {
      updateMessageById(chatId, runningMsgId, () => chatFailure(err, { retryPrompt: query }));
    }
  }

  // ── The search flow, from a request in words to a running search ───────────
  // Parse, apply the seller profile's defaults, then either put the preference card in front
  // of the search or, for a person lookup, run it straight away. Shared by the classifier's
  // "search" intent and the agent's search_contacts tool, so the two can never drift.
  //
  // Resolves to what happened, for the tool's benefit:
  //   'deferred'     the search is running or waiting on the card; executeSearch reports back
  //   'no_criteria'  nothing usable could be read from the request
  //   'cancelled'    the user pressed Stop
  //   'error'        the failure is already on the message
  async function startSearchFromQuery(chatId, runningMsgId, { parseInput, query, history, preset = null }) {
    try {
      let criteria;
      // `preset` is how the search resumes after the company was confirmed on a card: the criteria
      // it already parsed come back with the confirmed domain, and neither the parse nor the
      // profile defaults run a second time.
      let profileChanged = false;
      if (preset) {
        criteria       = preset.criteria;
        profileChanged = !!preset.profileChanged;
      } else {
      updateMessageById(chatId, runningMsgId, m => ({ ...m, thinking: false, lines: ['Parsing your request…'] }));

      try {
        criteria = await parseHunterQuery(parseInput, history, sellerProfile, chatsRef.current.find(c => c.id === chatId)?.active_constraints || null, memorySummaryRef.current);
      } catch (err) {
        updateMessageById(chatId, runningMsgId, () => chatFailure(err, { say: FAILED_PARSE, retryPrompt: query }));
        return 'error';
      }

      if (cancelRef.current) {
        updateLastAssistant(chatId, () => ({ status: 'done', done: true, lines: ['Stopped.'] }));
        return 'cancelled';
      }

      // ── Profile-change detection (BEFORE profile defaults) ──────────────────
      // Must run before applying profile defaults so that when we clear company/title
      // from history context, the profile defaults below can correctly fill them back in.
      // Uses localStorage (not useRef) so the stored hash survives navigation away and back.
      // useRef resets when VerifierPage unmounts (e.g. user goes to profile page and returns).
      const currentProfileHash = hashProfile(sellerProfile);
      const profileHashKey     = `repbuddy_phash_${chatId}`;
      const storedProfileHash  = localStorage.getItem(profileHashKey);
      profileChanged           = storedProfileHash !== null && storedProfileHash !== currentProfileHash;

      if (profileChanged) {
        // No longer forces bypassCache. That decision now belongs to the pool question on the
        // card, which surfaces `profileChanged` as a recommendation instead of silently
        // overriding the answer - being asked and then ignored is worse than not being asked.
        // Safe to defer: the cache key already includes industry, size, revenue and state
        // (services/cacheService.js), so most profile edits change the key and miss anyway.
        //
        // When the profile changes, Claude may have carried over company/title context
        // from earlier messages in this chat (e.g. previously searched "Cisco" and now
        // the user says "find me 5 contacts" - Claude infers Cisco from history).
        // If the company or title doesn't appear in the CURRENT query text, it came
        // from history and should be cleared so the profile defaults below take over.
        const queryLower = query.toLowerCase();

        const companyInQuery =
          (criteria.companyName   && queryLower.includes(criteria.companyName.toLowerCase())) ||
          (criteria.companyDomain && queryLower.includes(criteria.companyDomain.split('.')[0].toLowerCase()));

        if (!companyInQuery) {
          criteria.companyDomain = '';
          criteria.companyName   = '';

          const titleInQuery = criteria.titleKeyword &&
            criteria.titleKeyword.split(',').some(t => queryLower.includes(t.trim().toLowerCase()));
          if (!titleInQuery) {
            criteria.titleKeyword  = '';
            criteria.filterLevel   = ''; // also clear seniority - let profile re-apply it
          }
        }
      }
      // Always persist the latest hash so it survives navigation away and back
      try { localStorage.setItem(profileHashKey, currentProfileHash); } catch {}
      profileHashRef.current[chatId] = currentProfileHash;

      // ── Apply seller profile defaults for anything Claude couldn't extract ──
      // Runs AFTER profile-change detection so cleared fields get filled in correctly.
      // If sellerProfile state has an empty icp.titles, fall back to localStorage -
      // the Supabase API sync can sometimes overwrite state with a stale profile.
      const effectiveProfile = (sellerProfile?.icp?.titles?.length > 0)
        ? sellerProfile
        : (loadSellerProfile() || sellerProfile);
      if (effectiveProfile?.icp) {
        const icp = effectiveProfile.icp;

        // Title fallback - set both the singular string (for Lead411 search) and
        // the array (for batch search).
        //
        // Skipped when the request named a DEPARTMENT. "The sales team" is not an empty query
        // waiting to be filled in with the seller's usual titles - it is a different question,
        // and the parse route deliberately returns titleKeyword empty for it (server.js, the
        // `!parsed.functionScope` guard on the same fallback). Overwriting it here handed the
        // employment gate an SDR whitelist to judge a sales-team search against, which dropped
        // every seller found and returned zero contacts.
        //
        // The original reason for the unconditional fallback - "without titleKeyword being
        // non-empty, the backend's hasQuery check treats the request as empty and skips
        // Lead411" - no longer holds: hasQuery accepts functionScope.trim() as a query.
        if (!criteria.titleKeyword && !criteria.functionScope &&
            icp.titles?.length > 0 && !criteria.firstName && !criteria.lastName) {
          criteria.titleKeyword  = icp.titles.join(',');
          criteria.titleKeywords = icp.titles;
        }
        // Always use generic sourcing message - never surface an individual title
        criteria.displayTitle = '';

        // Geography fallback - geo is now an array; use the first entry for country
        const geoMap = {
          'United States': 'US', 'Canada': 'CA', 'United Kingdom': 'GB',
          'Australia': 'AU', 'India': 'IN', 'Europe': 'EU', 'Global': '',
        };
        {
          const geoArr  = Array.isArray(icp.geo) ? icp.geo : (icp.geo ? [icp.geo] : []);
          const primary = geoArr[0];
          if (primary && geoMap[primary] !== undefined) {
            criteria.countryCode = geoMap[primary] || 'US';
          }
        }
        // State filter - send to Lead411 ONLY when no specific company is named
        // AND the user did not explicitly name a country ("in the US", "nationwide").
        if (!criteria.stateArray && icp.geoStates?.length > 0 && !criteria.companyDomain && !criteria.companyName && criteria.geographyScope !== 'country') {
          criteria.stateArray = icp.geoStates.join(',');
        }

        // Seniority fallback:
        // - Single selection → set filterLevel on the Lead411 request (fast, server-side)
        // - Multiple selections → leave filterLevel empty, let post-filter handle it
        const seniorityMap = {
          c_suite: 'c_level', vp: 'vp_level',
          director: 'director_level', manager: 'manager_level',
        };
        if (!criteria.filterLevel && icp.seniorities?.length === 1) {
          const mapped = seniorityMap[icp.seniorities[0]];
          if (mapped) criteria.filterLevel = mapped;
        }
        // Multiple seniorities: filterLevel stays empty - post-filter does the job
      }

      if (!criteria.titleKeyword && !criteria.companyDomain && !criteria.companyName && !criteria.firstName && !criteria.lastName) {
        updateMessageById(chatId, runningMsgId, () => ({
          status: 'done',
          lines: [
            "I couldn't extract a job title or company from your request.",
            sellerProfile
              ? 'Add target job titles to your Seller Profile (👤 top-right) so I can use them as defaults.'
              : 'Try: "Find 10 VPs of Sales at Cisco"',
          ],
          done: true, contacts: [], valid: [], risky: [], invalid: [],
        }));
        return 'no_criteria';
      }

      } // end of the parse-and-defaults pass a preset skips

      // ── Always exclude already-shown emails in this chat (prevents duplicates on scroll) ──
      const shownEmails = getShownEmailsInChat(chatId);
      if (shownEmails.length > 0 && !criteria.firstName && !criteria.lastName) {
        criteria.excludeEmails = shownEmails;
      }

      // ── Ask before searching ─────────────────────────────────────────────────
      // One card carrying every question this search needs, and the search does not start
      // until each visible one is answered.
      //
      // These used to be two separate blocking cards, and the pool question `return`ed before
      // the preferences question could ever render - so on any repeat search the cap question
      // was structurally unreachable, and the pool branch then called executeSearch without
      // the cap at all. Merging them removes both bugs by construction: there is exactly one
      // place that starts a search from here, so there is nowhere left to forget a field.
      const isPersonQuery  = !!(criteria.firstName || criteria.lastName);
      const isNamedCompany = !!(criteria.companyDomain || criteria.companyName);

      // ── Which company? ─────────────────────────────────────────────────────
      // The parser only knows a domain for household names. Everything else arrives as a bare
      // name, and a bare name is not a search the backend will run (it refuses, rather than ask
      // the vendor's free-text field, which once answered "SDRs at Rackspace" from Huntress). So
      // the name is resolved here and the card asks which company, every candidate visible with
      // its website, the resolver's pick pre-selected. Only "no candidates at all" ends here.
      let companyCandidates = [];
      if (isNamedCompany && !isPersonQuery) {
        if (criteria.companyDomain) {
          companyCandidates = [{ domain: criteria.companyDomain, title: criteria.companyName || criteria.companyDomain, confidence: 'high', source: criteria.companySource || 'parsed' }];
        } else {
          const resolved = await confirmSearchCompany(chatId, runningMsgId, criteria);
          if (!resolved) return 'stopped';
          companyCandidates = resolved.candidates;
          criteria.companyDomain = resolved.best;
          criteria.companySource = 'resolved';
        }
      }
      const industries     = profileFilters?.industries || [];
      // Same reasoning as the cap below: naming a company makes the query fully user-directed and
      // executeSearch skips every profile post-filter, industry included. Asking how to split
      // results across industries that will not be applied is a question with no effect.
      const needsIndustry  = !isNamedCompany && industries.length > 1 && !criteria.industryOverride;
      // A per-company cap is meaningless when the user named the company - every result is that
      // company, and the backend ignores the cap in that case. Offering a control that provably
      // does nothing is worse than not offering it.
      const needsCap       = !isNamedCompany;

      if (!isPersonQuery) {
        // A query like "max 2 per company" pre-selects that chip rather than skipping the
        // card - the user still confirms, they just don't retype what they already said.
        const inline = extractInlinePrefs(query, criteria, profileFilters);
        const half   = Math.ceil(criteria.limit / 2);

        // ── The pool question answers itself ──────────────────────────────────
        //
        // It used to block every search, and the answer was never in doubt: if the profile
        // changed, or this chat already holds contacts, the user wants new names - otherwise the
        // cached ones are instant and free. Asking a question whose answer we can already derive
        // is a click charged for nothing.
        //
        // Auto-picked, not removed. The chip below still shows what was chosen and flipping it
        // re-runs with the other option, because this decides whether credits get spent and that
        // is not a decision to make invisibly.
        const lowerQ    = query.toLowerCase();
        const inlinePool = /\bnew\s*pool\b|\bfresh\b|\bnew\s*contacts?\b/.test(lowerQ) ? 'new'
                         : /\bexisting\b|\bcached?\b|\bsame\b/.test(lowerQ)            ? 'existing'
                         : undefined;
        const autoPool  = (profileChanged || shownEmails.length > 0) ? 'new' : 'existing';
        const poolPick  = inlinePool ?? autoPool;

        // The rep's usual cap pre-selects the chip when the message did not say. Still one
        // click to accept, and the card says where the number came from.
        const memoryCap     = memorySummaryRef.current?.usualCap ?? null;
        const capFromMemory = needsCap && inline?.perCompanyCap == null && memoryCap != null;
        // ── Company revenue ────────────────────────────────────────────────────
        // Broad searches only: a named company runs with no profile filters at all, so a revenue
        // band there would be a control that does nothing. The request's own figure wins (it may
        // be carried forward from the last search in this chat), then an older profile's saved
        // range, then the rep's usual, then Any. Always pre-answered, so it never blocks.
        const showRevenue       = !isNamedCompany;
        const queryRevenue      = parseRevenueRange(criteria.revenueRange || '');
        const profileRevenue    = parseRevenueRange(profileFilters?.revenue || '');
        const memoryRevenue     = memorySummaryRef.current?.usualRevenueRange || null;
        const revenueSource     = !showRevenue ? null
                                : queryRevenue   ? 'query'
                                : profileRevenue ? 'profile'
                                : memoryRevenue  ? 'memory'
                                : null;
        const revenuePick       = revenueSource === 'query' ? queryRevenue
                                : revenueSource === 'profile' ? profileRevenue
                                : revenueSource === 'memory' ? memoryRevenue
                                : '';
        // ── Which company, and where the people are ────────────────────────────
        // Both are answered on the card because both decide what the credits buy. The company
        // row lists every candidate the resolver found; the location row asks whether the place
        // in the request means the people or the company, pre-selected from the wording and,
        // when the wording said nothing, from the profile (whose geography is company-scoped).
        const needsCompany  = isNamedCompany && companyCandidates.length > 0;
        const placeNamed    = !!(criteria.geographyScope || criteria.stateArray);
        // The people-vs-company row as it was, for named-company searches only ("AEs at Cisco in
        // Texas"). Broad searches get the place picker below instead.
        const needsLocation = isNamedCompany && placeNamed && !!(criteria.countryCode || criteria.stateArray);
        const locationLabel = criteria.stateArray
          ? criteria.stateArray.split(',').map(s => s.trim()).filter(Boolean).join(', ')
          : (criteria.countryCode === 'US' || !criteria.countryCode ? 'the US' : criteria.countryCode);
        const profileWantsPeople = Array.isArray(profileFilters?.geoLocationType) && !profileFilters.geoLocationType.includes('hq') && profileFilters.geoLocationType.length > 0;
        const locationDefault = criteria.locationType === 'person' || criteria.locationType === 'company'
          ? criteria.locationType
          : (profileWantsPeople ? 'person' : 'company');

        // ── Where (broad searches) ─────────────────────────────────────────────
        // The place the request named, or that an older profile's saved states filled in, is
        // pre-selected and the hint says which. Nothing named means "anywhere", and the rep's
        // usual states are OFFERED as chips, never applied: they used to be injected into the
        // request and shown as if typed. Only what is picked here reaches the search and memory.
        const showPlace       = !isNamedCompany;
        const prefillPlace    = {
          states: String(criteria.stateArray || '').split(',').map(x => x.trim().toUpperCase()).filter(Boolean),
          city:   criteria.city || '',
        };
        const profileStateKey = (sellerProfile?.icp?.geoStates || []).join(',');
        const placeSource     = !hasPlace(prefillPlace) ? null
                              : (criteria.statesSource === 'profile' || (profileStateKey && criteria.stateArray === profileStateKey)) ? 'profile'
                              : 'query';
        const explicitCountry = criteria.geographyScope === 'country';
        const countryLabel    = !criteria.countryCode || criteria.countryCode === 'US' ? 'the US' : criteria.countryCode;
        const suggestedStates = countryLabel === 'the US'
          ? (criteria.suggestedStates || []).filter(st => !prefillPlace.states.includes(st))
          : [];

        const askAnswers = {
          cap:      needsCap ? (inline?.perCompanyCap ?? (capFromMemory ? memoryCap : undefined)) : null,
          pool:     poolPick,
          industry: needsIndustry ? (inline?.industryDistribution ?? undefined) : 'balanced',
          ...(needsCompany  ? { company: criteria.companyDomain } : {}),
          ...(needsLocation ? { locationType: locationDefault } : {}),
          ...(showRevenue   ? { revenue: revenuePick } : {}),
          ...(showPlace     ? {
            place: prefillPlace,
            ...(hasPlace(prefillPlace) || explicitCountry ? { locationType: locationDefault } : {}),
          } : {}),
        };
        const ask = {
          chatId, msgId: runningMsgId, criteria, needsCap, needsIndustry, answers: askAnswers, memoryCap: capFromMemory ? memoryCap : null,
          needsCompany, companyCandidates, needsLocation, locationLabel,
          showRevenue, memoryRevenue: revenueSource === 'memory' ? memoryRevenue : null,
          showPlace, prefillPlace, suggestedStates, explicitCountry, countryLabel, locationDefault,
        };

        // Two cards, because the two searches have nothing to ask each other's questions about.
        //
        //   scope  - a broad ICP search. Per-company cap and industry split are the whole point:
        //            the pool is drawn from many employers and the user is shaping the mix. The
        //            count came from their own prompt ("find me 5 founders"), so it is shown as
        //            context and not re-asked - a control that only repeats what they just typed
        //            is a click charged for nothing.
        //
        //   budget - a named company. Cap and split are both dead here (executeSearch skips every
        //            profile post-filter once a company is named), so the only question left is
        //            the one that matters most: a big employer has hundreds of sellers, and the
        //            default would quietly decide how deep to go. Credits spent cannot be handed
        //            back, which is why this is asked BEFORE the search rather than offered as a
        //            "want more?" afterwards - by then the first pass has already chosen.
        //
        // Neither blocks in any real sense: every question arrives pre-answered, so it is one
        // click to accept. What the budget card buys is the chance to say "no, go to 50" first.
        setPendingSearchAsk(ask);
        updateMessageById(chatId, runningMsgId, () => ({
          status: 'done', done: true, isPrefQuestion: true,
          lines: [],   // not rendered - the card is driven by prefMeta
          prefMeta: {
            mode:                 isNamedCompany ? 'budget' : 'scope',
            industries:           needsIndustry ? industries : [],
            limit:                criteria.limit,
            limitStated:          !!criteria.limitStated,
            exampleSplit:         needsIndustry ? `${half} ${industries[0]}, ${criteria.limit - half} ${industries[1]}` : '',
            showCapQuestion:      needsCap,
            capFromMemory,
            memoryCap:            capFromMemory ? memoryCap : null,
            showIndustryQuestion: needsIndustry,
            showRevenueQuestion:  showRevenue,
            revenueSource,
            revenuePick,
            shownCount:           shownEmails.length,
            profileChanged,
            companyLabel:         isNamedCompany ? (criteria.companyName || criteria.companyDomain) : '',
            // Shown as a link on the card: the one thing the user can check before spending.
            companyDomain:        isNamedCompany ? (criteria.companyDomain || '') : '',
            // The "which company?" row: every candidate with its website, the resolver's pick
            // pre-selected. The "location" row: people vs company for the place named.
            needsCompany,
            companyCandidates,
            needsLocation,
            locationLabel,
            showPlaceQuestion: showPlace,
            placeSource,
            suggestedStates,
            explicitCountry,
            countryLabel,
          },
        }));
        return 'deferred';
      }

      // Person searches run straight through. "Find John Smith at Acme" has neither a pool nor
      // a per-company dimension, and blocking the fastest path in the product behind a card
      // nobody can meaningfully answer would be a straight regression.
      await executeSearch(chatId, criteria, runningMsgId);
      return 'deferred';
    } catch (err) {
      updateMessageById(chatId, runningMsgId, () => chatFailure(err, { retryPrompt: query }));
      return 'error';
    }
  }

  // ── Confirm the company a search names ─────────────────────────────────────
  // Resolves a bare company name to a website before the search is offered. Mirrors the
  // look-alike seed flow: a confident answer (a known domain, or Gemini with a clear lead) goes
  // straight onto the card as a link; a probe guess or an ambiguous name opens the "which one?"
  // card with clickable candidates; nothing at all asks for the website. Resolves to
  //   'confirmed'  criteria.companyDomain is set; carry on to the card
  //   'deferred'   the disambiguation card is up; the watcher resumes the search on a choice
  //   'stopped'    no company could be identified; the user was asked for the website
  async function confirmSearchCompany(chatId, msgId, criteria) {
    const name = String(criteria.companyName || '').trim();
    updateMessageById(chatId, msgId, () => ({ thinking: false, lines: [`Finding the right "${name}"…`] }));

    let resolvedList = [], ambiguousList = [];
    try {
      const r = await resolveDomains([name], (sellerProfile?.icp?.industries || [])[0] || '', { candidates: true });
      resolvedList  = r.resolved  || [];
      ambiguousList = r.ambiguous || [];
    } catch { /* treated as unresolved below */ }

    const key = normEnrichName(name);
    const amb = ambiguousList.find(a => normEnrichName(a.name) === key);
    const res = resolvedList.find(r => normEnrichName(r.name) === key);

    // Everything the resolver considered, best first. The route used to keep only the winner
    // when one candidate looked dominant, which is how the card could not ask; now the list
    // comes back whole and the card shows it, pre-selecting the one the resolver would have
    // picked. A TLD probe is a slug guess with a mail server, so it is offered, never assumed.
    const candidates = (amb?.candidates?.length ? amb.candidates : (res?.candidates || []))
      .filter(c => c?.domain)
      .map(c => ({ domain: c.domain, title: c.title || name, confidence: c.confidence || '', source: c.source || 'gemini' }));
    if (!candidates.length && res?.domain) {
      candidates.push({ domain: res.domain, title: res.source === 'tld-probe' ? `${name} - best guess from domain lookup` : name, confidence: res.source === 'tld-probe' ? 'low' : 'high', source: res.source });
    }
    if (!candidates.length) {
      askForCompanyWebsite(chatId, msgId, name);
      return null;
    }
    const best = (res?.domain && candidates.find(c => c.domain === res.domain)) ? res.domain : candidates[0].domain;
    return { candidates, best };
  }

  // The one honest answer when a company cannot be identified: ask, never search by name.
  function askForCompanyWebsite(chatId, msgId, name, { fresh = false } = {}) {
    const reply = {
      status: 'done', done: true, isConversational: true, conversationalIntent: 'general_chat',
      conversationalResponse: `I couldn't pin down which company "${name}" is, so I haven't searched or spent anything. ` +
        `Paste their website and I'll search there, e.g. "find 5 SDRs at ${name} (their-site.com)".`,
      conversationalBullets: [], conversationalBlocks: [], lines: [], contacts: [], valid: [], risky: [], invalid: [],
    };
    if (fresh || !msgId) addMessageToChat(chatId, { role: 'assistant', ...reply });
    else updateMessageById(chatId, msgId, () => reply);
    // A search the agent was waiting on is not going to happen.
    clearAgentTurn(chatId);
  }

  // "Not this company?" on the card: retire the search and ask for the website. Reads the message,
  // not the card state, so it works on a card restored from a reload too.
  function rejectSearchCompany(chatId, msg) {
    setPendingSearchAsk(null);
    updateMessageById(chatId, msg.id, () => ({ isPrefQuestion: false, prefMeta: null, lines: [] }));
    askForCompanyWebsite(chatId, null, msg.prefMeta?.companyLabel || 'that company', { fresh: true });
    inputRef.current?.focus();
  }

  // ── After a search: ask the model what it makes of the list ──────────────
  // Streams RepBuddy's take into `debrief.text` on the result message and finishes with the
  // clickable next moves. Never throws; on any failure the field is marked failed and the
  // card is simply not rendered. The result message itself is already final by the time
  // this runs, so nothing here can make a search look like it went wrong.
  function runDebrief(chatId, msgId, summary, kind = 'search') {
    if (!msgId || !summary) return;
    // Through the ref: the `chats` closure is from whenever the flow started, which after an edit
    // still holds the version the user edited away. The result itself is described by `summary`.
    const live = chatsRef.current.find(c => c.id === chatId);
    const chat = live && { ...live, messages: (live.messages || []).filter(m => m.id !== msgId) };
    const recent = chat ? (assembleChatContext(chat)?.recentMessages || []).slice(-4) : [];
    // The summary and its kind ride along on the message so the model's history can describe
    // this run later, whichever flow produced it. Search results already carry resultSummary
    // from their final payload; for every other flow this is where it lands.
    updateMessageById(chatId, msgId, () => ({
      debrief: { status: 'pending', text: '', suggestions: [] },
      resultSummary: summary, resultKind: kind,
    }));

    let pendingText = '';
    let flushTimer = null;
    const flush = () => {
      flushTimer = null;
      if (!pendingText) return;
      const chunk = pendingText;
      pendingText = '';
      updateMessageById(chatId, msgId, m => ({ debrief: { ...(m.debrief || {}), text: ((m.debrief?.text) || '') + chunk } }));
    };

    searchDebrief(summary, sellerProfile, recent, chat?.active_goal || null, (event) => {
      if (event.type === 'delta') {
        pendingText += event.text;
        if (!flushTimer) flushTimer = setTimeout(flush, 40);
      }
    }, { kind, memory: memorySummaryRef.current }).then(result => {
      clearTimeout(flushTimer);
      if (!result || !result.narration) {
        updateMessageById(chatId, msgId, () => ({ debrief: { status: 'failed', text: '', suggestions: [] } }));
        return;
      }
      updateMessageById(chatId, msgId, () => ({
        debrief: { status: 'done', text: result.narration, suggestions: result.suggestions || [] },
      }));
    }).catch(() => {
      clearTimeout(flushTimer);
      updateMessageById(chatId, msgId, () => ({ debrief: { status: 'failed', text: '', suggestions: [] } }));
    });
  }

  // ── What the classifier's intents and the agent's tools have in common ─────
  // One body per action, called from the intent switch above and from executeAgentTool below,
  // so the two paths cannot drift: a search_clarify and an ask_user render the same card, a
  // memory_update and a remember record the same event.

  function renderConversationalReply(chatId, msgId, classified) {
    updateMessageById(chatId, msgId, () => ({
      status: 'done', done: true,
      isConversational: true,
      conversationalIntent: classified.intent,
      conversationalResponse: classified.response || "I'm not sure how to help with that. Try asking me to find contacts or look someone up.",
      conversationalBullets: classified.bullets || [],
      // Structured list/table blocks (SCRUM-41) - already validated by the backend
      conversationalBlocks: classified.blocks || [],
      // Clickable next moves the model wrote, rendered as chips under the reply.
      conversationalSuggestions: classified.suggestions || [],
      lines: [],
      contacts: [], valid: [], risky: [], invalid: [],
    }));
  }

  // The model's own question before a search, with chips for the likely answers. The reply is
  // intercepted at the top of the next turn and folded into the search.
  function renderSearchAsk(chatId, msgId, { question, chips, reason, originalQuery }) {
    updateMessageById(chatId, msgId, () => ({
      status: 'done', done: true, isConversational: true,
      conversationalIntent: 'search_clarify',
      conversationalResponse: question || 'Before I spend credits, which role should I look for?',
      conversationalBullets: [], conversationalBlocks: [], lines: [],
      isSearchAsk: true,
      searchAsk: { chips: chips || [], reason: reason || '', originalQuery, answered: false },
      contacts: [], valid: [], risky: [], invalid: [],
    }));
  }

  // "Remember that..." / "don't show me X again". Applied at once (the server folds it in and
  // returns the new memory), editable later under Account, Memory.
  function applyMemoryUpdate({ action, field, values }) {
    const list = Array.isArray(values) ? values : [];
    const f    = field || 'note';
    const event = action === 'avoid'   ? { type: 'avoid', companies: list, source: 'chat' }
                : action === 'unavoid' ? { type: 'unavoid', companies: list }
                : action === 'forget'  ? { type: 'forget', field: f, values: list }
                :                        { type: 'prefer', field: f, values: list };
    recordMemory([event]);
  }

  function applyClearFilter(chatId, field) {
    setChats(prev => prev.map(c => {
      if (c.id !== chatId) return c;
      const cur = c.active_constraints || {};
      let updated;
      if (field === 'title' || field === 'titles') {
        updated = { ...cur, titles: [] };
      } else if (field === 'location' || field === 'state') {
        updated = { ...cur, location: [] };
      } else if (field === 'size') {
        updated = { ...cur, employeeRange: '' };
      } else if (field === 'revenue') {
        updated = { ...cur, revenueRange: '' };
      } else {
        // industry, company, stage, city: scalar fields, cleared by name.
        updated = { ...cur, [field]: '' };
      }
      return { ...c, active_constraints: updated };
    }));
    // What the rep keeps taking off is a habit too.
    recordMemory([{ type: 'filter_removed', field }]);
  }

  // Wipe session context so the next search starts clean.
  function applyStartFresh(chatId) {
    setChats(prev => prev.map(c => c.id !== chatId ? c : {
      ...c,
      active_constraints: null,
      contacts_ref:       [],
      seen_ref:           [],
      enrichment_ref:     [],
      companies_ref:      [],
      company_research_ref: [],
      active_goal:        null,
      research_context:   null,
    }));
  }

  // People a named-company search saw and did not pull, kept on the chat so a later "who is the
  // CTO?" is answered by name. Used by the empty-result branch, which returns before the main
  // session-context update; the delivered branch folds the same call into that update.
  function recordSeenAtCompany(chatId, people, company) {
    if (!company || !Array.isArray(people) || people.length === 0) return;
    setChats(prev => prev.map(c => c.id !== chatId ? c
      : { ...c, seen_ref: buildSeenRef(people, company, c.seen_ref || [], c.contacts_ref || []) }));
  }

  function renderIcpProposal(chatId, msgId, { field, action, values = [], summary }) {
    const icp = sellerProfile?.icp || {};

    // Normalize an industry string → valid INDUSTRY_OPTIONS key.
    // Tries exact key match, then label match, then partial match.
    const normalizeIndustry = (raw) => {
      const r = raw.toLowerCase().trim();
      const exact = INDUSTRY_OPTIONS.find(o => o.key === r);
      if (exact) return exact.key;
      const byLabel = INDUSTRY_OPTIONS.find(o => o.label.toLowerCase() === r);
      if (byLabel) return byLabel.key;
      const partial = INDUSTRY_OPTIONS.find(o =>
        o.key.includes(r) || r.includes(o.key) ||
        o.label.toLowerCase().includes(r) || r.includes(o.label.toLowerCase())
      );
      return partial ? partial.key : null;
    };

    // Compute current and proposed values entirely in the frontend. currentLabel/proposedLabel
    // are what the card shows when the stored value isn't readable on its own (company size).
    let currentValue, proposedValue, currentLabel, proposedLabel;
    if (field === 'titles') {
      currentValue  = icp.titles || [];
      proposedValue = action === 'add'
        ? [...new Set([...currentValue, ...values])]
        : currentValue.filter(t => !values.map(v => v.toLowerCase()).includes(t.toLowerCase()));
    } else if (field === 'industries') {
      currentValue  = icp.industries || [];
      const normalizedValues = values.map(normalizeIndustry).filter(Boolean);
      proposedValue = action === 'add'
        ? [...new Set([...currentValue, ...normalizedValues])]
        : currentValue.filter(i => !normalizedValues.includes(i));
    } else if (field === 'companySize') {
      // The model proposes "51-200" or "500+"; the profile stores "min,max" on the dropdown
      // buckets, and the size filter splits on the comma. Store the normalized shape.
      currentValue  = normalizeCompanySize(icp.companySize);
      proposedValue = normalizeCompanySize(values[0]);
      currentLabel  = formatCompanySize(currentValue) || 'Not set';
      proposedLabel = formatCompanySize(proposedValue);
      if (!proposedValue) {
        // Confirming an empty value would wipe the saved size, so say so instead of offering it.
        updateMessageById(chatId, msgId, () => ({
          status: 'done', done: true,
          isConversational: true, conversationalIntent: 'general_chat',
          conversationalResponse: `I couldn't read an employee range from "${values[0] || ''}". Try something like "set my company size to 51 to 200 employees" or "500+ employees".`,
          conversationalBullets: [],
          lines: [], contacts: [], valid: [], risky: [], invalid: [],
        }));
        return;
      }
    } else if (field === 'valueProposition') {
      currentValue  = sellerProfile?.valueProp || sellerProfile?.valueProposition || 'Not set';
      proposedValue = values[0] || '';
    }

    updateMessageById(chatId, msgId, () => ({
      status: 'done', done: true,
      isIcpUpdate: true,
      icpUpdateProposal: { field, action, values, summary, currentValue, proposedValue, currentLabel, proposedLabel },
      lines: [],
      contacts: [], valid: [], risky: [], invalid: [],
    }));
  }

  // ── The agent loop ─────────────────────────────────────────────────────────
  // One model step per request (/api/chat/agent/step); the tools run here. A step ends in a
  // reply, which is rendered, or in a tool call, which is executed and answered:
  //
  //   immediate  remember, clear_filter, start_fresh   run now, answer now, next step
  //   terminal   ask_user, propose_icp_update          a card; the user's click is the next turn
  //   deferred   search_contacts, find_similar_companies
  //              the flow pauses on a card (preferences, count) and reports back from its
  //              completion site via resumeAgentTurn, which continues the loop with the
  //              result. The model's take on the list is that next step, rendered as the
  //              `debrief` under the result exactly where the separate debrief call used to put it.
  //
  // The turn waiting on a deferred tool is stored before the tool runs, because a person lookup
  // reports back from inside executeSearch, before the tool call has even returned.

  function readAgentTurn(chatId) {
    return _agentTurns[chatId] || chatsRef.current.find(c => c.id === chatId)?.agentTurn || null;
  }
  function saveAgentTurn(chatId, agentTurn) {
    if (agentTurn) _agentTurns[chatId] = agentTurn; else delete _agentTurns[chatId];
    setChats(prev => prev.map(c => c.id !== chatId ? c : { ...c, agentTurn: agentTurn || null }));
  }
  function clearAgentTurn(chatId) {
    if (!readAgentTurn(chatId)) return;
    saveAgentTurn(chatId, null);
  }

  // A step that failed. Before anything was shown, the classifier path takes over ('fallback').
  // After a result, the separate debrief call writes the take instead. Mid-conversation, the
  // bubble gets an honest line.
  function agentStepFailed(chatId, { msgId, into }, step, opts) {
    if (into === 'debrief') {
      if (opts.fallback) runDebrief(chatId, msgId, opts.fallback.summary, opts.fallback.kind);
      else updateMessageById(chatId, msgId, () => ({ debrief: { status: 'failed', text: '', suggestions: [] } }));
      return 'done';
    }
    if (step === 0) return 'fallback';
    updateMessageById(chatId, msgId, () => ({
      status: 'done', done: true, isConversational: true, conversationalIntent: 'general_chat',
      conversationalResponse: "I lost the thread on that one. Ask again and I'll pick it up.",
      conversationalBullets: [], conversationalBlocks: [], lines: [],
    }));
    return 'done';
  }

  // Execute one tool call. Resolves to { kind: 'immediate', result, isError? } | { kind: 'terminal' }
  // | { kind: 'deferred' }. `msgId` is the running bubble the tool's card or progress lands on.
  async function executeAgentTool(chatId, msgId, call, { query }) {
    if (call.error) return { kind: 'immediate', isError: true, result: { error: call.error } };
    const input = call.input || {};
    switch (call.name) {
      case 'remember':
        applyMemoryUpdate(input);
        return { kind: 'immediate', result: { saved: true } };
      case 'clear_filter':
        applyClearFilter(chatId, input.field);
        return { kind: 'immediate', result: { cleared: input.field } };
      case 'start_fresh':
        applyStartFresh(chatId);
        return { kind: 'immediate', result: { reset: true } };
      case 'ask_user':
        renderSearchAsk(chatId, msgId, { question: input.question, chips: input.chips, reason: input.reason, originalQuery: query });
        return { kind: 'terminal' };
      case 'propose_icp_update':
        renderIcpProposal(chatId, msgId, input);
        return { kind: 'terminal' };
      case 'search_contacts': {
        // The server never offers this tool to a writing request; if one gets here anyway, the
        // model hears why and writes the draft on its next step.
        if (isWritingRequest(query)) {
          return { kind: 'immediate', isError: true, result: { error: 'This message asks you to write something, not to search. Reply general_chat with the draft.' } };
        }
        const outcome = await startSearchFromQuery(chatId, msgId, { parseInput: input.query, query, history: buildParserHistory(chatId) });
        if (outcome === 'deferred') return { kind: 'deferred' };
        if (outcome === 'no_criteria') {
          return { kind: 'immediate', result: { status: 'no_criteria', message: 'No job title or company could be read from the query; the user was asked to add one.' } };
        }
        return { kind: 'terminal' };   // cancelled or failed: it is already on the message
      }
      case 'find_similar_companies':
        await handleLookalikeIntent(chatId, { name: input.seed_name, url: input.seed_domain }, msgId);
        return { kind: 'deferred' };
      case 'find_companies':
        openCompaniesCountAsk(chatId, input, msgId);
        return { kind: 'deferred' };
      case 'research_person': {
        // Deferred like research_company: the brief's take comes back through resumeAgentTurn.
        // A reply instead of a brief (who is it? which company? pick one of these) ends the turn.
        const outcome = await handlePersonResearch(chatId, { name: input.person_name, company: input.company, role: input.role }, msgId);
        return outcome === 'researched' ? { kind: 'deferred' } : { kind: 'terminal' };
      }
      case 'research_company': {
        // Deferred like the look-alike: the Company Brief lands on the bubble and the model's take
        // on it arrives through resumeAgentTurn, or after the user picks from the candidate card.
        const outcome = await handleCompanyResearch(chatId, { name: input.company_name, domain: input.domain }, msgId);
        return outcome === 'error' ? { kind: 'terminal' } : { kind: 'deferred' };
      }
      default:
        return { kind: 'immediate', isError: true, result: { error: `unknown tool ${call.name}` } };
    }
  }

  // One step, then whatever it leads to. `target.into` says where the model's words go: the
  // bubble ('reply') or the take under a result ('debrief'). Resolves to 'done' | 'deferred' |
  // 'cancelled' | 'fallback'.
  async function runAgentTurn(chatId, query, target, turn = [], step = 0, opts = {}) {
    const { msgId, into } = target;
    const chatCtxPayload = { ...assembleChatContext(chatsRef.current.find(c => c.id === chatId)), memory: memorySummaryRef.current };
    const streamCtrl = new AbortController();
    _searchAborts[chatId] = streamCtrl;   // so Stop can end a reply mid-sentence

    // Deltas are batched: a setChats per token would re-render this page dozens of times a
    // second. ~40ms is below what reads as lag and above what reads as thrash.
    let pendingText = '';
    let flushTimer = null;
    const append = (chunk) => (into === 'debrief'
      ? updateMessageById(chatId, msgId, m => ({ debrief: { ...(m.debrief || { status: 'pending', suggestions: [] }), text: ((m.debrief?.text) || '') + chunk } }))
      : updateMessageById(chatId, msgId, m => ({ conversationalResponse: (m.conversationalResponse || '') + chunk })));
    const flush = () => {
      flushTimer = null;
      if (!pendingText) return;
      const chunk = pendingText;
      pendingText = '';
      append(chunk);
    };
    const onEvent = (event) => {
      if (event.type === 'intent') {
        if (into === 'debrief') {
          updateMessageById(chatId, msgId, () => ({ debrief: { status: 'pending', text: '', suggestions: [] } }));
        } else if (CONVERSATIONAL_INTENTS.includes(event.intent)) {
          updateMessageById(chatId, msgId, () => ({
            thinking: false, streaming: true, isConversational: true,
            conversationalIntent: event.intent, conversationalResponse: '', lines: [],
          }));
        }
      } else if (event.type === 'delta') {
        pendingText += event.text;
        if (!flushTimer) flushTimer = setTimeout(flush, 40);
      }
    };

    let result;
    try {
      result = await agentStep({ message: query, sellerProfile, chatContext: chatCtxPayload, turn }, onEvent, { signal: streamCtrl.signal });
    } catch (err) {
      clearTimeout(flushTimer);
      if (err.cancelled || streamCtrl.signal.aborted) return 'cancelled';
      return agentStepFailed(chatId, target, step, opts);
    } finally {
      if (_searchAborts[chatId] === streamCtrl) delete _searchAborts[chatId];
    }
    clearTimeout(flushTimer);
    flush();
    if (!result || (!result.toolCall && !result.reply)) return agentStepFailed(chatId, target, step, opts);

    // ── A reply ──────────────────────────────────────────────────────────────
    if (!result.toolCall) {
      if (into === 'debrief') {
        // Nothing to say renders as nothing, the same rule the separate debrief follows.
        const text = result.reply.response || '';
        updateMessageById(chatId, msgId, () => ({
          debrief: text
            ? { status: 'done', text, suggestions: result.reply.suggestions || [] }
            : { status: 'failed', text: '', suggestions: [] },
        }));
      } else {
        renderConversationalReply(chatId, msgId, result.reply);
      }
      return 'done';
    }

    // ── A tool call ──────────────────────────────────────────────────────────
    const call     = result.toolCall;
    const nextTurn = [...turn, { role: 'assistant', content: result.content }];

    // A take that became another action: the result keeps its tally and the action gets a
    // bubble of its own underneath, the way a second message would.
    let actionMsgId = msgId;
    if (into === 'debrief') {
      updateMessageById(chatId, msgId, () => ({ debrief: { status: 'failed', text: '', suggestions: [] } }));
      actionMsgId = `${Date.now()}_${Math.random()}`;
      addMessageToChat(chatId, {
        id: actionMsgId, role: 'assistant', status: 'running',
        lines: [], thinking: true, contacts: [], done: false, startedAt: Date.now(),
      });
      setRunning(chatId, true);
    }

    const deferred = ['search_contacts', 'find_similar_companies', 'find_companies', 'research_company', 'research_person'].includes(call.name) && !call.error;
    if (deferred) saveAgentTurn(chatId, { query, turn: nextTurn, step: step + 1, pending: { toolUseId: call.id, name: call.name } });

    let outcome;
    try {
      outcome = await executeAgentTool(chatId, actionMsgId, call, { query });
    } catch (err) {
      outcome = { kind: 'immediate', isError: true, result: { error: err.message || 'tool failed' } };
    }
    if (outcome.kind === 'deferred') return 'deferred';
    if (deferred) clearAgentTurn(chatId);
    if (into === 'debrief' && outcome.kind !== 'deferred') setRunning(chatId, false);
    if (outcome.kind === 'terminal') return 'done';

    // Answer the tool and take the next step.
    if (step + 1 >= AGENT_MAX_STEPS) {
      renderConversationalReply(chatId, actionMsgId, { intent: 'general_chat', response: 'Done. Anything else you want changed before the next search?' });
      return 'done';
    }
    const toolResult = { type: 'tool_result', tool_use_id: call.id, content: JSON.stringify(outcome.result ?? {}) };
    if (outcome.isError) toolResult.is_error = true;
    return runAgentTurn(chatId, query, { msgId: actionMsgId, into: 'reply' }, [...nextTurn, { role: 'user', content: [toolResult] }], step + 1, opts);
  }

  // A deferred tool finished: continue the loop with its result as the take under `msgId`.
  // Returns false when no turn was waiting, so the caller runs the separate debrief instead.
  function resumeAgentTurn(chatId, msgId, toolName, summary, kind) {
    const waiting = readAgentTurn(chatId);
    if (!waiting?.pending || waiting.pending.name !== toolName) return false;
    saveAgentTurn(chatId, null);
    const turn = [...waiting.turn, {
      role: 'user',
      content: [{ type: 'tool_result', tool_use_id: waiting.pending.toolUseId, content: JSON.stringify(summary) }],
    }];
    // The card was already shown and the model has had the results; the debrief field is the
    // take's home either way, so a resumed step that fails renders the classic debrief.
    runAgentTurn(chatId, waiting.query, { msgId, into: 'debrief' }, turn, waiting.step, { fallback: { summary, kind } })
      .catch(() => runDebrief(chatId, msgId, summary, kind));
    return true;
  }

  // ── Execute search + display results - shared by handleRegularSend and clarification flow ──

  // msgId: the stable ID of the running assistant message to update.
  // Using a fixed ID prevents concurrent searches from clobbering each other's messages.
  async function executeSearch(chatId, criteria, msgId) {
    // Fallback: if no msgId provided, use updateLastAssistant (legacy callers like clarification flow)
    const update = (updater) => msgId
      ? updateMessageById(chatId, msgId, updater)
      : updateLastAssistant(chatId, updater);

    // ── Before the search runs: show the potential lead-credit cost against the balance ──
    // A hunt debits LEAD credits (1 per net-new verified contact delivered). Skip for
    // unlimited (dev) accounts. `requested` is what the user asked for; the estimate is
    // capped to what they can actually afford, matching the backend's pre-flight cap.
    const requested = Math.max(parseInt(criteria.limit, 10) || 0, 0);
    const showCost = msgId && !leadCredits?.unlimited;
    const leadBalBefore = meterBalance('leads');
    // The backend caps the fetch to what the user can afford, so the estimate - and the
    // "not charged" reconciliation below - are measured against this capped amount, never
    // the raw request (asking for 50 with 10 left can only ever attempt 10).
    const estUpTo = leadBalBefore.metered ? Math.min(requested, leadBalBefore.remaining) : requested;
    if (showCost) {
      setCostNote(chatId, msgId, 'estimate', estimateLine('hunt', { upTo: estUpTo, remaining: leadBalBefore.remaining, allowance: leadBalBefore.allowance }));
    }

    // When the user names a specific company, treat the query as fully user-directed -
    // skip ALL profile post-filters (geo, industry, company size, seniority, etc.).
    // The user explicitly said "at Cisco" or "at JPMorgan" - the profile's industry/size/geo
    // settings should not silently remove results for a company the user intentionally chose.
    // Profile filters only apply when the user doesn't name a company (broad prospecting mode).
    // The rep's avoid list rides on the same exclude-keyword post-filter as the profile's
    // keywords (matched against company name, industry and tags on the server). Broad path
    // only: a named company is wanted even if it was once avoided.
    const avoidKeywords = memoryExcludeKeywords(memorySummaryRef.current);
    //
    // Revenue needs a filter object to ride on: the server applies a request's revenueRange as an
    // override on profileFilters and skips every filter when that is null. A rep with no saved
    // ICP who picked "Under $10M" on the card used to get companies of any size. So a broad
    // search with a revenue gets an object even when the profile has none; one without keeps the
    // old null, so nothing else about that path changes. When the card answered revenue, the
    // profile's saved range is blanked and the card's choice (possibly Any) is the only one.
    const needsFilterObject = !!profileFilters || avoidKeywords.length > 0 || !!criteria.revenueRange;
    const effectiveFilters = (criteria.companyDomain || criteria.companyName || !needsFilterObject)
      ? null
      : {
          ...(profileFilters || {}),
          ...(avoidKeywords.length > 0 ? { excludeKeywords: [...new Set([...(profileFilters?.excludeKeywords || []), ...avoidKeywords])] } : {}),
          ...(criteria.revenueAnswered ? { revenue: criteria.revenueRange || '' } : {}),
        };

    // Registered before the request so Stop has something to abort from the first moment, and
    // torn down in the finally below so a later Stop cannot abort a search that already finished.
    const ctrl = new AbortController();
    _searchAborts[chatId] = ctrl;

    // Everything that hands a result to the bridge goes through here. The bridge survives
    // remounts and is drained on the next mount, so publishing after a Stop would resurrect a
    // search the user ended - and two of the three publish sites had no cancel check at all.
    const publish = (payload) => {
      if (ctrl.signal.aborted || !msgId) return;
      _pendingSearchResults[`${chatId}__${msgId}`] = payload;
      window.dispatchEvent(new CustomEvent('repbuddy:search-complete', { detail: { chatId, msgId } }));
    };

    let verifiedContacts = [], resultSource = 'Lead411';
    // The live lead-credit balance this search actually debited, captured from the `done` event
    // so the after-the-fact cost note can report the real charge against the new balance.
    let searchCredits = null;
    // The backend silently clamps the requested count to the remaining credit balance. Carry
    // both numbers so a short list can say WHY it is short - without this, asking for 20 on a
    // balance of 6 returns 6 and looks exactly like a search that found nothing more.
    let creditCappedTo = null, creditCappedFrom = null, creditCappedAsked = false;
    // Same idea one step further out. Credits are only one reason a list comes back short; the
    // others are the user's own delivery rules - contacts they have already been given, and the
    // per-company cap they chose on the pre-search card. Those are invisible without this, and a
    // search that matched 33 people and shipped 4 reads as thin data instead of as working.
    let shortfallInfo = null;
    // What this search actually billed, and why some rows were free. Hoisted for exactly the same
    // reason as every carrier above it: `result` is scoped to the try block below, and the
    // reporting code that needs these runs after that block has closed. Reading `result` directly
    // down there throws at runtime - and passes a parse check, because referencing a block-scoped
    // const from outside its block is valid syntax.
    //
    // `chargedCount` starts at null, not 0. The cost note reads null as "the backend did not say"
    // and falls back to the delivered-row count; starting at 0 would announce a paid search as
    // free, which is the failure this whole field exists to prevent.
    let chargedCount = null, ownedCount = 0, staleDropped = [], crmSkipped = 0;
    try {
      const result = await searchContactsStream(
        criteria,
        effectiveFilters,
        sellerProfile,
        (event) => {
          if (event.type === 'searching') {
            update(() => ({ lines: [event.message] }));
          } else if (event.type === 'unlocking') {
            update(m => {
              // The phase line is NOT dropped here any more. It used to be, on the assumption
              // that sourcing was finished once unlocking began - true when phases only fired
              // before the unlock loop. The narrator also reports email verification, top-up
              // pages, the country-wide fallback and HQ checks, all of which run after it, so
              // dropping the phase line meant the two lines took turns being last and the
              // spinner jumped between them.
              //
              // Instead the counter is inserted BEFORE any live phase line, so the phase stays
              // at the bottom and keeps the spinner.
              const base = (m.lines || []).filter(l => !l.startsWith('Fetching '));
              const line = `Fetching contacts… (${event.unlocked} of ~${event.total})`;
              const pi   = base.findIndex(l => l.startsWith(PHASE_PREFIX));
              return { lines: pi === -1 ? [...base, line] : [...base.slice(0, pi), line, ...base.slice(pi)] };
            });
          } else if (event.type === 'verified') {
            update(m => {
              const base = (m.lines || []).filter(l => !l.startsWith('Verified '));
              // "N of M" reads as progress toward a target the user set. When M is our default,
              // it is not - so report the count alone rather than inventing a goal for them.
              return { lines: [...base, criteria?.limitStated
                ? `Verified ${event.valid} of ${event.requested} contact${event.requested !== 1 ? 's' : ''}…`
                : `Verified ${event.valid} contact${event.valid !== 1 ? 's' : ''}…`] };
            });
          } else if (event.type === 'phase') {
            // A single self-replacing line reporting what the search is doing right now.
            //
            // Between "Sourcing contacts…" and the first unlock there used to be 30-60
            // seconds of complete silence - Gemini company discovery, per-company Lead411
            // waves, the state fan-out and company classification all run in there. A frozen
            // line next to a spinner reads as a hung app, so the backend now narrates it.
            //
            // Keyed on the PHASE_PREFIX so each update replaces the previous one rather than
            // stacking; the batch path uses the same trick with '🔄'.
            update(m => {
              const base = (m.lines || []).filter(l => !l.startsWith(PHASE_PREFIX));
              return { lines: [...base, `${PHASE_PREFIX}${event.message}`] };
            });
          } else if (event.type === 'activity') {
            // Concrete facts, kept as a short rolling window. Separate from `lines` because
            // these accumulate rather than replace, and because a status line renders a green
            // tick - wrong for "we skipped this company".
            update(m => ({
              activity: [...(m.activity || []), { kind: event.kind, text: event.text }].slice(-ACTIVITY_MAX),
            }));
          } else if (event.type === 'thinking') {
            update(m => {
              const base = (m.lines || []).filter(l => !l.startsWith('Writing '));
              return { lines: [...base, event.message] };
            });
          }
        },
        { signal: ctrl.signal },
      );
      console.log('[executeSearch] stream resolved -', result.contacts.length, 'contacts, source:', result.source);
      verifiedContacts = result.contacts;
      resultSource     = result.source;
      if (result.limitCappedByCredits && result.requestedLimit) {
        creditCappedFrom = result.requestedLimit;
        creditCappedTo   = verifiedContacts.length;
        creditCappedAsked = !!result.limitStated;
      }
      if (result.shortfall) shortfallInfo = result.shortfall;
      // Billing and provenance, captured here for the same reason as everything around it.
      if (typeof result.chargedCount === 'number') chargedCount = result.chargedCount;
      ownedCount   = result.ownedCount   || 0;
      staleDropped = result.staleDropped || [];
      crmSkipped   = result.crmSkipped   || 0;
      if (result.quota?.totalQuota != null) setCredits(result.quota.totalQuota);
      // Reflect the live lead-credit balance debited by this search (metered users only).
      if (result.credits && typeof result.credits.remaining === 'number') {
        searchCredits = result.credits;
        applyLeadBalance(result.credits);
      }
      // Out of credits - the backend didn't run the search. Show a clear message.
      if (result.creditBlocked) {
        if (showCost) setCostNote(chatId, msgId, 'actual', `No lead credits charged - you're out of credits. You have 0 of ${result.credits?.allowance ?? leadCredits?.allowance ?? ''} remaining.`);
        const blockedPayload = {
          status: 'done', done: true,
          lines: ["You've used all your lead credits for this trial. Each credit is one complete verified lead - upgrade your plan from the Account page to keep pulling contacts."],
          contacts: [], valid: [], risky: [], invalid: [],
        };
        update(() => blockedPayload);
        publish(blockedPayload);
        return;
      }
    } catch (err) {
      // The user stopped it. handleStop has already written 'Stopped.' and cleared the bridge;
      // reporting a failure on top of that would tell them something broke when nothing did.
      if (err.cancelled || ctrl.signal.aborted) return;
      update(() => chatFailure(err, {
        say: "The search hit a problem and didn't finish. Try running it again in a moment.",
      }));
      return;
    } finally {
      // Only if it is still ours: a second search in this chat will have replaced it.
      if (_searchAborts[chatId] === ctrl) delete _searchAborts[chatId];
    }

    if (cancelRef.current || ctrl.signal.aborted) {
      update(() => ({ status: 'done', done: true, lines: ['Stopped.'] }));
      return;
    }

    // Store constraints in module-level object so the event bridge applies them
    // with the current setChats — avoids stale closure drops on remount.
    try {
      const newConstraints = buildConstraintsFromCriteria(criteria);
      if (newConstraints) _pendingConstraints[chatId] = newConstraints;
    } catch {}

    const isPersonLookup = !!(criteria.firstName?.trim() || criteria.lastName?.trim());
    const personName     = [criteria.firstName, criteria.lastName].filter(Boolean).join(' ').trim();
    const personAt       = criteria.companyName || criteria.companyDomain || '';

    // A short list because of the balance is a different fact from a short list because the
    // data wasn't there, and the user cannot tell them apart from the count alone. Defined
    // above the empty-result branch because that branch returns early and needs it too.
    const creditCapLine = creditCappedFrom
      ? (creditCappedAsked
          ? `You asked for ${creditCappedFrom} — your remaining credits covered ${creditCappedTo}. Upgrade from the Account page to pull the full list.`
          : `Your remaining credits covered ${creditCappedTo} contact${creditCappedTo === 1 ? '' : 's'}. Upgrade from the Account page to pull more.`)
      : null;

    // And the same courtesy for the other reasons a list comes back short. Only rendered when
    // something was actually WITHHELD - a search that genuinely ran out of matching people says
    // nothing here, because "we found fewer" is already what the count means. Defined beside
    // creditCapLine so the empty-result branch below can use it too: zero delivered because
    // every match was already yours is the case that most needs explaining.
    // Who else was seen at a named company on the free look, minus anyone this chat has already
    // been given. The engine leaves off this run's delivered rows, but a New pool run never sees
    // the earlier ones, so the Head of Sales pulled last time would otherwise read as not pulled.
    // The count is trimmed the same way, so "and N more" never counts a person already on screen.
    const seenCompany = !isPersonLookup ? (criteria?.companyName || criteria?.companyDomain || '') : '';
    const seenAtCompany = (() => {
      if (!shortfallInfo || !seenCompany) return [];
      const pulled = new Set([
        ...((chatsRef.current.find(c => c.id === chatId)?.contacts_ref) || []).map(c => String(c?.name || '').trim().toLowerCase()),
        ...(verifiedContacts || []).map(v => String(v?.contact?.name || '').trim().toLowerCase()),
      ]);
      return (shortfallInfo.peopleAtCompany || []).filter(p => p?.name && !pulled.has(p.name.trim().toLowerCase()));
    })();
    if (shortfallInfo && Array.isArray(shortfallInfo.peopleAtCompany)) {
      const engineList = shortfallInfo.peopleAtCompany;
      const removed = engineList.length - seenAtCompany.length;
      shortfallInfo = {
        ...shortfallInfo,
        peopleAtCompany: seenAtCompany,
        peopleAtCompanyTotal: Math.max(seenAtCompany.length, (shortfallInfo.peopleAtCompanyTotal || 0) - removed),
      };
    }
    const seenLine = (() => {
      if (seenAtCompany.length === 0) return null;
      const shown = seenAtCompany.slice(0, 5).map(p => (p.title ? `${p.name} (${p.title})` : p.name));
      const more  = (shortfallInfo?.peopleAtCompanyTotal || seenAtCompany.length) - shown.length;
      return `Also at ${seenCompany}, not pulled: ${shown.join(', ')}${more > 0 ? `, and ${more} more seen` : ''}.`;
    })();

    const sf = shortfallInfo;
    let shortfallLine = null;
    if (sf && sf.delivered < sf.requested) {
      const parts = [];
      const were = n => (n === 1 ? 'was' : 'were');
      if (sf.alreadyShown > 0) {
        parts.push(`${sf.alreadyShown} ${were(sf.alreadyShown)} already delivered to you earlier`);
      }
      if (sf.cappedPerCompany > 0) {
        parts.push(`${sf.cappedPerCompany} ${were(sf.cappedPerCompany)} trimmed by your ` +
                   `${sf.capValue ? `${sf.capValue}-per-company` : 'per-company'} limit`);
      }
      if (sf.industrySplitTrimmed > 0) {
        // "your requested split" only when they requested one - the split is derived from the
        // count, and on a default that count was ours.
        parts.push(`${sf.industrySplitTrimmed} fell outside ` +
                   `${sf.requestedStated ? 'your requested' : 'the'} industry split`);
      }
      if (sf.notCurrentlyEmployed > 0) {
        parts.push(`${sf.notCurrentlyEmployed} ${were(sf.notCurrentlyEmployed)} dropped by a LinkedIn employment check`);
      }
      // A named company that came up short is a different story from a thin segment: the
      // company is the whole question, so say what it does have, or that it could not be
      // identified at all. Never "broaden the criteria" - that is how the wrong company
      // used to fill the order.
      if (sf.reason === 'unresolved_company') {
        shortfallLine =
          `I couldn't confirm which company that is, so nothing was searched and nothing was charged. ` +
          `Paste their website and I'll search there.`;
      } else if (sf.reason === 'title_mismatch' || sf.reason === 'none_at_company') {
        // The titles are only spelled out when the seen line below is not already naming the
        // people who hold them.
        const seen = seenAtCompany.length > 0 ? [] : (sf.titlesAtCompany || []).slice(0, 3).map(t => t.title).filter(Boolean);
        shortfallLine = seen.length > 0
          ? `Nobody there matched that title, and nothing was charged. Titles I did see: ${seen.join(', ')}.`
          : `Nobody there matched that title, and nothing was charged.`;
      } else if (sf.delivered === 0 && (sf.removedByIndustry || 0) > 0) {
        // The industry gate emptied the list. This branch did not exist, so the only line on
        // screen was "try broader criteria" for a search that had 26,126 people with the right
        // titles and rejected every company for not being the sector asked for. Say which sector,
        // how big the pool was, and which operators were actually checked.
        const asked   = Array.isArray(sf.industryAsked) && sf.industryAsked.length ? sf.industryAsked.join(' or ') : 'that industry';
        const checked = Array.isArray(sf.companiesChecked) ? sf.companiesChecked.filter(Boolean) : [];
        const total   = sf.companiesCheckedTotal || checked.length;
        const n       = sf.removedByIndustry;
        const pool    = sf.totalMatchedTitles > 0
          ? `Lead411 lists ${sf.totalMatchedTitles.toLocaleString()} people with those titles, but none of the ${n} I checked ${were(n)}`
          : `None of the ${n} people I checked ${were(n)}`;
        shortfallLine =
          `${pool} at a company I could confirm as ${asked}.` +
          (checked.length > 0
            ? ` I also checked ${total} ${asked} operator${total === 1 ? '' : 's'} directly (${checked.join(', ')}${total > checked.length ? ', and more' : ''}) and found nobody with those titles and a reachable address.`
            : '') +
          (sf.softDeadlineHit ? ' I stopped at the time limit rather than keep spending; asking again may turn up more.' : '') +
          ' Nothing was charged.';
      } else if (parts.length > 0) {
        // Whether trying again could possibly help. "We ran out of pages" and "there is nobody
        // else in this segment" produce the same number on screen and want opposite next moves,
        // and until the search reported which one it was, the user could only guess.
        const nextMove = sf.sourcesExhausted
          ? ` There's nobody else in this segment right now — broadening the criteria is the way forward.`
          : ` Asking again may turn up more.`;
        // Only quote a number back at the user when it was theirs.
        //
        // "You asked for 10" after someone typed "get me the sales team" is a lie about the
        // conversation - 10 is the parser's default, and saying otherwise makes the whole
        // explanation read as invented. Same facts either way; only the opening clause moves.
        const opening = sf.requestedStated
          ? `You asked for ${sf.requested} — ${sf.matched} matched your criteria, but `
          : `${sf.matched} matched your criteria, but `;
        shortfallLine =
          opening +
          `${parts.join(', and ')}. ` +
          (sf.delivered > 0
            ? `Showing the ${sf.delivered} new contact${sf.delivered === 1 ? '' : 's'} left.${nextMove}`
            : `That left none to show — broaden the criteria, or re-run without excluding contacts you already have.`);
      } else if (sf.delivered > 0) {
        // Short, but nothing was withheld.
        //
        // The four reasons above are all about contacts we FOUND and then held back, so when a
        // list is short for any other reason this said nothing at all: a search of CertifID
        // delivered 7 against a target of 10 with every counter at zero, and the user was left
        // with a bare number and no idea whether that was the whole team.
        //
        // It is not "we ran out of people" either - that run read 10 names off a roster of 20.
        // What it actually means is that this pass, at this size, found this many. Say that, and
        // point at the control that can go further.
        shortfallLine =
          `Showing all ${sf.delivered} this pass turned up` +
          (sf.requestedStated ? ` of the ${sf.requested} you asked for` : '') +
          `. Pulling deeper searches further into the company for more.`;
      }
    }

    // The mix behind the number, when it is not all one thing. A LinkedIn-only row is a real
    // prospect with a working channel and no verified address, and it was not charged for -
    // saying so is the difference between a filled list and a list that looks padded.
    const tierLine = (sf && sf.linkedinOnly > 0)
      ? `${sf.verifiedCount} with a verified email, ${sf.linkedinOnly} reachable on LinkedIn only ` +
        `(no address found — not charged).` +
        (sf.linkedinOnlyAvailable > 0
          ? ` ${sf.linkedinOnlyAvailable} more matched but weren't shown.`
          : '')
      : null;

    if (!verifiedContacts || verifiedContacts.length === 0) {
      // Nothing delivered → nothing charged. Reassure the user their balance is untouched.
      if (showCost) {
        const bal = searchCredits || meterBalance('leads');
        setCostNote(chatId, msgId, 'actual', actualLine('hunt', { used: 0, remaining: bal.remaining, allowance: bal.allowance }));
      }
      // A named company that came back empty is an answer about that company, not a hint to
      // widen the net; the line says so, and the take below says what to do about it.
      const namedFor = !isPersonLookup ? (criteria?.companyName || criteria?.companyDomain || '') : '';
      const emptySummary = summarizeSearchOutcome({
        criteria, contacts: [], shortfall: shortfallInfo, chargedCount: 0,
        credits: searchCredits, source: resultSource, staleDropped, crmSkipped, creditCappedFrom,
      });
      const emptyPayload = {
        status: 'done',
        resultSummary: emptySummary,
        lines: [
          isPersonLookup
            ? `"${personName}" wasn't found in any source${personAt ? ` at ${personAt}` : ''}. They may not be indexed yet. Try searching by title at the same company instead.`
            : namedFor && shortfallInfo?.reason === 'unresolved_company'
              ? `I couldn't identify "${namedFor}" as a company.`
              : namedFor
                ? `Nobody at ${namedFor} matched ${criteria?.titleKeyword ? criteria.titleKeyword.split(',').slice(0, 2).join(' or ') : 'that title'}.`
                : (shortfallInfo?.delivered === 0 && (shortfallInfo?.removedByIndustry || 0) > 0)
                  // The industry gate emptied the list and the shortfall line says which sector
                  // and what was checked. "Broader" is not the move - the titles exist.
                  ? 'No contacts found.'
                  : 'No contacts found. Try broader criteria.',
          ...(shortfallLine ? [shortfallLine] : []),
          ...(seenLine ? [seenLine] : []),
          ...(creditCapLine ? [creditCapLine] : []),
        ],
        done: true, contacts: [], valid: [], risky: [], invalid: [],
      };
      update(() => emptyPayload);
      publish(emptyPayload);
      // The take runs on an empty result too. This used to return before it, so an empty named
      // search ended in "No contacts found" with no next move, and an agent-loop search never
      // heard back at all. The summary carries the reason and the titles that were there.
      if (msgId && !ctrl.signal.aborted && !isPersonLookup) {
        if (!resumeAgentTurn(chatId, msgId, 'search_contacts', emptySummary, 'search')) {
          runDebrief(chatId, msgId, emptySummary, 'search');
        }
      } else if (msgId && isPersonLookup) {
        // A person lookup that found nobody still closes the agent's turn honestly.
        resumeAgentTurn(chatId, msgId, 'search_contacts', { ...emptySummary, personName, personAt }, 'person');
      }
      recordSeenAtCompany(chatId, seenAtCompany, seenCompany);
      return;
    }

    const valid   = verifiedContacts.filter(v => v?.result?.verdict === 'valid');
    const risky   = verifiedContacts.filter(v => v?.result?.verdict === 'risky');
    // Its own bucket, and not part of `invalid`. This filter used to be "anything that isn't
    // valid or risky", which would have counted a LinkedIn-only row - a real person we simply
    // have no address for - as a failed email check.
    // Both free tiers are their own bucket, and neither is part of `invalid`. This filter used to
    // be "anything that isn't valid or risky", which would have counted a real person we simply
    // have no address for as a failed email check.
    const linkedinOnly = verifiedContacts.filter(v => v?.result?.verdict === 'linkedin-only');
    const noEmail      = verifiedContacts.filter(v => v?.result?.verdict === 'no-email');
    const invalid = verifiedContacts.filter(v =>
      v?.result?.verdict !== 'valid' && v?.result?.verdict !== 'risky' &&
      v?.result?.verdict !== 'linkedin-only' && v?.result?.verdict !== 'no-email');

    // Where these contacts came from, only where it changes what the user paid or waited for.
    //
    // The backend composes `source` from up to three parts (`user-cache`, `global-cache`, the
    // query cache, `Lead411`), and this line used to narrate all of them: "3 from contacts we
    // already had, the rest newly sourced." That is our plumbing, not the user's business, and on
    // a bad day it is the first thing they read above a list they did not ask for. Two parts
    // still earn a line: rows they already own (those were free) and a pure cache hit (no API
    // calls). Everything else, including a source nobody here recognises, renders nothing.
    const sourceLine = (() => {
      const src = String(resultSource || '');
      if (src === 'cache') return 'Results loaded from cache: 0 API calls used.';
      if (src.includes('user-cache') && ownedCount > 0) {
        return `${ownedCount} from contacts you already own.`;
      }
      return null;
    })();

    // Saved contacts that re-verification positively contradicted. Empty on a clean re-pull, which
    // is the normal case - this only ever appears when the employment gate actually said someone
    // had moved on or changed role, never when it simply did not get to them.
    const staleLine = (() => {
      const dropped = staleDropped;
      if (dropped.length === 0) return null;
      const named = dropped.slice(0, 2)
        .map(d => `${d.name}${d.reason ? ` is ${d.reason}` : ''}`).join(', ');
      const rest = dropped.length - Math.min(2, dropped.length);
      return `${dropped.length} of your saved contacts ${dropped.length === 1 ? 'has' : 'have'} ` +
             `changed since you pulled them — ${named}${rest > 0 ? `, and ${rest} more` : ''}. ` +
             `Not shown, and not charged.`;
    })();

    const crmSkippedLine = crmSkipped > 0
      ? `${crmSkipped} contact${crmSkipped !== 1 ? 's' : ''} already in your HubSpot ${crmSkipped !== 1 ? 'were' : 'was'} skipped — no credits spent.`
      : null;

    const summaryLine = isPersonLookup
      ? verifiedContacts.length === 1
        ? `Found ${personName}${personAt ? ` at ${personAt}` : ''}: ${valid.length ? 'email verified ✓' : risky.length ? 'email risky ⚠' : 'email invalid ✗'}`
        : `Found ${verifiedContacts.length} matches for "${personName}"${personAt ? ` at ${personAt}` : ''}: ${valid.length} verified, ${risky.length} risky.`
      : `Done. ${verifiedContacts.length} contacts: ${valid.length} verified, ${risky.length} risky, ${invalid.length} invalid` +
        (linkedinOnly.length > 0 ? `, ${linkedinOnly.length} LinkedIn-only` : '') +
        (noEmail.length > 0 ? `, ${noEmail.length} reachable by phone` : '') +
        '.';

    // The shape of the list, for the model: what the debrief reads now and what the chat
    // remembers later. Counts and distributions only, never the rows.
    const resultSummary = summarizeSearchOutcome({
      criteria, contacts: verifiedContacts, shortfall: shortfallInfo, chargedCount,
      credits: searchCredits, source: resultSource, staleDropped, crmSkipped, creditCappedFrom,
    });
    // Which remembered defaults shaped this run, so the take can say so in passing.
    resultSummary.memoryUsed = {
      ...(criteria.titleSource  === 'memory' ? { titles: criteria.titleKeyword } : {}),
      ...(criteria.statesSource === 'memory' ? { states: criteria.stateArray }   : {}),
      ...(criteria.capSource    === 'memory' ? { cap: criteria.userPerCompanyCap } : {}),
      ...(effectiveFilters && avoidKeywords.length > 0 ? { avoidListApplied: avoidKeywords.length } : {}),
    };

    const finalPayload = {
      status: 'done',
      resultSummary,
      lines: [
        ...(sourceLine ? [sourceLine] : []), summaryLine,
        ...(tierLine ? [tierLine] : []),
        // Above the shortfall line on purpose: when a saved contact has moved on, that IS the
        // reason the list is short, and reading the explanation before the tally is the right
        // order.
        ...(staleLine ? [staleLine] : []),
        ...(crmSkippedLine ? [crmSkippedLine] : []),
        ...(shortfallLine ? [shortfallLine] : []),
        ...(seenLine ? [seenLine] : []),
        ...(creditCapLine ? [creditCapLine] : []),
      ],
      // One section for both free tiers: what they have in common is the thing the user needs to
      // know - no email was found - and the row's own Email Status says whether to open LinkedIn
      // or pick up the phone. Two near-identical tables would be a worse answer to the same
      // question. The key keeps its name because the section, its export and its row renderer all
      // read it; only the membership and the label widened.
      contacts: verifiedContacts, valid, risky, invalid,
      linkedinOnly: [...linkedinOnly, ...noEmail],
      done: true,
      isPersonSearch:   isPersonLookup,
      personSearchName: isPersonLookup ? personName : '',
      // Kept so the "pull more" control below can re-run this exact search with a bigger number.
      // A named-company search never renders the preferences card - which is precisely the case
      // the budget control exists for - so the results are the only place it can be offered.
      //
      // Without `excludeEmails`: on a new-pool search that array is every address the chat has
      // ever shown, and this object is persisted with the message on every PUT /api/chats. Storing
      // it would add hundreds of addresses per result to a payload already over a megabyte, to
      // save a value the re-run recomputes from the transcript anyway.
      searchCriteria:   isPersonLookup ? null : { ...criteria, excludeEmails: undefined },
      searchedFor:      sf?.requested || criteria?.limit || null,
      // Offered whenever the list came up short, NOT gated on `sourcesExhausted`.
      //
      // That flag means "the vendor has no more pages for this query at this size" - it says
      // nothing about what a bigger pull would reach. A search of CertifID read 10 names off a
      // roster of 20 and then reported sources exhausted, so the one control that could have
      // gone and got the other ten was hidden on the exact search that needed it.
      moreAvailable:    !isPersonLookup && !!sf &&
                        (sf.delivered < sf.requested || sf.linkedinOnlyAvailable > 0 || sf.budgetExhausted),
    };

    // ── After the search: the actual lead-credit spend + the reconciliation ─────
    // This used to assume 1 credit = 1 delivered contact and set `used = verifiedContacts.length`.
    // That held until contacts could be free. Since the re-pull shipped, a search that returned
    // five contacts the user already owned announced "Used 5 lead credits" while charging nothing
    // - the balance underneath it was correct, so the sentence was the only thing lying, which is
    // the worst place for it to be.
    //
    // `chargedCount` is what the backend actually debited. Falling back to the row count when it
    // is missing keeps older backends and the batch path behaving as they always did; it must
    // never fall back to 0, which would claim a paid search was free.
    if (showCost && searchCredits && typeof searchCredits.remaining === 'number') {
      const charged = typeof chargedCount === 'number'
        ? chargedCount
        : verifiedContacts.length;

      // Rows delivered but not billed, and why. `ownedCount` rather than ownedEmails.length: the
      // email list is deliberately a superset for billing safety, so it overstates what was
      // actually handed over.
      const owned = Math.min(ownedCount, verifiedContacts.length);
      // Whatever is left over is the old story - asked for N, some had no address.
      const noEmail = Math.max(0, estUpTo - charged - owned);

      setCostNote(chatId, msgId, 'actual', actualLine('hunt', {
        used: charged,
        remaining: searchCredits.remaining,
        allowance: searchCredits.allowance,
        notChargedParts: [
          owned > 0 && {
            count: owned,
            clause: owned === 1
              ? 'was a contact you already own, re-verified before showing you'
              : 'were contacts you already own, re-verified before showing you',
          },
          noEmail > 0 && {
            count: noEmail,
            clause: `${noEmail === 1 ? 'contact' : 'contacts'} had no verified email and ` +
                    `${noEmail === 1 ? 'was' : 'were'} not charged`,
          },
        ].filter(Boolean),
      }));
    }

    // ── Robust result delivery ─────────────────────────────────────────────
    update(() => finalPayload);
    publish(finalPayload);
    if (msgId) {
      const bridgeKey = `${chatId}__${msgId}`;
      _pendingSearchResults[bridgeKey] = finalPayload;
      window.dispatchEvent(new CustomEvent('repbuddy:search-complete', {
        detail: { chatId, msgId },
      }));
    }

    // ── RepBuddy's take on what came back ──────────────────────────────────
    // Fire and forget, after delivery: the results never wait on the commentary, and a
    // failed debrief renders as nothing rather than as a failed search.
    if (msgId && !ctrl.signal.aborted) {
      const takeSummary = isPersonLookup ? { ...resultSummary, personName, personAt } : resultSummary;
      const takeKind    = isPersonLookup ? 'person' : 'search';
      // When the agent loop asked for this search, the take is its next step; otherwise the
      // separate debrief call writes it.
      if (!resumeAgentTurn(chatId, msgId, 'search_contacts', takeSummary, takeKind)) {
        runDebrief(chatId, msgId, takeSummary, takeKind);
      }
      // What this rep actually ran. Person lookups are not a habit worth learning.
      if (!isPersonLookup) {
        recordMemory([{
          type: 'search',
          constraints: buildConstraintsFromCriteria(criteria) || {},
          seniority: criteria.filterLevel || '',
          cap: criteria.userPerCompanyCap,
          pool: criteria.newPool ? 'new' : 'existing',
          // Broad searches only: a named-company run ignores revenue, so it teaches nothing. 'any'
          // is recorded too, so a rep who stops using a ceiling un-learns it.
          ...(!criteria.companyDomain && !criteria.companyName ? { revenueRange: criteria.revenueRange || 'any' } : {}),
          industryDistribution: criteria.userIndustryDistribution || null,
        }]);
      }
    }

    // ── Update session context: contacts_ref + active_goal + seen_ref ──────
    // Runs after delivery so UI is never blocked by this. seen_ref is pruned against the updated
    // contacts_ref, which is how a person who was seen and then pulled leaves it; a person lookup
    // that delivers someone comes through here with nothing new to add and prunes the same way.
    if (verifiedContacts.length > 0) {
      setChats(prev => prev.map(c => {
        if (c.id !== chatId) return c;
        const updatedRef  = buildContactsRef(verifiedContacts, c.contacts_ref || []);
        const updatedGoal = buildUpdatedGoal(c.active_goal, criteria, verifiedContacts);
        const seenRef     = buildSeenRef(seenAtCompany, seenCompany, c.seen_ref || [], updatedRef);
        return { ...c, contacts_ref: updatedRef, active_goal: updatedGoal, seen_ref: seenRef };
      }));
    }

    // ── Trigger summarization if history is getting long ───────────────────
    setChats(prev => {
      const chat = prev.find(c => c.id === chatId);
      if (!chat) return prev;
      const doneMessages = (chat.messages || []).filter(
        m => m.role === 'user' || (m.role === 'assistant' && m.done)
      );
      if (doneMessages.length < SUMMARIZE_THRESHOLD) return prev; // no change needed yet

      // Fire summarization in the background — don't await, don't block UI
      const toSummarize = doneMessages.slice(0, doneMessages.length - 10)
        .map(m => ({
          role:    m.role,
          content: m.role === 'user' ? (m.contentText || m.content || '') : describeAssistantTurn(m),
        }));

      summarizeChat(toSummarize, chat.context_summary).then(summary => {
        if (!summary) return;
        setChats(p => p.map(c => c.id === chatId ? { ...c, context_summary: summary } : c));
      }).catch(() => {});

      return prev; // no immediate state change
    });
  }

  // ── HubSpot push ──────────────────────────────────────────────────────────
  async function pushToHubspot() {
    if (!exportPanel || isPushingToHubspot) return;
    setIsPushingToHubspot(true);
    try {
      const token = await import('../lib/supabaseClient').then(({ supabase }) =>
        supabase?.auth.getSession().then(({ data: { session } }) => session?.access_token)
      );
      const body = exportPanel.type === 'batch'
        ? { batchResults: exportPanel.batchResults }
        : { contacts: exportPanel.contacts };

      const r = await fetch(`${BACKEND_URL}/api/hubspot/push`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        // The server says why in `message` (expired token, not configured). Show that,
        // not a fixed "Push failed" that leaves the user guessing.
        const body = await r.json().catch(() => ({}));
        throw new Error(body.message || body.error || 'Push failed');
      }
      const result = await r.json();
      setHubspotToast({
        type:       'success',
        pushed:     result.pushed     || 0,
        updated:    result.updated    || 0,
        skipped:    result.skipped    || 0,
        failed:     result.failed     || 0,
        failReason: result.failReason || null,
        listName:   result.listName   || null,
      });
      // Keep the panel open when nothing got through, so a retry after reconnecting is
      // one click rather than a re-export.
      // A CRM push is the strongest signal of what this rep wants: remember the companies.
      if ((result.pushed || 0) + (result.updated || 0) > 0) {
        const rows = exportPanel.type === 'batch'
          ? (exportPanel.batchResults?.results || []).filter(r => r?.status === 'found')
          : (exportPanel.contacts || []);
        const companies = [...new Set(rows.map(r => r?.contact?.company || r?.company || '').filter(Boolean))];
        if (companies.length) recordMemory([{ type: 'taken', companies, via: 'hubspot' }]);
      }
      if ((result.pushed || 0) + (result.updated || 0) > 0 || !(result.failed > 0)) setExportPanel(null);
    } catch (err) {
      setHubspotToast({ type: 'error', message: err.message });
    } finally {
      setIsPushingToHubspot(false);
    }
  }

  // ── Export helpers ────────────────────────────────────────────────────────

  // ── Shared name-splitter ──────────────────────────────────────────────────
  // Use pre-split fields from Lead411 when available; fall back to splitting on spaces.
  function splitName(contact) {
    const first = contact.firstName || '';
    const last  = contact.lastName  || '';
    if (first || last) return { first, last };
    const parts = (contact.name || '').trim().split(/\s+/);
    return { first: parts[0] || '', last: parts.slice(1).join(' ') || '' };
  }

  // ── Last line of defence against shipping one person twice ────────────────
  //
  // The backend collapses duplicate records before it returns them (services/contactIdentity.js -
  // Lead411 carries the same human under more than one employee id, with more than one address).
  // With that working this never fires, and it is here anyway because the CSV is what the customer
  // actually receives: a duplicate row in a deliverable costs more than a redundant filter.
  //
  // Same key as the backend - normalised name plus the company domain. A row with no name or no
  // domain has no identity to compare and is always kept; two same-named people at different
  // companies are two people.
  function dedupeByPerson(rows) {
    const seen = new Set();
    return (rows || []).filter((row) => {
      const contact = row?.contact || {};
      const name = String(contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`)
        .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
      const email  = String(contact.email || row?.result?.email || '').toLowerCase().trim();
      const domain = String(contact.domain || '').toLowerCase().trim() || email.split('@')[1] || '';
      if (!name || !domain) return true;

      const key = `${name}|${domain}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  // `msgId` lets the export report back when it had to leave rows out - see the note by the
  // `kept` filter below.
  async function exportCSV(contacts, exportKey = null, msgId = null, config = null) {
    setExportingKey(exportKey);
    // An export is the rep taking the list with them: remember the companies.
    recordMemory([{ type: 'taken', via: 'export',
      companies: [...new Set((contacts || []).map(r => r?.contact?.company || '').filter(Boolean))] }]);
    try {
      // No draft pre-pass here any more. The CSV no longer carries an Email Draft column, and
      // that column was the only thing this generated for - a ~22 second AI call on every
      // export, in front of the verification the user is actually waiting on.
      //
      // The per-row Draft button is unaffected: it calls /api/linkedin/research on click, a
      // different endpoint from the batch one this used.

      // Run employment verification for all contacts at export time
      let exportEmpResults = {};
      try {
        const contactsForVerify = contacts
          .filter(({ contact, result }) => contact.linkedinUrl && result.email)
          .map(({ contact, result }) => ({
            email:       result.email,
            linkedinUrl: contact.linkedinUrl,
            company:     contact.company || '',
          }));
        if (contactsForVerify.length > 0) {
          const { results } = await linkdapiBatchEmpVerify(contactsForVerify);
          exportEmpResults = results || {};
        }
      } catch (err) {
        console.warn('[Export] Employment verification failed (continuing without it):', err.message);
      }

      // "Industry" is Lead411's raw label; "Industry (Specific)" is what the company says
      // about itself. Keeping both is the point - a lender and a SaaS company can share a
      // Lead411 label, and the contrast is what shows it.
      //
      // There is no match or source column: contacts whose industry contradicts the search are
      // dropped before export, so every surviving row would have read "Match" - and this file
      // is a customer deliverable, where naming our vendors describes our supply chain rather
      // than their prospect. Provenance still lives on the row's badge and hover tooltip.
      const headers = [
        'First Name', 'Last Name', 'Title', 'Company Name',
        'Employees', 'Estimated Revenue', 'Industry',
        'Industry (Specific)', 'Website',
        'LinkedIn URL', 'Email', 'Email Status', 'Direct Dial', 'Mobile Number',
        // City/State are the company's HQ; Location is the person's. Same order as ExportPanel's
        // CHAT_HEADERS, which this list must match column for column.
        'City', 'State', 'Location', 'Employment Verified',
      ];

      const rows = dedupeByPerson(contacts).map(({ contact, result }) => {
        const { first, last } = splitName(contact);
        // "Invalid" was the catch-all, which would have labelled a LinkedIn-only row as a failed
        // email check. It has no address to have failed - say that instead, so the buyer can see
        // at a glance which rows they reach on LinkedIn.
        const emailStatus = result.verdict === 'valid'         ? 'Verified'
                          : result.verdict === 'risky'         ? 'Risky'
                          : result.verdict === 'linkedin-only' ? 'LinkedIn only - no email'
                          // A real person at a confirmed company with no address and no profile -
                          // what we do have is their employer's own number, in the Direct Dial
                          // column. Say what to do with the row rather than what it lacks.
                          : result.verdict === 'no-email'      ? 'No email - call the company'
                          : 'Invalid';
        const empKey = (result.email || '').toLowerCase();
        const live   = exportEmpResults[empKey] || empVerifiedRef.current.get(empKey) || null;
        // Reconciles the backend gate's verdict with this live re-check. A contact either
        // source says has left is left OUT of the file rather than labelled "No - Now at X" -
        // a row nobody can act on has no business in a deliverable.
        const { verdict, rejected } = resolveEmployment(contact, live);
        if (rejected) return null;

        const empVerified = employmentLabel(verdict, Boolean(contact.linkedinUrl));
        // A profile we fetched and got nothing from is a link that 404s. Better to ship no URL
        // than one that breaks trust in the row it sits on.
        const linkedinUrl = deliverableLinkedinUrl(contact, live);

        // For contacts cached before directDial/mobilePhone were split,
        // fall back to the legacy phone field (which was direct || mobile).
        const directDial  = contact.directDial  || ((!contact.mobilePhone) ? contact.phone || '' : '');
        const mobilePhone = contact.mobilePhone || '';

        return [
          first, last, currentTitleFor(verdict, contact.title || ''), contact.company || '',
          excelSafeRange(contact.companySize), contact.revenue || '', contact.industry || '',
          industrySpecificLabel(contact),
          contact.domain || '', linkedinUrl, result.email || '',
          emailStatus, directDial, mobilePhone,
          contact.hqCity || '', contact.hqState || '', personLocationOf(contact, live),
          empVerified,
        ];
      });

      // A file quietly shorter than the screen is its own trust problem, so say so rather than
      // letting the user discover it by counting.
      const keptWithMeta = rows.map((cells, idx) => {
        if (!cells) return null;
        const orig = dedupeByPerson(contacts)[idx];
        return { cells, verdict: orig?.result?.verdict || null };
      }).filter(Boolean);
      if (msgId && keptWithMeta.length < rows.length) {
        const omitted = rows.length - keptWithMeta.length;
        updateMessageById(activeChatId, msgId, () => ({ exportOmitted: omitted }));
      }

      if (!config) {
        downloadCSV([headers, ...keptWithMeta.map(r => r.cells)], 'repbuddy-contacts.csv');
        return;
      }

      const activeIdx   = exportColumnOrder(config, headers.length);
      const fHeaders    = activeIdx.map(i => headers[i]);
      let   sortedKept  = [...keptWithMeta];
      if (config.sortCol !== null && config.sortCol < activeIdx.length) {
        const si = activeIdx[config.sortCol];
        sortedKept.sort((a, b) => {
          const cmp = String(a.cells[si] ?? '').localeCompare(String(b.cells[si] ?? ''), undefined, { sensitivity: 'base', numeric: true });
          return config.sortDir === 'asc' ? cmp : -cmp;
        });
      }
      const fRows = sortedKept.map(r => activeIdx.map(i => r.cells[i]));

      if (config.format !== 'xlsx') {
        downloadCSV([fHeaders, ...fRows], `${config.filename}.csv`);
        return;
      }
      const sheets = { 'All Contacts': [fHeaders, ...fRows] };
      if (config.sheetSplit === 'by-status') {
        const groups = { 'Verified': [], 'Risky': [], 'LinkedIn Only': [] };
        sortedKept.forEach((r, i) => {
          if (r.verdict === 'valid')         groups['Verified'].push(fRows[i]);
          else if (r.verdict === 'risky')    groups['Risky'].push(fRows[i]);
          else if (r.verdict === 'linkedin-only') groups['LinkedIn Only'].push(fRows[i]);
        });
        Object.entries(groups).forEach(([name, rows]) => {
          if (rows.length > 0) sheets[name] = [fHeaders, ...rows];
        });
      }
      downloadXLSX(sheets, config.filename);
    } finally {
      setExportingKey(null);
    }
  }

  async function exportBatchCSV(batchResults, exportKey = null, config = null) {
    setExportingKey(exportKey);
    recordMemory([{ type: 'taken', via: 'export',
      companies: [...new Set((batchResults?.results || []).filter(r => r?.status === 'found').map(r => r?.company || r?.contact?.company || '').filter(Boolean))] }]);
    try {
      // Draft pre-pass removed with the Email Draft column - see the note in exportCSV.

      // Run employment verification for all found contacts at export time
      let exportEmpResultsBatch = {};
      try {
        const contactsForVerify = batchResults.results
          .filter(r => r.status === 'found' && r.contact?.linkedinUrl && r.result?.email)
          // contact.company FIRST, matching exportCSV. `r.company` is the label off the user's
          // uploaded list - "BERLITZ CORP", "AUTOMANN.COM" - while the backend gate compared
          // against contact.company ("Berlitz Corporation"). Sending the label asked this
          // re-check a different question than the gate answered, and since resolveEmployment
          // lets a REJECTED from either side win, the export overruled the gate: three people
          // the run had confirmed shipped as "has left BERLITZ CORP (now at Berlitz
          // Corporation)". A CRM label is the right thing to SHOW on the row and the wrong
          // thing to measure LinkedIn against.
          .map(r => ({
            email:       r.result.email,
            linkedinUrl: r.contact.linkedinUrl,
            company:     r.contact?.company || r.company || '',
          }));
        if (contactsForVerify.length > 0) {
          const { results } = await linkdapiBatchEmpVerify(contactsForVerify);
          exportEmpResultsBatch = results || {};
        }
      } catch (err) {
        console.warn('[Export] Employment verification failed (continuing without it):', err.message);
      }

      const headers = [
        'Company Name', 'First Name', 'Last Name', 'Title',
        'Employees', 'Estimated Revenue', 'Industry',
        'Industry (Specific)', 'Website',
        'LinkedIn URL', 'Email', 'Email Status', 'Direct Dial', 'Mobile Number',
        // Same order as ExportPanel's BATCH_HEADERS, which this list must match column for column.
        'City', 'State', 'Location', 'Employment Verified',
        'Status',
      ];

      const rows = dedupeByPerson(batchResults.results).map(r => {
        const contact = r.contact || {};
        const result  = r.result  || {};

        // Company Name, then a blank for every column between it and Status, then Status.
        // Derived from headers.length rather than hand-counted, so adding or removing a
        // column can no longer shift every field after the gap one place left.
        const missRow = (message) => {
          const blanks = new Array(headers.length - 2).fill('');
          return [r.company || '', ...blanks, message];
        };

        if (r.status !== 'found') return missRow(r.message || 'Not found');

        const empKey = (result.email || '').toLowerCase();
        const live   = exportEmpResultsBatch[empKey] || empVerifiedRef.current.get(empKey) || null;
        const { verdict, rejected } = resolveEmployment(contact, live);
        // Unlike the contact CSV, this file is keyed to the company list the user uploaded, so
        // a rejected contact keeps its company row and explains itself rather than vanishing.
        // The backend already rewrites these; this catches anything that changed jobs between
        // the batch run and the download.
        if (rejected) {
          const moved = contact.employmentCheck?.currentCompany || live?.currentCompany;
          return missRow(`${contact.name || 'Contact'} has left ${r.company}${moved ? ` (now at ${moved})` : ''}`);
        }

        const { first, last } = splitName(contact);
        const emailStatus = result.verdict === 'valid'         ? 'Verified'
                          : result.verdict === 'risky'         ? 'Risky'
                          : result.verdict === 'linkedin-only' ? 'LinkedIn only - no email'
                          // A real person at a confirmed company with no address and no profile -
                          // what we do have is their employer's own number, in the Direct Dial
                          // column. Say what to do with the row rather than what it lacks.
                          : result.verdict === 'no-email'      ? 'No email - call the company'
                          : result.verdict === 'invalid'       ? 'Invalid' : '';
        const empVerified = employmentLabel(verdict, Boolean(contact.linkedinUrl));
        const linkedinUrl = deliverableLinkedinUrl(contact, live);

        const directDial  = contact.directDial  || ((!contact.mobilePhone) ? contact.phone || '' : '');
        const mobilePhone = contact.mobilePhone || '';

        return [
          r.company || '', first, last, currentTitleFor(verdict, contact.title || ''),
          excelSafeRange(contact.companySize), contact.revenue || '', contact.industry || '',
          industrySpecificLabel(contact),
          contact.domain || '', linkedinUrl, result.email || '',
          emailStatus, directDial, mobilePhone,
          contact.hqCity || '', contact.hqState || '', personLocationOf(contact, live),
          // A repeat is a "Both"-mode backfill: this user already had the contact and was not
          // charged again. Labelled so the export distinguishes it from a fresh find.
          empVerified, r.repeat ? 'Repeat' : 'Found',
        ];
      });

      // Build rows with meta for config-aware output
      const rowsWithMeta = dedupeByPerson(batchResults.results).map((r, idx) => ({
        cells: rows[idx],
        company: r.company || '',
        status: r.status,
        tier: r.tier || null,
        verdict: r.result?.verdict || null,
      }));

      if (!config) {
        downloadCSV([headers, ...rows], 'repbuddy-batch.csv');
        return;
      }

      const activeIdx  = exportColumnOrder(config, headers.length);
      const fHeaders   = activeIdx.map(i => headers[i]);
      let   sortedMeta = [...rowsWithMeta];
      if (config.sortCol !== null && config.sortCol < activeIdx.length) {
        const si = activeIdx[config.sortCol];
        sortedMeta.sort((a, b) => {
          const cmp = String(a.cells[si] ?? '').localeCompare(String(b.cells[si] ?? ''), undefined, { sensitivity: 'base', numeric: true });
          return config.sortDir === 'asc' ? cmp : -cmp;
        });
      }
      const fRows = sortedMeta.map(r => activeIdx.map(i => r.cells[i]));

      if (config.format !== 'xlsx') {
        downloadCSV([fHeaders, ...fRows], `${config.filename}.csv`);
        return;
      }

      const sheets = { 'All Contacts': [fHeaders, ...fRows] };

      if (config.sheetSplit === 'by-company') {
        const groups = new Map();
        sortedMeta.forEach((r, i) => {
          const co = r.company || 'Unknown';
          if (!groups.has(co)) groups.set(co, []);
          groups.get(co).push(fRows[i]);
        });
        groups.forEach((rows, co) => { sheets[co] = [fHeaders, ...rows]; });
      } else if (config.sheetSplit === 'by-status') {
        const groups = { 'Verified': [], 'Risky': [], 'LinkedIn Only': [], 'Not Found': [] };
        sortedMeta.forEach((r, i) => {
          if (r.status !== 'found')               groups['Not Found'].push(fRows[i]);
          else if (r.tier === 'linkedin_only')    groups['LinkedIn Only'].push(fRows[i]);
          else if (r.verdict === 'valid')         groups['Verified'].push(fRows[i]);
          else                                    groups['Risky'].push(fRows[i]);
        });
        Object.entries(groups).forEach(([name, rows]) => {
          if (rows.length > 0) sheets[name] = [fHeaders, ...rows];
        });
      }

      downloadXLSX(sheets, config.filename);
    } finally {
      setExportingKey(null);
    }
  }

  // Lead411 employee counts arrive as "10 - 19". Excel infers types on open and reads that
  // as a date, so the column showed "19-Oct". Quoting does not help - RFC-4180 quoting says
  // where the field ends, not what type it is.
  //
  // Swapping the hyphen for an en-dash is enough: Excel's date parser only recognises "-"
  // and "/", so the value stays text. The file remains a standard CSV that Sheets and pandas
  // still read, and the meaning is unchanged - unlike the ="..." trick, which is Excel-only
  // and doubles as a CSV-injection vector.
  //
  // Revenue ("5 - 10 Million") already survives on its trailing word and is left alone.
  function excelSafeRange(value) {
    return String(value || '').replace(/(\d)\s*-\s*(\d)/g, '$1 – $2');
  }

  // The columns to write, in the order the export panel arranged them.
  //
  // The panel's column order is a list of indexes into ITS header list, and the writers index
  // their own. When the two lists drifted (the panel gained Location, the writers did not) the
  // last index pointed past the end and every file ended in a column headed "undefined", while
  // hiding "Location" in the panel quietly hid Employment Verified. A mismatch now writes every
  // column in natural order and says so, rather than writing the wrong ones.
  function exportColumnOrder(config, headerCount) {
    const order = Array.isArray(config?.colOrder) ? config.colOrder : [];
    if (order.length !== headerCount || order.some(i => !(i >= 0 && i < headerCount))) {
      console.warn(
        `[Export] column list mismatch - panel sent ${order.length} column(s), the file has ${headerCount}; ` +
        'writing every column in its natural order'
      );
      return Array.from({ length: headerCount }, (_, i) => i);
    }
    const hidden = config.hiddenCols || [];
    return order.filter(i => !hidden.includes(i));
  }

  function downloadCSV(rows, filename) {
    const csv  = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    // The leading ﻿ is a UTF-8 BOM and it is not optional. The Blob constructor always
    // encodes as UTF-8, but nothing in a bare .csv tells Excel that, so on Windows it falls
    // back to the ANSI codepage and every multi-byte character breaks: an em-dash becomes
    // "â€"", a curly apostrophe becomes "â€™". The drafting prompt asks for contractions, so
    // this has been mangling "don't" in every Email Draft since the column existed.
    // charset in the MIME type does not help - Excel ignores it for local files.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }

  function downloadXLSX(sheets, filename) {
    const wb = XLSX.utils.book_new();
    Object.entries(sheets).forEach(([name, aoa]) => {
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
    });
    XLSX.writeFile(wb, `${filename}.xlsx`);
  }

  // Download a plain headers+rows grid honoring an ExportPanel config - the custom-mode partner
  // to exportCSV/exportBatchCSV, which are bound to the contact shape. Applies the panel's column
  // order/hiding first, then the sort (whose index is positional within the visible columns).
  function exportRowsConfigured(headers, rows, config) {
    const { filename, format, colOrder = [], hiddenCols = [], sortCol = null, sortDir = 'asc' } = config || {};
    const hidden = new Set(hiddenCols);
    const order = (colOrder.length ? colOrder : headers.map((_, i) => i)).filter(i => !hidden.has(i));
    let body = (rows || []).map(r => order.map(i => r[i] ?? ''));
    if (sortCol != null && sortCol < order.length) {
      body = [...body].sort((a, b) => {
        const cmp = String(a[sortCol]).localeCompare(String(b[sortCol]), undefined, { sensitivity: 'base', numeric: true });
        return sortDir === 'desc' ? -cmp : cmp;
      });
    }
    const grid = [order.map(i => headers[i]), ...body];
    const name = (filename || 'repbuddy-export').trim() || 'repbuddy-export';
    if (format === 'xlsx') downloadXLSX({ Export: grid }, name);
    else downloadCSV(grid, `${name}.csv`);
  }

  function formatDate(ts) {
    const diff = Date.now() - ts;
    if (diff < 60000)     return 'Just now';
    if (diff < 3600000)   return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000)  return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return new Date(ts).toLocaleDateString();
  }

  // Sidebar list derivation: search filters, then pinned chats float to a "Pinned"
  // group (most recently pinned first). Sorting happens here at render only - the
  // underlying chats array keeps its insertion order, which the merge-on-load and
  // autosave logic depend on.
  const chatSearchLower = chatSearch.trim().toLowerCase();
  const visibleChats = useMemo(() => {
    if (!chatSearchLower) return chats;
    return chats.filter(c => {
      const hay = (c.title + ' ' + (c.messages || []).map(m =>
        m.contentText || (typeof m.content === 'string' ? m.content : '') || (m.lines || []).join(' ')
      ).join(' ')).toLowerCase();
      return hay.includes(chatSearchLower);
    });
  }, [chats, chatSearchLower]);
  const pinnedChats   = useMemo(() => visibleChats.filter(c => c.pinned).sort((a, b) => (b.pinnedAt || 0) - (a.pinnedAt || 0)), [visibleChats]);
  // Un-pinned chats grouped ChatGPT-style by last activity (updatedAt falls back to
  // createdAt for chats made before the field existed). Calendar-day boundaries, not
  // rolling 24h windows - "Yesterday" means the previous calendar day.
  const chatGroups = useMemo(() => {
    const startOfToday = new Date().setHours(0, 0, 0, 0);
    const groups = [
      { label: 'Today',           chats: [] },
      { label: 'Yesterday',       chats: [] },
      { label: 'Previous 7 days', chats: [] },
      { label: 'Older',           chats: [] },
    ];
    for (const c of visibleChats) {
      if (c.pinned) continue;
      const ts = c.updatedAt || c.createdAt;
      if      (ts >= startOfToday)               groups[0].chats.push(c);
      else if (ts >= startOfToday - 864e5)       groups[1].chats.push(c);
      else if (ts >= startOfToday - 7 * 864e5)   groups[2].chats.push(c);
      else                                       groups[3].chats.push(c);
    }
    for (const g of groups) g.chats.sort((a, b) => (b.updatedAt || b.createdAt) - (a.updatedAt || a.createdAt));
    return groups.filter(g => g.chats.length > 0);
  }, [visibleChats]);

  function renderChatRow(chat) {
    const isRenaming = renamingChatId === chat.id;
    return (
      <div
        key={chat.id}
        className={`${styles.chatItem} ${chat.id === activeChatId ? styles.chatItemActive : ''} ${chatMenu?.id === chat.id ? styles.chatItemMenuOpen : ''}`}
        onClick={() => { if (!isRenaming) { openChat(chat.id); closeSidebarOnMobile(); } }}
      >
        {isRenaming ? (
          <input
            className={styles.chatRenameInput}
            value={renameDraft}
            autoFocus
            onChange={e => setRenameDraft(e.target.value)}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') { renameCancelledRef.current = true; setRenamingChatId(null); }
            }}
            onBlur={() => {
              // Escape unmounts the input which fires this blur - don't save a cancelled rename
              if (renameCancelledRef.current) { renameCancelledRef.current = false; return; }
              commitRename();
            }}
          />
        ) : (
          <div className={styles.chatItemTitle}>
            {chat.pinned && <span className={styles.pinGlyph}>📌</span>}{chat.title}
          </div>
        )}
        <div className={styles.chatItemMeta}>{formatDate(chat.updatedAt || chat.createdAt)}</div>
        <button
          className={styles.kebabBtn}
          data-kebab
          onClick={e => {
            e.stopPropagation();
            if (chatMenu?.id === chat.id) { setChatMenu(null); return; }
            const rect = e.currentTarget.getBoundingClientRect();
            setChatMenu({ id: chat.id, up: window.innerHeight - rect.bottom < 140 });
          }}
        >⋯</button>
        {chatMenu?.id === chat.id && (
          <div
            className={`${styles.chatItemMenu} ${chatMenu.up ? styles.chatItemMenuUp : ''}`}
            ref={chatMenuRef}
            onClick={e => e.stopPropagation()}
          >
            <button onClick={() => beginRename(chat)}>Rename</button>
            <button onClick={() => togglePin(chat.id)}>{chat.pinned ? 'Unpin' : 'Pin'}</button>
            <button
              className={styles.chatMenuDanger}
              onClick={e => {
                if (confirmDeleteId !== chat.id) { setConfirmDeleteId(chat.id); return; }
                setChatMenu(null);
                deleteChat(e, chat.id);
              }}
            >{confirmDeleteId === chat.id ? 'Confirm delete?' : 'Delete'}</button>
          </div>
        )}
      </div>
    );
  }

  const showWelcome = messages.length === 0 && !isRunning(activeChatId);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
    <div className={styles.app}>

      {/* Drawer backdrop. Mobile only - the stylesheet gives it a size and a colour just
          inside the 860px breakpoint, so on desktop this renders an element that paints
          nothing and cannot intercept a click. A <button> rather than a <div> so the drawer
          is dismissable from the keyboard and announces itself to a screen reader. */}
      {sidebarOpen && (
        <button
          type="button"
          className={styles.sidebarBackdrop}
          aria-label="Close sidebar"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <div className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : styles.sidebarClosed}`}>
        <div className={styles.sidebarTop}>
          <button className={styles.newChatBtn} onClick={startNewChat} data-tour="new-search">
            <span>+</span><span>New search</span>
          </button>
        </div>

        {/* Seller profile summary */}
        {sellerProfile ? (
          <div className={styles.profileBox} onClick={() => navigate('/profile')} title="Edit Seller Profile">
            <div className={styles.profileBoxLabel}>Seller Profile</div>
            <div className={styles.profileBoxName}>{sellerProfile.name}</div>
            <div className={styles.profileBoxSub}>{sellerProfile.company}</div>
            {sellerProfile.icp?.titles?.length > 0 && (
              <div className={styles.profileBoxMeta}>
                {sellerProfile.icp.titles.slice(0, 2).join(', ')}
                {sellerProfile.icp.titles.length > 2 ? ` +${sellerProfile.icp.titles.length - 2} more` : ''}
              </div>
            )}
            {sellerProfile.icp?.industries?.length > 0 && (
              <div className={styles.profileBoxMeta}>
                {sellerProfile.icp.industries.slice(0, 2).join(', ')}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.profileBoxEmpty} onClick={() => navigate('/profile')}>
            <span>👤</span>
            <span>Set up Seller Profile</span>
          </div>
        )}

        {chats.length > 0 && (
          <div className={styles.chatSearchWrap}>
            <input
              className={styles.chatSearchInput}
              placeholder="Search chats…"
              value={chatSearch}
              onChange={e => setChatSearch(e.target.value)}
            />
            {chatSearch && (
              <button className={styles.chatSearchClear} onClick={() => setChatSearch('')}>✕</button>
            )}
          </div>
        )}

        <div className={styles.chatList} data-tour="chat-list">
          {chats.length === 0 && <div className={styles.noChats}>No searches yet</div>}
          {chatSearchLower ? (
            /* Searching: flat filtered list, no Pinned/date grouping */
            visibleChats.length === 0 && chats.length > 0
              ? <div className={styles.noChats}>No matches</div>
              : visibleChats.map(renderChatRow)
          ) : (
            <>
              {pinnedChats.length > 0 && <div className={styles.chatSectionLabel}>Pinned</div>}
              {pinnedChats.map(renderChatRow)}
              {chatGroups.map(g => (
                <div key={g.label}>
                  <div className={styles.chatSectionLabel}>{g.label}</div>
                  {g.chats.map(renderChatRow)}
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* ── Main panel ── */}
      <div
        className={styles.main}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false); }}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          const file = e.dataTransfer.files?.[0];
          if (file) processFile(file);
        }}
      >

        {/* Drag-and-drop overlay */}
        {isDragging && (
          <div className={styles.dropOverlay}>
            <div className={styles.dropBox}>
              <span className={styles.dropIcon}>📂</span>
              <span className={styles.dropLabel}>Drop your file here</span>
              <span className={styles.dropSub}>CSV or Excel: batch contacts, named contacts, or enrichment</span>
            </div>
          </div>
        )}

        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <button className={styles.toggleSidebar} onClick={() => setSidebarOpen(o => !o)}>☰</button>
            <button className={styles.logo} onClick={() => navigate('/')} title="Go to RepBuddy home" aria-label="RepBuddy home">
              {logoOk ? (
                <img
                  className={styles.logoImg}
                  src={`${process.env.PUBLIC_URL}/logo.png`}
                  alt="RepBuddy"
                  onError={() => setLogoOk(false)}
                />
              ) : (
                <div className={styles.logoMark}>R</div>
              )}
              <span className={styles.logoName}>RepBuddy</span>
              <span className={styles.logoTag}>Four agents, one chat</span>
            </button>
          </div>
          <div className={styles.headerRight}>
            {/* Seller Profile - kept out of the dropdown for quick access */}
            <button
              className={`${styles.profileNavBtn} ${sellerProfile ? styles.profileNavBtnActive : styles.profileNavBtnSetup}`}
              onClick={() => navigate('/profile')}
              title={sellerProfile ? `Seller Profile: ${sellerProfile.name}${sellerProfile.company ? ` @ ${sellerProfile.company}` : ''}` : 'Set up your Seller Profile'}
              data-tour="seller-profile"
            >
              <span aria-hidden="true">🎯</span>
              {/* Wrapped so the mobile header can drop the words and keep the icon. Hiding it
                  with font-size:0 on the button instead would take the emoji with it, and
                  ::first-letter to win it back only works while this stays a block container. */}
              <span className={styles.profileNavLabel}>
                {sellerProfile ? 'Seller Profile' : 'Set up Seller Profile'}
              </span>
            </button>
            {user && (
              <div className={styles.profileMenu} ref={profileMenuRef}>
                {/* Single trigger - shows who's logged in; everything else lives in the dropdown */}
                <button
                  className={styles.profileTrigger}
                  onClick={() => setProfileMenuOpen(o => !o)}
                  aria-haspopup="menu"
                  aria-expanded={profileMenuOpen}
                  title="Account menu"
                  data-tour="account"
                >
                  <span className={styles.avatar} aria-hidden="true">{initials}</span>
                  <span className={styles.profileName}>{displayName}</span>
                  <span className={`${styles.caret} ${profileMenuOpen ? styles.caretOpen : ''}`} aria-hidden="true" />
                </button>

                {profileMenuOpen && (
                  <div className={styles.profileDropdown} role="menu">
                    {/* Identity */}
                    <div className={styles.pdIdentity}>
                      <span className={styles.avatarLg} aria-hidden="true">{initials}</span>
                      <div className={styles.pdIdentityText}>
                        <div className={styles.pdName}>{displayName}</div>
                        {user.email && <div className={styles.pdEmail}>{user.email}</div>}
                      </div>
                    </div>

                    {/* Credits */}
                    <div className={styles.pdCredits}>
                      {leadCredits?.unlimited ? (
                        <>
                          <div className={styles.pdCreditRow}>
                            <span>Lead411 credits</span>
                            <span className={styles.pdCreditVal}>{credits != null ? credits : '-'}</span>
                          </div>
                          <div className={styles.pdCreditRow}>
                            <span>LinkedIn checks</span>
                            <span className={styles.pdCreditVal}>{liRemaining != null ? (liRemaining >= 9999 ? '∞' : liRemaining) : '-'}</span>
                          </div>
                        </>
                      ) : (
                        // "used", matching the Usage section. The label carries the meaning here,
                        // so the number beside it is unambiguous either way round.
                        <div className={styles.pdCreditRow}>
                          <span>Lead credits used</span>
                          <span className={styles.pdCreditVal}>
                            {leadCredits ? `${leadCredits.used} of ${leadCredits.allowance}` : '-'}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className={styles.pdDivider} />

                    {/* Actions */}
                    <button className={styles.pdItem} role="menuitem" onClick={() => { setProfileMenuOpen(false); navigate('/account'); }}>
                      <span className={styles.pdIcon} aria-hidden="true">⚙</span>
                      Account &amp; billing
                    </button>

                    <button className={styles.pdItem} role="menuitem" onClick={() => { setProfileMenuOpen(false); navigate('/guide'); }}>
                      <span className={styles.pdIcon} aria-hidden="true">?</span>
                      Help Center
                    </button>

                    <button className={styles.pdItem} role="menuitem" onClick={replayTour}>
                      <span className={styles.pdIcon} aria-hidden="true">✦</span>
                      Replay tour
                    </button>

                    <div className={styles.pdDivider} />

                    <button className={`${styles.pdItem} ${styles.pdSignout}`} role="menuitem" onClick={() => { setProfileMenuOpen(false); signOut(); }}>
                      <span className={styles.pdIcon} aria-hidden="true">⎋</span>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </header>

        {/* Enrichment now lives in the chat (attach CSV + type "enrich") */}
        {false && (
          <div>
            <div className={styles.enrichInner}>

              {/* IDLE - upload zone */}
              {enrichPhase === 'idle' && (
                <>
                  <div className={styles.enrichHead}>
                    <h2 className={styles.enrichTitle}>Company Enrichment</h2>
                    <p className={styles.enrichSub}>Upload a CSV or Excel file with company names. Select the fields you want populated, and download the enriched file.</p>
                  </div>
                  <div
                    className={styles.enrichDropZone}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); handleEnrichFile(e.dataTransfer.files[0]); }}
                    onClick={() => enrichFileInputRef.current?.click()}
                  >
                    <span className={styles.enrichDropIcon}>📂</span>
                    <p className={styles.enrichDropTitle}>Drop your file here or click to browse</p>
                    <p className={styles.enrichDropSub}>.csv or .xlsx, max {ENRICH_MAX} companies</p>
                    <input ref={enrichFileInputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display:'none' }}
                      onChange={e => handleEnrichFile(e.target.files[0])} />
                  </div>
                  {enrichParseError && <p className={styles.enrichError}>{enrichParseError}</p>}
                </>
              )}

              {/* CONFIGURE + RUNNING */}
              {(enrichPhase === 'configure' || enrichPhase === 'running') && (
                <>
                  <div className={styles.enrichFileBar}>
                    <span>📄</span>
                    <span className={styles.enrichFileName}>{enrichFileName}</span>
                    <span className={styles.enrichFileCount}>
                      {enrichRawData.map(r => String(r[enrichHeaders.indexOf(enrichCompanyCol)] || '').trim()).filter(Boolean).length} companies
                    </span>
                    {enrichPhase === 'configure' && (
                      <button className={styles.enrichChangeBtn} onClick={handleEnrichReset}>Change file</button>
                    )}
                  </div>

                  {/* Column selector */}
                  <div className={styles.enrichSection}>
                    <div className={styles.enrichSectionTitle}>Company Name Column</div>
                    <div className={styles.enrichColPills}>
                      {enrichHeaders.map(h => (
                        <button key={h}
                          className={`${styles.enrichColPill} ${enrichCompanyCol === h ? styles.enrichColPillActive : ''}`}
                          onClick={() => setEnrichCompanyCol(h)}
                          disabled={enrichPhase === 'running'}
                        >{h}</button>
                      ))}
                    </div>
                  </div>

                  {/* Field checkboxes */}
                  <div className={styles.enrichSection}>
                    <div className={styles.enrichSectionTitle}>Fields to Enrich</div>
                    <div className={styles.enrichFieldGrid}>
                      {AVAILABLE_FIELDS.map(f => (
                        <label key={f.key} className={`${styles.enrichFieldCard} ${enrichSelectedFields.includes(f.key) ? styles.enrichFieldCardActive : ''}`}>
                          <input type="checkbox" style={{ display:'none' }}
                            checked={enrichSelectedFields.includes(f.key)}
                            onChange={() => toggleEnrichField(f.key)}
                            disabled={enrichPhase === 'running'} />
                          <span className={styles.enrichFieldLabel}>{f.label}</span>
                          <span className={styles.enrichFieldSource}>
                            {f.usesLinkdAPI && <span className={styles.enrichLiTag}>Li</span>}
                            {f.source}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {enrichRunError && <p className={styles.enrichError}>{enrichRunError}</p>}

                  {enrichPhase === 'configure' && (
                    <button className={styles.enrichRunBtn}
                      onClick={handleEnrichRun}
                      disabled={!enrichSelectedFields.length || !enrichCompanyCol}
                    >
                      Enrich {enrichRawData.map(r => String(r[enrichHeaders.indexOf(enrichCompanyCol)] || '').trim()).filter(Boolean).length} companies →
                    </button>
                  )}

                  {enrichPhase === 'running' && (
                    <div className={styles.enrichRunning}>
                      <span className={styles.spinner} />
                      <div>
                        <div className={styles.enrichRunningText}>Enriching companies…</div>
                        <div className={styles.enrichRunningNote}>Keep this tab open. This may take up to {Math.ceil(enrichRawData.length * 0.4)} seconds.</div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* DONE */}
              {enrichPhase === 'done' && enrichSummary && (
                <>
                  <div className={styles.enrichFileBar}>
                    <span>📄</span>
                    <span className={styles.enrichFileName}>{enrichFileName}</span>
                  </div>

                  <div className={styles.enrichSummaryBar}>
                    <span className={`${styles.enrichPill} ${styles.enrichPillGreen}`}>✓ {enrichSummary.enriched} Enriched</span>
                    <span className={`${styles.enrichPill} ${styles.enrichPillAmber}`}>◐ {enrichSummary.partial} Partial</span>
                    <span className={`${styles.enrichPill} ${styles.enrichPillRed}`}>✗ {enrichSummary.notFound} Not Found</span>
                  </div>

                  <button className={styles.enrichRunBtn} onClick={handleEnrichDownload}>⬇ Download Enriched File</button>

                  {enrichResults.filter(r => r.status === 'Not Found').length > 0 && (
                    <div className={styles.enrichNotFound}>
                      <div className={styles.enrichNotFoundTitle}>Not Found ({enrichResults.filter(r => r.status === 'Not Found').length})</div>
                      <div className={styles.enrichNotFoundList}>
                        {enrichResults.filter(r => r.status === 'Not Found').map((r, i) => (
                          <span key={i} className={styles.enrichNotFoundItem}>{r.inputName}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Preview table */}
                  <div className={styles.enrichSection}>
                    <div className={styles.enrichSectionTitle}>Preview</div>
                    <div className={styles.enrichTableWrap}>
                      <table className={styles.enrichTable}>
                        <thead>
                          <tr>
                            <th>Input Name</th><th>Matched As</th>
                            {enrichSelectedFields.map(f => <th key={f}>{FIELD_CSV_HEADERS[f]}</th>)}
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {enrichResults.map((r, i) => (
                            <tr key={i} className={styles[`enrichRow_${r.status.replace(/ /g,'_').toLowerCase()}`]}>
                              <td>{r.inputName}</td>
                              <td className={r.matchedAs && r.matchedAs !== r.inputName ? styles.enrichMatchedDiff : ''}>{r.matchedAs || '-'}</td>
                              {enrichSelectedFields.map(f => (
                                <td key={f} title={r[f] || ''}>{r[f] ? r[f].substring(0,55) + (r[f].length > 55 ? '…' : '') : '-'}</td>
                              ))}
                              <td><span className={styles[`enrichStatus_${r.status.replace(/ /g,'_').toLowerCase()}`]}>{r.status}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className={styles.enrichDoneRow}>
                    <button className={styles.enrichRunBtn} onClick={handleEnrichDownload}>⬇ Download Enriched File</button>
                    <button className={styles.enrichResetBtn} onClick={handleEnrichReset}>+ Enrich Another File</button>
                  </div>
                </>
              )}

            </div>
          </div>
        )}

        {/* ── Chat area + input ── */}
        <div className={styles.chat}>
          <div className={styles.chatInner}>

            {showWelcome && (
              <div className={styles.welcome}>
                <div className={styles.welcomeIcon}>🎯</div>
                <h1 className={styles.welcomeTitle}>What list do you need?</h1>
                <p className={styles.welcomeSub}>
                  Tell me who to find: job title, company, location. Attach a company list + job title for batch search.
                  Or attach a company CSV and type <strong>"enrich"</strong> to fill in company data.
                </p>
                <div className={styles.exampleWrap}>
                  <p className={styles.exampleLabel}>Try these:</p>
                  {[
                    'Find 10 VPs of Sales at Cisco',
                    'Get me 15 SDRs at SaaS companies in New York',
                    'Find 20 Account Executives in fintech',
                    'Attach a CSV and type "enrich" to fill in company data',
                  ].map(ex => (
                    <code key={ex} className={styles.example} onClick={() => ex.startsWith('Attach') ? undefined : handleSend(ex)}
                      style={ex.startsWith('Attach') ? { cursor: 'default', opacity: 0.6 } : {}}
                    >{ex}</code>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, msgIndex) => (
              <div key={msg.id} className={`${styles.msg} ${styles['msg_' + msg.role]}`}>

                {/* User bubble, or the prompt being edited in its place */}
                {msg.role === 'user' && editingMsgId === msg.id && (
                  <div className={styles.userEditBox}>
                    <textarea
                      className={styles.userEditInput}
                      value={editDraft}
                      autoFocus
                      rows={Math.min(8, Math.max(2, editDraft.split('\n').length))}
                      onChange={e => setEditDraft(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEditSubmit(activeChatId, msg.id, editDraft); }
                        else if (e.key === 'Escape') { e.preventDefault(); setEditingMsgId(null); }
                      }}
                      aria-label="Edit your message"
                    />
                    <div className={styles.userEditActions}>
                      <button type="button" className={styles.userEditCancel} onClick={() => setEditingMsgId(null)}>Cancel</button>
                      <button
                        type="button"
                        className={styles.userEditSend}
                        disabled={!editDraft.trim() || isRunning(activeChatId)}
                        onClick={() => handleEditSubmit(activeChatId, msg.id, editDraft)}
                      >Send</button>
                    </div>
                  </div>
                )}
                {msg.role === 'user' && editingMsgId !== msg.id && (
                  <div className={styles.userBubble}>
                    {msg.attachmentName && (
                      <div className={styles.msgAttachmentChip}>
                        <span className={styles.attachIcon}>📄</span>
                        <span>{msg.attachmentName}</span>
                        <span className={styles.attachCount}>{msg.attachmentCount} {msg.attachmentType === 'persons' ? 'contacts' : msg.attachmentType === 'emails' ? 'emails' : 'companies'}</span>
                      </div>
                    )}
                    {msg.contentText || msg.content}
                  </div>
                )}
                {/* Edit (hover-revealed) and the version arrows (always shown once there are two). */}
                {msg.role === 'user' && editingMsgId !== msg.id && (() => {
                  const info    = branchInfoFor(activeChat, msgIndex);
                  const blocked = editBlockReason(activeChat, msgIndex);
                  const atCap   = info?.count >= MAX_VERSIONS;
                  if (!info && blocked) return null;
                  const running = isRunning(activeChatId);
                  return (
                    <div className={styles.userMsgActions}>
                      {(!blocked || atCap) && (
                        <button
                          type="button"
                          className={styles.msgCopyBtn}
                          disabled={running || atCap}
                          title={atCap ? blocked : 'Edit this message'}
                          onClick={() => { setEditingMsgId(msg.id); setEditDraft(msg.contentText || msg.content || ''); }}
                        >✎ Edit</button>
                      )}
                      {info && (
                        <span className={styles.versionNav}>
                          <button
                            type="button"
                            className={styles.versionBtn}
                            disabled={running || info.index === 0}
                            onClick={() => handleSwitchVersion(activeChatId, info.anchorId, info.index - 1)}
                            aria-label="Previous version"
                          >‹</button>
                          <span>{info.index + 1}/{info.count}</span>
                          <button
                            type="button"
                            className={styles.versionBtn}
                            disabled={running || info.index === info.count - 1}
                            onClick={() => handleSwitchVersion(activeChatId, info.anchorId, info.index + 1)}
                            aria-label="Next version"
                          >›</button>
                        </span>
                      )}
                    </div>
                  );
                })()}

                {/* Assistant block */}
                {msg.role === 'assistant' && (
                  <div className={styles.assistantBlock}>

                    {/* Preference question card - shown instead of status lines */}
                    {msg.isPrefQuestion && msg.prefMeta && (
                      <div className={styles.prefCard}>
                        <div className={styles.prefCardHeader}>
                          <span className={styles.prefCardIcon}>⚙</span>
                          {/* Everything here is pre-answered, so the honest framing is "confirm",
                              not "answer" - which is why the scope card counts its questions
                              rather than announcing itself. The budget card gets a title that
                              says what is at stake, because it is the one card whose setting
                              decides how many credits the search may spend. */}
                          <span className={styles.prefCardTitle}>
                            {msg.prefMeta.mode === 'budget'
                              ? 'Before I spend credits'
                              : ((msg.prefMeta.showCapQuestion ? 1 : 0) + 1 + (msg.prefMeta.showIndustryQuestion ? 1 : 0) > 1
                                  ? 'A couple of quick preferences'
                                  : 'One quick preference')}
                          </span>
                        </div>
                        <div className={styles.prefCardPills}>
                          {/* The named company IS the scope, so it takes the slot the industry
                              pills hold on a broad search. */}
                          {msg.prefMeta.mode === 'budget'
                            ? (
                              <span className={styles.prefCardPill}>
                                {msg.prefMeta.companyLabel}
                                {/* The website, as a link: the user checks it here, before spending,
                                    rather than discovering the wrong company in the results. Follows
                                    the "Which company?" row below when it changes the pick. */}
                                {(() => {
                                  const site = (pendingSearchAsk?.msgId === msg.id && pendingSearchAsk.answers?.company) || msg.prefMeta.companyDomain;
                                  return site && site !== msg.prefMeta.companyLabel ? (
                                    <>
                                      {' · '}
                                      <a href={`https://${site}`} target="_blank" rel="noreferrer" className={styles.prefCardLink}>
                                        {site} ↗
                                      </a>
                                    </>
                                  ) : null;
                                })()}
                              </span>
                            )
                            : msg.prefMeta.industries.map(ind => (
                                <span key={ind} className={styles.prefCardPill}>{ind}</span>
                              ))}
                          {/* A batch carries no single count - its budget is "N per company"
                              across the file, which the cap question below asks directly. It
                              never set `limit`, so quoting one here read "up to undefined
                              contacts". */}
                          <span className={styles.prefCardPillCount}>
                            {msg.prefMeta.batch
                              ? `${msg.prefMeta.batchCompanyCount} ${msg.prefMeta.batchCompanyCount === 1 ? 'company' : 'companies'}`
                              : msg.prefMeta.limitStated
                                ? `${msg.prefMeta.limit} contacts`
                                : `up to ${msg.prefMeta.limit} contacts`}
                          </span>
                        </div>
                        <div className={styles.prefCardDivider} />
                        {(() => {
                          // Chips rather than free text. The old card asked for a typed answer
                          // and suggested "max 2" - which its own parser could not read, so the
                          // most obvious reply silently became "no preference". A click cannot
                          // be misparsed. Typing still works (handleRegularSend intercepts it)
                          // and lands in the same submitSearchAsk.
                          const ask     = pendingSearchAsk?.msgId === msg.id ? pendingSearchAsk : null;
                          const answers = ask?.answers || {};
                          const set     = (patch) => setPendingSearchAsk(p => ({ ...p, answers: { ...p.answers, ...patch } }));
                          const ready   = ask
                            && (!ask.needsCap      || answers.cap      !== undefined)
                            && (answers.pool !== undefined)
                            && (!ask.needsIndustry || answers.industry !== undefined)
                            && (!ask.needsCompany  || !!answers.company)
                            && (!ask.needsLocation || !!answers.locationType);
                          const chip = (label, active, onClick) => (
                            <button
                              key={label}
                              type="button"
                              className={active ? `${styles.prefChip} ${styles.prefChipActive}` : styles.prefChip}
                              onClick={onClick}
                              disabled={!ask}
                            >{label}</button>
                          );
                          const budget = answers.limit || msg.prefMeta.limit;
                          let n = 0;
                          return (
                            <>
                              <div className={styles.prefCardItems}>
                                {/* Which company. Every candidate the resolver found, each with its
                                    website as a separate link (a link inside a button is not valid
                                    HTML, and one click must not both open a tab and choose). The
                                    resolver's pick is pre-selected, so a confident match costs no
                                    extra click; a name that could mean two companies shows both. */}
                                {msg.prefMeta.needsCompany && (
                                <div className={styles.prefCardItem}>
                                  <span className={styles.prefCardNum}>{++n}</span>
                                  <div>
                                    <div className={styles.prefCardLabel}>Which company?</div>
                                    <div className={styles.prefCardHint}>
                                      {(msg.prefMeta.companyCandidates || []).length > 1
                                        ? 'More than one company goes by that name. Check the websites and pick the one you mean.'
                                        : 'Check the website before I search there.'}
                                    </div>
                                    <div className={styles.prefChips}>
                                      {(msg.prefMeta.companyCandidates || []).map(c => chip(
                                        `${c.title && c.title !== c.domain ? `${c.title} · ` : ''}${c.domain}`,
                                        answers.company === c.domain,
                                        () => set({ company: c.domain }),
                                      ))}
                                      <button
                                        type="button"
                                        className={styles.prefChip}
                                        disabled={!ask}
                                        onClick={() => rejectSearchCompany(activeChatId, msg)}
                                      >Other, paste website</button>
                                    </div>
                                    <div className={styles.disambigLinks}>
                                      {(msg.prefMeta.companyCandidates || []).map(c => (
                                        <a key={`clink-${c.domain}`} href={`https://${c.domain}`} target="_blank" rel="noreferrer" className={styles.disambigLink}>
                                          {c.domain} ↗
                                        </a>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                )}
                                {/* Where the place applies. "Live in the US" and "HQ'd in the US" are
                                    different searches: one checks where each person is, the other
                                    where the employer is. Pre-selected from the wording; the profile
                                    decides when the wording said nothing. */}
                                {msg.prefMeta.needsLocation && (
                                <div className={styles.prefCardItem}>
                                  <span className={styles.prefCardNum}>{++n}</span>
                                  <div>
                                    <div className={styles.prefCardLabel}>Location: {msg.prefMeta.locationLabel}</div>
                                    <div className={styles.prefCardHint}>
                                      People located there are checked one by one and anyone I cannot place is left out. Companies based there is the usual company filter.
                                    </div>
                                    <div className={styles.prefChips}>
                                      {chip(`People located in ${msg.prefMeta.locationLabel}`, answers.locationType === 'person',  () => set({ locationType: 'person' }))}
                                      {chip(`Companies based in ${msg.prefMeta.locationLabel}`, answers.locationType === 'company', () => set({ locationType: 'company' }))}
                                    </div>
                                  </div>
                                </div>
                                )}
                                {/* Where, on a broad search. Anywhere unless a place is picked; the
                                    rep's usual states are offered, each removable from memory with
                                    its ×; a box takes a state or a city ("Austin", "Boise, ID").

                                    What the place MEANS is a second question and gets its own
                                    numbered item once there is a place to scope. It used to sit
                                    inside this one as two more chips under the place chips, and
                                    read as more places to pick from rather than a choice to make. */}
                                {msg.prefMeta.showPlaceQuestion && (() => {
                                  const place    = answers.place || { states: [], city: '' };
                                  const states   = Array.isArray(place.states) ? place.states : [];
                                  const chosen   = hasPlace(place);
                                  const scoped   = chosen || msg.prefMeta.explicitCountry;
                                  const where    = describePlace(place) || msg.prefMeta.countryLabel;
                                  const usual    = (msg.prefMeta.suggestedStates || []).filter(st => !states.includes(st));
                                  const withType = (patch) => ({ ...patch, locationType: answers.locationType || ask?.locationDefault || 'company' });
                                  // A city is a metro with its own state; adding a state replaces it,
                                  // since one city filter cannot also cover other states.
                                  const addState = (st) => set(withType({ place: { states: place.city ? [st] : [...new Set([...states, st])], city: '' } }));
                                  const setCity  = (city, st) => set(withType({ place: { states: st ? [st] : [], city } }));
                                  const removeState = (st) => {
                                    const next = { states: states.filter(x => x !== st), city: '' };
                                    set({ place: next, ...(hasPlace(next) || msg.prefMeta.explicitCountry ? {} : { locationType: undefined }) });
                                  };
                                  const clearPlace = () => set({ place: { states: [], city: '' }, ...(msg.prefMeta.explicitCountry ? {} : { locationType: undefined }) });
                                  const forgetUsual = (st) => {
                                    recordMemory([{ type: 'forget', field: 'states', values: [st] }]);
                                    setPendingSearchAsk(p => (p ? { ...p, suggestedStates: (p.suggestedStates || []).filter(x => x !== st) } : p));
                                    updateMessageById(msg.chatId || activeChatId, msg.id, m => ({
                                      prefMeta: { ...m.prefMeta, suggestedStates: (m.prefMeta?.suggestedStates || []).filter(x => x !== st) },
                                    }));
                                  };
                                  const commitPlace = async (el) => {
                                    const text = el.value.trim();
                                    if (!text) return;
                                    el.value = '';
                                    const code = resolveStateLocal(text);
                                    if (code) { addState(code); setPendingSearchAsk(p => (p ? { ...p, placeError: '' } : p)); return; }
                                    const r = await resolvePlace(text);
                                    if (r?.kind === 'state' && r.states?.[0]) addState(r.states[0]);
                                    else if (r?.kind === 'city' && r.city) setCity(r.city, r.states?.[0]);
                                    setPendingSearchAsk(p => (p ? { ...p, placeError: r?.kind === 'unknown' ? `Couldn't place "${text}". Try a state, a major city, or "City, ST".` : '' } : p));
                                  };
                                  return (
                                    <>
                                    <div className={styles.prefCardItem}>
                                      <span className={styles.prefCardNum}>{++n}</span>
                                      <div>
                                        <div className={styles.prefCardLabel}>Location</div>
                                        <div className={styles.prefCardHint}>
                                          {msg.prefMeta.placeSource === 'query'
                                            ? 'Pre-selected from your request. Change it here for this search.'
                                            : msg.prefMeta.placeSource === 'profile'
                                              ? 'Pre-selected from your saved profile. Change it here for this search.'
                                              : usual.length
                                                ? `Anywhere in ${msg.prefMeta.countryLabel} unless you pick a state or city. Your usual states are offered, not applied.`
                                                : `Anywhere in ${msg.prefMeta.countryLabel} unless you pick a state or city.`}
                                        </div>
                                        <div className={styles.prefChips}>
                                          {chip(`Anywhere in ${msg.prefMeta.countryLabel}`, !chosen, clearPlace)}
                                          {/* "Selected:" so the chosen chip and the Anywhere chip do not
                                              read as two options in one list. */}
                                          {chosen && <span className={styles.prefCardAuto}>Selected:</span>}
                                          {place.city
                                            ? chip(`${describePlace(place)} ×`, true, clearPlace)
                                            : states.map(st => chip(`${STATE_NAMES[st] || st} ×`, true, () => removeState(st)))}
                                          {usual.map(st => (
                                            <span key={`usual-${st}`} className={styles.prefChipGroup}>
                                              <button type="button" className={styles.prefChip} disabled={!ask} onClick={() => addState(st)}>
                                                {STATE_NAMES[st] || st}<span className={styles.prefChipTag}> usual</span>
                                              </button>
                                              <button
                                                type="button"
                                                className={styles.prefChipX}
                                                disabled={!ask}
                                                title={`Stop suggesting ${STATE_NAMES[st] || st}`}
                                                aria-label={`Stop suggesting ${STATE_NAMES[st] || st}`}
                                                onClick={() => forgetUsual(st)}
                                              >×</button>
                                            </span>
                                          ))}
                                          <input
                                            type="text"
                                            className={`${styles.prefNumber} ${styles.prefText}`}
                                            placeholder="state or city"
                                            aria-label="Add a state or city"
                                            disabled={!ask}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitPlace(e.currentTarget); } }}
                                            onBlur={(e) => commitPlace(e.currentTarget)}
                                          />
                                        </div>
                                        {ask?.placeError && <div className={styles.prefCardHint}>{ask.placeError}</div>}
                                      </div>
                                    </div>
                                    {/* What the place means. Pre-answered, so it never blocks; it is a
                                        numbered question so it is seen as one. */}
                                    {scoped && (
                                      <div className={styles.prefCardItem}>
                                        <span className={styles.prefCardNum}>{++n}</span>
                                        <div>
                                          <div className={styles.prefCardLabel}>{where} means…</div>
                                          <div className={styles.prefCardHint}>
                                            People who live there: each person&rsquo;s LinkedIn location is checked and anyone I cannot place is left out.
                                            Companies based there: the usual headquarters filter; the people can live anywhere.
                                          </div>
                                          <div className={styles.prefChips}>
                                            {chip('People who live there',  answers.locationType === 'person',  () => set({ locationType: 'person' }))}
                                            {chip('Companies based there',  answers.locationType === 'company', () => set({ locationType: 'company' }))}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    </>
                                  );
                                })()}
                                {/* How deep to go. Named-company searches only - on a broad search
                                    the count came from the user's own prompt and the pill above
                                    already shows it.

                                    Pre-answered with the default so it never blocks; it is here to
                                    be RAISED, for the companies with hundreds of sellers where the
                                    default 10 is a rounding error. Labelled in both units because
                                    1 credit is 1 delivered contact, so the two numbers are the
                                    same number and pretending otherwise would misrepresent the
                                    cost. */}
                                {msg.prefMeta.mode === 'budget' && (
                                <div className={styles.prefCardItem}>
                                  <span className={styles.prefCardNum}>{++n}</span>
                                  <div>
                                    <div className={styles.prefCardLabel}>
                                      Up to {budget} contact{budget === 1 ? '' : 's'}
                                      <span className={styles.prefCardAuto}>
                                        {' '}· about {budget} credit{budget === 1 ? '' : 's'}
                                      </span>
                                    </div>
                                    <div className={styles.prefCardHint}>
                                      {leadCredits && !leadCredits.unlimited
                                        ? `You have ${leadCredits.remaining} left. Only delivered contacts are charged — LinkedIn-only rows are free.`
                                        : 'Only delivered contacts are charged — LinkedIn-only rows are free.'}
                                    </div>
                                    <div className={styles.prefChips}>
                                      {[10, 25, 50, 100].map(v => chip(String(v), budget === v, () => set({ limit: v })))}
                                      <input
                                        type="number"
                                        min="1"
                                        className={styles.prefNumber}
                                        placeholder="custom"
                                        value={[10, 25, 50, 100].includes(budget) ? '' : budget}
                                        disabled={!ask}
                                        onChange={(e) => {
                                          const v = parseInt(e.target.value, 10);
                                          if (Number.isFinite(v) && v > 0) set({ limit: v });
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                                )}
                                {msg.prefMeta.showCapQuestion ? (
                                  <div className={styles.prefCardItem}>
                                    <span className={styles.prefCardNum}>{++n}</span>
                                    <div>
                                      <div className={styles.prefCardLabel}>
                                        {msg.prefMeta.batch ? 'Contacts per company' : 'Max contacts from the same company'}
                                      </div>
                                      <div className={styles.prefCardHint}>
                                        {msg.prefMeta.batch
                                          ? `How many to pull from each of the ${msg.prefMeta.batchCompanyCount} ${msg.prefMeta.batchCompanyCount === 1 ? 'company' : 'companies'} in your file.`
                                          : msg.prefMeta.capFromMemory
                                            ? `Your usual is ${msg.prefMeta.memoryCap} per company, so it is pre-selected. Pick another to change it for this search.`
                                            : 'Keeps a big employer from filling the whole list.'}
                                      </div>
                                      <div className={styles.prefChips}>
                                        {[1, 2, 3, 5].map(v => chip(String(v), answers.cap === v, () => set({ cap: v })))}
                                        {/* "No limit" is a diversity control. On a batch it would mean
                                            "every contact at every company", which is not a thing the
                                            backend offers - it caps each company at 10. */}
                                        {!msg.prefMeta.batch && chip('No limit', answers.cap === 0, () => set({ cap: 0 }))}
                                      </div>
                                    </div>
                                  </div>
                                ) : null}
                                {/* Company revenue. Broad searches only. Lead411 has no revenue
                                    parameter, so the band narrows the pull by headcount and then
                                    checks each company's reported revenue before a credit is spent.
                                    Chips for the common bands, a box for anything else. */}
                                {msg.prefMeta.showRevenueQuestion && (() => {
                                  const current = answers.revenue ?? msg.prefMeta.revenuePick ?? '';
                                  const onChip  = REVENUE_CHIPS.some(c => c.value === current);
                                  const commit  = (el) => {
                                    const v = parseRevenueRange(el.value);
                                    if (v) { set({ revenue: v }); el.value = ''; }
                                  };
                                  return (
                                    <div className={styles.prefCardItem}>
                                      <span className={styles.prefCardNum}>{++n}</span>
                                      <div>
                                        <div className={styles.prefCardLabel}>Company revenue</div>
                                        <div className={styles.prefCardHint}>
                                          {msg.prefMeta.revenueSource === 'query'
                                            ? 'Pre-selected from your request. Pick another to change it for this search.'
                                            : msg.prefMeta.revenueSource === 'profile'
                                              ? 'Pre-selected from your saved profile. Pick another to change it for this search.'
                                              : msg.prefMeta.revenueSource === 'memory'
                                                ? `Your usual is ${formatRevenueRange(msg.prefMeta.revenuePick)}, so it is pre-selected. Pick another to change it for this search.`
                                                : 'Only companies in this band. Checked against reported revenue before any credit is spent.'}
                                        </div>
                                        <div className={styles.prefChips}>
                                          {REVENUE_CHIPS.map(c => chip(c.label, current === c.value, () => set({ revenue: c.value })))}
                                          {!onChip && (
                                            <span className={`${styles.prefChip} ${styles.prefChipActive}`}>{formatRevenueRange(current)}</span>
                                          )}
                                          <input
                                            type="text"
                                            className={`${styles.prefNumber} ${styles.prefText}`}
                                            placeholder="custom, e.g. 5M to 20M"
                                            aria-label="Custom revenue range"
                                            disabled={!ask}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(e.currentTarget); } }}
                                            onBlur={(e) => commit(e.currentTarget)}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                                {/* No "Searching <company>" row on the budget card - the company
                                    is in the pill above it, and a numbered item that asks nothing
                                    reads like a question the user has to answer. */}

                                <div className={styles.prefCardItem}>
                                  <span className={styles.prefCardNum}>{++n}</span>
                                  <div>
                                    <div className={styles.prefCardLabel}>
                                      {answers.pool === 'new' ? 'Pulling a new pool'
                                        : answers.pool === 'both' ? 'New first, filling gaps from existing'
                                        : 'Using existing results'}
                                      <span className={styles.prefCardAuto}> · auto, tap to change</span>
                                    </div>
                                    <div className={styles.prefCardHint}>
                                      {msg.prefMeta.profileChanged
                                        ? 'Your profile changed since the last search here, so this pulls fresh contacts.'
                                        : msg.prefMeta.shownCount > 0
                                          ? `You already have ${msg.prefMeta.shownCount} contact${msg.prefMeta.shownCount > 1 ? 's' : ''} in this chat, so this pulls names you haven't seen.`
                                          : 'Nothing cached for this chat yet — existing results are instant and free; a new pool spends credits.'}
                                    </div>
                                    {msg.prefMeta.batch && (
                                      <div className={styles.prefCardHint}>
                                        Cached contacts are matched per company, so a new pool re-searches every one.
                                        Both tries new people first, then fills any gap with contacts you already have (marked as repeats, not charged again).
                                      </div>
                                    )}
                                    <div className={styles.prefChips}>
                                      {chip('New pool',       answers.pool === 'new',      () => set({ pool: 'new' }))}
                                      {chip('Use existing',   answers.pool === 'existing', () => set({ pool: 'existing' }))}
                                      {/* Batch only for now: the chat engine's exclusion set threads
                                          through free re-pull and unlock budgeting, so its "both"
                                          is a different, larger change. */}
                                      {msg.prefMeta.batch && chip('Both', answers.pool === 'both', () => set({ pool: 'both' }))}
                                    </div>
                                  </div>
                                </div>

                                {msg.prefMeta.showIndustryQuestion && (
                                  <div className={styles.prefCardItem}>
                                    <span className={styles.prefCardNum}>{++n}</span>
                                    <div>
                                      <div className={styles.prefCardLabel}>How to split results across industries</div>
                                      <div className={styles.prefCardHint}>Balanced, or type a split like &ldquo;{msg.prefMeta.exampleSplit}&rdquo;.</div>
                                      <div className={styles.prefChips}>
                                        {chip('Balanced', answers.industry === 'balanced', () => set({ industry: 'balanced' }))}
                                        {Array.isArray(answers.industry) && (
                                          <span className={`${styles.prefChip} ${styles.prefChipActive}`}>
                                            {answers.industry.map(d => `${d.count} ${d.industry}`).join(', ')}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className={styles.prefCardFooter}>
                                <button
                                  type="button"
                                  className={styles.prefSubmit}
                                  disabled={!ready || isRunning(activeChatId)}
                                  onClick={() => submitSearchAsk(ask, ask.answers)}
                                >{ready
                                  ? (msg.prefMeta.batch ? `Run on ${msg.prefMeta.batchCompanyCount} companies` : 'Run search')
                                  : 'Choose an option for each'}</button>
                                <span className={styles.prefCardFooterNote}>Or just type your answer.</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {/* Cost note - the meter estimate before an action and the actual after
                        it. Kept as its own field (not a status line) so streaming updates,
                        which replace `lines`, never wipe it. Transitions estimate → actual. */}
                    {msg.costNote?.text && !msg.isPrefQuestion && !msg.isConversational && !msg.isIcpUpdate && (
                      <div className={`${styles.costNote} ${msg.costNote.kind === 'actual' ? styles.costNoteActual : ''} ${msg.costNote.kind === 'free' ? styles.costNoteFree : ''}`}>
                        <span className={styles.costNoteIcon} aria-hidden="true">
                          {msg.costNote.kind === 'actual' ? '✓' : msg.costNote.kind === 'free' ? '∞' : '≈'}
                        </span>
                        <span>{msg.costNote.text}</span>
                      </div>
                    )}

                    {/* Regular status lines (not shown for pref/conversational/icp-update) */}
                    {!msg.isPrefQuestion && !msg.isConversational && !msg.isIcpUpdate && (
                      <div className={styles.statusLines}>
                        {/* Before the model has said anything: a face and three dots, not a
                            spinner and the word "Thinking". */}
                        {msg.thinking && !msg.done && <TypingIndicator agent="hunter" />}
                        {msg.lines?.map((line, i) => {
                          // A batch ticker below takes over as the running line, so the last
                          // persisted line is finished work by then and should read as such.
                          const isLive = i === msg.lines.length - 1 && !msg.done && !batchTicker[msg.id];
                          // A failed step is not finished work, so it never gets the green tick.
                          const mark = isLive ? <span className={styles.spinner} />
                            : msg.status === 'error' ? <span className={styles.errMark} aria-hidden="true">!</span>
                            : <span className={styles.tick}>✓</span>;
                          return (
                            <div key={i} className={styles.statusLine}>
                              {mark}
                              <span className={styles.statusLineText}>{line}</span>
                              {/* Only on the line that is actually running. A clock next to a
                                  finished step would be reporting the wrong thing. */}
                              {isLive && <ElapsedTime since={msg.startedAt} />}
                            </div>
                          );
                        })}
                        {/* Live batch progress. Kept out of msg.lines on purpose - see batchTicker. */}
                        {!msg.done && batchTicker[msg.id] && (
                          <div className={styles.statusLine}>
                            <span className={styles.spinner} />
                            <span className={styles.statusLineText}>{batchTicker[msg.id]}</span>
                            <ElapsedTime since={msg.startedAt} />
                          </div>
                        )}
                        {!msg.done && <ActivityFeed items={msg.activity} />}
                      </div>
                    )}

                    {/* The New Pool / Not New buttons that used to live here are now the pool
                        question inside the preferences card above. They were a separate
                        blocking card that returned before the preferences card could render,
                        which is why the per-company question disappeared on repeat searches. */}

                    {/* LinkedIn prompt - Find Contact Info / Research This Person */}
                    {msg.isLinkedInPrompt && pendingLinkedIn?.msgId === msg.id && (
                      <div className={styles.clarificationBtns}>
                        <button
                          className={styles.clarifyNew}
                          onClick={() => handleLinkedInChoice('contact')}
                          disabled={isRunning(activeChatId) || !!pendingLinkedIn?.contactDone}
                        >{pendingLinkedIn?.contactDone ? '✓ Contact Found' : 'Find Contact Info'}</button>
                        <button
                          className={styles.clarifyKeep}
                          onClick={() => handleLinkedInChoice('research')}
                          disabled={isRunning(activeChatId) || !!pendingLinkedIn?.researchDone}
                        >{pendingLinkedIn?.researchDone ? '✓ Researched' : 'Research This Person'}</button>
                      </div>
                    )}

                    {/* ICP Update preview card */}
                    {msg.done && msg.isIcpUpdate && msg.icpUpdateProposal && (
                      <div className={styles.icpUpdateCard}>
                        <div className={styles.icpUpdateHeader}>
                          <span className={styles.icpUpdateIcon}>✏️</span>
                          <span className={styles.icpUpdateTitle}>Profile Update</span>
                        </div>
                        <p className={styles.icpUpdateSummary}>{msg.icpUpdateProposal.summary}</p>

                        {(Array.isArray(msg.icpUpdateProposal.currentValue) || Array.isArray(msg.icpUpdateProposal.proposedValue)) ? (
                          <div className={styles.icpUpdateDiff}>
                            <div className={styles.icpUpdateDiffCol}>
                              <div className={styles.icpUpdateDiffLabel}>Current</div>
                              <div className={styles.icpUpdatePills}>
                                {(msg.icpUpdateProposal.currentValue || []).map((v, i) => {
                                  const label = INDUSTRY_OPTIONS.find(o => o.key === v)?.label || v;
                                  const isRemoving = msg.icpUpdateProposal.action === 'remove' &&
                                    (msg.icpUpdateProposal.proposedValue || []).indexOf(v) === -1;
                                  return <span key={i} className={`${styles.icpPill} ${isRemoving ? styles.icpPillRemoved : ''}`}>{label}</span>;
                                })}
                                {(msg.icpUpdateProposal.currentValue || []).length === 0 && <span className={styles.icpPillEmpty}>None set</span>}
                              </div>
                            </div>
                            <span className={styles.icpUpdateArrow}>→</span>
                            <div className={styles.icpUpdateDiffCol}>
                              <div className={styles.icpUpdateDiffLabel}>Proposed</div>
                              <div className={styles.icpUpdatePills}>
                                {(msg.icpUpdateProposal.proposedValue || []).map((v, i) => {
                                  const label = INDUSTRY_OPTIONS.find(o => o.key === v)?.label || v;
                                  const isNew = msg.icpUpdateProposal.action === 'add' &&
                                    !(msg.icpUpdateProposal.currentValue || []).includes(v);
                                  return <span key={i} className={`${styles.icpPill} ${isNew ? styles.icpPillAdded : ''}`}>{label}</span>;
                                })}
                                {(msg.icpUpdateProposal.proposedValue || []).length === 0 && <span className={styles.icpPillEmpty}>None</span>}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.icpUpdateScalar}>
                            <div className={styles.icpUpdateScalarRow}>
                              <span className={styles.icpUpdateDiffLabel}>Current</span>
                              <span className={styles.icpScalarVal}>{msg.icpUpdateProposal.currentLabel || msg.icpUpdateProposal.currentValue || 'Not set'}</span>
                            </div>
                            <div className={styles.icpUpdateScalarRow}>
                              <span className={styles.icpUpdateDiffLabel}>Proposed</span>
                              <span className={`${styles.icpScalarVal} ${styles.icpScalarValNew}`}>{msg.icpUpdateProposal.proposedLabel || msg.icpUpdateProposal.proposedValue || 'Not set'}</span>
                            </div>
                          </div>
                        )}

                        {!msg.icpUpdateConfirmed && !msg.icpUpdateDismissed && (
                          <div className={styles.icpUpdateActions}>
                            <button
                              className={styles.icpUpdateConfirm}
                              onClick={() => handleIcpUpdateConfirm(msg.chatId || activeChatId, msg.id, msg.icpUpdateProposal)}
                            >Yes, update</button>
                            <button
                              className={styles.icpUpdateDismiss}
                              onClick={() => handleIcpUpdateDismiss(msg.chatId || activeChatId, msg.id)}
                            >Keep as is</button>
                          </div>
                        )}
                        {msg.icpUpdateConfirmed && (
                          <div className={styles.icpUpdateResult}>✓ Profile updated</div>
                        )}
                        {msg.icpUpdateDismissed && (
                          <div className={styles.icpUpdateResult}>No changes made.</div>
                        )}
                        {msg.icpUpdateError && (
                          <div className={styles.icpUpdateResultError}>Failed to save. Try again.</div>
                        )}
                      </div>
                    )}

                    {/* Conversational / ICP response card. Rendered while streaming too, so the
                        reply types in; bullets and blocks only exist once it is done. */}
                    {msg.isConversational && (msg.done || msg.streaming) && (
                      <div className={`${styles.chatCard} ${msg.conversationalIntent === 'icp_question' ? styles.chatCardIcp : styles.chatCardGeneral}`}>
                        {msg.conversationalIntent === 'icp_question' && (
                          <div className={styles.chatCardHeader}>
                            <AgentAvatar agent="analyst" size={18} className={styles.chatCardAvatar} />
                            <span className={styles.chatCardTitle}>ICP Insight</span>
                          </div>
                        )}
                        <div className={styles.chatCardWithAvatar}>
                          {msg.conversationalIntent !== 'icp_question' && (
                            <AgentAvatar agent={agentFor(msg)} size={22} className={styles.assistantAvatar} />
                          )}
                          <div className={styles.chatCardBody}>
                            {renderProse(msg.conversationalResponse || '', `r${msg.id}`, {
                              trailing: msg.streaming && !msg.done ? <span className={styles.caret} aria-hidden="true" /> : null,
                            })}
                          </div>
                        </div>
                        {/* The model's own question before a search: its likely answers as chips.
                            Retired once answered so it cannot start a second search. */}
                        {msg.done && msg.isSearchAsk && msg.searchAsk && !msg.searchAsk.answered && msg.searchAsk.chips?.length > 0 && (
                          <div className={styles.prefChips}>
                            {msg.searchAsk.chips.map((chip, i) => (
                              <button
                                key={i}
                                type="button"
                                className={styles.prefChip}
                                disabled={isRunning(activeChatId)}
                                onClick={() => handleSend(chip)}
                              >{chip}</button>
                            ))}
                          </div>
                        )}
                        {msg.done && msg.conversationalBullets?.length > 0 && (
                          <ul className={styles.chatCardBullets}>
                            {msg.conversationalBullets.map((b, i) => (
                              <li key={i}>{renderInline(b, `b${i}`)}</li>
                            ))}
                          </ul>
                        )}
                        <ChatBlocks
                          blocks={msg.conversationalBlocks}
                          sellerProfile={sellerProfile}
                          onEditBlock={(i, patch) => updateMessageById(activeChatId, msg.id, m => ({
                            conversationalBlocks: (m.conversationalBlocks || []).map(
                              (b, bi) => bi === i ? { ...b, ...patch } : b
                            ),
                          }))}
                        />
                        {/* Next moves the model offered with this reply. Same chips as the
                            post-search take: label shown, the full request sent on click. */}
                        {msg.done && msg.conversationalSuggestions?.length > 0 && (
                          <div className={styles.prefChips}>
                            {msg.conversationalSuggestions.map((s, i) => (
                              <button
                                key={i}
                                type="button"
                                className={styles.prefChip}
                                title={s.prompt}
                                disabled={isRunning(activeChatId)}
                                onClick={() => handleSend(s.prompt)}
                              >{s.label}</button>
                            ))}
                          </div>
                        )}
                        {/* Say it plainly where users most wonder about cost: a drafted email
                            never touches a meter. Only shown when the reply includes a draft. */}
                        {Array.isArray(msg.conversationalBlocks) && msg.conversationalBlocks.some(b => b?.type === 'email') && (
                          <div className={styles.freeNote}>
                            <span className={styles.freeNoteIcon} aria-hidden="true">∞</span>
                            <span>{neverMeteredLine()}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* The raw error behind a failure, for us. Never on a production build. */}
                    {SHOW_DEBUG && msg.done && msg.debugDetail && (
                      <div className={styles.debugDetail}>dev detail: {msg.debugDetail}</div>
                    )}

                    {/* LinkedIn Research Brief Card */}
                    {msg.done && msg.isResearch && msg.researchData && (
                      <div className={styles.researchCard}>
                        <div className={styles.researchHeader}>
                          <AgentAvatar agent="composer" size={18} className={styles.chatCardAvatar} />
                          <span className={styles.researchTitle}>Outreach Brief</span>
                          <span className={styles.researchSubject}>{msg.researchData.subject}</span>
                        </div>
                        {msg.researchData.companyMismatch && (
                          <div className={styles.companyMismatchBanner}>
                            ⚠ LinkedIn shows this person is now at <strong>{msg.researchData.freshCompany}</strong> — your record has <strong>{msg.researchData.originalCompany}</strong>. The draft is addressed to their current employer.
                          </div>
                        )}
                        {msg.researchData.companySnapshot?.whatTheyDo && (
                          <div className={styles.researchSection}>
                            <div className={styles.researchSectionLabel}>Their Company{msg.researchData.companySnapshot.name ? `: ${msg.researchData.companySnapshot.name}` : ''}</div>
                            <p className={styles.researchPara}>{msg.researchData.companySnapshot.whatTheyDo}</p>
                            <p className={styles.researchPara} style={{ marginTop: 6 }}>
                              <FitBadge fit={msg.researchData.companySnapshot.fit} />
                              {msg.researchData.companySnapshot.fitReason}
                            </p>
                          </div>
                        )}
                        {msg.researchData.who && (
                          <div className={styles.researchSection}>
                            <div className={styles.researchSectionLabel}>Who They Are</div>
                            <p className={styles.researchPara}>{msg.researchData.who}</p>
                          </div>
                        )}
                        {msg.researchData.angles?.length > 0 && (
                          <div className={styles.researchSection}>
                            <div className={styles.researchSectionLabel}>Outreach Angles</div>
                            <ul className={styles.researchList}>
                              {msg.researchData.angles.map((a, i) => (
                                <li key={i}>{a}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {msg.researchData.painPoints?.length > 0 && (
                          <div className={styles.researchSection}>
                            <div className={styles.researchSectionLabel}>Likely Priorities / Pain Points</div>
                            <ul className={styles.researchList}>
                              {msg.researchData.painPoints.map((p, i) => (
                                <li key={i}>{p}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {msg.researchData.outreachDraft && (
                          <ResearchDraftBlock
                            initialDraft={msg.researchData.outreachDraft}
                            styles={styles}
                            onEdit={text => updateMessageById(activeChatId, msg.id, m => ({
                              researchData: { ...m.researchData, outreachDraft: text },
                            }))}
                          />
                        )}
                      </div>
                    )}

                    {/* Company Brief: "what does X do?" / "research X" */}
                    {msg.done && msg.isCompanyResearch && msg.companyResearchData && (() => {
                      const d = msg.companyResearchData;
                      const facts = [
                        d.industry,
                        d.employees ? `${d.employees} employees` : '',
                        d.revenue ? `${d.revenue} revenue` : '',
                        d.hqLocation ? `HQ ${d.hqLocation}` : '',
                        d.foundedYear ? `founded ${d.foundedYear}` : '',
                        d.fundingStage,
                      ].filter(Boolean);
                      return (
                        <div className={styles.researchCard}>
                          <div className={styles.researchHeader}>
                            <AgentAvatar agent="composer" size={18} className={styles.chatCardAvatar} />
                            <span className={styles.researchTitle}>Company Brief</span>
                            <span className={styles.researchSubject}>{d.name}{d.domain ? ` · ${d.domain}` : ''}</span>
                          </div>
                          <div className={styles.researchSection}>
                            <div className={styles.researchSectionLabel}>What They Do</div>
                            <p className={styles.researchPara}>{d.whatTheyDo}</p>
                            {d.whoTheySell && <p className={styles.researchPara} style={{ marginTop: 6 }}>Sells to: {d.whoTheySell}</p>}
                            {facts.length > 0 && (
                              <div className={styles.companyFacts}>{facts.map((f, i) => <span key={i}>{f}</span>)}</div>
                            )}
                          </div>
                          <div className={styles.researchSection}>
                            <div className={styles.researchSectionLabel}>Fit For You</div>
                            <p className={styles.researchPara}><FitBadge fit={d.fit} />{d.fitReason}</p>
                          </div>
                          {(d.signals?.length > 0 || d.recentNews) && (
                            <div className={styles.researchSection}>
                              <div className={styles.researchSectionLabel}>Worth Knowing</div>
                              <ul className={styles.researchList}>
                                {d.recentNews && !(d.signals || []).includes(d.recentNews) && <li>{d.recentNews}</li>}
                                {(d.signals || []).map((s, i) => <li key={i}>{s}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Follow-up chips after auto-research (URL + instruction flow) */}
                    {msg.isLinkedInFollowUp && pendingLinkedIn?.msgId === msg.id && (
                      <div style={{ marginTop: '10px' }}>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                          Want me to do more with this contact?
                        </p>
                        <div className={styles.clarificationBtns}>
                          <button
                            className={styles.clarifyNew}
                            onClick={() => handleLinkedInChoice('contact')}
                            disabled={isRunning(activeChatId) || !!pendingLinkedIn?.contactDone}
                          >{pendingLinkedIn?.contactDone ? '✓ Contact Found' : 'Find Contact Info'}</button>
                          <button
                            className={styles.clarifyKeep}
                            onClick={() => handleLinkedInChoice('research')}
                            disabled={isRunning(activeChatId) || !!pendingLinkedIn?.researchDone}
                          >{pendingLinkedIn?.researchDone ? '✓ Researched' : 'Research More'}</button>
                        </div>
                      </div>
                    )}

                    {/* RepBuddy's take on the list, above the rows so the reaction reads before
                        the data. Pending with no text yet shows the typing dots; a failed debrief
                        shows nothing at all. */}
                    {msg.done && msg.debrief && msg.debrief.status !== 'failed' && (
                      msg.debrief.status === 'pending' && !msg.debrief.text
                        ? <TypingIndicator agent="hunter" />
                        : (
                          <div className={`${styles.chatCard} ${styles.chatCardGeneral} ${styles.debriefCard}`}>
                            <div className={styles.chatCardWithAvatar}>
                              <AgentAvatar agent="hunter" size={22} className={styles.assistantAvatar} />
                              <div className={styles.chatCardBody}>
                                {renderProse(msg.debrief.text || '', `d${msg.id}`, {
                                  trailing: msg.debrief.status === 'pending' ? <span className={styles.caret} aria-hidden="true" /> : null,
                                })}
                              </div>
                            </div>
                            {msg.debrief.status === 'done' && msg.debrief.suggestions?.length > 0 && (
                              <div className={styles.prefChips}>
                                {msg.debrief.suggestions.map((s, i) => (
                                  <button
                                    key={i}
                                    type="button"
                                    className={styles.prefChip}
                                    title={s.prompt}
                                    disabled={isRunning(activeChatId)}
                                    onClick={() => handleSend(s.prompt)}
                                  >{s.label}</button>
                                ))}
                              </div>
                            )}
                          </div>
                        )
                    )}

                    {/* Regular search results - only ever rendered from the final payload.
                        Rows used to stream in provisionally, which was worse than it sounded:
                        the cancel path merges `{ done: true }` onto the message, so the
                        half-filtered list would suddenly present itself as authoritative with
                        export enabled. Progress is reported by the status ticker instead. */}
                    {msg.done && msg.contacts?.length > 0 && (
                      <div className={styles.results}>
                        {msg.isPersonSearch && (
                          <div className={styles.personSearchHeader}>
                            {msg.contacts.length === 1
                              ? `Found: ${msg.personSearchName}`
                              : `${msg.contacts.length} matches found for "${msg.personSearchName}"`
                            }
                          </div>
                        )}
                        {msg.valid?.length > 0 && (
                          <div className={styles.section}>
                            <div className={styles.sectionHead}>
                              <span className={styles.dot} style={{ background: 'var(--green)' }} />
                              <span className={styles.sectionTitle}>Valid contacts</span>
                              <span className={styles.sectionCount}>{msg.valid.length}</span>
                              <button
                                className={styles.exportSmall}
                                onClick={() => setExportPanel({ type: 'contacts', contacts: msg.valid, exportKey: `${msg.id}_valid`, msgId: msg.id, canSplit: false, defaultFilename: 'repbuddy-verified' })}
                                disabled={exportingKey !== null}
                              >{exportingKey === `${msg.id}_valid` ? 'Verifying…' : 'Export'}</button>
                            </div>
                            <div className={styles.table}>
                              <div className={styles.tableHead}><span>Name</span><span>Title</span><span>Company</span><span>Email</span><span /></div>
                              {msg.valid.map(({ contact, result }, i) => (
                                <ContactRow key={i} contact={contact} result={result} type="valid" styles={styles} onVerified={setLiRemaining} onEmpVerified={handleEmpVerified} sellerProfile={sellerProfile} cachedDraft={draftRef.current.get((result?.email || '').toLowerCase()) || null} onDraftReady={(email, text) => { if (email) { draftRef.current.set(email.toLowerCase(), text); writeDraftToStorage(email, text); } }} />
                              ))}
                            </div>
                          </div>
                        )}
                        {msg.risky?.length > 0 && (
                          <div className={styles.section}>
                            <div className={styles.sectionHead}>
                              <span className={styles.dot} style={{ background: 'var(--amber)' }} />
                              <span className={styles.sectionTitle}>Risky: verify before sending</span>
                              <span className={styles.sectionCount}>{msg.risky.length}</span>
                            </div>
                            <div className={styles.table}>
                              <div className={styles.tableHead}><span>Name</span><span>Title</span><span>Company</span><span>Email</span><span /></div>
                              {msg.risky.map(({ contact, result }, i) => (
                                <ContactRow key={i} contact={contact} result={result} type="risky" styles={styles} onVerified={setLiRemaining} onEmpVerified={handleEmpVerified} sellerProfile={sellerProfile} cachedDraft={draftRef.current.get((result?.email || '').toLowerCase()) || null} onDraftReady={(email, text) => { if (email) { draftRef.current.set(email.toLowerCase(), text); writeDraftToStorage(email, text); } }} />
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Between risky and invalid, matching the rank the backend orders these
                            by. Until this block existed the tier was bucketed, counted, named in
                            the summary line and shipped in the CSV - but never drawn, so a search
                            that filled its list with LinkedIn-only rows showed the user nothing
                            and read as a failure. */}
                        {msg.linkedinOnly?.length > 0 && (
                          <div className={styles.section}>
                            <div className={styles.sectionHead}>
                              <span className={styles.dot} style={{ background: 'var(--blue, #3b82f6)' }} />
                              <span className={styles.sectionTitle}>No email found: reachable another way</span>
                              <span className={styles.sectionCount}>{msg.linkedinOnly.length}</span>
                              <button
                                className={styles.exportSmall}
                                onClick={() => setExportPanel({ type: 'contacts', contacts: msg.linkedinOnly, exportKey: `${msg.id}_linkedin`, msgId: msg.id, canSplit: false, defaultFilename: 'repbuddy-linkedin-only' })}
                                disabled={exportingKey !== null}
                              >{exportingKey === `${msg.id}_linkedin` ? 'Verifying…' : 'Export'}</button>
                            </div>
                            <div className={styles.table}>
                              <div className={styles.tableHead}><span>Name</span><span>Title</span><span>Company</span><span>Email</span><span /></div>
                              {msg.linkedinOnly.map(({ contact, result }, i) => (
                                <ContactRow key={i} contact={contact} result={result} type="linkedinOnly" styles={styles} onVerified={setLiRemaining} onEmpVerified={handleEmpVerified} sellerProfile={sellerProfile} cachedDraft={draftRef.current.get((result?.email || '').toLowerCase()) || null} onDraftReady={(email, text) => { if (email) { draftRef.current.set(email.toLowerCase(), text); writeDraftToStorage(email, text); } }} />
                              ))}
                            </div>
                          </div>
                        )}
                        {msg.invalid?.length > 0 && (
                          <div className={styles.section}>
                            <div className={styles.sectionHead}>
                              <span className={styles.dot} style={{ background: 'var(--red)' }} />
                              <span className={styles.sectionTitle}>Invalid: do not use</span>
                              <span className={styles.sectionCount}>{msg.invalid.length}</span>
                            </div>
                            <div className={styles.table}>
                              <div className={styles.tableHead}><span>Name</span><span>Title</span><span>Company</span><span>Email</span><span /></div>
                              {msg.invalid.map(({ contact, result }, i) => (
                                <ContactRow key={i} contact={contact} result={result} type="invalid" styles={styles} onVerified={setLiRemaining} onEmpVerified={handleEmpVerified} sellerProfile={sellerProfile} cachedDraft={draftRef.current.get((result?.email || '').toLowerCase()) || null} onDraftReady={(email, text) => { if (email) { draftRef.current.set(email.toLowerCase(), text); writeDraftToStorage(email, text); } }} />
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Go deeper on this company.
                            The preferences card is skipped whenever it has nothing to ask, which
                            is exactly the named-company search - so this is the only place a
                            budget can be raised for the big employers it was built for. It is
                            also the better moment: by now the summary above has already said
                            whether there are more people to be had. */}
                        {msg.searchCriteria && msg.moreAvailable && (
                          <div className={styles.pullMore}>
                            <span className={styles.pullMoreLabel}>
                              Want more? Pulling deeper costs about 1 credit per contact delivered.
                            </span>
                            <div className={styles.prefChips}>
                              {[25, 50, 100].filter(v => v > (msg.searchedFor || 0)).map(v => (
                                <button
                                  key={v}
                                  type="button"
                                  className={styles.prefChip}
                                  disabled={!!runningChats[activeChatId] || exportingKey !== null}
                                  onClick={() => rerunWithBudget(msg, v)}
                                >Up to {v}</button>
                              ))}
                            </div>
                          </div>
                        )}
                        <button
                          className={styles.exportAll}
                          onClick={() => setExportPanel({ type: 'contacts', contacts: msg.contacts, exportKey: `${msg.id}_all`, msgId: msg.id, canSplit: true, defaultFilename: 'repbuddy-contacts' })}
                          disabled={exportingKey !== null}
                        >{exportingKey === `${msg.id}_all` ? 'Verifying…' : 'Export full list'}</button>
                        {msg.exportOmitted > 0 && (
                          <div className={styles.exportOmitted}>
                            {msg.exportOmitted === 1
                              ? '1 contact was left out of the file — they have changed jobs since this search.'
                              : `${msg.exportOmitted} contacts were left out of the file — they have changed jobs since this search.`}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Disambiguation - user picks the correct company from multiple matches */}
                    {/* Verification spends a credit per address, so a large paste asks first.
                        Reuses the disambiguation card's classes rather than adding new ones -
                        it is the same shape: an assistant message with inline choices. */}
                    {msg.isVerifyConfirm && msg.verifyEmails?.length > 0 && (
                      <div className={styles.disambigBlock}>
                        <div className={styles.disambigChoices}>
                          <button
                            type="button"
                            className={styles.disambigBtn}
                            onClick={() => handleEmailVerifyChat(activeChatId, msg.id, msg.verifyEmails, { confirmed: true })}
                            disabled={isRunning(activeChatId)}
                          >
                            <span className={styles.disambigBtnTitle}>Verify {msg.verifyEmails.length} addresses</span>
                          </button>
                          <button
                            type="button"
                            className={styles.disambigBtn}
                            onClick={() => updateMessageById(activeChatId, msg.id, () => ({
                              status: 'done', done: true, lines: ['Cancelled - nothing was verified and no credits were used.'],
                              contacts: [], valid: [], risky: [], invalid: [],
                            }))}
                            disabled={isRunning(activeChatId)}
                          >
                            <span className={styles.disambigBtnTitle}>Cancel</span>
                          </button>
                        </div>
                      </div>
                    )}
                    {msg.isDisambiguation && msg.disambiguationItems?.length > 0 && (
                      <div className={styles.disambigBlock}>
                        {msg.disambiguationItems.map(item => {
                          const normKey = normEnrichName(item.name);
                          // Read decided/chosen state from pendingEnrich while it's active,
                          // then fall back to the final snapshot so the UI stays stable
                          // after pendingEnrich is cleared when enrichment kicks off.
                          const chosenDomain = pendingEnrich?.resolvedDomains?.[normKey]
                                            ?? msg.finalResolvedDomains?.[normKey];
                          const isDecided    = !!(pendingEnrich
                            ? pendingEnrich.decidedSet?.includes(normKey)
                            : msg.finalDecidedSet?.includes(normKey));
                          return (
                            <div key={item.name} className={styles.disambigItem}>
                              <div className={styles.disambigQuestion}>
                                🔍 Which <strong>"{item.name}"</strong> did you mean?
                              </div>
                              <div className={styles.disambigChoices}>
                                {item.candidates.map(c => (
                                  <button
                                    type="button"
                                    key={c.domain}
                                    className={`${styles.disambigBtn} ${chosenDomain === c.domain ? styles.disambigBtnChosen : ''} ${c.fromFile && !isDecided ? styles.disambigBtnPreselected : ''}`}
                                    onClick={() => handleDisambiguationChoice(item.name, c.domain)}
                                    disabled={isDecided || isRunning(activeChatId)}
                                  >
                                    <span className={styles.disambigBtnTitle}>
                                      {c.fromFile ? '📄 From your file' : (c.title.length > 55 ? c.title.slice(0, 55) + '…' : c.title)}
                                    </span>
                                    <span className={styles.disambigBtnDomain}>{c.domain}</span>
                                    {c.fromFile && !isDecided && <span className={styles.disambigFileBadge}>pre-selected</span>}
                                  </button>
                                ))}
                                {/* The same candidates as links, so a website can be checked before it is picked.
                                    Separate from the buttons: a link inside a button is not valid HTML, and a
                                    click that both opened a tab and made the choice would be the wrong click. */}
                                <div className={styles.disambigLinks}>
                                  {item.candidates.map(c => (
                                    <a key={`link-${c.domain}`} href={`https://${c.domain}`} target="_blank" rel="noreferrer" className={styles.disambigLink}>
                                      {c.domain} ↗
                                    </a>
                                  ))}
                                </div>
                                <button
                                  type="button"
                                  className={`${styles.disambigBtn} ${isDecided && !chosenDomain ? styles.disambigBtnSkip : ''}`}
                                  onClick={() => handleDisambiguationChoice(item.name, '')}
                                  disabled={isDecided || isRunning(activeChatId)}
                                >
                                  <span className={styles.disambigBtnTitle}>❓ None of these (skip)</span>
                                </button>
                              </div>
                              {isDecided && (
                                <div className={styles.disambigChosen}>
                                  {chosenDomain ? `✓ Locked in: ${chosenDomain}` : '✓ Skipped'}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Look-alike count card - "how many companies like X?" */}
                    {msg.isLookalikeAsk && msg.lookalikeAsk && (
                      <div style={{ marginTop: 8, padding: 12, border: '1px solid rgba(128,128,128,0.25)', borderRadius: 10 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span>How many companies like {msg.lookalikeAsk.seedName}?</span>
                          {msg.lookalikeAsk.seedDomain && <span className={styles.prefCardPill}>{msg.lookalikeAsk.seedDomain}</span>}
                        </div>
                        {(() => {
                          const active = pendingLookalike?.msgId === msg.id ? pendingLookalike : null;
                          const count  = active?.count || msg.lookalikeAsk.defaultCount || 10;
                          const setCount = (v) => setPendingLookalike(p => (p ? { ...p, count: v } : p));
                          const chip = (label, on, onClick) => (
                            <button type="button" key={label} className={on ? `${styles.prefChip} ${styles.prefChipActive}` : styles.prefChip} onClick={onClick} disabled={!active}>{label}</button>
                          );
                          return (
                            <>
                              <div className={styles.prefChips}>
                                {[5, 10, 20].map(v => chip(String(v), count === v, () => setCount(v)))}
                                <input
                                  type="number" min="1" max="20" className={styles.prefNumber} placeholder="custom"
                                  value={[5, 10, 20].includes(count) ? '' : count} disabled={!active}
                                  onChange={(e) => { const v = parseInt(e.target.value, 10); if (Number.isFinite(v) && v > 0) setCount(Math.min(v, 20)); }}
                                />
                              </div>
                              <div className={styles.prefCardFooter}>
                                <button
                                  type="button" className={styles.prefSubmit}
                                  disabled={!active || isRunning(activeChatId)}
                                  onClick={() => {
                                    if (!active) return;
                                    // Retire the card so it doesn't linger greyed-out above the results.
                                    updateMessageById(active.chatId, msg.id, () => ({ isLookalikeAsk: false, lookalikeAsk: null, status: 'done', done: true }));
                                    runLookalike(active.chatId, msg.lookalikeAsk.seedName, msg.lookalikeAsk.seedDomain, msg.lookalikeAsk.basis, count);
                                  }}
                                >Find {count} look-alikes</button>
                                <span className={styles.prefCardFooterNote}>Or just type a number.</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {/* Look-alike results - list with why-similar / how-differs + actions */}
                    {msg.isLookalike && Array.isArray(msg.lookalikeResults) && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                          {msg.lookalikeResults.length} {msg.lookalikeResults.length === 1 ? 'company' : 'companies'} like {msg.lookalikeSeed?.name || 'the seed'}
                        </div>
                        {(msg.lookalikeSeed?.industry || msg.lookalikeSeed?.deliveryModel || msg.lookalikeSeed?.employees) && (
                          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
                            Seed: {[msg.lookalikeSeed.industry, msg.lookalikeSeed.deliveryModel, msg.lookalikeSeed.employees && `${msg.lookalikeSeed.employees} employees`].filter(Boolean).join(' · ')}
                          </div>
                        )}
                        {msg.lookalikeShortfall && (
                          <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 8 }}>{msg.lookalikeShortfall}</div>
                        )}
                        {/* Stacked rows, not a table: full-width "Similar/Differs" lines wrap far
                            less than narrow columns, and the fixed-height scroll keeps a
                            20-company list tidy. */}
                        {msg.lookalikeResults.length > 0 && (
                          <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid rgba(128,128,128,0.2)', borderRadius: 10 }}>
                            {msg.lookalikeResults.map((r, i) => (
                              <div key={r.domain || i} style={{ padding: '9px 12px', borderBottom: i < msg.lookalikeResults.length - 1 ? '1px solid rgba(128,128,128,0.14)' : 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                                  <span style={{ fontWeight: 600 }}>{i + 1}. {r.name}</span>
                                  {r.domain && <a href={`https://${r.domain}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, opacity: 0.7 }}>{r.domain}</a>}
                                  {r.industry && <span style={{ fontSize: 12, opacity: 0.6 }}>· {r.industry}</span>}
                                </div>
                                {r.whySimilar && (
                                  <div style={{ fontSize: 12.5, marginTop: 3, lineHeight: 1.45 }}>
                                    <span style={{ opacity: 0.55 }}>Similar: </span>{r.whySimilar}
                                  </div>
                                )}
                                {r.howDiffers && (
                                  <div style={{ fontSize: 12.5, marginTop: 2, lineHeight: 1.45 }}>
                                    <span style={{ opacity: 0.55 }}>Differs: </span>{r.howDiffers}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {msg.lookalikeResults.length > 0 && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                            <button type="button" className={styles.prefSubmit} disabled={isRunning(activeChatId)} onClick={() => startLookalikeContacts(activeChatId, msg.lookalikeSeed?.name || '', msg.lookalikeResults)}>Find Contacts</button>
                            <button type="button" className={styles.prefSubmit} disabled={isRunning(activeChatId)} onClick={() => startLookalikeEnrich(activeChatId, msg.lookalikeSeed?.name || '', msg.lookalikeResults)}>Enrich Companies</button>
                            <button type="button" className={styles.prefSubmit} onClick={() => openLookalikeExport(msg.lookalikeSeed?.name || 'lookalikes', msg.lookalikeResults)}>Export List</button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Company-list count card - "how many startups in Austin?" */}
                    {msg.isCompaniesAsk && msg.companiesAsk && (
                      <div style={{ marginTop: 8, padding: 12, border: '1px solid rgba(128,128,128,0.25)', borderRadius: 10 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span>How many {describeCompanyCriteria(msg.companiesAsk.criteria)}?</span>
                          <span className={styles.prefCardPill}>free list</span>
                        </div>
                        {(() => {
                          const active = pendingCompanies?.msgId === msg.id ? pendingCompanies : null;
                          const count  = active?.count || msg.companiesAsk.defaultCount || 10;
                          const setCount = (v) => setPendingCompanies(p => (p ? { ...p, count: v } : p));
                          const chip = (label, on, onClick) => (
                            <button type="button" key={label} className={on ? `${styles.prefChip} ${styles.prefChipActive}` : styles.prefChip} onClick={onClick} disabled={!active}>{label}</button>
                          );
                          return (
                            <>
                              <div className={styles.prefChips}>
                                {[5, 10, 20].map(v => chip(String(v), count === v, () => setCount(v)))}
                                <input
                                  type="number" min="1" max="20" className={styles.prefNumber} placeholder="custom"
                                  value={[5, 10, 20].includes(count) ? '' : count} disabled={!active}
                                  onChange={(e) => { const v = parseInt(e.target.value, 10); if (Number.isFinite(v) && v > 0) setCount(Math.min(v, 20)); }}
                                />
                              </div>
                              <div className={styles.prefCardFooter}>
                                <button
                                  type="button" className={styles.prefSubmit}
                                  disabled={!active || isRunning(activeChatId)}
                                  onClick={() => {
                                    if (!active) return;
                                    updateMessageById(active.chatId, msg.id, () => ({ isCompaniesAsk: false, companiesAsk: null, status: 'done', done: true }));
                                    runCompanies(active.chatId, msg.companiesAsk.criteria, count, { msgId: msg.id, skipEcho: true });
                                  }}
                                >Find {count} companies</button>
                                <span className={styles.prefCardFooterNote}>Or just type a number. No credits are spent on the list.</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {/* Company-list results - stacked rows with HQ, size, founded + actions */}
                    {msg.isCompanyList && Array.isArray(msg.companyListResults) && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                          {msg.companyListResults.length} {describeCompanyCriteria(msg.companyListCriteria || {})}
                          {msg.companyListRequested && msg.companyListResults.length < msg.companyListRequested ? ` (asked for ${msg.companyListRequested})` : ''}
                        </div>
                        {msg.companyListShortfall && (
                          <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 8 }}>{msg.companyListShortfall}</div>
                        )}
                        {msg.companyListResults.length > 0 && (
                          <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid rgba(128,128,128,0.2)', borderRadius: 10 }}>
                            {msg.companyListResults.map((r, i) => (
                              <div key={r.domain || i} style={{ padding: '9px 12px', borderBottom: i < msg.companyListResults.length - 1 ? '1px solid rgba(128,128,128,0.14)' : 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                                  <span style={{ fontWeight: 600 }}>{i + 1}. {r.name}</span>
                                  {r.domain && <a href={`https://${r.domain}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, opacity: 0.7 }}>{r.domain}</a>}
                                  {r.confidence && r.confidence !== 'high' && (
                                    <span className={styles.prefCardPill} title={r.confidence === 'low' ? 'Headquarters or size could not be confirmed' : 'One detail could not be confirmed'}>{r.confidence === 'low' ? 'unconfirmed' : 'partly confirmed'}</span>
                                  )}
                                </div>
                                <div style={{ fontSize: 12.5, marginTop: 3, lineHeight: 1.45, opacity: 0.85 }}>
                                  {[
                                    [r.hqCity, r.hqState].filter(Boolean).join(', '),
                                    r.employees ? `${r.employees} employees` : '',
                                    r.foundedYear ? `founded ${r.foundedYear}` : '',
                                    r.industry || '',
                                  ].filter(Boolean).join(' · ') || r.why}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {msg.companyListResults.length > 0 && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                            <button type="button" className={styles.prefSubmit} disabled={isRunning(activeChatId)} onClick={() => startCompanyListContacts(activeChatId, msg.companyListCriteria || {}, msg.companyListResults)}>Pull contacts at these</button>
                            <button type="button" className={styles.prefSubmit} disabled={isRunning(activeChatId)} onClick={() => runCompanies(activeChatId, msg.companyListCriteria || {}, msg.companyListRequested || msg.companyListResults.length, { exclude: msg.companyListResults.map(r => r.domain).filter(Boolean) })}>Find {msg.companyListRequested || msg.companyListResults.length} more</button>
                            <button type="button" className={styles.prefSubmit} onClick={() => openCompanyListExport(msg.companyListCriteria || {}, msg.companyListResults)}>Export list</button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Enrich results - preview table + download */}
                    {msg.isEnrichResults && msg.enrichResults && (
                      <div className={styles.enrichResultsBlock}>
                        <div className={styles.enrichSummaryBar}>
                          <span className={`${styles.enrichPill} ${styles.enrichPillGreen}`}>✓ {msg.enrichSummary?.enriched} Enriched</span>
                          <span className={`${styles.enrichPill} ${styles.enrichPillAmber}`}>◐ {msg.enrichSummary?.partial} Partial</span>
                          <span className={`${styles.enrichPill} ${styles.enrichPillRed}`}>✗ {msg.enrichSummary?.notFound} Not Found</span>
                        </div>
                        <div className={styles.enrichTableWrap}>
                          <table className={styles.enrichTable}>
                            <thead>
                              <tr>
                                <th>Company</th>
                                {(msg.enrichSelectedFields || []).map(f => (
                                  <th key={f}>{FIELD_CSV_HEADERS[f] || f}</th>
                                ))}
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {msg.enrichResults.slice(0, 30).map((r, i) => (
                                <tr key={i} className={styles[`enrichRow_${(r.status || '').replace(/ /g,'_').toLowerCase()}`]}>
                                  <td title={r.inputName}>{r.matchedAs || r.inputName || '-'}</td>
                                  {(msg.enrichSelectedFields || []).map(f => (
                                    <td key={f} title={r[f] || ''}>
                                      {r[f] ? (r[f].length > 40 ? r[f].substring(0, 40) + '…' : r[f]) : '-'}
                                    </td>
                                  ))}
                                  <td>
                                    <span className={styles[`enrichStatus_${(r.status || '').replace(/ /g,'_').toLowerCase()}`]}>
                                      {r.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <button
                          className={styles.enrichDownloadBtn}
                          onClick={() => {
                            const { headers, rows } = buildEnrichedExportRows(
                              msg.enrichHeaders, msg.enrichRawData, msg.enrichColIndex,
                              msg.enrichSelectedFields, msg.enrichResults,
                            );
                            setExportPanel({
                              type: 'custom', headers, rows, canSplit: false,
                              defaultFilename: `${String(msg.enrichFileName || 'enriched').replace(/\.(csv|xlsx?)$/i, '')}_enriched`,
                            });
                          }}
                        >
                          ⬇ Download Enriched File
                        </button>
                      </div>
                    )}

                    {/* Website-lookup results - company → website table + CSV download */}
                    {msg.isWebsiteResults && msg.websiteRows?.length > 0 && (
                      <div className={styles.enrichResultsBlock}>
                        <div className={styles.enrichTableWrap}>
                          <table className={styles.enrichTable}>
                            <thead>
                              <tr><th>Company</th><th>Website</th><th>Source</th></tr>
                            </thead>
                            <tbody>
                              {msg.websiteRows.map((r, i) => (
                                <tr key={i}>
                                  <td title={r.company}>{r.company}</td>
                                  <td title={r.website}>
                                    {r.website
                                      ? <a href={`https://${r.website}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{r.website}</a>
                                      : <span className={styles.notFound}>-</span>}
                                  </td>
                                  <td>{r.source}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <button
                          className={styles.enrichDownloadBtn}
                          onClick={() => downloadWebsiteListFile(msg.websiteRows, msg.websiteFileName)}
                        >
                          ⬇ Download Website List (CSV)
                        </button>
                      </div>
                    )}

                    {/* Batch results */}
                    {msg.done && msg.batchResults && (
                      <div className={styles.results}>
                        <div className={styles.section}>
                          <div className={styles.sectionHead}>
                            <span className={styles.dot} style={{ background: 'var(--accent)' }} />
                            <span className={styles.sectionTitle}>Batch results</span>
                            <span className={styles.sectionCount}>
                              {msg.batchResults.contactsFound ?? msg.batchResults.found} contacts
                              {msg.batchResults.linkedinOnlyFound > 0 && (
                                <span style={{ fontWeight: 400, opacity: 0.7, marginLeft: 6 }}>
                                  ({msg.batchResults.verifiedFound} verified · {msg.batchResults.linkedinOnlyFound} LinkedIn only — not charged)
                                </span>
                              )}
                            </span>
                            {/* A partial run's contacts are real and already paid for, so Export
                                works exactly as it does on a complete run. This only offers the
                                missing tail. */}
                            {msg.batchResults.partial && msg.batchResume && (
                              <button
                                className={styles.exportSmall}
                                onClick={() => handleFinishRemaining(activeChatId, msg)}
                                disabled={isRunning(activeChatId)}
                              >Finish remaining</button>
                            )}
                            <button
                              className={styles.exportSmall}
                              onClick={() => setExportPanel({ type: 'batch', batchResults: msg.batchResults, exportKey: `${msg.id}_batch`, canSplit: true, defaultFilename: 'repbuddy-batch' })}
                              disabled={exportingKey !== null}
                            >{exportingKey === `${msg.id}_batch` ? 'Verifying…' : 'Export'}</button>
                          </div>
                          <div className={styles.table}>
                            <div className={styles.batchTableHead}>
                              <span>Company</span><span>Name</span><span>Title</span><span>Email</span><span>Verdict</span>
                            </div>
                            {msg.batchResults.results.map((r, i) => (
                              <div
                                key={i}
                                className={`${styles.tableRow} ${
                                  r.status === 'found'
                                    ? r.tier === 'linkedin_only'     ? styles.row_linkedinOnly
                                    : r.result?.verdict === 'valid'  ? styles.row_valid
                                    : r.result?.verdict === 'risky'  ? styles.row_risky
                                    : styles.row_invalid
                                    : styles.row_notfound
                                }`}
                              >
                                <span className={styles.cellName}>
                                  {r.company}
                                  {/* "Both"-mode backfill: this contact was delivered before and
                                      is re-sent free to fill the company's quota. */}
                                  {r.repeat && <span className={styles.repeatBadge} title="Already delivered to you before - re-sent to fill the gap, not charged again"> repeat</span>}
                                </span>
                                <span className={styles.cellMeta}>{r.status === 'found' ? r.contact?.name  || '-' : '-'}</span>
                                <span className={styles.cellMeta}>{r.status === 'found' ? r.contact?.title || '-' : '-'}</span>
                                <span className={styles.cellEmail}>
                                  {r.status === 'found'
                                    ? r.tier === 'linkedin_only'
                                      ? (deliverableLinkedinUrl(r.contact)
                                          ? <a href={deliverableLinkedinUrl(r.contact)} target="_blank" rel="noopener noreferrer" className={styles.linkedinLink}>LinkedIn →</a>
                                          : <span className={styles.notFound}>LinkedIn profile not found</span>)
                                      : r.result?.email || '-'
                                    : <span className={styles.notFound}>{r.message || 'Not found'}</span>}
                                </span>
                                <span className={styles.cellMeta}>
                                  {r.status === 'found'
                                    ? r.tier === 'linkedin_only' ? 'LinkedIn only' : r.result?.verdict || '-'
                                    : '-'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Message-level copy - hover-revealed, only once the message is done
                        and only when it has copyable prose (see assistantCopyText). */}
                    {msg.done && (() => {
                      const copyText = assistantCopyText(msg);
                      // Text replies only: anything a rerun could spend credits on is edited, not
                      // regenerated (see regenerateSource).
                      const canRegen = !!regenerateSource(activeChat, msgIndex);
                      return copyText || canRegen ? (
                        <div className={styles.msgCopyRow}>
                          {copyText && (
                            <CopyButton
                              text={copyText}
                              className={styles.msgCopyBtn}
                              doneClass={styles.msgCopyBtnDone}
                              label="⧉ Copy"
                              doneLabel="✓ Copied"
                              title="Copy this response"
                              ariaLabel="Copy this response to clipboard"
                            />
                          )}
                          {canRegen && (
                            <button
                              type="button"
                              className={styles.msgCopyBtn}
                              disabled={isRunning(activeChatId)}
                              onClick={() => handleRegenerate(activeChatId, msgIndex)}
                              title="Write this response again"
                            >↻ Regenerate</button>
                          )}
                        </div>
                      ) : null;
                    })()}

                  </div>
                )}

              </div>
            ))}

            <div ref={bottomRef} />
          </div>
        </div>

        {/* Input bar */}
        <div className={styles.inputWrap}>

          {/* Live progress indicator - reassures the user the tool is still working during
              longer live searches/verification, with an elapsed-time counter.
              The origin comes off the running message rather than from this component's own
              state, so the count survives the remounts a background token refresh causes. */}
          {isRunning(activeChatId) && (
            <WorkingIndicator
              since={[...messages].reverse()
                .find(m => m.role === 'assistant' && !m.done && m.startedAt)?.startedAt}
            />
          )}

          {/* Attachment chip - shown above input when file is loaded */}
          {attachment && (() => {
            const tags = [];
            if (attachment.hasDesignation) tags.push('🎯 designation');
            if (attachment.hasLocation)    tags.push('📍 location');
            if (attachment.hasCount)       tags.push('🔢 count');

            return (
              <div className={styles.attachmentChipRow}>
                <div className={styles.attachmentChip}>
                  <span className={styles.attachIcon}>📄</span>
                  <span className={styles.attachName}>{attachment.name}</span>
                  <span className={styles.attachCount}>
                    {attachment.type === 'persons'
                      ? `${attachment.persons.length} contacts`
                      : attachment.type === 'emails'
                      ? `${attachment.emails.length} emails`
                      : `${attachment.companies.length} companies`}
                  </span>
                  {tags.map(t => <span key={t} className={styles.attachTag}>{t}</span>)}
                  <button className={styles.attachRemove} onClick={() => { setAttachment(null); setPendingBatch(null); setAttachmentAction(null); }}>✕</button>
                </div>
              </div>
            );
          })()}

          {/* Attachment action buttons */}
          {attachment && !isRunning(activeChatId) && (
            <div className={styles.attachActions}>
              {attachment.type === 'persons' ? (
                <button
                  className={styles.attachActionBtn}
                  onClick={() => handleSend('Named contacts lookup')}
                >
                  Look Up All Contacts
                </button>
              ) : attachment.type === 'emails' ? (
                <button
                  className={styles.attachActionBtn}
                  onClick={() => handleSend('Verify emails')}
                >
                  Verify All Emails
                </button>
              ) : (
                <>
                  <button
                    className={styles.attachActionBtn}
                    onClick={() => handleSend('enrich')}
                  >
                    Enrich Companies
                  </button>
                  <button
                    className={`${styles.attachActionBtn} ${attachmentAction === 'find_contacts' ? styles.attachActionBtnActive : ''}`}
                    onClick={handleFindContactsClick}
                  >
                    Find Contacts
                  </button>
                  <button
                    className={styles.attachActionBtn}
                    onClick={() => handleSend('find websites')}
                  >
                    Find Websites
                  </button>
                </>
              )}
              {attachmentAction === 'find_contacts' && (
                <span className={styles.attachHint}>What job title should I search for?</span>
              )}
            </div>
          )}

          {/* "Waiting for count" indicator */}
          {pendingBatch && !attachment && (
            <div className={styles.attachmentChipRow}>
              <div className={styles.attachmentChip} style={{ borderColor: 'rgba(108,99,255,0.5)' }}>
                <span className={styles.attachIcon}>⏳</span>
                <span className={styles.attachName}>{pendingBatch.fileName}</span>
                <span className={styles.attachCount}>{pendingBatch.companies.length} companies</span>
                <button className={styles.attachRemove} onClick={() => setPendingBatch(null)}>✕</button>
              </div>
            </div>
          )}

          {/* ── Active constraint panel ── */}
          {(() => {
            const ac = activeChat?.active_constraints;
            if (!ac || !messages.length) return null;
            const hasAny = (ac.titles?.length > 0) || (ac.location?.length > 0) || ac.industry || ac.company || ac.employeeRange || ac.revenueRange || ac.stage || ac.city;
            if (!hasAny) return null;
            return (
              <div className={styles.constraintPanel}>
                <span className={styles.constraintLabel}>Active filters:</span>
                {(ac.titles || []).map(t => (
                  <span key={t} className={styles.constraintChip}>
                    <span className={styles.constraintChipType}>Title</span>
                    {t}
                    <button className={styles.constraintChipX} onClick={() => removeConstraint('titles', t)} title="Remove filter">×</button>
                  </span>
                ))}
                {(ac.location || []).map(s => (
                  <span key={s} className={styles.constraintChip}>
                    <span className={styles.constraintChipType}>State</span>
                    {s}
                    <button className={styles.constraintChipX} onClick={() => removeConstraint('location', s)} title="Remove filter">×</button>
                  </span>
                ))}
                {ac.industry && (
                  <span className={styles.constraintChip}>
                    <span className={styles.constraintChipType}>Industry</span>
                    {ac.industry}
                    <button className={styles.constraintChipX} onClick={() => removeConstraint('industry', null)} title="Remove filter">×</button>
                  </span>
                )}
                {ac.company && (
                  <span className={styles.constraintChip}>
                    <span className={styles.constraintChipType}>Company</span>
                    {ac.company}
                    <button className={styles.constraintChipX} onClick={() => removeConstraint('company', null)} title="Remove filter">×</button>
                  </span>
                )}
                {ac.employeeRange && (
                  <span className={styles.constraintChip}>
                    <span className={styles.constraintChipType}>Size</span>
                    {ac.employeeRange.replace(',', '–')} employees
                    <button className={styles.constraintChipX} onClick={() => removeConstraint('employeeRange', null)} title="Remove filter">×</button>
                  </span>
                )}
                {ac.revenueRange && (
                  <span className={styles.constraintChip}>
                    <span className={styles.constraintChipType}>Revenue</span>
                    {/* "–$10M" reads as "under $10M", "$500M–" as "over $500M" */}
                    {formatRevenueRange(ac.revenueRange) || ac.revenueRange}
                    <button className={styles.constraintChipX} onClick={() => removeConstraint('revenueRange', null)} title="Remove filter">×</button>
                  </span>
                )}
                {ac.stage && (
                  <span className={styles.constraintChip}>
                    <span className={styles.constraintChipType}>Stage</span>
                    {ac.stage === 'smb' ? 'small companies' : ac.stage === 'midmarket' ? 'mid-market' : ac.stage}
                    <button className={styles.constraintChipX} onClick={() => removeConstraint('stage', null)} title="Remove filter">×</button>
                  </span>
                )}
                {ac.city && (
                  <span className={styles.constraintChip}>
                    <span className={styles.constraintChipType}>City</span>
                    {ac.city}
                    <button className={styles.constraintChipX} onClick={() => removeConstraint('city', null)} title="Remove filter">×</button>
                  </span>
                )}
                <div className={styles.presetActions}>
                  <button className={styles.presetSaveBtn} onClick={() => { setShowPresetSave(v => !v); setShowPresetMenu(false); }} title="Save as preset">
                    Save preset
                  </button>
                  {showPresetSave && (
                    <div className={styles.presetSaveRow}>
                      <input
                        className={styles.presetNameInput}
                        placeholder="Preset name…"
                        value={presetName}
                        onChange={e => setPresetName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSavePreset(); if (e.key === 'Escape') setShowPresetSave(false); }}
                        autoFocus
                      />
                      <button className={styles.presetSaveConfirm} onClick={handleSavePreset} disabled={!presetName.trim()}>Save</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ── Preset loader (always visible when presets exist) ── */}
          {presets.length > 0 && (
            <div className={styles.presetLoaderRow}>
              <button className={styles.presetLoadBtn} onClick={() => { setShowPresetMenu(v => !v); setShowPresetSave(false); }}>
                Load preset ▾
              </button>
              {showPresetMenu && (
                <div className={styles.presetDropdown}>
                  {presets.map(p => (
                    <div key={p.id} className={styles.presetDropdownItem}>
                      <span className={styles.presetDropdownName} onClick={() => handleLoadPreset(p)}>{p.name}</span>
                      <button className={styles.presetDropdownDelete} onClick={() => handleDeletePreset(p.id)} title="Delete preset">×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Session-level export — aggregates chat contacts across searches and batches */}
          {sessionChatContacts.length > 0 && (
            <div className={styles.sessionExportRow}>
              <button
                className={styles.sessionExportBtn}
                onClick={() => setExportPanel({
                  type: 'contacts',
                  contacts: sessionChatContacts,
                  exportKey: 'session_all',
                  msgId: null,
                  canSplit: true,
                  defaultFilename: 'repbuddy-session',
                })}
                disabled={exportingKey !== null}
              >
                {exportingKey === 'session_all' ? 'Verifying…' : `Export all · ${sessionChatContacts.length} contact${sessionChatContacts.length !== 1 ? 's' : ''} from this chat`}
              </button>
            </div>
          )}

          <div className={styles.inputRow} data-tour="chat-input">

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {/* Attach button */}
            <button
              className={styles.attachBtn}
              onClick={() => fileInputRef.current?.click()}
              disabled={isRunning(activeChatId) || !!pendingBatch}
              title="Attach CSV or XLSX company list"
              data-tour="attach"
            >
              <svg viewBox="0 0 16 16" fill="none" width="15" height="15">
                <path
                  d="M13.5 7.5l-6 6a4 4 0 01-5.657-5.657l6-6a2.5 2.5 0 013.535 3.535l-6 6a1 1 0 01-1.414-1.414l5.5-5.5"
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                />
              </svg>
            </button>

            <textarea
              ref={inputRef}
              className={styles.input}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={
                pendingBatch
                  ? 'How many contacts per company? (or press Enter for 1)'
                  : attachment
                  ? (attachment.hasDesignation
                      ? 'Press Enter for 1 per company, or type a count to override'
                      : 'Type a job title (e.g. "VP of Sales"), or type "enrich"')
                  : isNarrowScreen()
                  ? 'Find contacts, e.g. "10 VPs at Cisco"'
                  : 'What contacts do you need? e.g. "Find 10 VPs of Sales at Cisco"'
              }
              rows={1}
              disabled={isRunning(activeChatId)}
            />

            {isRunning(activeChatId) ? (
              <button
                className={styles.stopBtn}
                onClick={handleStop}
                title="Stop generation"
              >
                <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                  <rect x="2.5" y="2.5" width="11" height="11" rx="2" />
                </svg>
              </button>
            ) : (
              <button
                className={styles.sendBtn}
                onClick={() => pendingBatch ? handleSend(input.trim() || '1') : handleSend()}
                disabled={(!input.trim() && !pendingBatch) || isRunning(activeChatId)}
              >
                <svg viewBox="0 0 16 16" fill="none" width="15" height="15">
                  <path d="M2 8h12M9 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>

          <p className={styles.hint}>
            Ask for leads by title and location, or attach a CSV to enrich and verify - your agents handle the rest.
          </p>
        </div>

      </div>
    </div>

    {/* ── Export preview panel ───────────────────────────────────────────── */}
    {exportPanel && (
      <ExportPanel
        contacts={exportPanel.type === 'contacts' ? exportPanel.contacts : null}
        batchResults={exportPanel.type === 'batch' ? exportPanel.batchResults : null}
        customHeaders={exportPanel.type === 'custom' ? exportPanel.headers : null}
        customRows={exportPanel.type === 'custom' ? exportPanel.rows : null}
        defaultFilename={exportPanel.defaultFilename}
        canSplit={exportPanel.canSplit}
        isBatch={exportPanel.type === 'batch'}
        isDownloading={exportingKey !== null}
        onClose={() => setExportPanel(null)}
        onDownload={(cfg) => {
          const { type, contacts, batchResults, exportKey, msgId, headers, rows } = exportPanel;
          setExportPanel(null);
          if (type === 'custom')     exportRowsConfigured(headers, rows, cfg);
          else if (type === 'batch') exportBatchCSV(batchResults, exportKey, cfg);
          else                       exportCSV(contacts, exportKey, msgId, cfg);
        }}
        hubspotConnected={hubspotConnected}
        onPushToHubSpot={pushToHubspot}
        isPushingToHubspot={isPushingToHubspot}
      />
    )}
    <HubSpotToast toast={hubspotToast} onDismiss={() => setHubspotToast(null)} />
    <OnboardingTour steps={TOUR_STEPS} isOpen={tourOpen} onClose={closeTour} />
    </>
  );
}

// ─── Helper - extract LinkedIn username from a profile URL ───────────────────

function extractLinkedInUsername(url) {
  if (!url) return '';
  const m = String(url).match(/linkedin\.com\/in\/([a-zA-Z0-9\-_%]+)/i);
  return m ? m[1] : '';
}

// ─── Contact Row ──────────────────────────────────────────────────────────────
// Each row shows name / title / company / email / action buttons.
// The "Li" button triggers a LinkdAPI employment check and renders a sub-row
// with the result - whether the person is still at the same company.

function draftStorageKey(email) {
  return email ? `repbuddy:draft:${email.toLowerCase()}` : null;
}
function readDraftFromStorage(email) {
  try { const k = draftStorageKey(email); return k ? sessionStorage.getItem(k) || null : null; } catch { return null; }
}
function writeDraftToStorage(email, text) {
  try { const k = draftStorageKey(email); if (k && text) sessionStorage.setItem(k, text); } catch {}
}

// Editable outreach draft for the Research This Person card.
// Extracted as a component so it can hold its own edited-text + copied state.
function ResearchDraftBlock({ initialDraft, styles, onEdit }) {
  const [draft,  setDraft]  = useState(initialDraft);
  const [copied, setCopied] = useState(false);
  const taRef = useRef(null);

  useEffect(() => {
    if (!taRef.current) return;
    taRef.current.style.height = 'auto';
    taRef.current.style.height = taRef.current.scrollHeight + 'px';
  }, [draft]);

  // Persist on blur so the edit survives reload; skip a no-op focus/blur.
  const commit = () => {
    if (onEdit && draft !== initialDraft) onEdit(draft);
  };

  return (
    <div className={styles.researchSection}>
      <div className={styles.outreachDraftHeader}>
        <span className={styles.researchSectionLabel}>Draft Outreach</span>
        <button
          className={styles.copyDraftBtn}
          onClick={() => {
            navigator.clipboard.writeText(draft);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >{copied ? 'Copied!' : 'Copy'}</button>
      </div>
      <textarea
        ref={taRef}
        className={styles.outreachDraftText}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        spellCheck
      />
    </div>
  );
}

function ContactRow({ contact, result, type, styles, onVerified, onEmpVerified, sellerProfile, cachedDraft, onDraftReady }) {
  // empStatus: null | 'checking' | { confirmed, currentCompany, currentTitle, linkedinUrl, checkedAt, remaining } | { error }
  const [empStatus,        setEmpStatus]        = useState(null);
  const [correctedLiUrl,   setCorrectedLiUrl]   = useState(null);
  const [removed,          setRemoved]          = useState(false);
  const [draftOpen,   setDraftOpen]   = useState(false);
  const [draftLoading,setDraftLoading]= useState(false);
  const [draftText,   setDraftText]   = useState(
    cachedDraft || readDraftFromStorage(result?.email) || null
  );
  const [draftError,  setDraftError]  = useState(null);
  const [draftCopied, setDraftCopied] = useState(false);
  const draftTaRef = useRef(null);

  useEffect(() => {
    if (!draftTaRef.current || !draftOpen) return;
    draftTaRef.current.style.height = 'auto';
    draftTaRef.current.style.height = draftTaRef.current.scrollHeight + 'px';
  }, [draftText, draftOpen]);

  const linkedinUsername = extractLinkedInUsername(contact.linkedinUrl);

  if (removed) return null;

  function copy() {
    navigator.clipboard.writeText(result.email || '').then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  }

  async function checkEmployment() {
    const slugToUse = correctedLiUrl ? extractLinkedInUsername(correctedLiUrl) : linkedinUsername;
    if (!slugToUse || empStatus === 'checking') return;
    setEmpStatus('checking');
    try {
      const data = await verifyEmployment(slugToUse, contact.company, contact.name || '');
      if (data.unverifiable) {
        setRemoved(true);
        return;
      }
      setEmpStatus(data);
      if (data.correctedLinkedinUrl) setCorrectedLiUrl(data.correctedLinkedinUrl);
      if (data.remaining != null && onVerified) onVerified(data.remaining);
      if (onEmpVerified && result?.email) {
        onEmpVerified(result.email, {
          status:    data.status,
          confirmed: data.confirmed,
          company:   data.currentCompany,
          title:     data.currentTitle,
        });
      }
    } catch (err) {
      setEmpStatus({ error: err.message });
    }
  }

  // Format a short month+year from an ISO timestamp
  function fmtDate(iso) {
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } catch { return ''; }
  }

  async function handleDraftClick() {
    if (draftText) {
      setDraftOpen(prev => !prev);
      return;
    }
    setDraftLoading(true);
    setDraftError(null);
    try {
      const profile = {
        name:        contact.name       || '',
        title:       contact.title      || '',
        company:     contact.company    || '',
        headline:    contact.headline   || '',
        linkedinUrl: contact.linkedinUrl || '',
      };
      const data = await linkedinResearch({ profile, sellerProfile });
      const text = data.outreachDraft || '';
      if (!text) {
        setDraftError('Draft generation failed - please try again.');
      } else {
        setDraftText(text);
        setDraftOpen(true);
        writeDraftToStorage(result?.email, text);
        if (onDraftReady && result?.email) {
          onDraftReady(result.email, text);
        }
      }
    } catch (err) {
      setDraftError(err.message);
    } finally {
      setDraftLoading(false);
    }
  }

  return (
    <div className={styles.contactWrapper}>

      {/* ── Main row ── */}
      <div className={`${styles.tableRow} ${styles['row_' + type]}`}>
        <span className={styles.cellName}>
          {contact.name || '-'}
          {contact.source === 'Gemini-permutation' && (
            <span className={styles.geminiTag} title="Email found via AI-assisted permutation + verification">AI</span>
          )}
          {/* Where the NAME came from, which is a different question from whether the address
              verified. Both of these carry a pattern-inferred email rather than a purchased
              one, so they must not look identical to a database row - but "read off the
              company's LinkedIn roster" is stronger evidence than "inferred from the web",
              and the badge says which. */}
          {contact.source === 'linkedin-roster' && (
            <span className={styles.geminiTag} title="Found on the company's LinkedIn employee list; email inferred from the company pattern and verified">Li</span>
          )}
          {/* Found by where they LIVE: a LinkedIn people search filtered on the city asked for,
              then checked for their current employer and title before the address was found. */}
          {contact.source === 'linkedin-search' && (
            <span className={styles.geminiTag} title="Found on LinkedIn by where they live; current employer and title checked, email found and verified">Li</span>
          )}
          {contact.source === 'web-search' && (
            <span className={styles.geminiTag} title="Found via web search; email inferred from the company pattern and verified">Web</span>
          )}
        </span>
        <span className={styles.cellMeta}>
          {contact.title   || '-'}
          {/* Where the person is, when a check placed them. Lead411 rows carry only the company's
              city, which is what an export once stamped on someone in Sydney. */}
          {(contact.location || contact.employmentCheck?.currentLocation) && (
            <span className={styles.cellSub} title="Where LinkedIn places this person"> · 📍 {contact.location || contact.employmentCheck.currentLocation}</span>
          )}
        </span>
        <span className={styles.cellMeta}>
          {(empStatus && typeof empStatus === 'object' && !empStatus.error && !empStatus.confirmed && empStatus.currentCompany)
            ? empStatus.currentCompany
            : contact.company || '-'}
          {/* Only when the industry verdict is worth a second look. A confident match is
              the expected case and stays silent - badging every row would train people to
              stop reading them. Hover carries the evidence and who supplied it. */}
          {shouldFlagIndustry(contact) && (
            <span className={styles.geminiTag} title={industryTooltip(contact)}>
              {contact.industryClass?.verdict === 'mismatch' ? '!' : '?'}
            </span>
          )}
        </span>
        <span className={styles.cellEmail}>{result.email   || '-'}</span>
        <span className={styles.cellAction}>
          {type !== 'invalid' && result.email && (
            /* Short labels - this button sits in a fixed-width action column. */
            <CopyButton
              text={result.email}
              className={styles.copyBtn}
              doneClass={styles.copyBtnDone}
              doneLabel="✓"
              failedLabel="✕"
              resetMs={1500}
              title={`Copy ${result.email}`}
              ariaLabel={`Copy email address ${result.email}`}
            />
          )}
          {linkedinUsername && (
            <button
              className={styles.verifyEmpBtn}
              onClick={checkEmployment}
              disabled={empStatus === 'checking'}
              aria-busy={empStatus === 'checking'}
              title={empStatus === 'checking' ? 'Checking LinkedIn…' : 'Check if still employed here via LinkedIn'}
            >
              {empStatus === 'checking'
                ? <span className={styles.btnSpinner} aria-label="Checking" />
                : 'Li'}
            </button>
          )}
          {/* Drafting calls the model and can take 10-30s, so the button shows a
              live spinner rather than going quiet until the panel appears. */}
          <button
            className={styles.draftBtn}
            onClick={handleDraftClick}
            disabled={draftLoading}
            aria-busy={draftLoading}
            aria-expanded={draftText ? draftOpen : undefined}
            title={draftLoading
              ? 'Writing your draft — this can take a few seconds'
              : draftText
                ? (draftOpen ? 'Hide this draft' : 'Show the draft again')
                : 'Draft a cold email to this contact'}
          >
            {draftLoading
              ? <span className={styles.btnSpinner} aria-label="Writing draft" />
              : draftText ? (draftOpen ? 'Hide' : 'Draft ✓') : 'Draft'}
          </button>
        </span>
      </div>

      {/* ── Employment verification sub-row ── */}
      {empStatus && empStatus !== 'checking' && (
        <div className={`${styles.employmentRow} ${
          empStatus.error
            ? styles.empError
            : empStatus.confirmed
              ? styles.empConfirmed
              : styles.empUnconfirmed
        }`}>
          {empStatus.error ? (
            <>
              <span className={styles.empIcon}>⚠</span>
              <span className={styles.empText}>Could not verify: {empStatus.error}</span>
            </>
          ) : empStatus.confirmed ? (
            <>
              <span className={styles.empIcon}>✓</span>
              <span className={styles.empText}>
                Still at <strong>{empStatus.currentCompany}</strong>
                {empStatus.currentTitle ? ` · ${empStatus.currentTitle}` : ''}
                {empStatus.currentLocation ? ` · 📍 ${empStatus.currentLocation}` : ''}
              </span>
              <span className={styles.empMeta}>· {fmtDate(empStatus.checkedAt)}</span>
            </>
          ) : (
            <>
              <span className={styles.empIcon}>⚠</span>
              <span className={styles.empText}>
                LinkedIn shows: <strong>{empStatus.currentCompany || 'unknown company'}</strong>
                {empStatus.currentTitle ? ` · ${empStatus.currentTitle}` : ''}
                {empStatus.currentLocation ? ` · 📍 ${empStatus.currentLocation}` : ''}
              </span>
              <span className={styles.empMeta}>· {fmtDate(empStatus.checkedAt)}</span>
            </>
          )}

          {/* Found via name search note */}
          {empStatus.foundViaSearch && (
            <span className={styles.empFoundViaSearch}>· found via name search</span>
          )}

          {/* Link to LinkedIn profile - prefer corrected URL if found via search.
              Suppressed when the verdict is "unverifiable": we fetched that profile and got
              nothing, so the link almost certainly 404s. A corrected URL found via name search
              is fine to show - that one we resolved. */}
          {!empStatus.error &&
           (correctedLiUrl || shouldShowLinkedinUrl(empStatus)) &&
           (correctedLiUrl || empStatus.linkedinUrl || contact.linkedinUrl) && (
            <a
              href={correctedLiUrl || empStatus.linkedinUrl || contact.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.linkedinLink}
            >
              View ↗
            </a>
          )}

          {/* Dismiss */}
          <button
            className={styles.empDismiss}
            onClick={() => setEmpStatus(null)}
            title="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Drafting progress ──
          The model call runs 10-30s. Without this the row goes quiet after the
          click and the wait reads as a hang. */}
      {draftLoading && (
        <div className={styles.draftLoadingRow} role="status" aria-live="polite">
          <span className={styles.btnSpinner} aria-hidden="true" />
          <span>Writing a draft for {contact.name || 'this contact'}…</span>
          <span className={styles.draftLoadingHint}>this takes a few seconds</span>
        </div>
      )}

      {/* ── Email draft inline panel ── */}
      {draftOpen && draftText && (
        <div className={styles.draftPanel}>
          <div className={styles.draftPanelHeader}>
            <span className={styles.draftPanelLabel}>Email Draft</span>
            <CopyButton
              text={draftText}
              className={styles.copyDraftBtn}
              onClick={() => {
                navigator.clipboard.writeText(draftText);
                setDraftCopied(true);
                setTimeout(() => setDraftCopied(false), 1500);
              }}
            >{draftCopied ? 'Copied!' : 'Copy'}</CopyButton>
          </div>
          <textarea
            ref={draftTaRef}
            className={styles.draftPanelText}
            value={draftText}
            onChange={e => setDraftText(e.target.value)}
            spellCheck
          />
        </div>
      )}
      {draftError && !draftOpen && (
        <div className={styles.draftErrorRow}>
          <span>Draft failed: {draftError}</span>
          <button className={styles.empDismiss} onClick={() => setDraftError(null)}>✕</button>
        </div>
      )}

    </div>
  );
}
